<template>
    <div v-if="pageData.data" class="africa-digital-regulations">
        <div class="hero">
            <figure class="image-hero">
                <img src="https://carnegie-production-assets.s3.amazonaws.com/static/media/images/AfricaTechTracker_Header.png" alt="Map of Africa with digital connections" class="image">
            </figure>
        </div>
        <MainWithAsidesTier>
            <template #main-left>
            </template>
            <template #main-center>
                <slot name="main-center">
                    <h2>{{ pageData.data.title }}</h2>
                    <RichTextElements 
                        :elements="pageData.data.dek" 
                        bodyStyle="sans-large"
                        pageContext="publication"
                        class="dek" 
                    />
                    <div class="rich-text">
                        <p>AfTech is a research and policy tool that advances evidence-based policymaking in Africa’s rapidly evolving digital economy by providing a one-stop repository of national and continental technology frameworks. By cataloging policies across key pillars like digital infrastructure, platforms, skills, and innovation, AfTech makes it easy to explore, compare, and analyze policy actions shaping the continent’s digital future. AfTech offers valuable insights into the diverse approaches countries are taking toward digital governance. Our aim is to enhance visibility on the burgeoning digital policy environment in Africa and for AfTech to become an essential resource that informs decisionmaking, fosters cross-border collaboration, and helps guide the continent toward a more integrated and resilient digital economy. </p>
                        <p>Dive in and discover how Africa is advancing in the digital age.</p>
                    </div>
                    
                </slot>
            </template>
            <template #main-right>
                <div class="partnership">
                    <Typography variant="body-type-micro">In partnership with:</Typography>
                    <div class="partner-logo"></div>
                </div>
                <div class="about-button">
                    <a class="button teal overlay-trigger" href="#my-overlay-content">About the Tracker</a>
                    <div class="overlay-content" id="my-overlay-content">
                        <div class="banded padded"> 
                            <p> <em>Thanks for interacting with the Africa Tech Policy Tracker. Please note that this is an ongoing project. Our team periodically updates this data set with new legal documents as they become available. For questions and comments, email the project’s contacts.</em></p>
                            <h4>Introduction</h4>
                            <p>The African continent is undergoing a digital revolution. Increasing access to digital products and services has the potential to transform lives and economies, and African governments have developed policies to fully harness the impact of this digital revolution. As a result, the continent is rife with regional and national strategies envisioning digital transformations, as well as legal frameworks to define the rules for digital transactions and services. However, there remains neither a comprehensive resource for tracking technological policy strategies, nor a mechanism for peer learning among African countries on the policies that can best foster digital innovation. </p>
                            <p>To address this need, the Carnegie Africa Program, in collaboration with the Africa Telecommunications Union (ATU), has developed the Africa Technology Policy Tracker (AfTech), the first-ever continent-wide aggregate of digital economy laws, policies, and regulations. </p>
                            <p>The objectives of the research are:</p>
                            <ol> 
                                <li>to provide a resource for accessing digital economy–relevant policies, laws, and regulations in the African continent, and a tool to support comparative policy analysis and tracking of digital economy regulations;</li>
                                <li>to enhance visibility on the burgeoning tech regulatory environment in Africa by tracking the policies, laws, and regulatory actions of African governments; and</li>
                                <li>to support knowledge dissemination and learning among African policymakers and stakeholders.</li>
                            </ol>
                            <h4>The Africa Tech Regulation Tracker  </h4>
                            <p>AfTech is a one-stop repository of national and continental technology frameworks in Africa. It is a research and policy tool that advances evidence-based policymaking in Africa's rapidly evolving digital economy.  By cataloging policies across four main pillars digital infrastructure, platforms, skills, and innovation, AfTech makes it easy to explore, compare, and analyze policy actions shaping the continent’s digital future.  </p>
                            <h4>Methodology</h4>
                            <p>Our approach to developing AfTech is summarized in four main steps:</p>
                            <img class="gutters" src="https://ceipimg.s3.amazonaws.com/static/ATRT_About_Figures-01.png?v=1">
                            <ol> 
                                <li>Develop the analytical framework by synthesizing global and regional digital economy frameworks.</li>
                                <li>Develop inclusion criteria based on digital economy policies, strategies, laws, and regulations that have been enacted. The requirements are based on the policy and governance framework dimensions that define priority themes, as well as relevant timelines and content formats.</li>
                                <li>Using the analytical framework and inclusion criteria: <br />
                                    <ol>
                                        <li><em>Pilot Phase:</em> undertake desktop research; consideration is given to documents that offer English versions. Once documents (or information) are identified, they are reviewed to ensure they meet the inclusion criteria.</li>
                                        <li><em>Current Phase:</em> collaboration with the ATU and country focal points—country representatives appointed by their government—to collect data through questionnaires.  </li>
                                    </ol>
                                </li>
                                <li>Finally, materials are added to the database. This involves extracting metadata, classifying according to the analytical framework, and tagging with search data points and source links.</li>
                            </ol>
                            <h4>The Data  </h4>
                            <p>AfTech is designed to cover all fifty-five African countries, the current phase covers thirty-six countries with over 550 data points on digital economy policies, laws, and regulations published between 1988-2024. The data is classified under four pillars and fifty data points that help users to search using thematic areas, such as artificial intelligence or cyber security (see glossary for full list of key data points).</p>
                            <ol> 
                                <li> <strong>Pillars: </strong>This study has developed an analytical framework defined by four pillars and nine sub-pillars.<img class="gutter-top gutter-bottom" src="https://ceipimg.s3.amazonaws.com/static/ATRT_About_Figures-02.png?v=1"></li>
                                <li> <strong>The Pilot (2022–23)</strong><br /><br />The project started with development of a pilot consisting of eight pilot countries (Egypt, Kenya, Mauritius, Nigeria, South Africa, Rwanda and Senegal) that were chosen with the following criteria:<br />
                                    <ol> 
                                        <li>Geographical representation </li>
                                        <li>A mix of digital development indicators (use of information and communication technology [ICT], contribution of ICT to GDP)</li>
                                        <li>The presence of national digital policies </li>
                                    </ol>
                                    <br />
                                    The pilot phase was undertaken in FY 2022/2023 with a private launch on October 18, 2023. The Africa Program at the Carnegie Endowment for International Peace, in collaboration with the ATU, co-hosted a roundtable on Tracking Digital Technologies in Africa to launch the tracker prototype and to engage African policymakers and digital experts in a comprehensive discussion on the tracker. The event was on the sidelines of the Mobile World Congress Kigali 2023 Policy Leaders Forum and brought together stakeholders from government, civil society, and the private sector, including mobile operators, device manufacturers, and technology providers. AfTech was presented to select stakeholders who interacted with the tracker and provided vital feedback for the tracker’s further development. 
                                </li>
                                <li><strong>Current Phase: (2024–2025)</strong><br /><br />
                                In recognizing the need to collect accurate and up-to-date information to construct the AfTech data base, the African Program at Carnegie collaborated with the ATU, the specialized agency of the African Union (AU) in the field of telecommunications. The ATU is the leading continental organization fostering the development of information and communication technologies infrastructure and services. The agency draws its membership from the AU currently has forty-eight Member States and fifty-four Associate Members (comprising fixed and mobile telecom operators). <br /><br />
                                    The mission of the ATU is to promote the rapid development of info-communications in Africa to achieve universal access, and full inter-country connectivity. Through its member states, the ATU designated two experts in ICT and digital policies and regulations to serve as focal points for this project. The focal points played a crucial role in contributing to the database. Through online meetings focal points attended a workshop for sensitization on the project and data collection. Utilizing data collection instruments (via Google form and Excel spreadsheet) and supporting documents (FAQs and data collection dictionary available in English and French) focal points submitted data points from their respective countries for the current phase between April and August 2024. Additional data points collected by the ATU secretariat and the Carnegie Africa Program were also included. <br /><br />
                                    The tracker consists of thirty-six countries and over 500 data points. Additionally, the tracker consists of ten regional data point documents developed by the African Union. <br /><br />
                                    African Union <br />
                                    Algeria<br />
                                    Angola<br />
                                    Benin<br />
                                    Burundi<br />
                                    Cameroon<br />
                                    Chad<br />
                                    Côte d'Ivoire <br />
                                    Country<br />
                                    DRC<br />
                                    Egypt<br />
                                    Eswatini<br />
                                    Gabon<br />
                                    Gambia<br />
                                    Ghana<br />
                                    Guinea-Bissau<br />
                                    Kenya<br />
                                    Lesotho<br />
                                    Libya<br />
                                    Mali<br />
                                    Mauritania<br />
                                    Mauritius<br />
                                    Morocco<br />
                                    Mozambique<br />
                                    Namibia<br />
                                    Nigeria<br />
                                    Rwanda<br />
                                    Senegal<br />
                                    Somalia<br />
                                    South Africa<br />
                                    Sudan<br />
                                    Tanzania<br />
                                    Togo<br />
                                    Tunisia<br />
                                    Uganda<br />
                                    Zambia<br />
                                    Zimbabwe
                                </li>
                                <li> <strong>Search and Data Points: </strong><br /><br />A search can be undertaken with pillars, sub-pillars, and key data points. A full list of the data points is available in the glossary section of this page.
                                </li>
                                <li><strong>Next Phase</strong><br /><br />
                                The next phase of the tracker will include:<br />
                                <ol>
                                    <li>Country representation to cover all African countries and other regional documents by organizations such as SMART Africa.</li>
                                    <li>Thematic analysis: The project will provide research and analytical works that provide insights to help accelerate Africa’s digital transformation. For countries to effectively leverage digital technologies to transform the public and private sectors, as well as improve the quality of life for their citizens, governments need to build a solid foundation that fosters innovation and fuels the digital economy. This foundation consists of an enabling legal and regulatory framework.</li></ol>
                                </li>
                                <li><strong>Thanks</strong><br /><br />
                                    The completion of this project was the result of several months of collaborative work between the teams of the African Telecommunications Union (ATU) and Carnegie Endowment for International Peace with the great support of the Union Member States to whom we would like to express our deep gratitude and our sincere thanks.<br /><br />
                                    By the work of collecting key information on the digital policies and strategies for approximately thirty-six African countries, we are delighted to have succeeded today in developing the first African platform that describes the digital landscape on the continent, and which will not only serve as a reference for measuring digital development in Africa but will provide the necessary research and analysis elements for the policy makers, researchers, and all stakeholders.<br /><br />
                                    A special thanks to the representatives of the ATU Member States who gave their time in collecting and transmitting data that contributed effectively to the project. We cite: Benin, Burkina Faso, Burundi, DRC, Cabo Verde, Egypt, Gabon, Gambia, Ghana, Guinee Bissau, Kenya, Liberia, Mali, Mauritius, Morocco, Namibia, Nigeria, Senegal, Somalia, Sudan, Chad, Zambia, and Zimbabwe. 
                                </li>
                            </ol>
                            
                        </div>
                    </div>
                </div>
                <div class="about-button">
                    <a class="button teal" href="https://carnegie-production-assets.s3.amazonaws.com/static/files/Africa Tech Tracker FAQ-2024.pdf" target="_blank">FAQ</a>
                </div>
                <div class="about-button">
                    <a class="button teal" href="https://carnegie-production-assets.s3.amazonaws.com/static/files/Africa Tech Tracker Glossary-2024.pdf" target="_blank">Glossary</a>
                </div>
                <div class="about-button">
                    <a class="button teal" href="https://carnegieendowment.org/programs/africa/technology?lang=en" target="_blank">Related Research</a>
                </div>
            </template>
        </MainWithAsidesTier>
        <div id="aboutTabClick"></div>
        <div class="section boxes"> 
            <div class="foreground">
                <h3 class="title">DIGITAL ECONOMY PILLARS</h3>
                <div class="cols cols--equal-heights">
                    <div class="col col-20 infrastructure pillar-box active" id="pillarInfrastructure" @click="pillarBox">
                        <div class="gutters">
                            <div class="icon"></div>
                            <h3>Digital Infrastructure</h3>
                        </div>
                    </div>
                    <div class="col col-20 platforms pillar-box" id="pillarPlatforms" @click="pillarBox">
                        <div class="gutters">
                            <div class="icon"></div>
                            <h3>Digital Platforms</h3>
                        </div>
                    </div>
                    <div class="col col-20 skills pillar-box" id="pillarSkills" @click="pillarBox">
                        <div class="gutters">
                            <div class="icon"></div>
                            <h3>Digital Skills</h3>
                        </div>
                    </div>
                    <div class="col col-20 innovation pillar-box" id="pillarInnovation" @click="pillarBox">
                        <div class="gutters">
                            <div class="icon"></div>
                            <h3>Digital Innovation</h3>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="section description-section"> 
            <div class="foreground">
                <div class="cols"> 
                    <div class="infrastructure" id="pillarDescription">
                        <div class="col col-50"> 
                            <div class="gutters">
                                <h4>Digital Pillar</h4>
                                <p><span>Digital Infrastructure</span> is the availability of affordable and quality internet which is instrumental to bringing more people online.</p>
                            </div>
                        </div>
                        <div class="col col-50"> 
                            <div class="gutters">
                                <h4>Sub-Pillars</h4>
                                <p><span>Physical:</span> Physical structures powered by technology that increase access and connectivity to the digital economy.</p>
                                <p><span>Invisible:</span> Systems that strengthen public confidence in the digital economy.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ClientOnly>
            <SubscribeModal
                :isModalOpen="isSubscribeModalOpen"
                :closeModal="closeSubscribeModal"
                :newsletterFormName="newsletterFormName"
                :scrollOut=true
                />
        </ClientOnly>
        <div class="section countries"> 
            <div class="foreground">
                <div class="cols"> 
                    <div class="col col-50"> 
                        <div class="country-select filters">
                            <strong>Select a country from the map or the dropdown list below to begin exploring.</strong>
                            <div class="form-dropdown"> 
                                <div class="form-opener country">Country <span class="ceip-icon icon-arrow-down"></span></div>
                                <div class="form-menu"> 
                                    <form class="clean-form africa-regulations-country toggle-wrapper padded-less" name="africa-regulations-country" id="africa-regulations-country" action="" method="get" enctype="multipart/form-data" autocomplete="off">
                                        <fieldset>
                                            <div class="control-group radio">
                                                <div class="checkbox" v-for="country in countryDataArray">
                                                    <label :for="country.checkbox" class="africa-regulations-country-checkbox-label" @click="countrySVG">
                                                        <input type="radio" :id="country.checkbox"  class="country-input-value" name="country" :value="country.countryName" />{{country.descriptionCountryNameValue}}</label>
                                                </div>
                                            </div>
                                        </fieldset>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div class="country-info" id="countryDescription">
                            <h4>{{descriptionCountryName}}</h4>

                            <div id="longerCountryDescription" v-if="descriptionCountryName !== 'African Union' && descriptionCountryName !== 'All Countries'">
                                    <p><strong>Population:</strong> {{descriptionCountryPopulation}}<br />
                                        <strong>Total Area (Land and water):</strong> {{descriptionCountryLandMass}}</p>

                            </div>
                            <div id="longerCountryDescription" v-else-if="descriptionCountryName === 'African Union'">
                                    <p><strong>Number of Members:</strong> 55</p>

                            </div>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>&nbsp;</td>
                                        <td>Laws</td>
                                        <td>Policies</td>
                                        <td>Regulations</td>
                                    </tr>
                                    <tr>
                                        <td><span class='dig-inf-icon'></span><strong>Digital Infrastructure</strong></td>
                                        <td class="number">{{infrastructureLaws}}</td>
                                        <td class="number">{{infrastructurePolicies}}</td>
                                        <td class="number">{{infrastructureRegs}}</td>
                                    </tr>
                                    <tr>
                                        <td><span class='dig-pl-icon'></span><strong>Digital Platforms</strong></td>
                                        <td class="number">{{platformLaws}}</td>
                                        <td class="number">{{platformPolicies}}</td>
                                        <td class="number">{{platformRegs}}</td>
                                    </tr>
                                    <tr>
                                        <td><span class='dig-sk-icon'></span><strong>Digital Skills</strong></td>
                                        <td class="number">{{skillsLaws}}</td>
                                        <td class="number">{{skillsPolicies}}</td>
                                        <td class="number">{{skillsRegs}}</td>
                                    </tr>
                                    <tr>
                                        <td><span class='dig-inv-icon'></span><strong>Digital Innovation</strong></td>
                                        <td class="number">{{innovationLaws}}</td>
                                        <td class="number">{{innovationPolicies}}</td>
                                        <td class="number">{{innovationRegs}}</td>
                                    </tr>
                                </tbody>
                            </table>

                        </div>
                    </div>
                    <div class="col col-50"> 
                        <div class="gutters"> 
                            <!--?xml version="1.0" encoding="UTF-8"?-->
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1170.82 1038.47">
                                <g id="NigerCountry">
                                    <polygon class="cls-2" points="377.38 339.8 377.15 340.5 377.15 341.66 376.91 343.28 376.68 345.37 377.84 346.76 379.23 348.15 379.46 348.62 379.23 350.02 379.46 350.93 380.16 352.33 381.32 354.19 382.49 355.81 383.4 356.04 384.34 356.28 384.8 356.51 385.5 356.97 386.2 357.21 386.89 357.44 387.36 357.91 387.83 359.06 389.21 359.76 390.14 360.45 389.67 360.92 388.51 360.69 387.36 360.45 387.36 360.92 387.12 363 387.36 364.64 387.58 364.86 388.75 365.1 391.53 367.42 394.08 369.5 395.02 369.97 396.4 370.21 397.8 370.43 398.5 370.21 400.12 369.05 400.81 369.05 401.51 369.05 401.97 369.27 402.68 369.97 403.36 371.36 403.6 372.29 403.36 372.75 403.14 372.99 401.97 373.22 401.51 373.46 401.28 373.68 401.51 374.38 401.75 374.85 403.14 376.7 404.99 379.25 405.69 379.72 405.22 380.64 404.3 382.51 403.6 383.43 400.35 386.21 398.72 385.76 395.47 386.45 394.79 385.76 394.08 385.52 393.16 385.76 392.69 385.99 392.69 386.21 392.23 386.68 391.76 387.84 391.29 388.07 390.61 388.07 389.91 388.07 389.45 388.3 389.45 388.77 389.21 389.24 388.75 389.46 388.51 390.16 388.75 390.62 388.28 390.87 387.83 390.62 387.36 390.62 387.12 391.32 386.65 391.79 386.2 391.79 382.94 391.79 381.78 392.25 380.86 392.25 380.86 392.02 380.86 391.79 376.68 390.62 373.67 390.16 370.64 389.46 370.42 390.16 369.95 390.4 369.48 390.4 369.02 390.4 368.56 390.87 368.09 391.56 367.39 391.79 366.7 392.25 366.46 392.48 366 392.48 365.3 391.79 364.38 391.79 362.75 391.79 362.06 391.56 360.9 391.56 358.57 391.56 354.63 391.32 353.93 391.56 353.71 391.56 349.76 391.79 345.58 391.79 341.87 391.79 338.61 391.79 338.61 391.56 337.69 391.56 337.46 392.02 336.76 395.28 336.53 396.89 336.99 398.05 337.69 398.75 338.15 399.22 338.15 399.45 337.69 399.91 337.93 400.61 338.38 401.07 338.61 401.76 338.15 402.23 338.38 403.62 338.61 405.95 338.61 407.58 338.38 408.03 338.61 409.19 339.32 410.81 339.32 411.52 339.08 411.98 338.61 412.44 337.93 412.44 336.99 411.28 336.76 410.81 336.07 409.89 335.6 408.97 334.9 408.5 334.2 408.03 333.28 406.87 332.58 406.18 331.66 406.4 330.49 406.18 327.94 405.71 325.15 405.95 324 406.18 322.84 406.64 320.05 407.58 318.89 408.27 318.19 409.43 317.26 409.43 316.11 408.97 315.64 408.5 314.25 408.5 313.09 408.03 311.92 406.87 311 406.4 309.84 405.71 309.6 404.09 308.68 403.17 308.21 401.54 307.06 400.84 305.89 400.61 304.49 400.61 303.34 400.14 302.65 399.22 302.87 398.51 303.11 397.36 303.34 396.44 303.58 394.57 303.34 392.48 303.11 391.09 303.81 390.4 304.96 389.93 305.43 389 306.12 386.68 306.36 384.82 306.12 384.13 305.89 383.43 305.66 382.51 305.43 381.57 305.66 380.64 306.36 379.95 307.28 379.25 307.98 378.79 309.84 378.56 312.16 378.1 313.32 377.4 314.25 376.94 314.71 376.47 315.41 375.54 316.11 374.61 316.8 373.91 317.03 371.83 317.03 370.68 316.57 370.21 316.11 369.5 319.59 367.88 319.59 366.72 319.12 365.56 318.42 364.4 318.19 363.71 319.12 362.54 319.81 361.85 320.52 361.16 321.67 360.22 323.07 359.99 324.46 360.22 327.94 362.54 328.64 362.78 329.33 362.54 330.26 362.08 331.42 361.38 331.89 359.99 331.89 357.67 332.12 356.51 332.81 356.28 334.9 356.75 335.6 356.75 336.07 356.51 336.53 356.28 336.53 355.35 336.53 354.89 336.99 352.56 338.38 350.93 340.94 348.86 341.63 348.62 342.56 348.39 346.97 349.78 347.66 349.31 348.83 345.83 349.99 345.6 351.61 345.6 352.54 345.13 353.01 344.91 355.09 343.74 358.8 341.89 360.9 340.97 361.37 340.73 362.75 339.57 364.61 337.94 365.77 337.71 367.63 337.71 368.56 337.94 369.02 338.17 369.26 338.4 371.57 337.94 374.59 338.87 377.38 339.8"/>
                                    <path class="cls-2" d="m564.2,47.59l-.46.7-1.16-.24-1.16-.7v-1.62l1.16.46,1.16.93.46.46Zm-3.25-2.55l-.69.23-.92-.46-.24-.23,1.16-.23.46.23.23.46h0Z"/>
                                    <path class="cls-2" d="m300.32,745.5l-1.16.23.24-.7.7-.7h.7v.93l-.47.23Zm-112.79-106.07h-.7v-.23l.24-.46.23-.46h.23l.46.46.23.23-.23.23-.46.23Z"/>
                                    <polygon class="cls-2" points="751.96 402.7 751.96 410.13 751.26 410.81 750.1 411.98 749.88 412.91 750.1 413.6 739.89 413.6 739.2 412.91 737.58 410.13 736.88 408.03 736.88 402.7 751.96 402.7"/>
                                    <polyline class="cls-2" points="842.24 475.35 839.93 475.35 837.37 475.35 834.82 475.35 832.26 475.35 829.71 475.35 827.39 475.35 824.84 475.35 822.28 475.35"/>
                                    <polygon class="cls-2" points="569.54 230.72 570.93 238.61 572.09 245.34 572.09 249.74 572.33 250.91 572.79 251.6 573.72 252.3 577.9 258.56 576.97 259.72 577.43 261.59 578.59 262.52 582.08 266.22 582.31 266.92 582.31 267.38 579.75 271.79 579.52 272.73 579.06 278.3 578.59 282.25 578.13 287.57 577.66 294.08 577.19 299.42 576.5 306.38 576.04 313.11 572.56 316.82 566.52 323.32 561.64 328.66 559.1 332.14 554.46 339.1 552.14 343.51 550.52 345.83 549.58 346.99 550.52 350.24 551.68 355.81 549.35 355.81 547.96 356.28 546.33 357.67 544.47 358.13 542.16 359.29 540.76 360.22 539.37 360.92 537.52 362.54 536.81 363.94 534.95 364.17 532.42 363.94 530.78 362.78 526.85 361.38 524.28 360.69 523.12 360.45 517.32 360.22 511.05 360.92 507.8 361.38 507.11 361.61 505.25 362.54 503.86 363.47 499.68 367.64 494.35 367.64 491.1 367.19 488.54 366.49 484.6 364.4 479.96 361.38 478.1 360.92 476.48 360.69 476.01 360.69 470.44 363.71 469.28 363.71 467.89 364.17 466.96 364.86 466.5 365.1 465.8 365.33 464.87 365.1 463.94 364.64 463.25 363.71 460.93 360.45 460.46 359.76 459.3 358.82 457.67 357.21 456.51 356.51 455.82 356.28 455.12 356.51 450.48 355.12 446.07 353.72 445.14 353.72 444.45 354.19 442.82 355.12 440.97 355.35 438.65 355.35 437.25 355.12 435.4 355.58 434 355.81 432.15 356.75 429.83 358.6 429.13 358.82 428.43 359.06 427.74 364.4 427.04 366.02 425.88 368.11 423.55 370.21 421.94 371.36 421.94 372.99 421.94 375.77 421.94 377.63 421.94 378.79 421.7 379.49 421.47 379.95 421.7 380.64 421.94 381.11 422.17 381.57 422.17 382.04 421.47 382.51 420.54 381.35 419.38 380.41 418.22 379.95 417.53 379.49 417.06 378.56 415.45 376.94 411.95 373.68 411.72 373.46 411.02 373.46 410.1 373.68 409.4 374.38 408.94 374.61 408.48 374.61 406.62 375.08 405.46 375.54 405.22 376.01 405.93 378.56 405.69 379.72 404.99 379.25 403.14 376.7 401.75 374.85 401.51 374.38 401.28 373.68 401.51 373.46 401.97 373.22 403.14 372.99 403.36 372.75 403.6 372.29 403.36 371.36 402.68 369.97 401.97 369.27 401.51 369.05 400.81 369.05 400.12 369.05 398.5 370.21 397.8 370.43 396.4 370.21 395.02 369.97 394.08 369.5 391.53 367.42 388.75 365.1 387.58 364.86 387.36 364.64 387.12 363 387.36 360.92 387.36 360.45 388.51 360.69 389.67 360.92 390.14 360.45 389.21 359.76 387.83 359.06 387.36 357.91 386.89 357.44 386.2 357.21 385.5 356.97 384.8 356.51 384.34 356.28 383.4 356.04 382.49 355.81 381.32 354.19 380.16 352.33 379.46 350.93 379.23 350.02 379.46 348.62 379.23 348.15 377.84 346.76 376.68 345.37 376.91 343.28 377.15 341.66 377.15 340.5 377.38 339.8 377.6 339.1 378.31 338.87 380.16 338.87 383.87 339.34 386.89 338.87 387.12 338.87 389.21 337.01 391.53 334.93 395.02 334.93 398.72 334.69 401.75 334.46 406.16 334.46 409.64 334.23 413.58 334.23 413.81 333.3 414.05 333.07 414.27 333.07 417.29 333.52 420.07 333.99 420.32 332.14 422.87 330.05 424.25 329.59 424.49 329.35 424.95 328.66 425.18 327.5 425.41 326.8 425.88 326.1 426.35 324.94 426.81 322.85 428.21 320.54 428.9 317.52 429.13 314.74 429.36 312.41 429.59 311.95 429.59 308 429.59 304.06 429.59 300.81 429.59 296.62 429.59 292.92 429.59 288.97 429.59 285.49 429.59 283.17 432.38 282.46 435.4 282.01 439.57 281.09 444.21 280.15 449.33 279.22 450.48 278.52 454.43 275.28 456.05 273.65 459.53 270.63 462.08 268.08 465.56 265.06 469.05 262.05 472.07 259.72 476.48 256.94 483.21 252.76 489.94 248.59 496.67 244.4 503.4 240.23 510.13 236.05 516.86 231.87 523.59 227.7 530.32 223.52 537.29 225.15 543.55 226.54 550.05 228.16 551.68 229.09 555.15 231.87 559.56 235.82 559.79 235.82 564.2 233.49 569.54 230.72 569.54 230.72"/>
                                </g>
                                <g id="Cabo_Verde" data-name="Cabo Verde">
                                    <path class="cls-2" d="m58.02,340.5l-1.16.7-.7-.23-.7-.71-.23-.7.23-.7,1.39-.7.93.24.23,1.16v.93Zm14.63-3.71h-.47l-.46-.46.22-.93-.22-.24.46-.7h.93l.23.7.24,1.4-.92.23Zm-3.49,1.85l-.7,1.17-1.86-.23-.7-.46-1.15-1.39v-1.15l.46-1.17v-1.16l.46.23.23.47,1.63,1.63.46.23,1.16,1.85Zm6.97-16.47l.93.23h1.15l.7.7.24.7-.46.93-1.4.69h-.69l-1.16-.69.7-1.4v-1.16Zm-15.32-5.1l.46.24.23.46h-.69l-2.09-.46-.46.23-.46,1.16-.93-1.63v-.69h.23l1.4.22,2.32.47Zm15.55-.47l-.24.71-.7-1.16-.23-.23v-1.4l.7-.47h.46v2.55Zm-26-2.32l-.92.47h-.71l-.92-.47.23-.69.93-.47.93-.24.46,1.16v.24Zm-3.71-1.63l-1.16.23h-.46l-.24-1.15-.22-.7v-.23l3-1.39.93.22.69,1.17-.46.46-2.08,1.39Z"/>
                                </g>
                                <g id="Sao_Tome_and_Principe" data-name="Sao Tome and Principe">
                                    <path class="cls-2" d="m461.16,533.83l-1.16.92-.47-.22-.46-.7-.23-1.39v-.7l.69-.7,1.4-.7.7-.23.92,1.16v.92l-1.39,1.63Zm9.98-18.79l-.47.22-.46-.22-.23-.47.7-.93.47-.23.23.23.24.23v.23l-.46.94Z"/>
                                </g>
                                <g id="Chad">
                                    <polygon class="cls-2 activeCountry" points="686.75 278.3 686.75 284.32 686.75 290.36 686.75 296.39 686.75 302.44 686.75 308.47 686.75 314.27 686.98 320.3 686.98 326.33 686.98 328.2 686.75 329.12 686.28 329.35 683.27 328.66 681.86 328.66 680.01 329.12 677.23 329.35 675.37 329.35 674.21 330.28 673.28 331.44 673.75 334.46 673.51 335.39 673.28 336.56 672.35 337.48 671.43 338.17 670.96 338.63 670.26 340.03 669.8 340.73 670.03 341.42 669.8 342.35 669.34 342.82 667.95 343.04 667.01 343.51 666.55 344.21 666.09 344.67 666.33 345.37 666.55 346.07 666.78 347.45 666.78 348.15 667.49 348.86 667.95 349.31 667.95 349.78 667.71 350.24 666.09 351.17 665.39 351.64 664.69 352.1 664.46 352.33 663.3 353.03 662.6 353.96 662.38 354.65 662.38 355.58 663.07 356.97 663.77 358.13 664.01 359.06 664.01 359.99 664.01 360.92 663.77 361.61 663.07 362.32 660.98 363.71 659.82 365.1 658.89 366.95 658.67 367.88 658.89 368.58 659.36 369.27 660.05 369.5 660.98 369.5 662.6 369.27 664.01 369.05 665.62 369.74 666.33 371.13 666.09 372.29 666.78 374.38 667.25 376.7 667.25 377.63 667.49 377.86 668.41 378.1 668.64 378.56 668.18 382.74 668.64 383.9 669.34 384.82 670.03 385.29 670.96 385.76 671.19 386.21 672.12 386.21 673.05 387.15 673.28 388.07 673.28 389 672.59 391.32 672.35 392.71 671.66 392.48 670.5 392.25 669.1 392.02 667.49 391.79 665.85 392.25 664.01 392.94 663.53 393.65 663.07 393.87 662.38 393.87 661.67 393.87 661.22 394.57 660.51 395.04 657.97 396.44 657.5 396.89 657.26 397.13 657.26 397.82 657.5 398.75 657.5 399.91 656.81 401.07 656.35 401.76 655.41 402 654.95 402 654.49 402.46 653.09 404.79 652.62 405.25 651.46 405.01 648.21 408.5 647.76 409.66 646.58 411.06 644.96 412.68 643.8 413.37 643.57 413.6 643.12 414.06 642.4 414.3 639.4 416.16 635.91 416.16 634.3 416.86 632.9 417.32 630.58 417.55 630.12 417.55 627.1 417.78 623.85 417.78 622.69 418.02 621.29 418.7 620.6 419.41 620.37 419.64 620.6 419.87 620.6 420.11 622.92 421.73 623.38 422.42 622.69 423.12 622.46 423.36 622.22 424.05 620.83 425.67 618.75 427.77 617.58 428.46 617.12 428.93 616.65 430.32 616.19 430.55 614.79 430.55 612.02 430.78 608.07 431.24 605.74 431.47 604.35 431.24 602.26 432.17 601.56 432.41 601.1 432.63 599.25 433.56 597.4 434.96 596.93 435.19 594.61 435.88 593.43 436.82 593.22 437.04 591.59 435.66 590.66 434.49 589.96 433.1 589.96 432.87 589.74 432.87 588.81 433.34 588.11 434.03 587.88 435.19 585.32 435.88 583.24 436.58 582.31 437.51 580.91 437.98 579.06 437.74 577.66 437.51 576.04 437.28 576.74 436.11 576.97 435.42 577.19 434.49 576.97 433.79 576.27 433.56 575.58 433.1 574.41 429.85 573.25 426.83 571.4 423.82 569.54 421.73 568.15 420.57 567.68 420.33 566.98 420.11 566.29 419.64 563.73 417.55 561.18 415.23 560.5 414.3 559.1 412.68 557.71 411.06 557 410.13 556.55 408.97 557.47 407.58 558.63 406.18 560.04 405.01 561.88 405.01 564.66 405.48 567.68 405.48 570.7 405.25 571.64 405.01 572.33 405.01 573.95 405.48 576.97 405.25 578.36 404.79 576.74 403.62 575.11 402 573.48 400.14 572.56 398.51 571.64 396.2 570.7 393.65 570.24 390.16 570.24 388.07 570.7 386.68 571.4 384.59 570.93 383.2 570.93 382.04 570.93 380.41 570.7 379.72 569.54 376.94 569.31 376.7 568.39 374.85 567.92 371.83 566.75 369.74 564.9 368.81 563.98 367.64 563.51 365.56 562.81 364.86 560.04 364.17 557.71 364.17 556.07 361.85 553.76 358.82 551.68 355.81 550.52 350.24 549.58 346.99 550.52 345.83 552.14 343.51 554.46 339.1 559.1 332.14 561.64 328.66 566.52 323.32 572.56 316.82 576.04 313.11 576.5 306.38 577.19 299.42 577.66 294.08 578.13 287.57 578.59 282.25 579.06 278.3 579.52 272.73 579.75 271.79 582.31 267.38 582.31 266.92 582.08 266.22 578.59 262.52 577.43 261.59 576.97 259.72 577.9 258.56 573.72 252.3 572.79 251.6 572.33 250.91 572.09 249.74 572.09 245.34 570.93 238.61 569.54 230.72 574.41 228.4 578.13 226.77 582.77 224.44 586.95 226.77 593.22 230.02 599.48 233.26 605.74 236.51 612.02 239.76 618.29 243.02 624.54 246.26 630.58 249.52 636.85 252.76 643.12 255.78 649.37 259.03 655.64 262.27 661.91 265.53 668.18 268.78 674.44 272.02 680.48 275.04 686.75 278.3"/>
                                </g>
                                <g id="Central_African_Republic" data-name="Central African Republic">
                                    <polygon class="cls-2" points="672.35 392.71 673.05 394.34 673.51 394.81 677.47 398.75 678.16 399.68 680.01 402.46 681.18 404.33 682.57 407.11 682.57 408.5 682.34 409.89 682.11 413.37 681.86 414.53 680.25 416.39 680.01 417.32 680.48 418.02 680.94 418.25 681.18 418.7 681.18 420.33 681.64 421.03 682.8 421.49 686.05 421.73 687.68 421.96 689.06 422.42 689.53 422.42 690 423.12 689.53 424.98 689.75 426.14 690.93 427.3 692.09 427.77 693.02 428 696.73 428.46 698.11 429.16 700.2 431.47 702.76 433.56 703.23 434.73 703.23 435.66 702.52 436.82 702.52 437.28 703.68 438.45 705.09 439.83 707.39 441.23 711.57 443.31 713.66 444.71 714.36 445.87 715.29 447.02 716.91 448.18 717.84 448.88 717.14 451.2 717.39 452.14 717.84 452.82 718.54 453.53 719 454.92 719.93 456.31 720.86 457.01 722.48 457.23 723.41 457.93 725.5 459.1 727.13 460.03 728.06 460.95 728.52 461.42 728.99 462.11 729.22 463.05 729.22 464.44 729.45 466.53 730.38 467.91 731.31 468.84 727.59 467.68 727.13 467.68 726.43 467.91 724.57 469.31 723.88 469.53 723.18 469.31 721.32 469.08 715.29 468.15 710.88 466.99 709.5 466.53 706.93 466.29 705.3 466.99 703.68 469.53 703.46 470 700.9 470.7 699.74 470.47 696.96 471.16 692.78 470.23 691.16 470.47 690 470.94 686.98 472.09 685.12 472.79 683.03 473.25 680.94 474.17 679.54 474.64 678.16 474.64 677 474.17 675.6 473.72 673.98 473.72 672.35 473.95 670.96 474.88 670.26 475.58 669.1 477.43 667.71 480.68 667.01 481.15 666.78 481.37 666.55 481.61 659.82 479.98 657.03 479.75 654.95 480.22 652.62 479.29 651.46 479.05 651 479.29 649.62 479.05 647.51 477.9 645.43 477.43 643.34 477.66 642.19 477.2 641.26 476.27 640.1 474.42 638.01 472.56 634.99 470.94 633.14 469.77 632.44 469.08 630.8 468.61 628.49 468.61 626.17 469.31 622.92 471.63 619.67 476.5 618.04 478.36 616.65 478.83 616.43 479.98 617.12 481.84 617.12 483.93 616.65 487.41 616.88 490.19 616.19 489.72 615.51 488.57 615.03 488.34 613.18 488.81 612.02 489.26 611.54 489.72 611.08 489.96 610.38 489.26 609.93 489.04 608.99 489.26 608.3 489.26 607.83 489.04 607.36 489.26 606.44 488.81 602.95 487.65 602.26 487.41 601.56 487.41 599.72 488.34 598.78 488.57 596.01 489.04 592.76 489.26 591.59 489.51 590.9 489.72 590.42 490.43 589.96 491.59 589.26 493.67 589.04 494.14 589.26 495.07 589.04 496.46 588.81 497.62 589.04 498.55 588.11 500.18 587.18 502.27 586.25 504.12 585.32 505.98 584.63 504.59 584.4 503.19 584.16 501.56 584.16 501.11 583.93 500.64 583.7 499.24 583.93 498.55 583.7 497.62 583 496.7 582.31 495.99 582.08 495.3 581.6 495.07 580.91 495.07 579.99 494.6 578.59 493.45 577.43 492.05 575.81 490.43 574.65 489.04 573.02 487.18 571.64 485.55 570.7 483.93 570.24 483.24 570.7 483 571.4 483 571.64 482.77 571.64 482.53 570.93 481.15 570.7 479.52 570.24 478.59 568.39 477.2 566.98 476.04 566.29 475.58 566.06 474.64 565.36 469.53 565.13 467.91 564.66 467.22 564.43 466.99 564.2 466.53 564.2 465.59 564.43 464.9 564.43 464.44 564.9 463.74 564.9 459.1 564.66 458.64 564.43 458.4 563.98 458.4 563.51 458.4 562.81 457.7 562.57 456.77 562.57 456.07 563.04 455.61 563.51 455.15 564.2 454.68 566.06 453.98 566.52 453.53 566.75 453.06 566.98 452.37 568.15 450.04 569.54 447.49 570.24 447.02 570.93 445.4 571.86 443.31 572.33 442.38 572.56 441.46 573.02 440.76 574.88 439.36 576.04 437.28 577.66 437.51 579.06 437.74 580.91 437.98 582.31 437.51 583.24 436.58 585.32 435.88 587.88 435.19 588.11 434.03 588.81 433.34 589.74 432.87 589.96 432.87 589.96 433.1 590.66 434.49 591.59 435.66 593.22 437.04 593.43 436.82 594.61 435.88 596.93 435.19 597.4 434.96 599.25 433.56 601.1 432.63 601.56 432.41 602.26 432.17 604.35 431.24 605.74 431.47 608.07 431.24 612.02 430.78 614.79 430.55 616.19 430.55 616.65 430.32 617.12 428.93 617.58 428.46 618.75 427.77 620.83 425.67 622.22 424.05 622.46 423.36 622.69 423.12 623.38 422.42 622.92 421.73 620.6 420.11 620.6 419.87 620.37 419.64 620.6 419.41 621.29 418.7 622.69 418.02 623.85 417.78 627.1 417.78 630.12 417.55 630.58 417.55 632.9 417.32 634.3 416.86 635.91 416.16 639.4 416.16 642.4 414.3 643.12 414.06 643.57 413.6 643.8 413.37 644.96 412.68 646.58 411.06 647.76 409.66 648.21 408.5 651.46 405.01 652.62 405.25 653.09 404.79 654.49 402.46 654.95 402 655.41 402 656.35 401.76 656.81 401.07 657.5 399.91 657.5 398.75 657.26 397.82 657.26 397.13 657.5 396.89 657.97 396.44 660.51 395.04 661.22 394.57 661.67 393.87 662.38 393.87 663.07 393.87 663.53 393.65 664.01 392.94 665.85 392.25 667.49 391.79 669.1 392.02 670.5 392.25 671.66 392.48 672.35 392.71"/>
                                </g>
                                <g id="Congo">
                                    <polygon class="cls-2" points="616.88 490.19 617.12 492.52 615.95 495.3 615.26 497.39 613.4 500.87 611.78 503.89 609.93 509.23 609.93 513.17 609.7 515.49 608.99 516.88 607.61 520.83 607.36 524.31 608.07 528.5 607.36 532.44 605.97 536.15 605.27 539.17 605.74 542.66 602.95 545.43 599.48 548.44 597.4 549.38 595.53 550.32 594.38 551.48 593.9 551.92 592.99 553.32 590.9 557.5 589.96 559.36 588.58 560.98 586.49 562.84 585.79 563.77 585.32 565.17 585.55 567.48 585.79 574.91 585.32 577 584.86 580.48 582.77 584.43 581.16 586.75 579.75 587.45 577.66 587.91 576.74 588.84 576.04 589.77 575.11 590.7 573.25 591.62 571.16 593.48 568.84 596.72 566.98 598.59 566.06 599.05 565.13 599.05 564.2 598.8 563.28 598.59 562.81 598.8 562.57 598.8 562.11 598.34 562.11 597.66 562.11 596.49 561.64 595.11 562.11 594.17 562.81 593.48 562.57 593.02 562.11 592.32 561.41 591.39 560.95 591.62 559.79 592.32 558.63 592.78 557.47 593.02 556.55 593.48 556.07 593.95 555.39 593.95 554.93 593.48 553.99 593.25 553.52 593.25 553.05 593.48 553.05 594.64 552.82 595.55 552.82 596.49 552.36 596.97 550.98 597.43 550.05 598.13 549.35 598.59 548.64 598.34 547.73 597.66 546.8 596.72 546.11 596.03 545.87 595.55 545.63 595.55 544.94 595.33 544.7 595.8 544.47 595.55 543.55 594.88 542.39 593.48 541.92 593.25 541.22 593.25 540.07 593.71 539.12 594.41 537.29 595.33 535.89 595.55 535.66 596.27 535.43 596.97 534.73 597.66 533.57 597.66 532.87 598.59 531.7 599.97 531.02 600.66 530.78 600.44 530.32 599.97 529.39 598.8 528.46 597.43 528.23 596.72 528 596.49 528 594.88 526.37 593.25 522.89 590.23 522.43 589.3 519.41 586.51 520.34 584.43 520.8 583.5 521.5 582.81 524.28 581.41 524.75 581.41 526.6 583.5 527.29 583.5 528 583.5 528.93 583.73 529.16 583.27 529.39 582.81 528.69 582.1 528.69 581.41 529.16 580.71 529.39 580.02 529.86 579.09 529.86 578.62 529.39 578.16 528 577.46 527.07 576.77 526.85 576.3 527.07 575.37 527.77 574.68 527.77 574.21 527.07 573.75 526.6 573.05 526.14 572.59 524.75 572.36 524.98 571.43 525.44 570.27 525.68 569.34 525.21 566.78 525.21 566.32 525.68 565.86 526.37 566.32 527.29 566.55 529.39 566.09 530.09 566.09 530.78 566.55 531.7 566.78 536.59 565.86 536.81 564.69 537.05 563.77 537.05 563.07 536.81 562.37 536.59 562.14 536.35 561.44 536.35 560.51 536.81 560.28 538.45 559.36 538.91 559.36 540.07 559.84 541.22 560.51 542.16 562.37 542.62 563.77 543.78 565.62 545.87 566.32 548.41 566.78 549.82 566.55 551.89 565.17 553.05 564 553.3 563.31 553.99 563.53 554.69 565.17 555.39 565.86 555.39 566.32 555.15 567.02 555.39 567.48 556.79 567.94 557.93 567.48 558.63 567.02 559.56 566.09 559.56 565.39 558.87 564.93 558.87 564.23 559.56 563.77 560.04 562.6 560.04 561.44 560.72 560.98 561.41 560.51 561.88 560.06 562.34 557.73 562.11 556.8 562.11 556.11 562.57 555.42 562.81 553.79 562.57 551.48 562.34 549.84 562.11 548.22 562.57 545.91 563.04 543.58 563.04 542.88 562.34 542.19 561.64 541.49 559.56 541.02 558.87 540.1 558.16 539.17 557.71 538.94 555.61 538.71 554.93 538.01 555.15 536.61 555.39 534.53 555.39 532.9 555.85 531.74 556.3 530.81 557.23 529.89 557.71 528.73 557.93 528.5 559.79 528.26 560.5 527.79 561.18 527.32 561.41 526.64 561.88 525.71 562.57 524.78 562.57 524.31 562.34 523.62 561.88 522.46 561.18 521.3 560.72 520.83 560.04 518.28 559.1 517.58 557.71 517.35 554.93 516.88 553.3 517.35 550.74 518.28 548.64 518.97 547.49 519.21 546.57 519.21 546.33 518.74 546.8 518.52 547.04 517.58 546.8 516.42 546.33 515.49 545.87 514.1 546.11 512.25 546.57 510.38 547.49 508.29 547.73 507.37 550.74 507.37 553.99 507.37 557.23 507.37 560.5 507.37 563.28 507.37 564.43 506.91 565.59 507.6 566.06 507.84 566.29 507.84 566.75 508.53 568.15 508.29 568.39 508.53 568.61 509.23 570 509.23 570.7 509.45 571.16 509.45 571.86 509 572.56 509 573.48 509.7 574.18 510.16 575.34 509.92 577.66 510.16 579.52 510.63 581.38 511.78 582.54 512.48 583.7 513.64 584.16 513.41 584.4 513.17 584.63 512.94 584.63 512.01 583.93 510.38 583.7 509.23 583.93 508.07 584.4 507.13 585.08 506.67 585.32 505.98 586.25 504.12 587.18 502.27 588.11 500.18 589.04 498.55 588.81 497.62 589.04 496.46 589.26 495.07 589.04 494.14 589.26 493.67 589.96 491.59 590.42 490.43 590.9 489.72 591.59 489.51 592.76 489.26 596.01 489.04 598.78 488.57 599.72 488.34 601.56 487.41 602.26 487.41 602.95 487.65 606.44 488.81 607.36 489.26 607.83 489.04 608.3 489.26 608.99 489.26 609.93 489.04 610.38 489.26 611.08 489.96 611.54 489.72 612.02 489.26 613.18 488.81 615.03 488.34 615.51 488.57 616.19 489.72 616.88 490.19"/>
                                </g>
                                <g id="Gabon">
                                    <polygon class="cls-2 activeCountry" points="547.73 507.37 547.49 508.29 546.57 510.38 546.11 512.25 545.87 514.1 546.33 515.49 546.8 516.42 547.04 517.58 546.8 518.52 546.33 518.74 546.57 519.21 547.49 519.21 548.64 518.97 550.74 518.28 553.3 517.35 554.93 516.88 557.71 517.35 559.1 517.58 560.04 518.28 560.72 520.83 561.18 521.3 561.88 522.46 562.34 523.62 562.57 524.31 562.57 524.78 561.88 525.71 561.41 526.64 561.18 527.32 560.5 527.79 559.79 528.26 557.93 528.5 557.71 528.73 557.23 529.89 556.3 530.81 555.85 531.74 555.39 532.9 555.39 534.53 555.15 536.61 554.93 538.01 555.61 538.71 557.71 538.94 558.16 539.17 558.87 540.1 559.56 541.02 561.64 541.49 562.34 542.19 563.04 542.88 563.04 543.58 562.57 545.91 562.11 548.22 562.34 549.84 562.57 551.48 562.81 553.79 562.57 555.42 562.11 556.11 562.11 556.8 562.34 557.73 561.88 560.06 561.41 560.51 560.72 560.98 560.04 561.44 560.04 562.6 559.56 563.77 558.87 564.23 558.87 564.93 559.56 565.39 559.56 566.09 558.63 567.02 557.93 567.48 556.79 567.94 555.39 567.48 555.15 567.02 555.39 566.32 555.39 565.86 554.69 565.17 553.99 563.53 553.3 563.31 553.05 564 551.89 565.17 549.82 566.55 548.41 566.78 545.87 566.32 543.78 565.62 542.62 563.77 542.16 562.37 541.22 560.51 540.07 559.84 538.91 559.36 538.45 559.36 536.81 560.28 536.35 560.51 536.35 561.44 536.59 562.14 536.81 562.37 537.05 563.07 537.05 563.77 536.81 564.69 536.59 565.86 531.7 566.78 530.78 566.55 530.09 566.09 529.39 566.09 527.29 566.55 526.37 566.32 525.68 565.86 525.21 566.32 525.21 566.78 525.68 569.34 525.44 570.27 524.98 571.43 524.75 572.36 526.14 572.59 526.6 573.05 527.07 573.75 527.77 574.21 527.77 574.68 527.07 575.37 526.85 576.3 527.07 576.77 528 577.46 529.39 578.16 529.86 578.62 529.86 579.09 529.39 580.02 529.16 580.71 528.69 581.41 528.69 582.1 529.39 582.81 529.16 583.27 528.93 583.73 528 583.5 527.29 583.5 526.6 583.5 524.75 581.41 524.28 581.41 521.5 582.81 520.8 583.5 520.34 584.43 519.41 586.51 518.25 585.36 517.09 583.27 515.93 581.87 513.14 579.79 512.45 578.16 509.2 574.68 504.79 571.19 501.54 568.18 501.08 567.48 501.78 567.72 504.79 569.1 505.25 569.1 505.49 568.64 504.32 567.94 502.93 567.25 501.78 566.78 500.62 567.02 499.91 566.32 499.45 565.39 499.22 564.47 498.76 563.53 496.9 561.91 496.67 561.22 495.74 560.28 496.2 560.06 498.06 560.98 498.29 560.76 498.06 560.06 496.2 559.36 495.28 559.12 495.04 558.66 495.04 557.96 493.89 555.42 492.49 553.32 492.26 552.4 495.97 556.8 496.43 556.8 497.13 556.8 498.52 556.34 498.29 555.65 497.6 555.17 496.9 555.42 495.97 555.42 495.51 555.17 495.28 554.71 496.2 552.63 495.97 552.87 495.51 553.08 495.04 553.32 494.35 553.55 492.49 552.4 491.1 549.38 490.63 548.91 490.16 547.76 489.71 547.29 487.85 543.12 488.54 543.58 489.48 544.74 491.1 544.5 491.79 543.82 492.26 543.82 492.73 543.58 493.42 542.88 495.51 540.1 496.2 536.15 495.97 534.07 495.74 531.74 496.43 531.05 496.67 531.51 496.67 532.2 497.13 532.9 497.82 533.37 499.22 533.6 501.31 534.3 502.01 534.99 502.24 533.83 504.79 532.9 504.09 532.67 501.78 532.9 498.76 531.51 497.82 530.81 496.9 529.19 495.97 528.26 495.97 527.56 498.29 526.87 498.76 526.87 498.99 527.79 499.68 528.03 499.91 527.32 499.91 525.24 499.22 522.46 499.45 521.99 499.91 521.76 500.62 521.53 500.84 521.3 501.54 521.53 502.01 521.99 502.24 522.46 502.93 522.69 503.63 522.92 504.09 522.92 504.56 522.46 505.03 522.46 507.11 522.46 508.97 522.46 512.45 522.46 515.93 522.46 519.41 522.46 522.19 522.46 522.19 520.83 522.19 518.52 522.19 515.49 522.19 512.71 522.19 510.16 521.96 507.13 522.19 506.44 522.43 505.98 522.43 505.52 524.98 505.52 530.09 505.75 532.18 505.75 532.87 505.75 535.43 505.52 537.75 505.75 538.68 505.98 539.37 505.98 542.16 506.22 545.63 505.98 546.8 505.98 547.27 506.44 547.73 507.37"/>
                                </g>
                                <g id="Equatorial_Guinea" data-name="Equatorial Guinea">
                                    <path class="cls-2" d="m522.19,520.83v1.62h-17.63l-.47.46h-.46l-.7-.23-.69-.23-.24-.46-.47-.46-.7-.23-.23.23-.7.23-.46.23v-.23l-1.17-.93h-.69l-.92-.23.69-2.08.92-1.87,1.17-1.16.46-.45.24-.71.92-2.08,1.17-1.87-.24-1.85.24-3.01h.22v.23l.24.47.46.46,1.4.46h17.4l.23,3.03v10.67Zm-33.88-34.36h2.32l.46.7v.92l-2.08,3.02-.47,1.39-.69.93h-.7l-2.33-.47-.23-.46-.23-.46.23-1.17.23-.46,1.16-.24h.23l.7-1.38v-1.16l.69-.93.71-.24Z"/>
                                </g>
                                <g id="Cameroon">
                                    <path class="cls-2 activeCountry" points="576.04 437.28 574.88 439.36 573.02 440.76 572.56 441.46 572.33 442.38 571.86 443.31 570.93 445.4 570.24 447.02 569.54 447.49 568.15 450.04 566.98 452.37 566.75 453.06 566.52 453.53 566.06 453.98 564.2 454.68 563.51 455.15 563.04 455.61 562.57 456.07 562.57 456.77 562.81 457.7 563.51 458.4 563.98 458.4 564.43 458.4 564.66 458.64 564.9 459.1 564.9 463.74 564.43 464.44 564.43 464.9 564.2 465.59 564.2 466.53 564.43 466.99 564.66 467.22 565.13 467.91 565.36 469.53 566.06 474.64 566.29 475.58 566.98 476.04 568.39 477.2 570.24 478.59 570.7 479.52 570.93 481.15 571.64 482.53 571.64 482.77 571.4 483 570.7 483 570.24 483.24 570.7 483.93 571.64 485.55 573.02 487.18 574.65 489.04 575.81 490.43 577.43 492.05 578.59 493.45 579.99 494.6 580.91 495.07 581.6 495.07 582.08 495.3 582.31 495.99 583 496.7 583.7 497.62 583.93 498.55 583.7 499.24 583.93 500.64 584.16 501.11 584.16 501.56 584.4 503.19 584.63 504.59 585.32 505.98 585.08 506.67 584.4 507.13 583.93 508.07 583.7 509.23 583.93 510.38 584.63 512.01 584.63 512.94 584.4 513.17 584.16 513.41 583.7 513.64 582.54 512.48 581.38 511.78 579.52 510.63 577.66 510.16 575.34 509.92 574.18 510.16 573.48 509.7 572.56 509 571.86 509 571.16 509.45 570.7 509.45 570 509.23 568.61 509.23 568.39 508.53 568.15 508.29 566.75 508.53 566.29 507.84 566.06 507.84 565.59 507.6 564.43 506.91 563.28 507.37 560.5 507.37 557.23 507.37 553.99 507.37 550.74 507.37 547.73 507.37 547.27 506.44 546.8 505.98 545.63 505.98 542.16 506.22 539.37 505.98 538.68 505.98 537.75 505.75 535.43 505.52 532.87 505.75 532.18 505.75 530.09 505.75 524.98 505.52 522.43 505.52 522.43 505.98 522.19 506.44 521.96 507.13 518.94 507.13 515 507.13 511.29 507.13 508.73 507.13 504.56 507.13 503.16 506.67 502.7 506.22 502.46 505.75 502.46 505.52 502.24 505.52 502.46 502.27 502.93 499.71 503.16 497.39 504.09 495.3 503.63 493.22 503.16 492.29 500.38 489.26 501.78 488.34 500.15 488.34 499.68 487.18 498.99 486.02 499.45 485.78 499.91 485.1 501.31 485.31 501.31 484.86 500.15 483.7 500.15 483.24 500.62 482.53 500.38 482.3 499.45 483 498.99 483 498.29 482.53 498.06 482.53 498.29 483.24 497.82 484.16 497.36 484.4 496.43 484.4 495.74 484.16 495.51 483.7 495.04 483.47 493.19 482.77 491.79 482.07 491.33 480.22 490.86 479.52 490.63 478.59 490.4 477.66 490.63 476.04 490.4 475.8 489.94 475.8 489.24 475.8 488.54 475.8 487.85 474.88 487.38 474.64 487.61 476.27 487.15 476.5 486.22 476.5 485.76 475.8 485.76 475.35 486.22 473.48 485.99 473.48 486.22 472.56 487.15 471.16 488.08 469.77 489.24 467.68 489.94 464.2 490.4 462.11 490.86 460.03 491.79 458.4 492.49 457.01 494.81 454.68 496.67 453.06 497.6 452.37 498.06 451.67 499.22 450.97 500.38 450.28 501.08 448.65 502.01 447.25 502.46 447.02 503.16 446.79 505.25 445.17 506.65 444.24 506.88 444.71 507.11 445.17 507.34 445.64 508.5 445.87 510.13 445.64 511.05 445.64 511.52 445.17 511.98 443.55 512.21 443.31 512.68 443.31 514.31 444.24 515.69 445.64 517.09 447.02 517.78 447.49 518.25 448.18 518.72 450.51 519.18 451.2 519.87 451.43 520.8 451.43 521.96 450.97 523.12 450.28 524.05 449.35 524.75 448.65 524.98 448.18 525.21 446.1 525.44 445.64 526.37 444.71 528 443.31 528.93 442.62 528.93 442.38 528.23 441.46 527.77 440.52 528.23 439.6 528.93 438.89 531.02 436.35 531.02 435.66 531.25 434.73 532.87 431.71 533.8 428 533.8 427.3 534.95 425.44 536.12 423.36 538.45 422.89 539.37 422.19 540.29 421.27 541 420.33 541.22 419.41 541.46 417.78 541.92 415.69 542.16 413.84 542.86 412.44 544.02 411.52 546.11 410.81 546.33 410.59 546.8 409.43 547.04 407.34 547.04 405.95 547.04 405.25 547.27 404.33 549.11 402.46 550.05 399.68 550.74 396.89 552.82 393.41 555.39 389.7 556.55 388.77 557.71 388.3 558.63 388.3 559.56 388.07 562.11 386.45 563.28 385.76 564.2 385.05 564.43 384.59 564.43 383.9 564.2 382.04 564.66 380.64 564.9 378.56 565.13 376.94 564.9 376.47 564.43 375.77 564.43 375.54 563.51 374.38 562.34 373.91 560.5 373.68 559.34 373.46 559.34 372.52 559.1 372.06 559.1 371.6 558.87 370.43 557.71 364.17 560.04 364.17 562.81 364.86 563.51 365.56 563.98 367.64 564.9 368.81 566.75 369.74 567.92 371.83 568.39 374.85 569.31 376.7 569.54 376.94 570.7 379.72 570.93 380.41 570.93 382.04 570.93 383.2 571.4 384.59 570.7 386.68 570.24 388.07 570.24 390.16 570.7 393.65 571.64 396.2 572.56 398.51 573.48 400.14 575.11 402 576.74 403.62 578.36 404.79 576.97 405.25 573.95 405.48 572.33 405.01 571.64 405.01 570.7 405.25 567.68 405.48 564.66 405.48 561.88 405.01 560.04 405.01 558.63 406.18 557.47 407.58 556.55 408.97 557 410.13 557.71 411.06 559.1 412.68 560.5 414.3 561.18 415.23 563.73 417.55 566.29 419.64 566.98 420.11 567.68 420.33 568.15 420.57 569.54 421.73 571.4 423.82 573.25 426.83 574.41 429.85 575.58 433.1 576.27 433.56 576.97 433.79 577.19 434.49 576.97 435.42 576.74 436.11 576.04 437.28"/>
                                </g>
                                <g id="Nigeria">
                                    <path class="cls-2 activeCountry" d="m469.52,477.9l-1.16.46-.93-.23,1.17-1.63.69.24.7.23-.46.93Zm-19.04-122.79l4.64,1.39.71-.23.69.23,1.16.7,1.63,1.61,1.16.93.47.7,2.32,3.26.69.92.92.46.93.23.7-.23.47-.24.92-.7,1.39-.46h1.16l5.57-3.02h.47l1.62.23,1.86.46,4.64,3.01,3.94,2.09,2.55.7,3.25.46h5.34l4.17-4.18,1.4-.92,1.86-.93.69-.23,3.25-.46,6.27-.7,5.8.23,1.16.24,2.56.7,3.94,1.39,1.63,1.16,2.54.23,1.86-.23.71-1.39,1.86-1.62,1.38-.7,1.4-.93,2.31-1.16,1.86-.46,1.63-1.39,1.38-.46h2.33l2.08,3.01,2.31,3.03,1.63,2.31,1.16,6.27.23,1.17v.46l.24.46v.93l1.16.23,1.84.23,1.17.47.92,1.16v.23l.46.7.23.47-.23,1.63-.23,2.08-.46,1.4.23,1.86v.69l-.23.46-.92.7-1.17.7-2.54,1.62-.94.23h-.92l-1.16.46-1.15.93-2.57,3.71-2.08,3.48-.7,2.79-.94,2.78-1.84,1.87-.23.92v2.08l-.23,2.09-.47,1.16-.22.23-2.09.7-1.16.92-.7,1.39-.24,1.86-.46,2.09-.24,1.63-.22.92-.71.93-.91.92-.93.7-2.32.47-1.17,2.09-1.15,1.86v.7l-.93,3.71-1.62,3.02-.23.93v.69l-2.09,2.55-.7.71-.46.92.46.93.7.92v.24l-.93.7-1.62,1.39-.93.93-.23.46-.23,2.09-.23.46-.7.7-.93.93-1.16.7-1.16.46h-.93l-.69-.23-.46-.7-.47-2.32-.46-.7-.7-.46-1.39-1.38-1.38-1.4-1.63-.92h-.47l-.23.23-.46,1.63-.47.47h-.92l-1.63.23-1.16-.23-.23-.47-.24-.46-.23-.47-1.39.93-2.09,1.62-.7.23-.46.23-.93,1.4-.7,1.63-1.16.7-1.16.7-.46.7-.92.69-1.86,1.62-2.32,2.33-.7,1.39-.93,1.63-.46,2.09-.46,2.08-.7,3.48-1.16,2.09-.92,1.39-.93,1.39-.23.92h-.23l-.24.46-1.15-.46-.47-.69-.7-.24-1.15-1.39-.24.24,1.15,3.48-.46,1.16h-3.48l-3.01.46h-1.86l-1.16-.46-.46-1.16-.24.7-.7.46-2.09.23-1.16-.92-.69-.93-.93-.47v.47l1.16.93-.23,1.39-1.86,1.39h-1.16l-.7-.47-.23-1.16-.24-1.62-.47-.93h-.23l.23.93v2.32l.93,1.16-1.39.47h-1.62l-.24-.47-.23-.92-.24-.24-.23,1.63-.93.23h-.46l-2.09.24h-.46v-.24l.23-.47v-.92l-.92.7v1.16l-.47.23-1.16-.23-1.39-.69-.92-.47-1.4-.92-2.79-2.55-.46-1.16-.7-1.39-.7-1.39-.7-2.33.23-.24.7.24.24-.24-1.17-.46-.23-.24v-1.85l.92-.24.7-.23.47-.7.23-.46-2.09.92-2.08-1.15-.23-.7v-.47h2.31l.93-.46-.46-.23h-.93l-.22-.23v-.93l-.47.23-.24.7-1.39.46-.7-.46-.22-1.17v-.46l-.7-.47-2.32-3.02-3.02-2.32-2.78-1.86-3.94-.7h-8.36l-.46-.23.69-.47.7-.23,2.56-1.39-.46-.24-2.8.93h-.92l-1.16,1.86-7.42.23h-.7v-.7l.23-2.33.23-.7.24-.69-.24-.93-.23-.93-.23-1.63.46-.69v-4.89l.24-.22.23-.23v-.47l-.47-.93-.46-.93v-2.77l-.47-.7.23-2.32.24-2.79-.24-1.39.46-.93v-4.4l.47-3.49,1.62-.22,2.09-.24.71-1.39.46-1.63v-1.86l.22-.46.93-.93,1.39-1.39-.23-1.39.46-.47.7-.46.93-.23.92-.7.7-1.16.47-2.09-.93-1.4.24-.46.23-.7.47-.7.46-.23,1.15.23v-.23l.24-.24.69-2.09v-.69l-.93-1.63-.22-.92-.24-1.63-.23-1.39-.24-.69-.46-.23-.24-.46-2.09-2.78.23-1.4.7-1.85.7-.7.71-.46v-.47l-.24-.46-.23-.47-.24-.69.24-.46.23-.7v-7.43l1.62-1.15,2.33-2.09,1.16-2.09.7-1.62.7-5.34.7-.24.7-.22,2.32-1.86,1.86-.93,1.39-.23,1.85-.46,1.4.24h2.32l1.85-.24,1.63-.92.7-.47h.92l4.41,1.39Z"/>
                                </g>
                                <g id="Togo">
                                    <polygon class="cls-2 activeCountry" points="386.2 391.79 385.97 393.18 385.27 394.81 384.8 395.5 384.57 399.68 384.8 400.14 387.12 401.54 389.91 403.39 391.76 404.79 392 405.25 392 408.03 392 410.59 392.46 411.75 392.69 413.14 393.16 414.3 395.02 416.16 395.47 417.32 395.47 421.03 395.47 423.82 395.71 427.53 395.71 430.55 395.71 434.73 395.71 439.36 395.71 444.24 394.55 444.24 395.25 445.87 395.25 447.02 395.47 447.49 395.02 448.18 395.25 449.35 395.94 449.58 397.34 451.67 397.8 453.29 395.47 453.98 395.71 454.45 391.53 455.39 389.91 456.07 389.91 455.39 389.45 455.15 388.75 454.92 388.28 454.68 387.58 453.76 387.36 453.06 386.42 452.82 385.27 452.14 384.11 451.2 383.64 450.51 383.87 450.04 383.64 449.58 383.4 449.58 382.25 447.49 381.78 446.79 381.32 446.1 381.56 445.64 381.32 444.93 381.56 444.47 382.02 444.01 382.25 443.78 382.25 442.85 382.72 441.23 382.72 439.6 382.25 439.13 381.56 438.89 381.09 438.45 381.09 437.74 381.09 437.04 382.49 434.73 382.25 429.16 382.25 428.22 382.94 427.77 383.4 427.06 383.4 426.37 382.49 424.74 380.86 423.58 379.93 422.42 379.46 421.49 379.46 421.03 380.39 420.33 380.86 419.87 380.86 419.41 380.62 418.25 380.62 416.39 381.09 415 381.32 413.14 381.32 412.68 380.39 411.52 379.93 411.52 379.46 411.52 378.31 412.22 377.84 412.44 377.6 412.22 377.6 411.75 377.84 411.52 377.84 410.81 378.07 410.35 378.77 410.13 378.99 409.89 378.07 409.89 378.07 409.43 378.07 409.19 378.31 409.19 378.53 409.19 378.77 408.97 378.99 407.34 378.99 406.87 379.23 405.71 379.23 401.76 379.46 401.3 379.46 400.84 378.77 400.84 377.38 399.68 376.45 398.75 375.75 398.05 375.06 397.36 373.9 396.44 373.43 395.97 373.43 395.28 373.67 394.34 374.36 392.94 374.59 391.32 374.36 390.87 373.67 390.16 376.68 390.62 380.86 391.79 380.86 392.02 380.86 392.25 381.78 392.25 382.94 391.79 386.2 391.79"/>
                                </g>
                                <g id="Benin">
                                    <polygon class="cls-2 activeCountry" points="421.47 382.51 420.77 383.2 420.07 385.05 419.85 386.45 421.94 389.24 422.17 389.7 422.63 389.93 422.87 390.62 423.1 392.02 423.33 393.65 423.55 394.57 424.49 396.2 424.49 396.89 423.79 398.98 423.55 399.22 423.55 399.45 422.4 399.22 421.94 399.45 421.47 400.14 421.24 400.84 421 401.3 421.94 402.7 421.47 404.79 420.77 405.95 419.85 406.64 418.91 406.87 418.22 407.34 417.76 407.81 417.99 409.19 416.6 410.59 415.66 411.52 415.45 411.98 415.45 413.84 414.98 415.47 414.27 416.86 412.19 417.09 410.57 417.32 410.1 420.8 410.1 422.89 410.1 425.2 409.64 426.14 409.87 427.53 409.64 430.32 409.4 432.63 409.87 433.34 409.87 434.73 409.87 436.11 410.33 437.04 410.8 437.98 410.8 438.45 410.57 438.67 410.33 438.89 410.33 442.38 410.33 443.31 410.33 443.78 409.87 444.47 410.1 446.1 410.33 447.02 410.57 447.96 410.33 448.65 410.1 449.35 409.87 451.67 409.87 452.37 404.3 452.82 398.26 453.76 395.71 454.45 395.47 453.98 397.8 453.29 397.34 451.67 395.94 449.58 395.25 449.35 395.02 448.18 395.47 447.49 395.25 447.02 395.25 445.87 394.55 444.24 395.71 444.24 395.71 439.36 395.71 434.73 395.71 430.55 395.71 427.53 395.47 423.82 395.47 421.03 395.47 417.32 395.02 416.16 393.16 414.3 392.69 413.14 392.46 411.75 392 410.59 392 408.03 392 405.25 391.76 404.79 389.91 403.39 387.12 401.54 384.8 400.14 384.57 399.68 384.8 395.5 385.27 394.81 385.97 393.18 386.2 391.79 386.65 391.79 387.12 391.32 387.36 390.62 387.83 390.62 388.28 390.87 388.75 390.62 388.51 390.16 388.75 389.46 389.21 389.24 389.45 388.77 389.45 388.3 389.91 388.07 390.61 388.07 391.29 388.07 391.76 387.84 392.23 386.68 392.69 386.21 392.69 385.99 393.16 385.76 394.08 385.52 394.79 385.76 395.47 386.45 398.72 385.76 400.35 386.21 403.6 383.43 404.3 382.51 405.22 380.64 405.69 379.72 405.93 378.56 405.22 376.01 405.46 375.54 406.62 375.08 408.48 374.61 408.94 374.61 409.4 374.38 410.1 373.68 411.02 373.46 411.72 373.46 411.95 373.68 415.45 376.94 417.06 378.56 417.53 379.49 418.22 379.95 419.38 380.41 420.54 381.35 421.47 382.51"/>
                                </g>
                                <g id="Ghana">
                                    <polygon class="cls-2 activeCountry" points="373.67 390.16 374.36 390.87 374.59 391.32 374.36 392.94 373.67 394.34 373.43 395.28 373.43 395.97 373.9 396.44 375.06 397.36 375.75 398.05 376.45 398.75 377.38 399.68 378.77 400.84 379.46 400.84 379.46 401.3 379.23 401.76 379.23 405.71 378.99 406.87 378.99 407.34 378.77 408.97 378.53 409.19 378.31 409.19 378.07 409.19 378.07 409.43 378.07 409.89 378.99 409.89 378.77 410.13 378.07 410.35 377.84 410.81 377.84 411.52 377.6 411.75 377.6 412.22 377.84 412.44 378.31 412.22 379.46 411.52 379.93 411.52 380.39 411.52 381.32 412.68 381.32 413.14 381.09 415 380.62 416.39 380.62 418.25 380.86 419.41 380.86 419.87 380.39 420.33 379.46 421.03 379.46 421.49 379.93 422.42 380.86 423.58 382.49 424.74 383.4 426.37 383.4 427.06 382.94 427.77 382.25 428.22 382.25 429.16 382.49 434.73 381.09 437.04 381.09 437.74 381.09 438.45 381.56 438.89 382.25 439.13 382.72 439.6 382.72 441.23 382.25 442.85 382.25 443.78 382.02 444.01 381.56 444.47 381.32 444.93 381.56 445.64 381.32 446.1 381.78 446.79 382.25 447.49 383.4 449.58 383.64 449.58 383.87 450.04 383.64 450.51 384.11 451.2 385.27 452.14 386.42 452.82 387.36 453.06 387.58 453.76 388.28 454.68 388.75 454.92 389.45 455.15 389.91 455.39 389.91 456.07 388.98 456.54 388.28 457.23 387.58 458.4 386.89 459.79 384.34 460.26 383.17 460.26 377.84 460.49 372.97 462.81 369.95 463.74 368.32 465.12 365.77 466.06 364.15 467.22 360.67 467.91 354.86 469.77 353.24 470.47 351.38 471.86 348.37 473.48 347.21 473.25 345.11 471.86 343.26 471.16 339.08 470 335.83 469.77 334.44 469.31 333.97 469.08 334.44 468.61 335.14 468.61 336.07 468.84 336.76 468.37 337.93 468.37 338.15 467.91 338.15 466.75 338.15 466.06 338.61 465.59 338.61 464.66 338.15 462.34 337.69 462.11 336.07 461.88 335.83 461.42 335.6 460.95 335.14 459.79 334.67 458.17 333.97 456.07 332.81 452.6 332.58 451.43 332.35 450.28 332.35 448.65 332.58 448.18 332.58 447.49 332.35 446.56 333.28 444.93 334.9 442.85 335.36 441.92 335.6 441.46 335.6 440.76 336.07 438.21 336.76 435.19 337.22 434.03 337.69 433.34 338.15 432.41 338.15 431.94 339.78 430.78 340.47 430.55 340.71 430.09 340.47 429.38 340.47 429.16 340.94 428.93 341.41 428.69 341.87 428.22 341.18 424.52 340.71 420.8 340.71 420.57 340.24 420.11 340.01 418.48 339.55 417.55 338.85 417.32 338.85 416.39 339.55 415 339.78 414.3 339.32 414.06 339.32 413.37 339.55 412.22 339.32 411.52 339.32 410.81 338.61 409.19 338.38 408.03 338.61 407.58 338.61 405.95 338.38 403.62 338.15 402.23 338.61 401.76 338.38 401.07 337.93 400.61 337.69 399.91 338.15 399.45 338.15 399.22 337.69 398.75 336.99 398.05 336.53 396.89 336.76 395.28 337.46 392.02 337.69 391.56 338.61 391.56 338.61 391.79 341.87 391.79 345.58 391.79 349.76 391.79 353.71 391.56 353.93 391.56 354.63 391.32 358.57 391.56 360.9 391.56 362.06 391.56 362.75 391.79 364.38 391.79 365.3 391.79 366 392.48 366.46 392.48 366.7 392.25 367.39 391.79 368.09 391.56 368.56 390.87 369.02 390.4 369.48 390.4 369.95 390.4 370.42 390.16 370.64 389.46 373.67 390.16"/>
                                </g>
                                <g id="Cote_D_I_viore" data-name="Cote_D_I_viore">
                                    <path class="cls-2 activeCountry" d="m334.44,468.61l-.47.47-1.62-.24.23-.46,1.86.24Zm-41.09-71.72v.47l.24,1.15.23.93v.46l-.23.7-.24.46v.23l.47.46,1.16.47h.92l.7-.7.46-.47.47-.23.23-.7.71-.46,1.85-.7h1.86l.47.24.7.92,1.15.47h1.4l1.16.23,1.15.7.47,1.63.92.92.24,1.62,1.16.69.92.47,1.17,1.16,1.16.47h1.39l.46.46,1.15.46h.93l.7-1.16,1.16-.69,2.78-.93,1.16-.47,1.15-.23,2.79-.24,2.55.46,1.17.23.92-.23.7.7.92,1.16.7.47.7.46.47.92.69.92.24.47.93,1.16h.69l.46-.46.24-.46.23.7-.23,1.15v.7l.46.24-.23.7-.7,1.39v.92l.7.24.46.93.23,1.62.46.46v.23l.47,3.72.69,3.71-.46.47-.47.24-.46.23v.23l.24.71-.24.46-.7.23-1.63,1.16v.47l-.46.93-.47.69-.46,1.16-.69,3.02-.47,2.55v.7l-.24.47-.46.92-1.63,2.08-.92,1.63.23.92v.7l-.23.46v1.63l.23,1.16.23,1.16,1.16,3.48.7,2.09.47,1.62.46,1.16.23.47.24.46,1.62.23.46.23.47,2.32v.93l-.47.46v1.86l-.22.46h-1.17l-.69.46-.93-.23v-.24l-.47-.23-1.39-.47.23-1.85-.7-.24-.46.24-.93,2.32-.46.46-6.73-1.15-1.63-.93-1.62-.24h-3.25l-2.55.24-.7.69,6.5-.46.69.24.23.23-8.12.93-3.01.23h-.93l-.7-.7-3.49-.22-.7.22-.23.7,1.16-.23h2.09l.7.46-6.5.47-4.64,1.15-1.85.7-6.51,2.55-3.72,1.16-1.16.46-1.62,1.16-2.33.7-2.54,1.39-1.62.47-.24-.47v-2.55l-.23-3.25v-1.16l.23-1.17v-.92l.7-.47.23-.46.23-1.16.7-1.16v-2.09l.23-.23.24-.69-.47-1.17-.46-2.56v-.22l-.24.22h-.46l-1.62-.93-1.17-.23-.92-.7v-.7l-.47-.46-.23-1.16-.46-.92-1.16-.7-1.15-.24-.7.24h-1.15l-.93-.47-.69-.46-.93-.7-.47-.7h-1.16l-.7-.46-.23-.24,2.55-2.55.93-1.17.22-.92v-.7l.24-.7v-1.39l-1.39-4.4-.47-1.39-.23-.24-.24-.23.7-.47h.93l1.62.47.24-.47,1.15-2.08v-1.39l.7-1.63.46-.7.23-.46v-.93l-.47-.23h-.46l-.7-.24-.92-.47-.7-.46.24-2.1.23-.69.24-.24.46-.23h1.63l1.17.23,1.15.24h.71l.46.46.7.7h.47l.24-.47-.24-2.09-.23-.92-.93-1.17-2.08-.69-.23-1.39.23-1.16.46-.47,1.63-.92-.23-.46-.47-.47-1.15-.46.22-1.63.24-1.39-.92.24h-.93l-.7-.46-.7-.7v-5.12l-.23-1.15.23-.7.7-.7.93-.69.23-.47h.47l.92-.24.93-.7.92-1.39,1.16-1.15h1.15l.47-.24h.46l.47.7.7.69h.24l.46.93,2.31.46.93.47.93.69h.23l.46-.23.22-.24v-.23l-.46-.7.24-.7.46-.47.46-.23h2.78l.47-.46-.47-1.63.23-.92v-.69l.24-.24,1.16.92,1.16.24h.93l-.23-1.16v-.23l.23-.24h.68l1.17-.46h.23l.23,1.62Z"/>
                                </g>
                                <g id="Liberia">
                                    <polygon class="cls-2" points="264.12 436.82 264.35 437.04 264.58 437.28 265.05 438.67 266.44 443.08 266.44 444.47 266.2 445.17 266.2 445.87 265.98 446.79 265.05 447.96 262.49 450.51 262.72 450.75 263.42 451.2 263.89 451.2 264.58 451.2 265.05 451.9 265.98 452.6 266.67 453.06 267.6 453.53 268.75 453.53 269.46 453.29 270.61 453.53 271.78 454.23 272.24 455.15 272.47 456.31 272.94 456.77 272.94 457.47 273.86 458.17 275.03 458.4 276.65 459.32 277.11 459.32 277.35 459.1 277.35 459.32 277.81 461.88 278.28 463.05 278.05 463.74 277.81 463.97 277.81 466.06 277.11 467.22 276.88 468.37 276.65 468.84 275.95 469.31 275.95 470.23 275.72 471.4 275.72 472.56 275.95 475.8 275.95 478.36 276.19 478.83 274.79 478.59 270.38 476.74 266.9 475.58 255.53 469.53 252.51 467.22 248.8 463.5 240.67 456.31 238.82 455.15 236.5 454.45 235.1 453.76 234.18 453.06 233.25 451.2 231.16 450.04 227.45 448.18 224.67 445.4 225.36 444.71 226.29 442.85 227.68 440.99 229.08 440.07 230.23 438.89 231.4 437.98 232.79 437.04 235.34 434.49 235.81 434.26 236.26 432.41 236.97 430.09 237.66 429.38 239.29 428.93 239.51 428.69 240.22 427.06 240.67 425.2 240.67 424.74 241.37 424.74 242.3 424.28 242.99 424.52 243.23 424.98 243.46 425.44 246.94 424.28 247.18 424.05 247.4 424.05 247.64 425.2 248.11 424.98 248.11 424.74 248.34 424.74 248.8 424.74 249.03 425.44 249.5 425.67 250.19 426.14 250.66 426.59 250.42 427.53 250.66 428.69 251.13 428.93 251.13 429.62 251.35 430.32 251.35 430.78 251.59 431.47 251.59 432.17 251.59 432.87 252.28 433.79 252.51 434.96 252.51 435.88 252.28 436.58 252.05 437.51 251.35 438.45 251.35 438.67 251.59 438.89 252.28 438.89 252.75 438.89 253.91 439.13 254.6 439.83 255.06 440.52 255.53 440.76 255.76 441.23 256.69 441.23 257.62 440.76 257.85 440.52 258.08 440.76 258.78 440.76 259.24 439.83 259.48 438.89 260.41 437.98 260.64 437.74 260.86 437.04 260.86 436.11 261.1 435.42 261.8 435.19 262.49 435.19 262.72 435.19 262.96 435.88 263.65 436.58 264.12 436.82"/>
                                </g>
                                <g id="Sierra_Leone" data-name="Sierra Leone">
                                    <path class="cls-2" d="m211.43,438.45l-.23.23-.93-.7-4.41-1.4,1.16-.7h3.25l.93.23.46.47v.46l-.24,1.4Zm17.41-32.74l.92,1.16.92.71,1.17,1.62,1.16,1.86,1.39,1.86.93.92v.7l-.46.93-.23.92v.24l.23.23,1.39.24.24,1.16v1.39l.7,1.39.7.93-.24.46-1.62,1.62-.47,1.86-.46.47v.22l.24.24h1.63l.92-.46,1.15-1.63.47-.23,1.16.23v.46l-.46,1.86-.71,1.63-.23.24-1.63.46-.69.71-.7,2.32-.46,1.86-.47.23-2.55,2.55-1.39.93-1.16.92-1.15,1.17-1.4.92-1.39,1.86-.93,1.86-.69.7-.47-.47-2.55-1.86-2.55-1.39-5.34-2.08-1.86-.46v-.69l.7-1.4-.92-1.63.22-1.15h-.22l-.93.7-1.63-.23-.92-.93-.92-.46-.46-.47-.7-2.55-.24-1.16-.93-.7-1.61-.24-.7-1.63-.92-1.15.23-.7h.7l.46.46,1.16.24,1.15-1.39.93-.69.23-.47v-.46l-.69.46h-1.85l-.46.47-.7.23-.71-1.62.24-.93.23-.92,1.63-.24.23-.46-1.15-.24-1.63-1.16-.24-.7.69-.22h.71l.7.22.7-.22.69-.46.24-.71.7-1.39,1.63-.47.93-.92.93-1.62.46-.93.23-.46.46-.46v-.47l.46-.46.47-1.15.24-.93.92-.47,2.09-.46,1.62.69,3.01-.69v-.92h8.35l.93.22.23.7Z"/>
                                </g>
                                <g id="Guinea">
                                    <polygon class="cls-2" points="226.29 372.99 225.82 373.46 225.36 374.15 225.12 375.08 224.67 375.77 224.9 376.23 225.82 377.16 227.21 378.32 227.92 378.32 228.61 378.1 229.54 377.16 230.47 376.23 231.16 375.77 232.09 375.77 232.79 376.47 233.71 378.1 234.64 379.49 235.1 379.72 235.57 379.72 236.04 379.49 236.26 379.25 236.73 378.56 238.12 376.7 239.51 376.01 239.98 376.01 240.67 375.54 242.08 376.01 244.16 376.94 246.71 377.86 247.4 378.1 248.11 377.86 248.8 376.47 249.73 376.01 250.89 375.31 252.05 375.08 252.75 375.08 252.98 374.61 252.98 374.15 252.98 373.68 252.28 372.52 252.28 372.29 252.51 372.06 253.44 372.06 254.6 372.06 255.76 372.52 256.69 373.22 257.39 373.91 258.08 375.54 258.55 376.94 259.7 379.49 259.7 380.88 259.7 382.74 260.17 382.98 260.86 383.2 261.1 383.43 261.8 384.82 262.27 385.29 262.96 385.29 264.12 386.21 265.05 386.45 265.05 386.68 265.05 387.15 264.81 387.61 264.35 387.84 263.65 388.54 262.96 389.24 261.8 391.09 261.57 391.56 262.03 391.79 262.49 391.79 262.96 391.79 264.12 391.09 265.05 391.32 265.98 391.79 266.2 392.25 266.44 392.94 266.2 393.87 266.2 395.04 266.44 396.67 266.9 398.29 267.36 398.98 270.16 400.61 270.61 401.07 270.61 401.76 270.38 402.7 270.16 403.17 269.22 403.86 268.53 404.55 268.3 405.25 268.53 406.4 268.53 408.97 268.53 411.52 269.22 412.22 269.92 412.68 270.85 412.68 271.78 412.44 271.54 413.84 271.32 415.47 272.47 415.92 272.94 416.39 273.16 416.86 271.54 417.78 271.08 418.25 270.85 419.41 271.08 420.8 273.16 421.49 274.1 422.66 274.33 423.58 274.57 425.67 274.33 426.14 273.86 426.14 273.16 425.44 272.71 424.98 272 424.98 270.85 424.74 269.69 424.52 268.06 424.52 267.6 424.74 267.36 424.98 267.13 425.67 266.9 427.77 267.6 428.22 268.53 428.69 269.22 428.93 269.69 428.93 270.16 429.16 270.16 430.09 269.92 430.55 269.46 431.24 268.75 432.87 268.75 433.34 268.75 434.26 267.6 436.35 267.36 436.82 265.74 436.35 264.81 436.35 264.12 436.82 263.65 436.58 262.96 435.88 262.72 435.19 262.49 435.19 261.8 435.19 261.1 435.42 260.86 436.11 260.86 437.04 260.64 437.74 260.41 437.98 259.48 438.89 259.24 439.83 258.78 440.76 258.08 440.76 257.85 440.52 257.62 440.76 256.69 441.23 255.76 441.23 255.53 440.76 255.06 440.52 254.6 439.83 253.91 439.13 252.75 438.89 252.28 438.89 251.59 438.89 251.35 438.67 251.35 438.45 252.05 437.51 252.28 436.58 252.51 435.88 252.51 434.96 252.28 433.79 251.59 432.87 251.59 432.17 251.59 431.47 251.35 430.78 251.35 430.32 251.13 429.62 251.13 428.93 250.66 428.69 250.42 427.53 250.66 426.59 250.19 426.14 249.5 425.67 249.03 425.44 248.8 424.74 248.34 424.74 248.11 424.74 248.11 424.98 247.64 425.2 247.4 424.05 247.18 424.05 246.94 424.28 243.46 425.44 243.23 424.98 242.99 424.52 242.3 424.28 241.37 424.74 240.67 424.74 239.51 424.52 239.05 424.74 237.89 426.37 236.97 426.83 236.5 426.83 235.81 426.83 235.34 426.83 235.1 426.59 235.1 426.37 235.57 425.9 236.04 424.05 237.66 422.42 237.89 421.96 237.2 421.03 236.5 419.64 236.5 418.25 236.26 417.09 234.87 416.86 234.64 416.63 234.64 416.39 234.87 415.47 235.34 414.53 235.34 414.06 235.34 413.84 234.41 412.91 233.02 411.06 231.86 409.19 230.69 407.58 229.77 406.87 228.84 405.71 228.61 405.01 227.68 404.79 225.12 404.79 222.11 404.79 219.32 404.79 219.32 405.71 216.31 406.4 214.69 405.71 212.6 406.18 211.67 406.64 211.43 407.58 210.97 408.73 210.5 409.19 210.5 409.66 210.04 410.13 209.81 410.59 209.35 411.52 208.42 413.14 207.49 414.06 205.86 414.53 205.17 415.92 204.93 416.63 204.24 417.09 203.55 417.32 202.85 417.09 202.14 417.09 201.45 417.32 201.22 416.86 201.69 415.69 201.45 415 200.06 413.84 200.06 413.14 199.59 412.44 197.74 410.81 196.12 410.81 196.59 409.43 196.59 407.81 196.12 406.64 196.35 405.71 195.88 405.71 195.42 406.4 194.5 406.18 192.87 405.25 191.94 404.09 191.7 403.39 191.7 402.93 191.02 403.17 190.08 403.17 186.61 401.54 184.28 397.59 184.28 396.67 184.52 395.04 184.52 394.81 183.36 395.73 183.13 395.04 182.42 393.41 182.19 392.48 181.26 392.02 180.56 392.02 180.11 392.25 179.4 394.1 178.94 394.1 178.48 393.87 178.72 392.48 179.17 391.79 179.87 390.62 181.95 386.21 182.89 385.29 183.36 384.82 184.28 384.82 186.36 384.36 187.99 383.43 188.69 382.98 190.55 382.98 192.87 382.98 195.66 381.8 195.66 380.64 195.66 379.03 195.66 378.32 194.5 377.63 194.03 377.16 193.56 376.47 192.87 376.01 192.87 375.54 193.56 375.08 194.25 375.08 195.42 375.08 195.66 374.61 196.12 374.38 196.35 373.22 196.35 372.06 195.66 370.68 195.66 369.5 200.06 369.74 200.3 369.74 202.14 369.97 203.55 369.97 204.24 369.97 204.47 370.21 204.47 370.68 204.24 371.36 204.47 372.06 205.17 372.06 205.39 371.83 205.86 371.6 206.1 371.36 206.8 371.6 207.96 372.52 208.88 372.75 210.27 373.22 211.21 373.46 212.36 373.46 213.06 373.91 214.44 374.15 216.31 373.46 217.69 373.22 219.79 373.22 220.72 373.22 223.73 372.75 225.36 372.99 226.29 372.99"/>
                                </g>
                                <g id="Guinea_Bissau" data-name="Guinea_Bissau">
                                    <path class="cls-2 activeCountry" d="m167.57,390.62l-.23.24h-.7l.24-.47h-.24l.24-1.16v-.23l.46.46v.24l.23.92Zm-2.78.24l-1.16.23-.47-.7v-.24l.47-.23h.24l.46-.47.47-.46h.46l.23,1.16-.23.46-.46.24Zm4.87-2.1v.47h-.46l-.24-.23.46-.93.47-.46h.46l.23.24v.46l-.23.24-.7.23Zm-2.31-3.25l-.46.46-.71-.23-.23-.47v-.7l.7-.92.7.23v1.62Zm4.64-.93l-.23.24-.71-.24,1.17-1.16.69-.23v.92l-.46.24-.46.22Zm-5.56-4.63l-.71,1.62-.92-.23-.47-.93-.24-.46h2.34Zm29.94-7.89v1.16l-.23,1.16-.46.23-.24.47h-1.86l-.7.46v.47l.7.46.46.7.47.47,1.16.69v3.48l-2.79,1.17h-4.18l-.7.46-1.63.93-2.08.46h-.92l-.47.46-.93.92-2.08,4.41-.7,1.17-.46.69-.71-.92.46-1.86h-.46l-1.16,1.39-.47.23v-1.86h-.69l-.7.24-.93-.93v-1.62l.7-.46-.23-.23h-1.16l-.47-.24.7-1.16,2.32-.92,1.16-.23,1.16-.23-.7-.69-1.39-.47-1.16.23-.46.7h-.7l-1.16-1.39v-.7l.46-.93.7-.23h2.54l1.17-.46.23-.23.46-.46v-.24h-.46l-.93.7-3.24-.24-1.17.24-1.61,1.39-2.33.7-1.62-.23.46-1.86-.24-.24-.46-.22-2.33.46-1.62-.7-.69-.93v-1.39l.92-.7v-.46h-.92l-1.63.46-3.48-2.08h.7l1.86.23,1.39-.23.92-.46,1.16-.7,1.39-.24,3.95.46,3.49-.69,2.55-1.39,2.31-1.17h19.04v1.17l.7,1.39Z"/>
                                </g>
                                <g id="Gambia" data-name="Gambia">
                                    <polygon class="cls-2 activeCountry" points="156.19 364.4 156.19 363.24 155.5 360.69 156.43 359.53 157.35 358.82 158.06 359.53 158.29 360.45 158.98 361.16 160.84 361.61 162.7 361.38 163.86 361.38 163.63 360.92 164.08 360.22 166.42 359.76 168.73 359.53 171.05 359.06 173.14 359.06 173.61 359.06 173.37 358.82 171.75 358.6 168.04 359.06 164.32 359.29 161.54 360.69 160.37 360.45 159.21 359.06 158.75 357.44 162.23 357.21 166.17 357.44 170.59 357.44 172.44 357.44 173.61 355.58 175.69 354.65 177.78 354.42 178.94 354.42 180.11 354.89 182.19 356.28 183.58 356.75 184.74 356.97 185.67 357.91 186.83 358.6 187.99 358.82 188.69 358.6 189.62 358.38 190.31 358.13 192.41 357.91 194.03 358.82 194.5 359.76 194.25 360.69 191.94 361.38 188.92 362.08 186.61 361.61 183.36 360.45 181.73 359.76 181.03 359.53 179.87 359.06 178.94 358.38 178.01 358.13 177.31 357.91 176.62 358.13 176.38 358.82 175.93 359.53 175.47 359.99 172.9 360.22 170.59 360.45 169.43 360.69 168.5 360.92 168.26 363.24 165.71 363 163.16 363 160.6 363.24 157.82 363.24 156.9 363.71 156.19 364.4"/>
                                </g>
                                <g id="Senegal">
                                    <polygon class="cls-2 activeCountry" points="214.69 341.19 215.85 343.28 215.62 344.45 215.38 345.83 216.07 346.99 216.78 347.69 217.47 348.39 217.93 349.31 218.16 350.93 217.93 352.33 218.4 352.8 218.85 353.72 218.63 354.19 218.4 354.65 217.69 355.58 217.47 356.75 218.85 358.38 219.56 359.29 219.56 359.76 219.79 360.45 220.48 360.92 220.72 360.92 221.18 360.45 221.42 359.99 222.57 360.22 223.04 360.22 223.73 361.38 223.96 362.08 224.2 363 224.9 364.17 225.58 364.86 225.58 365.33 226.29 366.02 225.82 367.42 225.82 368.11 225.58 370.21 225.36 371.13 225.36 371.36 226.29 372.06 226.29 372.99 225.36 372.99 223.73 372.75 220.72 373.22 219.79 373.22 217.69 373.22 216.31 373.46 214.44 374.15 213.06 373.91 212.36 373.46 211.21 373.46 210.27 373.22 208.88 372.75 207.96 372.52 206.8 371.6 206.1 371.36 205.86 371.6 205.39 371.83 205.17 372.06 204.47 372.06 204.24 371.36 204.47 370.68 204.47 370.21 204.24 369.97 203.55 369.97 202.14 369.97 200.3 369.74 200.06 369.74 195.66 369.5 191.47 369.5 187.76 369.5 182.89 369.5 179.64 369.5 176.62 369.5 174.31 370.68 171.75 372.06 168.26 372.75 164.32 372.29 162.93 372.52 161.76 373.22 160.84 373.68 159.45 373.91 157.59 373.68 156.9 373.68 156.43 373.22 155.96 372.29 156.19 371.6 157.35 371.13 158.98 370.43 159.91 370.68 160.37 370.68 160.37 370.43 160.14 370.21 158.98 369.74 158.29 369.05 157.82 369.27 157.35 370.21 157.12 370.43 156.43 370.68 156.19 370.21 155.96 369.5 156.19 369.05 156.19 366.72 156.19 365.56 156.19 364.4 156.9 363.71 157.82 363.24 160.6 363.24 163.16 363 165.71 363 168.26 363.24 168.5 360.92 169.43 360.69 170.59 360.45 172.9 360.22 175.47 359.99 175.93 359.53 176.38 358.82 176.62 358.13 177.31 357.91 178.01 358.13 178.94 358.38 179.87 359.06 181.03 359.53 181.73 359.76 183.36 360.45 186.61 361.61 188.92 362.08 191.94 361.38 194.25 360.69 194.5 359.76 194.03 358.82 192.41 357.91 190.31 358.13 189.62 358.38 188.69 358.6 187.99 358.82 186.83 358.6 185.67 357.91 184.74 356.97 183.58 356.75 182.19 356.28 180.11 354.89 178.94 354.42 177.78 354.42 175.69 354.65 173.61 355.58 172.44 357.44 170.59 357.44 166.17 357.44 162.23 357.21 158.75 357.44 158.53 356.04 157.82 354.89 156.43 353.96 156.19 353.26 156.66 352.56 157.82 351.87 158.06 351.4 157.59 351.4 156.43 351.87 155.96 351.87 155.74 350.7 154.81 349.08 153.41 346.52 152.02 345.6 150.86 343.51 149.7 342.59 148.54 342.35 147.61 342.35 147.38 343.28 146.23 341.89 147.85 341.42 151.33 339.8 155.27 334.69 158.75 328.89 159.21 327.5 159.68 326.33 159.91 324.02 160.37 322.63 160.84 322.39 161.54 321.23 162.23 319.38 163.16 318.21 164.08 317.98 164.79 318.21 165.25 318.44 166.64 318.68 169.2 318.91 171.05 318.44 172.44 317.98 174.31 317.52 176.38 317.52 177.55 317.29 177.78 316.58 178.01 316.58 178.48 316.82 178.94 316.58 179.4 316.37 179.64 316.37 180.11 316.58 181.95 316.58 185.2 316.58 188.23 317.52 191.02 319.83 192.63 321.23 192.63 321.92 193.09 322.63 194.03 323.32 194.72 323.55 195.42 323.09 195.88 323.09 196.35 323.55 197.04 323.79 197.97 323.32 198.66 323.55 198.66 323.79 198.91 324.02 199.37 324.02 199.83 324.48 200.76 325.63 201.45 327.26 201.92 329.35 202.61 330.51 203.32 330.51 203.77 330.98 204.01 331.44 204.24 331.91 204.71 332.14 205.39 331.91 206.1 332.61 207.02 334.23 207.26 334.69 207.02 335.15 207.02 335.39 207.72 335.62 208.18 336.09 208.65 337.01 209.58 337.48 211.21 338.17 212.36 339.1 212.83 340.03 214.22 341.19 214.69 341.19"/>
                                </g>
                                <g id="Mali">
                                    <polygon class="cls-2 activeCountry" points="429.59 283.17 429.59 285.49 429.59 288.97 429.59 292.92 429.59 296.62 429.59 300.81 429.59 304.06 429.59 308 429.59 311.95 429.36 312.41 429.13 314.74 428.9 317.52 428.21 320.54 426.81 322.85 426.35 324.94 425.88 326.1 425.41 326.8 425.18 327.5 424.95 328.66 424.49 329.35 424.25 329.59 422.87 330.05 420.32 332.14 420.07 333.99 417.29 333.52 414.27 333.07 414.05 333.07 413.81 333.3 413.58 334.23 409.64 334.23 406.16 334.46 401.75 334.46 398.72 334.69 395.02 334.93 391.53 334.93 389.21 337.01 387.12 338.87 386.89 338.87 383.87 339.34 380.16 338.87 378.31 338.87 377.6 339.1 377.38 339.8 374.59 338.87 371.57 337.94 369.26 338.4 369.02 338.17 368.56 337.94 367.63 337.71 365.77 337.71 364.61 337.94 362.75 339.57 361.37 340.73 360.9 340.97 358.8 341.89 355.09 343.74 353.01 344.91 352.54 345.13 351.61 345.6 349.99 345.6 348.83 345.83 347.66 349.31 346.97 349.78 342.56 348.39 341.63 348.62 340.94 348.86 338.38 350.93 336.99 352.56 336.53 354.89 336.53 355.35 336.53 356.28 336.07 356.51 335.6 356.75 334.9 356.75 332.81 356.28 332.12 356.51 331.89 357.67 331.89 359.99 331.42 361.38 330.26 362.08 329.33 362.54 328.64 362.78 327.94 362.54 324.46 360.22 323.07 359.99 321.67 360.22 320.52 361.16 319.81 361.85 319.12 362.54 318.19 363.71 318.42 364.4 319.12 365.56 319.59 366.72 319.59 367.88 316.11 369.5 316.57 370.21 317.03 370.68 317.03 371.83 316.8 373.91 316.11 374.61 315.41 375.54 314.71 376.47 314.25 376.94 313.32 377.4 312.16 378.1 309.84 378.56 307.98 378.79 307.28 379.25 306.36 379.95 305.66 380.64 305.43 381.57 305.66 382.51 305.89 383.43 306.12 384.13 306.36 384.82 306.12 386.68 305.43 389 304.96 389.93 303.81 390.4 303.11 391.09 303.34 392.48 303.58 394.57 303.34 396.44 303.11 397.36 302.87 398.51 302.65 399.22 302.18 398.98 300.32 398.98 298.47 399.68 297.77 400.14 297.54 400.84 297.07 401.07 296.61 401.54 295.91 402.23 294.98 402.23 293.82 401.76 293.35 401.3 293.35 401.07 293.59 400.61 293.82 399.91 293.82 399.45 293.59 398.51 293.35 397.36 293.35 396.89 293.13 395.28 292.9 395.28 291.73 395.73 291.04 395.73 290.81 395.97 290.81 396.2 291.04 397.36 290.81 397.36 290.11 397.36 288.95 397.13 287.79 396.2 287.55 396.44 287.55 397.13 287.32 398.05 287.79 399.68 287.32 400.14 286.63 400.14 285.47 400.14 284.54 400.14 284.08 400.37 283.62 400.84 283.38 401.54 283.84 402.23 283.84 402.46 283.62 402.7 283.15 402.93 282.92 402.93 281.99 402.23 281.05 401.76 278.74 401.3 278.28 400.37 278.05 400.37 277.35 399.68 276.88 398.98 276.42 398.98 275.95 399.22 274.79 399.22 273.63 400.37 272.71 401.76 271.78 402.46 270.85 402.7 270.38 402.7 270.61 401.76 270.61 401.07 270.16 400.61 267.36 398.98 266.9 398.29 266.44 396.67 266.2 395.04 266.2 393.87 266.44 392.94 266.2 392.25 265.98 391.79 265.05 391.32 264.12 391.09 262.96 391.79 262.49 391.79 262.03 391.79 261.57 391.56 261.8 391.09 262.96 389.24 263.65 388.54 264.35 387.84 264.81 387.61 265.05 387.15 265.05 386.68 265.05 386.45 264.12 386.21 262.96 385.29 262.27 385.29 261.8 384.82 261.1 383.43 260.86 383.2 260.17 382.98 259.7 382.74 259.7 380.88 259.7 379.49 258.55 376.94 258.08 375.54 257.39 373.91 256.69 373.22 255.76 372.52 254.6 372.06 253.44 372.06 252.51 372.06 252.28 372.29 252.28 372.52 252.98 373.68 252.98 374.15 252.98 374.61 252.75 375.08 252.05 375.08 250.89 375.31 249.73 376.01 248.8 376.47 248.11 377.86 247.4 378.1 246.71 377.86 244.16 376.94 242.08 376.01 240.67 375.54 239.98 376.01 239.51 376.01 238.12 376.7 236.73 378.56 236.26 379.25 236.04 379.49 235.57 379.72 235.1 379.72 234.64 379.49 233.71 378.1 232.79 376.47 232.09 375.77 231.16 375.77 230.47 376.23 229.54 377.16 228.61 378.1 227.92 378.32 227.21 378.32 225.82 377.16 224.9 376.23 224.67 375.77 225.12 375.08 225.36 374.15 225.82 373.46 226.29 372.99 226.29 372.06 225.36 371.36 225.36 371.13 225.58 370.21 225.82 368.11 225.82 367.42 226.29 366.02 225.58 365.33 225.58 364.86 224.9 364.17 224.2 363 223.96 362.08 223.73 361.38 223.04 360.22 222.57 360.22 221.42 359.99 221.18 360.45 220.72 360.92 220.48 360.92 219.79 360.45 219.56 359.76 219.56 359.29 218.85 358.38 217.47 356.75 217.69 355.58 218.4 354.65 218.63 354.19 218.85 353.72 218.4 352.8 217.93 352.33 218.16 350.93 217.93 349.31 217.47 348.39 216.78 347.69 216.07 346.99 215.38 345.83 215.62 344.45 215.85 343.28 214.69 341.19 217.01 342.12 217.24 341.66 217.93 341.19 219.1 340.26 220.02 338.63 220.26 337.01 220.48 335.39 220.95 333.99 221.42 333.07 222.57 331.91 223.51 330.98 224.67 330.28 225.36 330.28 226.52 331.44 229.08 333.77 231.16 335.62 231.86 336.56 232.55 336.56 233.71 334.93 234.87 333.3 235.34 333.07 236.73 332.83 237.89 332.83 239.05 332.83 240.91 333.07 241.84 333.3 242.53 333.52 245.09 333.77 247.4 333.3 249.73 332.83 251.59 332.61 251.59 331.91 251.59 330.98 251.81 330.28 252.28 329.82 252.75 329.59 252.98 331.68 253.44 331.91 255.06 332.14 257.39 332.14 260.17 332.14 262.72 332.14 265.51 332.14 268.06 332.14 270.85 332.14 273.4 332.14 276.19 332.14 278.74 332.14 281.29 332.14 284.08 332.14 286.63 332.14 289.41 332.14 291.97 332.14 294.76 332.14 297.3 332.14 300.09 332.14 302.65 332.14 303.58 328.2 304.27 324.48 304.73 321.46 302.87 319.38 301.25 317.75 300.79 314.5 300.56 311.25 300.09 307.77 299.86 304.51 299.4 301.28 298.93 297.79 298.7 294.54 298.23 291.06 298 287.81 297.54 284.32 297.07 281.09 296.84 277.6 296.38 274.35 296.15 270.86 295.69 267.61 295.22 264.13 294.98 260.89 294.52 257.41 294.29 254.16 293.82 250.67 293.35 247.19 293.13 243.94 292.66 240.46 292.43 236.98 291.97 233.73 291.51 230.25 291.27 226.77 290.81 223.52 290.57 220.04 290.11 216.55 289.65 213.07 289.41 209.83 288.95 206.35 288.73 203.1 292.66 203.1 296.84 203.1 301.02 203.1 307.06 203.1 311.7 203.1 315.64 205.65 319.36 208.2 323.53 211.21 327.94 214.24 332.12 217.02 336.3 220.04 340.71 223.06 344.89 226.07 349.06 228.86 353.48 231.87 357.65 234.89 362.06 237.67 366.23 240.7 370.42 243.48 374.82 246.5 378.99 249.52 383.17 252.3 387.58 255.31 389.45 256.48 389.67 257.17 389.91 258.1 389.67 259.49 389.67 260.43 390.37 261.12 391.29 261.82 395.47 263.9 395.94 264.37 395.94 265.3 396.4 266.46 397.34 266.92 398.5 267.61 399.66 267.85 403.36 268.08 404.3 268.78 405.93 270.63 406.85 271.1 409.17 271.56 411.02 272.02 411.95 272.02 413.58 272.73 415.21 273.65 416.37 274.35 416.37 274.58 416.13 275.28 416.13 277.6 416.6 278.75 416.83 279.46 416.83 279.91 416.37 280.38 416.13 280.84 415.9 281.54 415.45 282.25 414.98 282.93 415.21 283.64 415.9 284.09 416.83 284.79 417.76 285.25 418.22 285.25 418.91 285.25 419.38 285.03 422.4 284.56 425.41 283.86 429.59 283.17"/>
                                </g>
                                <g id="Mauritania">
                                    <path class="cls-2 activeCountry" d="m161.31,275.51l-.93,1.39-.24-.7-.23-.7.7-1.39.47-.47.69-.23-.46,2.08Zm132.98-21.35l.23,3.25.46,3.48.24,3.25.47,3.48.46,3.25.23,3.49.46,3.25.23,3.49.47,3.24.46,3.49.23,3.25.46,3.48.23,3.25.46,3.49.47,3.24.23,3.26.46,3.48.23,3.25.46,3.25,1.62,1.63,1.86,2.09-.46,3.02-.69,3.72-.93,3.95h-47.58l-1.62-.24-.47-.23-.23-2.09-.47.23-.46.46-.23.7v1.63l-1.85.22-2.33.46-2.32.47-2.55-.24-.69-.23-.93-.23-1.87-.24h-2.31l-1.39.24-.46.23-1.16,1.63-1.16,1.63h-.69l-.7-.93-2.08-1.86-2.56-2.33-1.16-1.16h-.69l-1.16.7-.93.93-1.16,1.16-.46.92-.47,1.4-.23,1.62-.24,1.62-.92,1.63-1.16.93-.69.47-.24.46-2.32-.92h-.47l-1.39-1.16-.47-.93-1.15-.93-1.63-.69-.92-.47-.47-.92-.46-.46-.7-.23v-.24l.24-.46-.24-.46-.92-1.62-.7-.7-.69.24-.46-.24-.23-.47-.24-.46-.46-.46h-.7l-.7-1.16-.46-2.09-.69-1.63-.93-1.15-.46-.46h-.46l-.24-.23v-.24l-.69-.23-.93.46-.69-.24-.47-.46h-.46l-.7.46-.7-.23-.93-.7-.46-.7v-.69l-1.61-1.4-2.79-2.32-3.02-.93h-5.1l-.46-.22h-.24l-.46.22-.47.24-.46-.24h-.23l-.23.71-1.17.23h-2.08l-1.87.46-1.39.46-1.85.47-2.55-.23-1.39-.24-.46-.23-.71-.24-.92.24-.92,1.16-.7,1.86-.7,1.16-.46.24-.47,1.39-.23,2.31-.46,1.16v-6.03l.69-2.32.23-1.86,1.4-4.42,1.86-3.47,1.86-4.89.46-4.4-.23-4.64-.47-3.94-.7-2.55-.92-3.95-1.16-2.09-2.33-1.62-.46-1.16.46-.47,1.4-.23.92-1.39-1.86.69,2.09-4.4.69-2.79-.23-1.85.46-1.16-1.63-2.55-1.16-3.25-.7-.47-.69-.46v.92l-.47.7-.69-.47-1.4-2.32-1.85-3.94-.7-.23-.7.46-.23.47-.7,3.25-.24-1.39.24-1.39.7-1.86.46-2.56h51.28v-1.85l-.22-1.4v-2.08l-.24-1.87v-1.86l-.23-1.85v-1.86l-.23-1.63v-1.62l-.24-.7-.46-1.86-.24-.93.24-.92.46-.92,1.16-1.39,1.62-1.39,2.1-1.4,1.62-.92.69-.46,2.32-.23,2.09-.93,1.87-.69.69-.47v-35.04h43.64v-18.1l2.32,1.62,2.55,1.62,2.31,1.63,2.56,1.39,2.32,1.62,2.55,1.62,2.31,1.39,2.55,1.62,2.33,1.39,2.55,1.63,2.55,1.63,2.32,1.39,2.54,1.62,2.34,1.63,2.54,1.39,2.32,1.62,2.09,1.16,3.25,2.09,2.79,2.09,3.02,1.86h-22.97l.23,3.25.46,3.48.24,3.25.46,3.48.46,3.49.24,3.49.46,3.24.24,3.49.46,3.48.46,3.25.23,3.48.47,3.48.23,3.25.47,3.48.46,3.49Z"/>
                                </g>
                                <g id="Western_Sahara" data-name="Western Sahara">
                                    <polygon class="cls-2" points="261.57 165.96 261.57 168.28 261.57 171.07 261.57 173.62 261.57 176.17 261.57 178.96 261.57 182.2 261.57 185.45 261.57 187.54 261.57 189.17 258.78 189.17 256.46 189.17 253.91 189.17 251.59 189.17 249.03 189.17 246.71 189.17 244.16 189.17 241.84 189.17 239.51 189.17 236.97 189.17 234.64 189.17 232.09 189.17 229.77 189.17 227.21 189.17 224.9 189.17 222.34 189.17 220.02 189.17 217.93 189.17 217.93 190.79 217.93 192.88 217.93 194.74 217.93 196.6 217.93 198.45 217.93 200.31 217.93 202.17 217.93 204.02 217.93 205.88 217.93 207.97 217.93 209.83 217.93 211.68 217.93 213.54 217.93 215.4 217.93 217.25 217.93 219.1 217.93 220.96 217.93 222.59 217.93 224.21 217.24 224.68 215.38 225.37 213.28 226.31 210.97 226.54 210.27 227 208.65 227.93 206.55 229.33 204.93 230.72 203.77 232.11 203.32 233.04 203.08 233.96 203.32 234.89 203.77 236.75 204.01 237.45 204.01 239.07 204.24 240.7 204.24 242.55 204.47 244.4 204.47 246.26 204.71 248.13 204.71 250.21 204.93 251.6 204.93 253.45 203.08 253.45 200.06 253.45 197.04 253.45 194.03 253.45 191.02 253.45 188.23 253.45 185.2 253.45 182.19 253.45 179.17 253.45 176.16 253.45 173.14 253.45 170.12 253.45 167.1 253.45 164.32 253.45 161.31 253.45 158.29 253.45 155.27 253.45 153.65 253.45 153.18 256.02 152.49 257.87 152.25 259.27 152.49 260.65 151.79 259.96 152.95 252.76 153.18 252.3 154.11 245.8 155.74 242.08 157.35 240.46 159.45 239.76 161.54 236.29 162.23 232.8 163.63 231.41 163.86 230.25 163.39 229.33 164.79 227.47 166.17 224.68 166.88 223.06 168.73 220.27 168.96 219.81 168.73 219.1 168.04 219.57 167.34 220.73 166.42 221.43 166.88 220.51 167.57 219.1 168.96 217.48 171.51 215.86 176.62 210.29 178.72 209.36 180.34 207.04 181.03 204.72 181.26 200.08 181.95 197.53 183.13 195.43 184.28 191.72 185.44 190.09 186.14 186.85 186.83 185.69 188.23 184.98 190.08 183.37 192.87 182.44 196.12 180.12 197.74 178.96 198.66 176.87 199.83 173.15 201.92 168.98 202.85 165.96 206.55 165.96 210.27 165.96 213.99 165.96 217.47 165.96 221.18 165.96 224.9 165.96 228.61 165.96 232.09 165.96 235.81 165.96 239.51 165.96 243.23 165.96 246.71 165.96 250.42 165.96 254.14 165.96 257.85 165.96 259.7 165.96 261.57 165.96"/>
                                </g>
                                <g id="Morocco">
                                    <polygon class="cls-2 activeCountry" points="314.25 57.34 318.19 58.04 322.6 56.64 325.15 56.64 326.55 56.18 327.71 56.88 330.26 57.1 332.81 56.88 334.67 55.71 335.83 54.32 336.07 55.02 336.07 55.71 336.53 56.18 337.22 57.81 337.46 58.5 338.85 58.26 340.24 58.73 343.03 58.5 345.58 58.73 346.04 59.89 346.74 60.82 349.52 62.68 351.15 64.07 351.15 64.3 350.68 65.46 350.45 66.16 350.91 66.86 351.85 67.78 351.85 68.25 351.61 68.72 351.15 69.64 352.31 72.42 352.54 75.21 352.31 77.07 352.31 78.23 352.31 79.16 353.24 81.24 352.78 84.95 353.48 87.05 354.4 88.44 354.86 91.46 355.56 92.85 356.96 94.01 357.65 94.24 359.04 95.4 359.97 96.1 360.67 97.26 359.51 98.42 358.34 99.11 358.12 100.27 358.57 101.67 358.57 102.6 357.88 102.83 355.33 102.83 353.24 102.6 350.91 102.6 347.44 102.36 345.58 102.36 342.56 102.13 341.63 102.36 339.08 102.83 337.22 103.06 336.99 103.06 336.3 103.52 336.07 104.68 335.6 105.85 335.14 106.54 329.79 108.4 327.48 108.63 326.31 108.4 325.39 108.63 324.68 108.87 324.46 109.56 324.46 110.26 324.46 111.18 325.15 112.11 325.15 113.28 324.92 113.98 324.68 114.9 324.68 115.59 324.92 116.06 325.39 116.06 325.84 116.53 326.78 116.99 327.25 117.45 327.25 118.38 326.78 119.07 326.31 119.31 324.23 119.54 322.6 119.78 320.52 121.17 318.19 122.79 315.64 123.95 314.48 124.18 312.39 124.88 309.84 126.04 308.68 128.13 307.06 130.45 305.66 132.07 303.58 133.47 301.71 133.93 299.4 134.63 296.38 135.1 294.29 135.33 293.59 135.55 291.73 135.55 290.81 135.33 290.11 135.33 289.88 135.55 289.88 135.78 289.65 136.72 289.65 137.64 288.95 138.35 288.73 138.81 288.02 139.03 286.63 138.81 285.24 138.58 282.22 138.11 281.52 138.11 281.29 138.35 280.36 138.81 278.74 139.97 277.81 140.89 277.11 141.36 275.26 141.59 274.57 142.06 271.08 144.61 270.38 145.07 266.9 147.4 265.98 148.08 265.05 148.79 263.19 150.41 261.8 151.11 261.57 151.33 261.57 152.27 261.57 154.59 261.57 156.68 261.57 159.46 261.57 162.48 261.57 165.96 259.7 165.96 257.85 165.96 254.14 165.96 250.42 165.96 246.71 165.96 243.23 165.96 239.51 165.96 235.81 165.96 232.09 165.96 228.61 165.96 224.9 165.96 221.18 165.96 217.47 165.96 213.99 165.96 210.27 165.96 206.55 165.96 202.85 165.96 204.71 164.33 205.86 162.25 207.96 161.55 212.13 161.09 218.4 159.23 224.2 156.68 225.58 155.75 227.45 153.66 230.23 151.11 235.57 147.85 237.89 146 241.61 141.59 244.16 137.88 246.24 135.55 247.64 133.47 248.56 131.14 249.27 127.67 248.8 126.5 247.18 124.18 246.24 123.48 246.01 122.56 246.48 120.7 246.48 117.45 246.71 112.35 248.56 108.17 252.75 102.83 253.67 100.51 254.14 97.02 254.14 95.87 259.48 90.75 262.49 86.81 263.65 85.89 266.44 84.26 275.95 80.32 281.52 77.53 284.77 75.45 286.63 73.12 291.73 63.6 296.84 50.14 297.3 48.75 299.62 48.29 301.25 48.05 302.65 47.59 304.27 46.67 305.89 46.9 304.96 47.59 304.96 49.21 306.12 51.31 307.98 53.4 311.47 56.18 314.25 57.34"/>
                                </g>
                                <g id="Algeria">
                                    <polygon class="cls-2 activeCountry" points="473.69 30.65 476.01 32.5 477.63 32.74 480.43 32.04 486.22 31.58 486.46 32.27 486.46 32.97 485.3 33.67 484.6 34.13 483.44 35.99 481.82 37.38 481.35 37.85 481.35 38.07 482.74 38.77 482.97 39.24 483.21 39.93 482.74 42.72 482.27 44.8 481.82 47.36 482.05 48.52 482.51 49.68 482.97 50.61 482.97 51.77 482.74 54.32 483.44 55.94 483.9 57.34 482.74 58.96 482.27 60.59 482.05 62.91 481.82 64.3 481.12 65.46 480.43 66.86 479.26 67.56 478.1 68.25 476.71 68.94 475.55 71.26 473 73.12 472.3 73.82 472.07 75.45 472.3 77.53 472.76 79.16 473.92 81.7 474.85 84.26 475.31 85.66 475.55 86.12 477.17 87.05 479.72 88.21 480.19 88.91 481.35 90.75 482.74 94.01 482.97 96.33 485.52 97.95 487.61 99.58 489.71 100.98 492.02 102.6 492.26 103.06 493.19 106.32 493.89 109.56 494.57 113.04 495.51 116.76 496.43 120.93 497.13 123.24 497.6 126.04 498.52 129.52 497.13 130.21 495.74 131.14 496.9 133 498.76 135.78 500.15 137.88 500.62 139.03 501.54 141.82 502.24 144.61 502.46 145.3 502.7 147.4 502.46 153.19 502.93 160.62 503.63 164.11 502.46 167.35 501.54 170.6 501.54 172 502.01 174.55 502.7 176.41 503.4 177.34 503.16 180.35 502.93 181.52 500.62 183.13 498.06 184.53 497.36 185.69 497.13 187.08 497.6 188.24 499.22 190.79 502.01 194.28 504.79 198.45 505.03 199.38 505.03 202.39 506.42 205.88 507.58 207.5 508.04 208.66 508.97 209.59 509.9 210.06 510.59 210.29 513.61 209.36 519.18 210.99 524.28 212.61 524.75 212.85 525.91 214.93 527.77 218.42 529.16 220.96 530.32 223.52 523.59 227.7 516.86 231.87 510.13 236.05 503.4 240.23 496.67 244.4 489.94 248.59 483.21 252.76 476.48 256.94 472.07 259.72 469.05 262.05 465.56 265.06 462.08 268.08 459.53 270.63 456.05 273.65 454.43 275.28 450.48 278.52 449.33 279.22 444.21 280.15 439.57 281.09 435.4 282.01 432.38 282.46 429.59 283.17 425.41 283.86 422.4 284.56 419.38 285.03 418.91 285.25 418.22 285.25 417.76 285.25 416.83 284.79 415.9 284.09 415.21 283.64 414.98 282.93 415.45 282.25 415.9 281.54 416.13 280.84 416.37 280.38 416.83 279.91 416.83 279.46 416.6 278.75 416.13 277.6 416.13 275.28 416.37 274.58 416.37 274.35 415.21 273.65 413.58 272.73 411.95 272.02 411.02 272.02 409.17 271.56 406.85 271.1 405.93 270.63 404.3 268.78 403.36 268.08 399.66 267.85 398.5 267.61 397.34 266.92 396.4 266.46 395.94 265.3 395.94 264.37 395.47 263.9 391.29 261.82 390.37 261.12 389.67 260.43 389.67 259.49 389.91 258.1 389.67 257.17 389.45 256.48 387.58 255.31 383.17 252.3 378.99 249.52 374.82 246.5 370.42 243.48 366.23 240.7 362.06 237.67 357.65 234.89 353.48 231.87 349.06 228.86 344.89 226.07 340.71 223.06 336.3 220.04 332.12 217.02 327.94 214.24 323.53 211.21 319.36 208.2 315.64 205.65 311.7 203.1 308.68 201.24 305.89 199.15 302.65 197.06 300.56 195.9 298.23 194.28 295.69 192.88 293.35 191.26 290.81 189.64 288.49 188.24 285.93 186.61 283.38 184.98 281.05 183.6 278.5 181.98 276.19 180.58 273.63 178.96 271.32 177.34 268.75 175.94 266.44 174.31 263.89 172.69 261.57 171.07 261.57 168.28 261.57 165.96 261.57 162.48 261.57 159.46 261.57 156.68 261.57 154.59 261.57 152.27 261.57 151.33 261.8 151.11 263.19 150.41 265.05 148.79 265.98 148.08 266.9 147.4 270.38 145.07 271.08 144.61 274.57 142.06 275.26 141.59 277.11 141.36 277.81 140.89 278.74 139.97 280.36 138.81 281.29 138.35 281.52 138.11 282.22 138.11 285.24 138.58 286.63 138.81 288.02 139.03 288.73 138.81 288.95 138.35 289.65 137.64 289.65 136.72 289.88 135.78 289.88 135.55 290.11 135.33 290.81 135.33 291.73 135.55 293.59 135.55 294.29 135.33 296.38 135.1 299.4 134.63 301.71 133.93 303.58 133.47 305.66 132.07 307.06 130.45 308.68 128.13 309.84 126.04 312.39 124.88 314.48 124.18 315.64 123.95 318.19 122.79 320.52 121.17 322.6 119.78 324.23 119.54 326.31 119.31 326.78 119.07 327.25 118.38 327.25 117.45 326.78 116.99 325.84 116.53 325.39 116.06 324.92 116.06 324.68 115.59 324.68 114.9 324.92 113.98 325.15 113.28 325.15 112.11 324.46 111.18 324.46 110.26 324.46 109.56 324.68 108.87 325.39 108.63 326.31 108.4 327.48 108.63 329.79 108.4 335.14 106.54 335.6 105.85 336.07 104.68 336.3 103.52 336.99 103.06 337.22 103.06 339.08 102.83 341.63 102.36 342.56 102.13 345.58 102.36 347.44 102.36 350.91 102.6 353.24 102.6 355.33 102.83 357.88 102.83 358.57 102.6 358.57 101.67 358.12 100.27 358.34 99.11 359.51 98.42 360.67 97.26 359.97 96.1 359.04 95.4 357.65 94.24 356.96 94.01 355.56 92.85 354.86 91.46 354.4 88.44 353.48 87.05 352.78 84.95 353.24 81.24 352.31 79.16 352.31 78.23 352.31 77.07 352.54 75.21 352.31 72.42 351.15 69.64 351.61 68.72 351.85 68.25 351.85 67.78 350.91 66.86 350.45 66.16 350.68 65.46 351.15 64.3 351.15 64.07 349.52 62.68 346.74 60.82 346.04 59.89 345.58 58.73 348.37 58.96 349.52 58.96 352.78 57.57 355.33 55.94 357.18 55.02 358.8 52.93 360.43 51.77 362.52 50.38 369.02 47.59 369.95 47.59 372.04 48.29 373.9 48.05 375.06 46.9 376.45 44.58 378.53 43.17 381.32 41.56 384.8 40.16 387.12 39 390.83 37.85 400.35 37.15 404.99 36.45 408.24 36.69 411.49 34.6 413.35 33.9 420.32 33.67 423.79 32.04 436.56 32.04 437.95 32.74 439.57 33.44 442.13 35.52 443.52 35.75 445.14 35.52 449.09 33.67 453.5 32.74 455.82 31.58 456.98 29.95 459.07 29.26 460.23 30.65 464.64 31.81 467.66 31.34 468.82 31.11 468.35 29.26 471.37 29.72 473.69 30.65"/>
                                </g>
                                <g id="Tunisia">
                                    <path class="cls-2 activeCountry" d="m517.32,79.16h-.46l-.71.46h-.22l-.94-.46h-.45l-.47-.24.23-1.86.24-.46h2.32l1.16.92.22.23v.47l-.46.46-.46.47Zm4.18-15.33l-2.09,1.17.46-.93,1.16-1.16.46.23v.69Zm-19.49-36.44l.46,1.16.93-.69-.24-.47v-.69h1.4l1.39.22,1.39.46-.23,2.78,2.09,2.33-.7,1.4,1.63.7,1.38-.92.69-1.17,2.55-.92,2.32-1.86,1.39-.24.24,1.63.69,1.39-.93.46-1.16,1.39-2.09,3.72-2.09,1.16-1.62,1.39-.47,1.16v1.17l.24,2.08,1.16,2.09,1.39,1.39,1.16.47,2.79,1.86v1.39l.46,1.39v1.86l1.16,1.39-2.09,3.02-1.38,2.08-2.09,3.02-2.08,2.08-4.41,2.78-.92.93-.7,1.16-.47.92.23,1.39,1.39,3.01,1.86,1.63,1.86.93,3.49-.23-.24,1.17.24,1.38,1.39-.23h.92l.93-1.39,1.62.92.7,2.78,1.39.93.24.23-.47.24-.46.23.46.23,1.39.46.7-.23,1.39.7v.23l-.46,2.78v.93l-.23,1.86v1.86l1.16,1.85v.7l-.46.92-1.85.92-2.56,1.16-2.09,1.39-2.31,1.16-.7.93-1.15.7-.93.7-.23.69-.7,1.16-.71.93-2.32.46-.46.23-.92,1.39-.47.7-.7,1.16.93,3.02.93,3.25v2.33l-.46,1.16-1.16,1.61-.93,1.17-1.63,2.33-.46.46-1.15.69-2.09.93-1.63.7-.92-3.49-.47-2.79-.7-2.31-.92-4.17-.93-3.72-.69-3.48-.7-3.25-.92-3.26-.24-.46-2.31-1.62-2.1-1.4-2.08-1.63-2.55-1.62-.23-2.32-1.39-3.26-1.16-1.85-.47-.7-2.55-1.16-1.62-.93-.24-.46-.46-1.4-.93-2.55-1.16-2.55-.46-1.63-.23-2.08.23-1.63.7-.7,2.55-1.86,1.16-2.33,1.39-.68,1.16-.7,1.17-.7.7-1.4.7-1.16.24-1.38.22-2.32.47-1.63,1.16-1.62-.46-1.4-.7-1.62.23-2.55v-1.16l-.46-.93-.45-1.16-.24-1.16.46-2.55.47-2.08.46-2.79-.24-.69-.23-.47-1.39-.7v-.23l.47-.47,1.62-1.39,1.16-1.86.7-.46,1.16-.7v-.69l-.24-.7,3.25-.93,3.01-2.32,1.16-.7,6.96-2.08h.92l1.16.46-.24.7-.46.69Z"/>
                                </g>
                                <g id="Libya">
                                    <polygon class="cls-2 activeCountry" points="702.05 109.1 701.6 109.56 700.9 110.26 700.45 111.18 699.29 112.35 698.11 113.74 698.35 115.59 698.57 117.69 699.29 119.54 699.74 121.63 699.51 123.03 699.04 124.88 698.57 126.28 696.48 129.29 696.27 129.99 696.27 130.92 697.65 134.39 697.65 135.55 698.35 138.81 699.04 141.59 699.74 143.9 699.74 144.37 699.74 147.63 699.74 150.88 699.74 153.89 699.74 157.14 699.74 160.39 699.74 163.41 699.74 166.66 699.74 169.67 699.74 172.92 699.74 176.17 699.74 179.18 699.74 182.44 699.74 185.45 699.74 188.7 699.74 191.72 699.74 194.97 699.74 197.99 699.74 201 699.74 204.25 699.74 207.27 699.74 210.52 699.74 213.54 699.74 216.55 699.74 219.81 699.74 222.82 699.74 225.84 699.74 229.09 699.74 232.11 699.74 235.12 699.74 238.14 699.74 241.39 699.74 244.4 699.74 251.14 699.74 257.87 699.74 264.83 699.74 271.56 696.48 271.56 693.24 271.56 690 271.56 686.75 271.56 686.75 273.19 686.75 275.04 686.75 276.67 686.75 278.3 680.48 275.04 674.44 272.02 668.18 268.78 661.91 265.53 655.64 262.27 649.37 259.03 643.12 255.78 636.85 252.76 630.58 249.52 624.54 246.26 618.29 243.02 612.02 239.76 605.74 236.51 599.48 233.26 593.22 230.02 586.95 226.77 582.77 224.44 578.13 226.77 574.41 228.4 569.54 230.72 564.2 233.49 559.79 235.82 559.56 235.82 555.15 231.87 551.68 229.09 550.05 228.16 543.55 226.54 537.29 225.15 530.32 223.52 529.16 220.96 527.77 218.42 525.91 214.93 524.75 212.85 524.28 212.61 519.18 210.99 513.61 209.36 510.59 210.29 509.9 210.06 508.97 209.59 508.04 208.66 507.58 207.5 506.42 205.88 505.03 202.39 505.03 199.38 504.79 198.45 502.01 194.28 499.22 190.79 497.6 188.24 497.13 187.08 497.36 185.69 498.06 184.53 500.62 183.13 502.93 181.52 503.16 180.35 503.4 177.34 502.7 176.41 502.01 174.55 501.54 172 501.54 170.6 502.46 167.35 503.63 164.11 502.93 160.62 502.46 153.19 502.7 147.4 502.46 145.3 502.24 144.61 501.54 141.82 500.62 139.03 500.15 137.88 498.76 135.78 496.9 133 495.74 131.14 497.13 130.21 498.52 129.52 500.15 128.82 502.24 127.89 503.4 127.2 503.86 126.74 505.49 124.42 506.42 123.24 507.58 121.63 508.04 120.47 508.04 119.31 508.04 118.15 507.11 114.9 506.18 111.88 506.88 110.72 507.34 110.02 508.27 108.63 508.73 108.4 511.05 107.94 511.76 107 512.45 105.85 512.68 105.15 513.61 104.46 514.76 103.76 515.47 102.83 517.78 101.67 519.87 100.27 522.43 99.11 524.28 98.19 524.75 97.26 524.75 96.56 523.59 94.71 523.59 92.85 523.82 90.99 523.82 90.06 524.28 87.28 524.28 87.05 526.37 87.98 528.46 88.21 534.5 91.69 536.35 92.16 540.53 92.62 545.63 90.99 547.49 90.75 550.74 92.16 552.36 92.62 554.69 92.62 558.87 93.78 560.04 94.24 562.34 96.1 563.51 96.8 572.09 98.42 573.25 99.58 574.65 101.9 574.65 104.46 575.34 106.54 576.27 109.1 577.66 110.95 579.06 112.35 580.68 113.28 584.4 114.67 588.81 115.14 592.99 115.36 600.42 117.22 606.69 119.54 608.3 120.7 611.31 121.63 617.58 126.97 621.06 128.59 623.62 129.06 625.71 128.59 629.65 126.97 631.28 125.81 635.22 121.4 636.39 119.07 636.85 117.45 636.85 115.59 636.39 114.2 635.22 112.57 634.53 110.49 634.06 106.77 634.75 104.23 635.45 102.6 636.62 100.98 639.86 97.95 643.12 95.63 648.92 92.85 652.16 92.85 653.56 92.62 656.35 90.53 657.5 90.53 658.89 90.99 663.53 90.75 665.39 91.46 667.95 92.62 670.96 93.32 673.05 94.24 675.14 95.17 675.84 97.72 675.6 98.42 675.37 99.35 677.69 100.98 684.43 101.9 685.81 102.36 687.68 103.52 688.83 103.99 693.24 104.23 696.04 103.76 698.57 104.23 699.51 104.68 700.45 105.85 701.6 108.17 702.05 109.1"/>
                                </g>
                                <g id="Egypt">
                                    <polygon class="cls-2 activeCountry" points="819.96 113.98 820.2 114.2 820.42 115.59 821.59 118.61 822.51 120.93 823.68 124.18 824.12 125.58 824.12 126.5 826 129.99 826.92 133 827.62 135.55 828.55 139.03 829.02 140.19 828.32 140.89 826.92 143.22 825.3 150.41 823.21 156.21 822.98 159.69 822.51 160.86 821.36 162.71 820.2 164.57 817.87 163.64 814.16 160.39 812.07 157.6 809.74 155.75 807.43 153.19 806.97 151.33 806.97 150.18 806.03 147.4 805.33 146 802.56 142.99 801.86 141.36 801.15 140.66 800.7 139.5 799.77 135.55 798.61 133.23 797.45 133.93 797.67 134.86 796.51 136.25 796.05 138.11 796.51 139.5 798.61 141.59 799.08 142.52 799.55 144.37 799.55 147.16 799.77 148.08 801.4 149.95 802.08 151.11 802.32 152.27 803.02 153.19 804.65 154.82 806.97 158.3 809.07 160.39 810.68 161.55 811.38 162.48 811.6 165.26 811.38 166.66 812.76 169.21 813.46 170.37 814.62 171.53 815.32 172.69 816.01 174.55 816.71 180.12 817.87 181.52 821.59 188.7 824.6 193.35 826.23 196.83 828.32 201 832.96 210.29 835.52 213.07 836.68 214.7 838.52 215.86 840.62 217.72 838.52 217.48 838.05 217.72 837.37 217.95 837.14 219.1 836.91 219.81 837.14 224.44 837.6 227 839.46 231.41 840.62 232.8 841.32 233.49 842.24 234.2 846.41 235.82 848.75 238.84 854.08 243.02 854.77 244.18 854.77 244.4 850.36 244.4 846.18 244.4 841.78 244.4 837.6 244.4 833.42 244.4 829.02 244.4 824.84 244.4 820.42 244.4 816.25 244.4 812.07 244.4 807.66 244.4 803.48 244.4 799.08 244.4 794.89 244.4 790.73 244.4 786.31 244.4 783.99 244.4 784.21 243.25 784.68 242.32 784.21 241.62 783.53 241.62 782.83 241.85 781.67 244.18 780.96 244.4 779.35 244.4 774.47 244.4 769.6 244.4 764.49 244.4 759.61 244.4 754.52 244.4 749.63 244.4 744.53 244.4 739.67 244.4 734.79 244.4 729.68 244.4 724.81 244.4 719.7 244.4 714.82 244.4 709.72 244.4 704.86 244.4 699.74 244.4 699.74 241.39 699.74 238.14 699.74 235.12 699.74 232.11 699.74 229.09 699.74 225.84 699.74 222.82 699.74 219.81 699.74 216.55 699.74 213.54 699.74 210.52 699.74 207.27 699.74 204.25 699.74 201 699.74 197.99 699.74 194.97 699.74 191.72 699.74 188.7 699.74 185.45 699.74 182.44 699.74 179.18 699.74 176.17 699.74 172.92 699.74 169.67 699.74 166.66 699.74 163.41 699.74 160.39 699.74 157.14 699.74 153.89 699.74 150.88 699.74 147.63 699.74 144.37 699.74 143.9 699.04 141.59 698.35 138.81 697.65 135.55 697.65 134.39 696.27 130.92 696.27 129.99 696.48 129.29 698.57 126.28 699.04 124.88 699.51 123.03 699.74 121.63 699.29 119.54 698.57 117.69 698.35 115.59 698.11 113.74 699.29 112.35 700.45 111.18 700.9 110.26 701.6 109.56 702.05 109.1 702.98 110.72 705.09 111.18 711.8 109.56 719 111.18 723.18 111.65 729.45 113.04 733.16 115.36 734.32 115.82 736.88 115.59 738.73 116.99 745.93 117.69 749.63 119.31 751.96 120.47 753.12 120.93 754.29 120.93 755.92 120.47 757.77 119.54 759.85 118.38 764.26 115.14 765.89 114.67 766.81 114.9 768.2 114.9 768.67 113.98 769.36 113.28 769.6 112.81 770.29 111.88 772.62 111.65 777.26 110.26 776.79 110.95 772.62 112.57 774.24 112.57 776.1 112.11 778.19 111.88 778.65 111.18 778.88 110.02 779.35 109.8 780.74 110.02 785.15 111.88 786.08 111.88 789.09 110.95 789.79 110.72 790.73 111.18 793.04 113.51 792.34 113.51 789.79 111.65 789.56 112.57 788.4 114.2 790.03 115.14 791.42 115.36 792.12 116.29 792.56 117.22 793.97 116.76 794.89 115.59 794.42 114.9 793.97 114.2 794.42 114.2 795.6 114.9 798.15 116.99 799.08 117.45 800.24 117.45 802.32 116.76 803.02 116.99 806.03 116.06 806.27 116.76 806.73 117.22 809.28 116.53 813 116.53 816.01 115.82 819.5 114.2 819.96 113.98"/>
                                </g>
                                <g id="Sudan">
                                    <polygon class="cls-2 activeCountry" points="784.21 243.25 783.99 244.4 786.31 244.4 790.73 244.4 794.89 244.4 799.08 244.4 803.48 244.4 807.66 244.4 812.07 244.4 816.25 244.4 820.42 244.4 824.84 244.4 829.02 244.4 833.42 244.4 837.6 244.4 841.78 244.4 846.18 244.4 850.36 244.4 854.77 244.4 854.77 247.42 855.46 249.97 857.33 253.45 859.19 255.31 859.64 256.48 859.88 256.94 859.64 257.41 859.19 256.94 858.25 256.48 858.25 258.1 858.48 259.49 858.72 261.59 859.41 263.9 858.72 266.22 858.95 269.95 859.88 274.35 859.64 277.13 861.04 283.86 862.44 287.57 863.36 288.27 864.29 288.97 865.91 289.2 868.47 291.06 870.32 292.92 871.02 294.08 871.95 295.24 872.64 295.01 873.11 294.77 873.81 295.47 876.82 297.56 877.29 298.49 876.12 299.42 874.96 300.81 874.5 301.5 874.5 301.97 874.27 302.44 874.04 302.66 873.11 303.36 872.88 303.6 872.64 304.06 872.18 304.28 871.72 304.28 871.25 304.51 870.79 304.98 869.63 304.75 868.7 304.98 868.47 305.44 867.54 305.68 866.84 305.68 866.61 305.91 865.91 306.38 864.99 307.07 863.82 307.54 863.59 307.54 863.13 308 862.2 310.55 861.74 311.02 860.8 311.25 859.64 311.25 858.48 311.49 857.09 311.02 856.39 311.25 856.39 311.72 856.17 313.8 856.17 314.74 855.46 315.66 855 317.05 855.23 319.15 855.23 321.46 854.08 324.72 853.86 325.41 852.7 327.96 852.22 328.89 850.82 333.77 850.14 335.39 848.97 337.01 849.2 339.57 849.45 342.12 849.67 344.67 850.14 348.62 849.2 352.1 849.2 353.96 848.51 356.97 847.81 358.13 847.35 359.06 846.87 359.76 846.18 361.61 845.5 363.94 845.26 366.49 845.03 367.88 845.03 368.35 844.79 368.81 843.18 369.05 841.08 369.27 839.93 369.74 838.99 370.21 838.05 371.36 836.21 374.38 835.27 376.47 833.66 379.03 831.8 380.88 831.33 381.8 831.1 383.43 830.41 385.99 829.94 388.07 829.94 389.46 829.25 392.02 829.48 393.41 828.78 394.1 827.85 394.81 827.39 395.04 826 394.1 825.06 393.41 824.84 393.18 823.91 393.65 822.98 394.34 821.82 396.2 820.89 397.82 821.36 401.54 821.36 402.23 821.12 403.17 819.73 405.95 819.5 406.64 818.8 408.27 818.34 411.28 818.34 411.98 815.79 411.75 815.55 411.28 815.55 410.81 815.79 409.66 816.01 408.5 816.71 406.87 816.71 406.64 816.94 406.64 816.94 406.4 816.71 406.18 816.71 405.95 816.71 405.48 816.71 404.79 816.71 404.33 816.71 404.09 816.71 403.86 816.01 402.46 816.01 402.23 810.21 397.59 809.28 396.2 809.07 396.2 806.03 395.04 806.03 394.81 806.03 394.57 806.49 394.1 806.49 393.87 806.49 393.65 805.33 383.9 805.33 383.67 805.33 383.43 805.57 383.2 805.57 382.98 805.8 382.74 805.8 382.51 806.03 380.64 806.03 379.25 806.73 376.7 806.97 375.54 800.7 375.54 800.7 375.77 800.7 376.01 800.7 376.23 800.7 376.47 800.7 376.7 800.7 377.16 800.92 377.63 800.92 377.86 800.92 378.1 800.92 378.32 792.12 378.32 795.6 382.27 795.83 382.51 795.83 382.74 795.83 383.9 795.6 386.21 795.6 387.38 795.83 388.3 796.76 390.16 796.76 390.4 796.51 390.87 790.48 395.97 790.26 396.44 789.32 398.51 788.63 399.68 788.17 400.14 786.78 402 781.21 407.34 780.27 407.81 777.49 408.03 776.1 408.03 775.86 408.03 775.63 408.03 775.41 408.27 775.17 408.5 775.17 408.27 774.94 408.27 771.45 405.01 765.19 401.07 764.72 401.54 761.24 403.17 760.54 403.62 760.08 403.86 760.08 405.71 759.38 406.87 758.45 407.81 755.44 408.5 753.82 408.97 752.18 409.89 751.96 410.13 751.96 402.7 736.88 402.7 736.88 408.03 737.58 410.13 736.65 410.13 727.13 409.89 725.73 410.13 723.18 411.28 721.8 411.52 720.4 411.06 715.29 405.25 714.36 404.55 713.89 404.09 713.2 403.17 712.04 402.46 711.8 402.23 711.57 401.54 711.57 400.14 711.34 399.45 710.41 399.22 703.92 400.61 702.76 400.37 701.36 400.61 700.9 400.84 700.45 401.76 700.2 402.46 700.2 403.39 700.2 404.09 699.74 405.01 697.65 406.87 697.41 407.81 697.41 409.89 697.2 411.06 696.96 411.52 696.27 412.44 695.79 412.91 695.79 413.6 695.57 415 695.57 415.69 694.41 417.32 694.16 417.78 694.16 418.94 693.95 419.41 690.93 420.33 689.75 421.03 689.06 421.96 689.06 422.42 687.68 421.96 686.05 421.73 682.8 421.49 681.64 421.03 681.18 420.33 681.18 418.7 680.94 418.25 680.48 418.02 680.01 417.32 680.25 416.39 681.86 414.53 682.11 413.37 682.34 409.89 682.57 408.5 682.57 407.11 681.18 404.33 680.01 402.46 678.16 399.68 677.47 398.75 673.51 394.81 673.05 394.34 672.35 392.71 672.59 391.32 673.28 389 673.28 388.07 673.05 387.15 672.12 386.21 671.19 386.21 670.96 385.76 670.03 385.29 669.34 384.82 668.64 383.9 668.18 382.74 668.64 378.56 668.41 378.1 667.49 377.86 667.25 377.63 667.25 376.7 666.78 374.38 666.09 372.29 666.33 371.13 665.62 369.74 664.01 369.05 662.6 369.27 660.98 369.5 660.05 369.5 659.36 369.27 658.89 368.58 658.67 367.88 658.89 366.95 659.82 365.1 660.98 363.71 663.07 362.32 663.77 361.61 664.01 360.92 664.01 359.99 664.01 359.06 663.77 358.13 663.07 356.97 662.38 355.58 662.38 354.65 662.6 353.96 663.3 353.03 664.46 352.33 664.69 352.1 665.39 351.64 666.09 351.17 667.71 350.24 667.95 349.78 667.95 349.31 667.49 348.86 666.78 348.15 666.78 347.45 666.55 346.07 666.33 345.37 666.09 344.67 666.55 344.21 667.01 343.51 667.95 343.04 669.34 342.82 669.8 342.35 670.03 341.42 669.8 340.73 670.26 340.03 670.96 338.63 671.43 338.17 672.35 337.48 673.28 336.56 673.51 335.39 673.75 334.46 673.28 331.44 674.21 330.28 675.37 329.35 677.23 329.35 680.01 329.12 681.86 328.66 683.27 328.66 686.28 329.35 686.75 329.12 686.98 328.2 686.98 326.33 686.98 320.3 686.75 314.27 686.75 308.47 686.75 302.44 686.75 296.39 686.75 290.36 686.75 284.32 686.75 278.3 686.75 276.67 686.75 275.04 686.75 273.19 686.75 271.56 690 271.56 693.24 271.56 696.48 271.56 699.74 271.56 699.74 264.83 699.74 257.87 699.74 251.14 699.74 244.4 704.86 244.4 709.72 244.4 714.82 244.4 719.7 244.4 724.81 244.4 729.68 244.4 734.79 244.4 739.67 244.4 744.53 244.4 749.63 244.4 754.52 244.4 759.61 244.4 764.49 244.4 769.6 244.4 774.47 244.4 779.35 244.4 780.96 244.4 781.67 244.18 782.83 241.85 783.53 241.62 784.21 241.62 784.68 242.32 784.21 243.25"/>
                                </g>
                                <g id="South_Sudan" data-name="South Sudan">
                                    <polygon class="cls-2" points="816.71 406.87 816.01 408.5 815.79 409.66 815.55 410.81 815.55 411.28 815.79 411.75 818.34 411.98 818.34 412.44 818.34 415 818.57 417.32 818.57 421.27 818.57 422.19 818.57 423.36 818.34 423.82 817.64 424.52 816.71 425.2 814.62 425.44 812.76 425.44 811.38 425.2 809.52 425.2 807.91 425.2 807.43 425.9 806.49 427.77 805.11 430.55 804.41 431.71 804.18 432.41 804.41 432.87 805.33 433.34 807.19 434.26 809.28 434.73 810.91 434.96 812.07 435.19 813 435.42 816.01 437.51 816.94 438.45 817.64 439.36 817.64 440.3 818.1 441.23 819.96 443.08 820.89 444.01 823.68 445.4 824.6 447.02 825.53 447.72 826.46 448.65 827.16 449.81 828.32 453.29 829.02 455.15 829.71 456.54 830.18 459.1 830.64 460.26 831.33 461.42 832.5 462.81 833.66 463.5 833.88 463.97 831.33 466.29 828.78 468.84 825.53 471.86 822.28 475.35 819.73 477.9 816.94 480.45 813.92 483.47 811.6 485.78 811.38 486.02 810.68 486.48 808.59 486.48 806.27 486.25 804.18 484.86 802.08 486.02 800.92 486.25 800 486.48 798.15 486.48 795.6 487.18 794.42 487.88 793.73 488.34 793.26 489.51 793.04 489.51 792.56 489.51 791.89 489.04 790.48 488.34 789.79 487.18 789.09 486.25 788.63 486.02 786.54 487.18 785.38 487.65 784.46 487.41 782.83 486.72 781.21 486.02 780.27 486.02 778.88 486.94 777.26 488.1 776.57 489.26 776.1 489.96 775.86 489.51 775.63 488.81 775.17 488.34 774.24 487.88 773.54 488.1 772.85 488.34 772.62 487.88 772.38 486.94 772.15 486.02 771.92 485.55 770.76 484.86 767.74 483.47 765.42 480.9 764.26 479.75 763.56 479.05 762.4 476.97 761.01 475.58 759.38 475.11 758.23 475.35 757.29 476.74 755.2 478.13 754.29 478.36 752.88 477.43 751.5 476.97 748.71 476.74 747.55 477.43 745.93 478.36 744.77 479.05 744.08 479.29 743.14 478.83 742.45 478.83 741.75 478.83 740.12 477.9 739.43 476.97 738.95 476.5 738.04 476.04 737.11 475.58 736.41 474.88 736.17 474.17 735.48 473.25 734.79 472.33 732.47 470.7 731.77 469.77 731.31 468.84 730.38 467.91 729.45 466.53 729.22 464.44 729.22 463.05 728.99 462.11 728.52 461.42 728.06 460.95 727.13 460.03 725.5 459.1 723.41 457.93 722.48 457.23 720.86 457.01 719.93 456.31 719 454.92 718.54 453.53 717.84 452.82 717.39 452.14 717.14 451.2 717.84 448.88 716.91 448.18 715.29 447.02 714.36 445.87 713.66 444.71 711.57 443.31 707.39 441.23 705.09 439.83 703.68 438.45 702.52 437.28 702.52 436.82 703.23 435.66 703.23 434.73 702.76 433.56 700.2 431.47 698.11 429.16 696.73 428.46 693.02 428 692.09 427.77 690.93 427.3 689.75 426.14 689.53 424.98 690 423.12 689.53 422.42 689.06 422.42 689.06 421.96 689.75 421.03 690.93 420.33 693.95 419.41 694.16 418.94 694.16 417.78 694.41 417.32 695.57 415.69 695.57 415 695.79 413.6 695.79 412.91 696.27 412.44 696.96 411.52 697.2 411.06 697.41 409.89 697.41 407.81 697.65 406.87 699.74 405.01 700.2 404.09 700.2 403.39 700.2 402.46 700.45 401.76 700.9 400.84 701.36 400.61 702.76 400.37 703.92 400.61 710.41 399.22 711.34 399.45 711.57 400.14 711.57 401.54 711.8 402.23 712.04 402.46 713.2 403.17 713.89 404.09 714.36 404.55 715.29 405.25 720.4 411.06 721.8 411.52 723.18 411.28 725.73 410.13 727.13 409.89 736.65 410.13 737.58 410.13 739.2 412.91 739.89 413.6 750.1 413.6 749.88 412.91 750.1 411.98 751.26 410.81 751.96 410.13 752.18 409.89 753.82 408.97 755.44 408.5 758.45 407.81 759.38 406.87 760.08 405.71 760.08 403.86 760.54 403.62 761.24 403.17 764.72 401.54 765.19 401.07 771.45 405.01 774.94 408.27 775.17 408.27 775.17 408.5 775.41 408.27 775.63 408.03 775.86 408.03 776.1 408.03 777.49 408.03 780.27 407.81 781.21 407.34 786.78 402 788.17 400.14 788.63 399.68 789.32 398.51 790.26 396.44 790.48 395.97 796.51 390.87 796.76 390.4 796.76 390.16 795.83 388.3 795.6 387.38 795.6 386.21 795.83 383.9 795.83 382.74 795.83 382.51 795.6 382.27 792.12 378.32 800.92 378.32 800.92 378.1 800.92 377.86 800.92 377.63 800.7 377.16 800.7 376.7 800.7 376.47 800.7 376.23 800.7 376.01 800.7 375.77 800.7 375.54 806.97 375.54 806.73 376.7 806.03 379.25 806.03 380.64 805.8 382.51 805.8 382.74 805.57 382.98 805.57 383.2 805.33 383.43 805.33 383.67 805.33 383.9 806.49 393.65 806.49 393.87 806.49 394.1 806.03 394.57 806.03 394.81 806.03 395.04 809.07 396.2 809.28 396.2 810.21 397.59 816.01 402.23 816.01 402.46 816.71 403.86 816.71 404.09 816.71 404.33 816.71 404.79 816.71 405.48 816.71 405.95 816.71 406.18 816.94 406.4 816.94 406.64 816.71 406.64 816.71 406.87"/>
                                </g>
                                <g id="Eritrea">
                                    <path class="cls-2" d="m897.24,329.35l.7.7h.24l.23-.24.24-.46,2.07.92v.7h-1.38l-1.41-.24h-1.16l-1.62-.23-.46-1.16,1.15.46h.47v-.23l-.71-.7-.91-.24v-.46l.46-.24.22-.23-.45-.93,1.14.24.7.46.46.46v1.39Zm-.69-5.1l.22,1.15-1.16-.46-.23-.22.47-.46.22-.24.47.24Zm-19.26-25.77l3.95,7.65,1.62,4.65,1.39,4.87,1.16,6.96.93,3.71,1.62,1.87,1.16,3.48h.92l.7.93,1.15,3.24.7,1.16.47-.92v-.7l-.25-.93.25-1.39.69-.7,1.39,1.15.92.71.25,1.62.22.71,1.62,1.85,1.17.7h1.84l1.4.46,1.17.7,2.07,1.86,4.88,1.62,3.95,4.88,2.32,3.48,7.66,5.34,1.39,2.55.7,2.33h1.62l2.79,2.55.7,2.08,2.31.7.47-1.15.93.92.46,1.63-1.39.7-1.62.46h-.23l-.46.69-.7,1.86-.93.7h-.46l-2.55-1.86h-.23l-.7.23-.23.46-1.17-1.39-.92-1.15-1.16-1.39-1.16-.71-1.17-.92-1.15-1.86-1.39-2.09-1.86-1.62-3.25-2.55-3.25-3.25-2.32-3.25-1.63-1.61-.69-.47-3.03-1.16-2.3-1.4-1.63-1.15-1.17-.47h-.91l-2.33.23-1.63-.7h-.91l-1.16-.24-.94-.22-1.16.22-2.31.46h-.94l-.46-.69-.22-.7-.94-.7h-.69l-.23.7-2.33,1.39-4.17.71h-.94l-.69-.71-2.09-2.32-.46-.24-.46-.23-.93-.23-.93-.47-.7-.92-.7-.46-.93,1.86-1.39,3.25-.93,1.86-.93,2.33h-.23l-.69-.24-1.87-2.79-1.39-1.16-.92.23-.71.46-.22.93-.7.7-.46.24-.94-.24-1.84-.46-1.63.23-1.87.7h-.21l-.47-3.95-.22-2.55-.24-2.55-.23-2.55,1.17-1.62.68-1.62,1.4-4.87.47-.93,1.16-2.55.22-.69,1.15-3.25v-2.32l-.23-2.09.46-1.39.71-.92v-.93l.22-2.09v-.47l.7-.23,1.39.46,1.16-.24h1.16l.94-.23.46-.47.93-2.55.46-.46h.23l1.17-.47.92-.7.7-.46.23-.24h.7l.93-.23.23-.46.93-.23,1.16.23.46-.47.46-.23h.46l.46-.23.23-.46.23-.24.93-.7.23-.23.23-.47v-.46l.46-.7,1.16-1.39,1.17-.93Z"/>
                                </g>
                                <g id="Djibouti">
                                    <polygon class="cls-2" points="937.62 385.05 936.7 386.92 935.08 389 933.46 391.56 932.53 391.56 931.6 391.56 931.13 391.09 929.97 390.62 928.79 390.62 927.65 391.09 925.56 391.56 923.7 391.79 922.08 392.02 920.91 392.48 919.76 392.25 918.83 391.79 918.6 389.24 918.36 386.21 918.36 383.9 918.83 382.51 919.06 382.04 920.91 380.41 921.38 379.49 923.47 376.7 925.1 374.15 926.49 372.29 926.72 371.83 927.42 371.6 927.65 371.6 930.21 373.46 930.67 373.46 931.6 372.75 932.29 370.89 932.76 370.21 932.99 370.21 934.62 369.74 936.01 369.05 936.24 369.74 938.33 372.29 939.03 373.68 939.95 376.01 939.49 377.16 939.03 378.1 938.09 378.79 935.08 380.64 931.83 381.8 929.73 384.13 928.35 384.13 928.58 385.05 929.04 385.05 929.97 384.82 931.83 384.13 933.46 383.9 935.08 383.9 936.7 384.13 937.62 385.05"/>
                                </g>
                                <g id="Ethiopia">
                                    <polygon class="cls-2" points="874.96 346.3 875.9 346.3 880.07 345.6 882.4 344.21 882.63 343.51 883.32 343.51 884.25 344.21 884.48 344.91 884.94 345.6 885.88 345.6 888.19 345.13 889.35 344.91 890.29 345.13 891.45 345.37 892.36 345.37 894 346.07 896.33 345.83 897.24 345.83 898.41 346.3 900.04 347.45 902.34 348.86 905.37 350.02 906.06 350.49 907.68 352.1 910.01 355.35 913.26 358.6 916.51 361.16 918.36 362.78 919.76 364.86 920.91 366.72 922.08 367.64 923.24 368.35 924.4 369.74 925.32 370.89 926.49 372.29 925.1 374.15 923.47 376.7 921.38 379.49 920.91 380.41 919.06 382.04 918.83 382.51 918.36 383.9 918.36 386.21 918.6 389.24 918.83 391.79 919.76 392.25 920.91 392.48 922.08 392.02 923.7 391.79 925.56 391.56 927.65 391.09 928.79 390.62 929.97 390.62 931.13 391.09 931.6 391.56 932.53 391.56 933.46 391.56 933.21 392.25 932.76 392.94 932.04 393.65 931.37 394.34 930.21 396.67 929.97 396.89 930.21 397.36 930.9 398.29 931.6 399.91 932.04 401.3 932.53 402.23 933.46 402.93 934.62 404.55 935.31 405.71 936.93 406.4 937.39 407.81 938.55 409.89 939.72 411.52 940.87 412.91 942.03 413.37 942.5 413.6 945.3 415.92 947.38 417.78 947.85 418.02 951.55 419.41 955.74 420.8 959.21 421.96 963.62 423.36 967.8 424.74 971.75 426.14 977.55 428 981.94 429.38 985.68 430.78 986.37 431 990.55 431 994.96 431 999.36 431 996.11 434.26 992.4 437.74 988.69 441.46 986.13 443.78 982.43 447.49 979.17 450.75 975.69 454.23 972.67 457.23 968.73 461.65 966.17 464.44 962.23 468.61 959.9 471.4 959.44 471.63 955.74 471.4 952.24 471.16 947.85 470.94 947.38 470.94 946.22 471.16 945.3 471.4 942.03 472.09 941.56 472.33 938.79 473.48 936.24 474.88 934.84 476.04 933.68 477.43 933.21 478.59 932.76 479.05 931.83 479.52 926.03 480.68 924.4 480.68 921.85 481.61 920.46 483 919.99 483.7 918.13 483.7 914.65 483.7 913.26 483.93 912.56 484.16 911.4 484.16 910.24 483.93 909.54 483.47 908.85 482.53 906.76 480.9 905.37 479.75 902.34 480.9 899.57 482.3 895.61 483.93 893.3 485.31 892.83 486.48 890.99 488.81 889.35 490.19 888.89 490.43 885.41 490.19 884 489.72 881.93 489.51 879.15 489.04 877.29 488.57 875.2 488.57 872.41 488.34 870.56 487.88 868.7 486.72 866.38 485.1 863.82 483.47 861.27 481.84 858.48 479.98 855.23 477.9 854.3 477.66 854.08 477.66 850.59 477.66 846.87 477.43 844.34 477.43 843.62 477.2 843.18 476.74 842.24 475.35 841.32 474.17 840.39 472.79 840.15 470.94 840.39 468.84 840.85 468.15 840.62 467.68 840.62 466.75 840.15 465.83 836.44 464.9 835.74 464.9 835.27 465.36 834.58 465.59 834.11 465.36 833.88 464.9 833.88 464.2 833.88 463.97 833.66 463.5 832.5 462.81 831.33 461.42 830.64 460.26 830.18 459.1 829.71 456.54 829.02 455.15 828.32 453.29 827.16 449.81 826.46 448.65 825.53 447.72 824.6 447.02 823.68 445.4 820.89 444.01 819.96 443.08 818.1 441.23 817.64 440.3 817.64 439.36 816.94 438.45 816.01 437.51 813 435.42 812.07 435.19 810.91 434.96 809.28 434.73 807.19 434.26 805.33 433.34 804.41 432.87 804.18 432.41 804.41 431.71 805.11 430.55 806.49 427.77 807.43 425.9 807.91 425.2 809.52 425.2 811.38 425.2 812.76 425.44 814.62 425.44 816.71 425.2 817.64 424.52 818.34 423.82 818.57 423.36 818.57 422.19 818.57 421.27 818.57 417.32 818.34 415 818.34 412.44 818.34 411.98 818.34 411.28 818.8 408.27 819.5 406.64 819.73 405.95 821.12 403.17 821.36 402.23 821.36 401.54 820.89 397.82 821.82 396.2 822.98 394.34 823.91 393.65 824.84 393.18 825.06 393.41 826 394.1 827.39 395.04 827.85 394.81 828.78 394.1 829.48 393.41 829.25 392.02 829.94 389.46 829.94 388.07 830.41 385.99 831.1 383.43 831.33 381.8 831.8 380.88 833.66 379.03 835.27 376.47 836.21 374.38 838.05 371.36 838.99 370.21 839.93 369.74 841.08 369.27 843.18 369.05 844.79 368.81 845.03 368.35 845.03 367.88 845.26 366.49 845.5 363.94 846.18 361.61 846.87 359.76 847.35 359.06 847.81 358.13 848.51 356.97 849.2 353.96 849.2 352.1 850.14 348.62 850.36 348.62 852.22 347.92 853.86 347.69 855.7 348.15 856.63 348.39 857.09 348.15 857.79 347.45 858.02 346.52 858.72 346.07 859.64 345.83 861.04 346.99 862.9 349.78 863.59 350.02 863.82 350.02 864.75 347.69 865.68 345.83 867.07 342.59 868 340.73 868.7 341.19 869.39 342.12 870.32 342.59 871.25 342.82 871.72 343.04 872.18 343.28 874.27 345.6 874.96 346.3"/>
                                </g>
                                <g id="Somalia">
                                    <polygon class="cls-2 activeCountry" points="1039.75 387.15 1040.22 390.62 1040.43 396.2 1040.43 396.89 1039.99 397.82 1039.99 398.29 1039.29 398.51 1039.05 398.98 1039.51 398.98 1041.13 398.51 1041.13 397.82 1041.13 397.59 1042.54 398.29 1043.46 398.51 1043.7 399.22 1043.7 399.68 1042.3 399.45 1041.38 399.22 1039.29 399.68 1037.89 400.37 1037.43 401.54 1036.96 405.71 1036.49 408.5 1036.49 412.22 1034.65 414.76 1033.94 416.39 1031.4 419.87 1029.77 422.89 1029.31 424.28 1026.98 428.46 1023.72 431.47 1022.57 435.42 1021.41 437.98 1020.01 440.3 1017.23 444.24 1015.62 447.02 1013.75 451.9 1013.29 454.92 1008.18 463.74 1002.61 470.94 999.36 476.74 993.34 483.7 984.98 492.75 974.3 503.19 971.28 505.28 959.44 511.78 951.79 517.35 948.07 521.06 943.89 524.31 940.64 527.32 930.67 537.78 929.73 538.71 928.79 539.64 927.65 541.49 926.72 542.19 924.4 544.97 922.78 546.6 921.15 548.22 920.46 549.16 919.99 550.54 919.53 551.24 917.9 554.01 916.74 556.11 915.35 557.5 915.58 556.59 915.35 555.87 913.95 554.26 911.64 551.24 910.01 549.16 908.17 546.83 908.17 544.97 908.17 539.4 908.17 528.5 908.17 517.58 908.17 506.44 907.93 501.11 907.93 498.78 908.17 498.32 910.24 496.46 913.02 493.67 916.51 488.57 918.36 486.02 919.99 483.7 920.46 483 921.85 481.61 924.4 480.68 926.03 480.68 931.83 479.52 932.76 479.05 933.21 478.59 933.68 477.43 934.84 476.04 936.24 474.88 938.79 473.48 941.56 472.33 942.03 472.09 945.3 471.4 946.22 471.16 947.38 470.94 947.85 470.94 952.24 471.16 955.74 471.4 959.44 471.63 959.9 471.4 962.23 468.61 966.17 464.44 968.73 461.65 972.67 457.23 975.69 454.23 979.17 450.75 982.43 447.49 986.13 443.78 988.69 441.46 992.4 437.74 996.11 434.26 999.36 431 994.96 431 990.55 431 986.37 431 985.68 430.78 981.94 429.38 977.55 428 971.75 426.14 967.8 424.74 963.62 423.36 959.21 421.96 955.74 420.8 951.55 419.41 947.85 418.02 947.38 417.78 945.3 415.92 942.5 413.6 942.03 413.37 940.87 412.91 939.72 411.52 938.55 409.89 937.39 407.81 936.93 406.4 935.31 405.71 934.62 404.55 933.46 402.93 932.53 402.23 932.04 401.3 931.6 399.91 930.9 398.29 930.21 397.36 929.97 396.89 930.21 396.67 931.37 394.34 932.04 393.65 932.76 392.94 933.21 392.25 933.46 391.56 935.08 389 936.7 386.92 937.62 385.05 940.19 387.15 942.72 391.09 945.51 394.57 949.69 397.59 951.08 398.51 952.48 399.22 959.9 398.98 965.01 396.2 969.65 394.1 971.28 393.87 973.84 394.34 976.85 394.57 979.63 395.04 981.03 395.04 986.37 392.71 989.62 390.4 991.93 389.24 992.86 389.24 995.89 390.16 999.82 389.7 1005.39 387.84 1007.02 387.38 1008.41 387.38 1011.43 388.3 1011.89 388.3 1013.52 388.07 1017.7 387.15 1020.95 385.76 1027.22 384.59 1031.86 382.04 1032.55 380.88 1033.94 379.25 1036.04 378.56 1041.13 380.41 1042.07 380.64 1041.83 381.8 1041.6 382.98 1040.43 385.05 1039.75 387.15"/>
                                </g>
                                <g id="Kenya">
                                    <path class="cls-2 activeCountry" d="m908.38,563.53l-.46.24.24-.94,1.38-.93.7.23v.46l-.23.23-1.63.7Zm-74.27-98.17l.46.23.7-.23.46-.46h.71l3.71.93.47.92v.92l.22.47-.46.69-.24,2.1.24,1.86.93,1.38.92,1.17.94,1.39.45.46.71.24h2.54l3.71.23h3.72l.93.24,3.25,2.08,2.79,1.86,2.55,1.63,2.55,1.63,2.32,1.62,1.87,1.16,1.85.46,2.79.24h2.09l1.86.46,2.79.47,2.07.22,1.41.47,3.47.24.46-.24,1.63-1.38,1.84-2.33.47-1.16,2.31-1.38,3.96-1.62,2.78-1.4,3.03-1.15,1.38,1.15,2.09,1.63.7.93.7.46,1.16.23h1.16l.7-.23,1.39-.23h5.34l-1.63,2.32-1.86,2.55-3.48,5.1-2.79,2.79-2.07,1.86-.24.46v2.34l.24,5.33v40.39l1.84,2.33,1.63,2.08,2.31,3.02,1.4,1.62.22.71-.22.91-1.87,2.34-1.62.92-2.08.46h-.47l-.93-.46-.21.69-.24.7h-.47l-.46-.23.22,1.4.23.69-.23.94-1.16.93v.69l-2.3,2.09h-3.04l-1.62,1.16-.7.7-.7,1.63.22,2.55-.92,2.09v.92l-1.63,1.4-.68,1.16-.71,1.16-.46.69-.46,2.79-.71,1.62-.21.47-.24.46-.46.93-.46.7-.23.46-1.87,4.18-1.62,2.09-1.18-.25-.68.71v.22h-.47l-.94-.69-2.07-1.63-2.09-1.39-1.86-1.38-2.09-1.39-1.86-1.39-2.09-1.62-2.09-1.39-1.86-1.39-1.16-.93-.69-.46-.24-.94-.23-.24-.46-.46h-.7l-.23-.23v-.46l.23-.7.7-1.16.22-.94-.22-.92-.22-1.39-.24-.23-1.39-.7-2.78-1.63-2.79-1.62-2.79-1.39-2.55-1.63-2.78-1.62-2.79-1.38-2.78-1.63-2.78-1.62-2.8-1.39-2.79-1.62-2.78-1.63-2.79-1.39-2.79-1.62-2.79-1.63-2.78-1.39-2.79-1.63-1.16-.7-.93-.46h-.93v-2.09l.23-5.8v-4.87l.23-2.55,1.39-1.62.46-.94.46-1.62.7-1.39,1.39-1.16.23-.46,1.62-1.86.93-2.09.7-.93.69-.7.7-.46.94-.23.93-.23v-.23l.23-.24-.23-1.62.23-.23.46-1.16.7-.7.46-.69.46-.47v-5.57l-.7-2.09-.46-2.54.23-.71-.46-1.39-.23-.24-.46-.22-.46-1.39-.46-1.17-.23-.46-1.64-.93-.94-2.55-.91-.7-.7-2.32v-.93l.46-2.31v-.7l-.46-.47-1.62-.69-1.39-.93.23-.47v-.22l-.7-.23-2.09-4.42,2.79-2.54,2.55-2.55,3.25-3.49,3.25-3.02,2.54-2.55,2.55-2.31v.92l.23.46Z"/>
                                </g>
                                <g id="Comoros">
                                    <path class="cls-2" d="m944.83,696.53l.92.7-2.55-.23-.47-.7v-.46h.94l1.17.7Zm8.82-3.02l.68,1.86v1.39l-.21.46-.46-.23-1.17-1.16-2.1-1.16h2.1l.47-.7v-.23l.46-.23h.23Zm-13.01-2.32l-.22.23-1.17-.93h-.7l-1.16-1.39.47-4.18.22-.46.24-.23h.7l.7.46-.23,2.79.93,1.62.45,1.63-.22.46Z"/>
                                </g>
                                <g id="Uganda">
                                    <polygon class="cls-2 activeCountry" points="816.01 548.44 814.62 548.44 812.3 548.44 809.98 548.44 807.66 548.44 805.33 548.44 803.02 548.44 800.7 548.44 798.39 548.44 796.05 548.44 793.73 548.44 791.42 548.44 789.09 548.44 786.78 548.44 784.46 548.44 782.13 548.44 779.8 548.44 777.49 548.44 776.33 548.44 775.86 548.44 774.94 548.68 774.01 549.16 773.08 549.38 771.92 549.38 771.45 549.38 770.53 549.38 769.83 549.38 769.36 549.84 768.9 550.76 767.97 551.7 767.28 552.63 766.59 553.32 765.19 554.26 764.26 554.49 764.02 554.49 763.79 554.26 763.34 553.08 762.86 552.87 760.08 553.55 759.61 553.55 759.85 553.08 759.61 550.08 759.61 548.22 759.85 547.07 760.08 545.66 760.08 544.5 760.54 542.41 760.54 541.25 761.01 536.84 761.24 536.15 761.48 534.07 761.94 533.6 762.4 533.37 762.86 531.98 763.79 529.89 764.26 528.96 764.26 526.64 764.26 525.24 764.49 524.78 765.89 524.31 767.51 522.69 768.44 521.06 769.36 519.9 771.45 519.21 777.49 513.64 780.27 510.38 781.44 508.76 781.67 508.29 781.9 507.6 781.44 506.91 780.74 506.44 780.51 505.98 780.04 505.75 779.35 505.75 778.88 505.28 778.19 504.59 777.72 504.12 776.1 504.12 774.7 503.43 774.7 502.49 775.17 500.64 776.33 498.32 776.33 497.85 776.1 497.15 775.86 496.7 775.41 496.46 774.94 495.77 775.41 494.14 776.1 492.75 776.57 491.82 777.03 491.13 776.79 490.43 776.1 489.96 776.57 489.26 777.26 488.1 778.88 486.94 780.27 486.02 781.21 486.02 782.83 486.72 784.46 487.41 785.38 487.65 786.54 487.18 788.63 486.02 789.09 486.25 789.79 487.18 790.48 488.34 791.89 489.04 792.56 489.51 793.04 489.51 793.26 489.51 793.73 488.34 794.42 487.88 795.6 487.18 798.15 486.48 800 486.48 800.92 486.25 802.08 486.02 804.18 484.86 806.27 486.25 808.59 486.48 810.68 486.48 811.38 486.02 811.6 485.78 813.92 483.47 816.94 480.45 819.03 484.86 819.73 485.1 819.73 485.31 819.5 485.78 820.89 486.72 822.51 487.41 822.98 487.88 822.98 488.57 822.51 490.89 822.51 491.82 823.21 494.14 824.12 494.83 825.06 497.39 826.69 498.32 826.92 498.78 827.39 499.95 827.85 501.34 828.32 501.56 828.55 501.8 829.02 503.19 828.78 503.89 829.25 506.44 829.94 508.53 829.94 511.32 829.94 512.25 829.94 512.94 829.94 514.1 829.48 514.57 829.02 515.26 828.32 515.96 827.85 517.12 827.62 517.35 827.85 518.97 827.62 519.21 827.62 519.44 826.69 519.67 825.76 519.9 825.06 520.37 824.37 521.06 823.68 521.99 822.75 524.08 821.12 525.94 820.89 526.4 819.5 527.56 818.8 528.96 818.34 530.57 817.87 531.51 816.48 533.14 816.25 535.69 816.25 540.56 816.01 546.35 816.01 548.44"/>
                                </g>
                                <g id="Rwanda">
                                    <polygon class="cls-2 activeCountry" points="771.92 549.38 771.45 549.6 771.45 550.08 771.92 551.24 773.54 553.32 774.47 553.55 775.17 554.49 775.86 555.87 776.1 557.5 775.63 559.59 775.86 560.98 776.57 562.14 776.57 563.31 776.33 564.93 776.1 565.86 775.63 566.32 775.17 566.32 774.47 566.32 773.78 566.32 772.85 566.78 772.38 566.78 772.15 566.55 771.45 566.32 770.53 565.62 768.67 566.09 768.2 566.09 767.51 566.32 767.05 566.78 766.81 567.02 766.35 566.78 764.95 565.86 764.26 565.86 764.02 568.64 763.79 570.27 763.56 570.73 762.4 571.43 761.24 571.89 760.78 571.89 758.23 572.13 757.29 572.13 756.83 571.89 756.13 570.27 754.76 569.58 753.6 569.34 753.12 569.34 752.66 570.27 752.42 570.96 751.26 570.5 750.79 569.8 750.79 568.64 750.33 567.25 750.57 566.78 751.02 566.32 751.96 565.62 753.6 564.47 753.82 564 754.04 563.31 754.04 561.22 753.82 559.59 754.04 559.12 754.76 557.96 755.67 556.59 756.83 555.17 757.53 555.17 758.23 554.49 759.15 553.79 759.61 553.55 760.08 553.55 762.86 552.87 763.34 553.08 763.79 554.26 764.02 554.49 764.26 554.49 765.19 554.26 766.59 553.32 767.28 552.63 767.97 551.7 768.9 550.76 769.36 549.84 769.83 549.38 770.53 549.38 771.45 549.38 771.92 549.38"/>
                                </g>
                                <g id="Burundi">
                                    <polygon class="cls-2 activeCountry" points="772.38 566.78 772.15 567.02 771 569.58 770.76 569.8 771 570.03 771.45 570.5 771 571.43 770.76 572.36 770.76 572.82 771.22 573.05 771.92 573.52 773.08 573.75 774.47 574.21 775.41 574.44 775.63 574.68 775.63 575.37 775.86 576.07 775.86 577.23 775.41 578.16 774.01 578.62 773.31 579.09 773.08 579.32 773.31 579.55 773.54 580.02 772.15 580.95 770.76 582.1 770.53 583.03 770.06 583.96 769.83 584.66 768.67 585.59 767.74 587.45 767.05 588.61 764.49 591.62 762.17 593.02 761.48 593.48 757.53 593.48 757.07 591.39 756.6 588.84 755.2 586.51 754.98 585.36 754.98 583.5 754.98 580.71 754.98 579.32 754.98 578.16 755.2 576.3 755.2 575.14 754.29 573.99 753.12 572.59 752.42 571.89 752.42 571.43 752.42 570.96 752.66 570.27 753.12 569.34 753.6 569.34 754.76 569.58 756.13 570.27 756.83 571.89 757.29 572.13 758.23 572.13 760.78 571.89 761.24 571.89 762.4 571.43 763.56 570.73 763.79 570.27 764.02 568.64 764.26 565.86 764.95 565.86 766.35 566.78 766.81 567.02 767.05 566.78 767.51 566.32 768.2 566.09 768.67 566.09 770.53 565.62 771.45 566.32 772.15 566.55 772.38 566.78"/>
                                </g>
                                <g id="Dem_Rep_Congo" data-name="Dem_Rep_Congo">
                                    <polygon class="cls-2 activeCountry" points="731.31 468.84 731.77 469.77 732.47 470.7 734.79 472.33 735.48 473.25 736.17 474.17 736.41 474.88 737.11 475.58 738.04 476.04 738.95 476.5 739.43 476.97 740.12 477.9 741.75 478.83 742.45 478.83 743.14 478.83 744.08 479.29 744.77 479.05 745.93 478.36 747.55 477.43 748.71 476.74 751.5 476.97 752.88 477.43 754.29 478.36 755.2 478.13 757.29 476.74 758.23 475.35 759.38 475.11 761.01 475.58 762.4 476.97 763.56 479.05 764.26 479.75 765.42 480.9 767.74 483.47 770.76 484.86 771.92 485.55 772.15 486.02 772.38 486.94 772.62 487.88 772.85 488.34 773.54 488.1 774.24 487.88 775.17 488.34 775.63 488.81 775.86 489.51 776.1 489.96 776.79 490.43 777.03 491.13 776.57 491.82 776.1 492.75 775.41 494.14 774.94 495.77 775.41 496.46 775.86 496.7 776.1 497.15 776.33 497.85 776.33 498.32 775.17 500.64 774.7 502.49 774.7 503.43 776.1 504.12 777.72 504.12 778.19 504.59 778.88 505.28 779.35 505.75 780.04 505.75 780.51 505.98 780.74 506.44 781.44 506.91 781.9 507.6 781.67 508.29 781.44 508.76 780.27 510.38 777.49 513.64 771.45 519.21 769.36 519.9 768.44 521.06 767.51 522.69 765.89 524.31 764.49 524.78 764.26 525.24 764.26 526.64 764.26 528.96 763.79 529.89 762.86 531.98 762.4 533.37 761.94 533.6 761.48 534.07 761.24 536.15 761.01 536.84 760.54 541.25 760.54 542.41 760.08 544.5 760.08 545.66 759.85 547.07 759.61 548.22 759.61 550.08 759.85 553.08 759.61 553.55 759.15 553.79 758.23 554.49 757.53 555.17 756.83 555.17 755.67 556.59 754.76 557.96 754.04 559.12 753.82 559.59 754.04 561.22 754.04 563.31 753.82 564 753.6 564.47 751.96 565.62 751.02 566.32 750.57 566.78 750.33 567.25 750.79 568.64 750.79 569.8 751.26 570.5 752.42 570.96 752.42 571.43 752.42 571.89 753.12 572.59 754.29 573.99 755.2 575.14 755.2 576.3 754.98 578.16 754.98 579.32 754.98 580.71 754.98 583.5 754.98 585.36 755.2 586.51 756.6 588.84 757.07 591.39 757.53 593.48 757.53 593.95 757.07 596.27 756.36 598.59 756.36 599.29 756.6 600.44 757.77 603 758.45 604.85 758.7 605.79 759.15 607.16 759.85 609.02 760.08 609.96 759.85 610.65 758.7 613.2 758.45 613.9 758.7 616 759.15 617.84 759.85 618.78 761.48 621.79 762.63 622.72 764.72 624.11 766.59 625.74 767.28 626.43 767.97 627.36 769.36 629.45 770.06 631.31 770.53 632.7 771.45 635.02 772.62 637.12 773.78 639.43 774.7 641.29 774.94 642.45 772.85 642.69 769.6 643.15 765.89 643.85 762.17 644.31 758.45 645.01 754.98 645.45 751.72 645.93 750.79 646.17 751.26 647.56 751.02 648.95 750.57 650.11 749.41 651.5 748.94 652.2 748.02 653.11 747.31 653.83 745.47 655.21 744.3 655.91 744.3 656.61 746.15 659.62 747.09 661.95 747.31 663.79 747.31 664.96 747.31 667.51 747.31 670.3 747.09 671.22 747.55 673.31 747.55 674.95 746.15 676.56 745.93 678.42 745.24 680.74 744.53 683.99 743.84 685.62 744.08 686.78 744.53 687.48 744.77 688.4 745.47 690.03 746.15 690.96 746.61 691.19 749.18 693.05 750.33 693.98 751.96 695.83 753.12 696.99 754.76 697.45 756.6 697.92 757.77 698.16 758.45 697.92 758.7 697.69 758.7 696.76 758.7 696.07 758.93 695.59 759.38 695.14 761.24 695.14 761.94 694.67 762.63 694.44 762.63 696.53 762.63 698.39 762.63 700.7 762.63 703.5 762.63 705.59 762.63 707.91 762.63 710.68 762.63 710.92 762.4 711.62 761.7 711.62 760.78 711.16 760.54 710.68 760.32 709.75 760.08 709.06 759.38 709.06 758.45 709.3 757.07 710 755.44 710.68 754.76 710.92 753.6 710.92 752.42 710.68 751.5 709.75 751.26 708.6 750.33 707.21 749.18 705.33 748.71 704.65 748.02 703.96 747.09 703.73 746.4 703.5 745.93 702.33 745.47 700.7 745 700 744.53 699.32 743.84 698.85 742.22 698.16 740.12 697.23 737.34 696.29 735.95 696.29 734.54 696.07 733.63 695.37 733.16 695.14 732.47 693.51 731.77 691.65 729.22 689.56 728.75 687.24 728.29 687.01 727.36 687.01 726.65 687.48 726.43 687.94 725.97 690.26 725.5 691.19 725.27 691.42 724.81 691.65 723.88 692.12 722.72 692.12 720.86 692.12 718.77 691.9 717.61 691.65 714.36 691.19 713.43 690.96 712.28 690.49 711.34 690.03 708.09 689.1 706.71 689.1 706.01 688.4 705.55 688.18 704.62 687.48 704.39 686.55 703.68 684.69 703.92 683.53 704.15 682.37 703.92 682.14 703.23 682.14 702.52 682.37 701.12 682.6 698.57 683.3 697.65 683.53 696.48 683.76 695.79 683.99 693.95 685.15 693.24 685.15 692.09 684.69 691.38 684.22 692.09 683.53 692.32 682.6 691.86 680.98 691.16 680.28 689.53 679.58 688.83 679.58 688.61 678.66 688.14 677.97 687.21 677.72 686.75 677.5 686.05 677.97 685.81 678.42 685.81 679.13 684.89 679.36 683.03 679.36 681.41 678.88 680.01 678.88 679.32 678.88 676.07 680.28 675.14 680.51 671.66 680.28 669.8 680.05 668.41 680.05 667.49 680.51 666.09 681.44 665.16 681.91 664.69 681.91 664.46 681.44 664.01 680.98 663.77 679.36 663.3 677.97 663.77 677.03 664.69 676.33 664.92 675.17 664.69 673.31 664.69 672.15 664.92 671.22 664.69 669.6 663.53 666.58 662.14 664.27 660.28 662.4 659.13 660.79 658.67 659.16 658.89 655.21 659.36 651.74 659.82 648.95 659.6 644.31 658.44 641.29 658.19 638.04 658.89 634.56 658.89 632.24 658.44 631.08 658.19 630.84 657.73 630.84 654.72 630.61 650.54 630.38 646.83 630.38 642.89 630.38 642.19 629.92 641.96 629.24 641.96 628.29 642.89 625.97 642.65 625.74 641.26 625.74 637.55 625.97 634.99 626.43 633.37 626.66 630.58 627.36 628.96 628.75 628.26 630.38 628.26 631.77 628.26 632.93 627.33 634.09 626.87 635.26 626.87 635.95 626.39 639.43 623.85 639.9 621.29 639.9 620.6 639.9 617.35 638.97 616.19 638.97 615.26 639.43 613.4 639.9 611.54 640.13 609.46 641.29 608.99 641.29 607.83 640.82 605.97 640.82 604.35 641.06 603.42 641.06 602.95 640.82 601.35 638.27 599.01 635.02 597.85 632.7 597.4 632.24 596.69 631.54 595.77 630.15 595.31 628.75 595.53 627.6 594.84 625.97 593.43 623.89 592.52 621.79 592.06 619.93 592.06 618.31 592.28 616.92 592.06 615.77 591.12 615.29 590.9 614.37 590.42 613.9 589.96 613.2 588.58 612.27 586.95 612.04 583.7 611.82 579.29 611.82 575.34 612.04 570.93 612.04 566.52 612.04 565.36 612.27 562.11 612.27 559.34 612.04 558.4 611.82 556.55 611.82 553.76 611.82 552.36 611.82 548.64 611.82 548.41 611.82 547.73 612.04 546.33 611.82 544.7 611.82 543.78 611.57 541.92 611.82 541.22 612.04 539.59 613.2 537.52 613.67 536.81 613.67 536.12 613.43 534.95 612.27 534.03 611.12 533.57 610.42 534.03 610.42 535.89 610.2 537.05 609.96 537.29 609.73 537.52 606.24 537.52 602.54 537.05 602.07 536.81 601.84 536.59 601.6 537.29 601.14 538.21 600.66 538.68 600.44 539.59 599.29 541.7 597.19 543.09 596.72 544.47 596.03 544.7 595.8 544.94 595.33 545.63 595.55 545.87 595.55 546.11 596.03 546.8 596.72 547.73 597.66 548.64 598.34 549.35 598.59 550.05 598.13 550.98 597.43 552.36 596.97 552.82 596.49 552.82 595.55 553.05 594.64 553.05 593.48 553.52 593.25 553.99 593.25 554.93 593.48 555.39 593.95 556.07 593.95 556.55 593.48 557.47 593.02 558.63 592.78 559.79 592.32 560.95 591.62 561.41 591.39 562.11 592.32 562.57 593.02 562.81 593.48 562.11 594.17 561.64 595.11 562.11 596.49 562.11 597.66 562.11 598.34 562.57 598.8 562.81 598.8 563.28 598.59 564.2 598.8 565.13 599.05 566.06 599.05 566.98 598.59 568.84 596.72 571.16 593.48 573.25 591.62 575.11 590.7 576.04 589.77 576.74 588.84 577.66 587.91 579.75 587.45 581.16 586.75 582.77 584.43 584.86 580.48 585.32 577 585.79 574.91 585.55 567.48 585.32 565.17 585.79 563.77 586.49 562.84 588.58 560.98 589.96 559.36 590.9 557.5 592.99 553.32 593.9 551.92 594.38 551.48 595.53 550.32 597.4 549.38 599.48 548.44 602.95 545.43 605.74 542.66 605.27 539.17 605.97 536.15 607.36 532.44 608.07 528.5 607.36 524.31 607.61 520.83 608.99 516.88 609.7 515.49 609.93 513.17 609.93 509.23 611.78 503.89 613.4 500.87 615.26 497.39 615.95 495.3 617.12 492.52 616.88 490.19 616.65 487.41 617.12 483.93 617.12 481.84 616.43 479.98 616.65 478.83 618.04 478.36 619.67 476.5 622.92 471.63 626.17 469.31 628.49 468.61 630.8 468.61 632.44 469.08 633.14 469.77 634.99 470.94 638.01 472.56 640.1 474.42 641.26 476.27 642.19 477.2 643.34 477.66 645.43 477.43 647.51 477.9 649.62 479.05 651 479.29 651.46 479.05 652.62 479.29 654.95 480.22 657.03 479.75 659.82 479.98 666.55 481.61 666.78 481.37 667.01 481.15 667.71 480.68 669.1 477.43 670.26 475.58 670.96 474.88 672.35 473.95 673.98 473.72 675.6 473.72 677 474.17 678.16 474.64 679.54 474.64 680.94 474.17 683.03 473.25 685.12 472.79 686.98 472.09 690 470.94 691.16 470.47 692.78 470.23 696.96 471.16 699.74 470.47 700.9 470.7 703.46 470 703.68 469.53 705.3 466.99 706.93 466.29 709.5 466.53 710.88 466.99 715.29 468.15 721.32 469.08 723.18 469.31 723.88 469.53 724.57 469.31 726.43 467.91 727.13 467.68 727.59 467.68 731.31 468.84"/>
                                </g>
                                <g id="Tanzania">
                                    <path class="cls-2 activeCountry" d="m891.68,639.67h-.93l-.46-.7.7-.46.7-.94,1.86-1.16.46-.92.24-.23v.93l-1.16,2.32-.71.23-.68.93Zm-2.8-23.66l.94,2.78v.46l-.7.47h-.46l-.47-.47-.22-.94-.47.23-.94-1.16h-.92l-.92-1.38.46-1.17-.24-2.1.94-.92.46-1.86.7,1.16.23,1.85.7,2.32.92.48v.23Zm4.88-16.5v.46l-.22.7v3.27l-.71,1.86-.68.7-.46-.24-.48-.23-.45-.46.7-3.47-.25-2.33,1.4.22,1.16-.46Zm-117.91-51.06h41.08l.93.46,1.16.7,2.79,1.63,2.78,1.39,2.79,1.63,2.79,1.62,2.79,1.39,2.78,1.63,2.79,1.62,2.8,1.39,2.78,1.62,2.78,1.63,2.79,1.38,2.78,1.62,2.55,1.63,2.79,1.39,2.79,1.62,2.78,1.63,1.39.7.24.23.22,1.39.22.92-.22.94-.7,1.16-.23.7v.46l.23.23h.7l.46.46.23.24.24.94.69.46,1.16.93,1.86,1.39,2.09,1.39,2.09,1.62,1.86,1.39,2.09,1.39,1.86,1.38,2.09,1.39,2.07,1.63.94.69h.47l-.47,1.17-.94,2.78v.94l-.45,1.39-.46.92-.93,3.71-.93,1.39-1.17,3.25-.22,2.55.69,1.86.24,1.63,1.39,1.63,1.15.45.69.7,1.41,1.86.68,1.63,2.57.92.92,1.86-.46,1.16-1.16,1.16-.93,1.86-.95,2.32v3.49l.71-.47,1.16.7.22,2.55-1.38,3.25-.46,1.39v1.15l.93,3.72,1.62,1.86-.22.46-.47.46,2.56,3.24-.23,3.02.93,2.08.46,1.86.7,1.63v.92l-.7,1.16,1.86.23.94.93.46.93h1.39l.69.47.94.69,2.31,1.41.7.68.24.46v.23l-1.38,1.16-2.33,1.86-2.33,1.86-2.3,1.16-1.62.47-1.62.22-1.63.93-1.63,1.16-1.84.46h-2.55l-2.32.93-2.55,1.39-1.39.93-2.32-1.39-1.86-.22h-3.25l-.46.46-.46.7-.46,1.39-1.16,1.39-2.55,1.17-2.09.46-2.08-.23-1.39-.7-.7-.7-.92-.23h-1.4l-1.38.46-1.16,1.16-2.08.23h-2.8l-1.38-.46-.25-.94-1.38-.92-2.1-.94-1.62-.23-1.16,1.17-.94.69-.92.23h-.7l-.7-.23h-.47l-3.01-.23-3.02.23v-.46l-.23-1.16-.46-.93-.7-.46-.46-.23h-.46l-.23-.46-.46-.93-.46-.7-.7-.7-.23-.7-.24-.46.24-.69.46-1.62.24-.94v-1.16l-.46-1.16-.7-1.15.22-.47-.22-.93v-2.32l-.71-2.3v-.48l-.68-1.15-1.86-2.55v-.25l-3.02-2.54-1.39-.72-.46.47v1.86h-1.39l-1.16-.7-.7-.22-2.32.22h-1.39l-1.15-1.16-1.4-.23-1.16-.24-2.07-1.16h-.71l-.69-.23-1.4-.69-1.85-.7-1.63-.92-.7-.7-1.62-.24-1.16-.23-1.16-.69h-1.4l-1.15-.23-.22-.48v-.91l-.47-.23-.92-.23h-.93l-.46.23-.48-.23-.68-.46-.94-.94-.22-1.15-1.16-.7-1.16-.69h-4.18l-.93-.71-.93-.92-.7-1.16-.69-1.63-.47-.93-.23-1.16-.93-1.86-1.16-2.31-1.17-2.1-.92-2.32-.46-1.39-.7-1.86-1.39-2.09-.7-.93-.69-.7-1.87-1.62-2.09-1.39-1.15-.93-1.63-3.01-.7-.94-.45-1.84-.25-2.11.25-.7,1.14-2.55.23-.7-.23-.94-.7-1.86-.45-1.38-.25-.94-.69-1.85-1.17-2.56-.23-1.15v-.7l.71-2.32.46-2.32v-.46h3.95l.69-.46,2.32-1.4,2.56-3.01.69-1.16.93-1.86,1.16-.93.23-.7.46-.93.23-.94,1.39-1.15,1.39-.93-.23-.46-.23-.23.23-.23.7-.46,1.4-.46.45-.93v-1.16l-.22-.7v-.7l-.22-.23-.94-.23-1.39-.46-1.16-.23-.7-.46-.46-.23v-.46l.24-.93.45-.93-.45-.46-.24-.23.24-.22,1.15-2.56.23-.23h.46l.93-.46h1.86l.46-.46.23-.93.24-1.62v-1.17l-.71-1.16-.22-1.39.46-2.09-.24-1.63-.69-1.38-.7-.94-.93-.23-1.63-2.08-.47-1.16v-.47l.47-.22h1.16l.93-.22.93-.47.92-.24h0Z"/>
                                </g>
                                <g id="Seychelles">
                                    <polyline class="cls-2" points="1098.12 594.3 1098.37 594.77 1098.82 595.22 1098.82 594.05 1097.9 592.19 1096.72 592.87 1097.18 593.35 1098.12 594.05 1098.12 594.3"/>
                                </g>
                                <g id="Malawi">
                                    <path class="cls-2" d="m826.69,693.98h-.46l-.23-.23v-.7l.23-.46h.7l.23.23v.46l-.23.46-.23.23Zm-.94-1.39l-.46.7h-.46l-.46-.46.22-.46.46-.23.47.23.22.23Zm-17.16-32.73l1.15,1.16h1.39l2.32-.22.7.22,1.16.7h1.39v-1.86l.46-.47,1.39.72,3.02,2.54v.25l1.86,2.55.68,1.15v.48l.71,2.3v2.32l.22.93-.22.47.7,1.15.46,1.16v1.16l-.24.94-.46,1.62-.24.69.24.46.23.7.7.7.46.7.46.93.23.46h.46l.46.23.7.46.46.93.23,1.16v.46l-1.62-.23-2.09.23-.7.46v.93l-.7,1.86-.47.7-.68,1.39-1.16,1.62-.23.7v.7l.7,2.32.7,2.55.23,1.15.45,3.25.25,2.56v1.39l.22,1.86.7,1.16.7.45,2.32.47.7.47,1.39,1.14,3.25,3.48,1.62,2.1,1.4,1.86,2.54,3.49,2.11,2.78.22,2.54.47.46-.7,1.86-.47,3.02.23,2.09v3.48l-.46,3.71-.47,1.39-.69.46-1.41.46-3,.46-.47.23-.46.93-.7,1.62-.69,1.62-.23.93h.23l.69.93.46,2.32.23,3.95-.23.22-.92.24-.93-.24-.46-.22-.46-.24-.23-.92.7-.71.23-.91-.47-.93h-.69l-1.16-.94-2.32-2.54-1.86-1.86-1.18-1.39-1.14-.7-.23-.23-.23-.7v-1.63l-.23-.69-1.16-1.16-.46-.7-.23-.7.46-.93,1.16-.93.7-1.86.23-1.16,1.39-2.32.22-2.08v-3.02l-.47-2.56-.21-1.62-1.63-2.31-.7-.22-1.63.22-1.39.22-.7.46-.93.23-2.79.23-.7.24-.7.46h-.22l-1.63-1.85-1.62-1.86-1.85-3.25-.46-.46-.7-.94-.71.24-.7.7-.46.22h-.22l-.22-.22v-.47l-.71-1.16-.69-.93-.7-.23-.7-.46.25-.46.46-.23-.24-.22-.21-.46-1.4-.71v-.22l1.15-.46.7-.7.46-.46.7-1.39.46-1.4.48-.46v-1.86l.22-1.16v-1.17l-.22-.46-.48-.93.48-1.39.7-.94,2.78-.92,2.09-.93.46-.23.71-.93.22-.7-.22-.23h-1.62l-.24-.23-1.17-2.55.46-3.02.23-1.16v-1.39l-.23-1.16-.46-.23-.46-.69.24-1.63h.47l.92-2.09.46-1.16-.69-.93-.47-1.38-.21-.94-.25-.22.46-.71.71-.46.7-.24.92-.23,2.32-2.55.23-.46-.46-.7-.94-1.39-.22-.45-.24-1.63-.23-.46-1.38-.94-1.17-1.16.24-1.15.23-1.16-.47-.7-.7-.69-.69-.93-.24-.7-.46-.24-.71-.22-.23.46h-1.15l-.22-.71v-.69l-.24-.47-.47-.7v-.46h.71l2.07,1.16,1.16.24,1.4.23Z"/>
                                </g>
                                <g id="Zambia">
                                    <polygon class="cls-2 activeCountry" points="803.24 658.22 803.24 658.69 803.72 659.38 803.96 659.86 803.96 660.54 804.18 661.26 804.87 661.26 805.33 661.26 805.57 660.79 806.27 661.02 806.73 661.26 806.97 661.95 807.66 662.88 808.35 663.57 808.83 664.27 808.59 665.43 808.35 666.58 809.52 667.74 810.91 668.68 811.14 669.14 811.38 670.78 811.6 671.22 812.54 672.62 813 673.31 812.76 673.78 810.44 676.33 809.52 676.56 808.83 676.8 808.12 677.26 807.66 677.97 807.91 678.19 808.12 679.13 808.59 680.51 809.28 681.44 808.83 682.6 807.91 684.69 807.43 684.69 807.19 686.32 807.66 687.01 808.12 687.24 808.35 688.4 808.35 689.8 808.12 690.96 807.66 693.98 808.83 696.53 809.07 696.76 810.68 696.76 810.91 696.99 810.68 697.69 809.98 698.62 809.52 698.85 807.43 699.78 804.65 700.7 803.96 701.64 803.48 703.03 803.96 703.96 804.18 704.41 804.18 705.59 803.96 706.75 803.96 707.91 803.96 708.6 803.48 709.06 803.02 710.46 802.32 711.85 801.86 712.32 801.15 713.01 800 713.47 800 713.69 801.4 714.41 801.62 714.87 801.86 715.09 801.4 715.33 801.15 715.79 801.86 716.25 802.56 716.48 803.24 717.41 803.96 718.57 803.96 719.04 804.18 719.27 804.41 719.27 804.87 719.04 805.57 718.35 806.27 718.1 806.97 719.04 804.18 719.98 802.56 720.66 798.39 722.05 794.89 723.21 793.97 723.45 791.89 724.15 790.95 724.39 787.71 725.53 786.31 726.01 785.15 726.7 782.6 727.39 780.04 728.09 777.03 729.02 774.01 729.94 772.15 730.41 771 731.11 768.2 732.03 768.2 732.5 768.2 733.2 768.44 734.59 769.13 735.98 769.83 736.91 770.06 739 770.29 740.86 768.44 740.86 765.19 740.86 761.7 740.86 758.45 741.55 755.92 742.71 752.88 744.34 751.96 744.8 751.02 745.5 750.57 745.96 750.33 747.59 750.33 749.68 750.1 751.3 749.18 752.7 744.3 754.55 741.29 755.95 738.27 757.57 735.95 759.66 734.54 762.45 731.77 765.93 729.22 768.71 726.43 771.72 723.18 772.88 720.63 772.65 717.39 771.5 715.06 771.26 712.97 771.97 711.34 771.72 709.72 770.57 708.33 770.1 707.39 770.34 706.01 770.34 703.46 769.63 701.36 767.55 700.2 766.63 699.29 766.15 696.73 765.93 690.69 765.47 690 765.47 687.68 765.93 684.43 766.4 681.86 767.09 679.09 767.55 676.53 765.22 673.51 762.91 670.5 759.89 668.18 757.81 667.01 756.41 664.92 754.79 663.53 754.09 663.07 753.63 661.44 748.98 660.75 745.04 660.75 742.02 660.75 737.61 660.75 733.44 660.75 729.02 660.75 724.84 660.75 720.44 660.75 716.25 660.75 712.07 660.75 707.67 660.75 705.81 663.77 705.81 667.25 705.81 670.74 705.81 674.68 705.81 678.39 705.81 682.34 705.81 685.12 705.81 685.81 705.59 686.52 705.59 686.75 705.11 685.59 703.03 685.59 702.33 685.81 700.94 686.28 699.54 686.98 698.16 686.98 696.99 686.52 693.98 686.52 692.35 686.75 690.49 686.98 688.87 686.75 687.71 686.98 687.01 687.21 686.08 687.44 685.15 687.68 684.69 687.68 684.22 687.44 683.53 687.21 681.67 686.98 679.36 686.75 677.5 687.21 677.72 688.14 677.97 688.61 678.66 688.83 679.58 689.53 679.58 691.16 680.28 691.86 680.98 692.32 682.6 692.09 683.53 691.38 684.22 692.09 684.69 693.24 685.15 693.95 685.15 695.79 683.99 696.48 683.76 697.65 683.53 698.57 683.3 701.12 682.6 702.52 682.37 703.23 682.14 703.92 682.14 704.15 682.37 703.92 683.53 703.68 684.69 704.39 686.55 704.62 687.48 705.55 688.18 706.01 688.4 706.71 689.1 708.09 689.1 711.34 690.03 712.28 690.49 713.43 690.96 714.36 691.19 717.61 691.65 718.77 691.9 720.86 692.12 722.72 692.12 723.88 692.12 724.81 691.65 725.27 691.42 725.5 691.19 725.97 690.26 726.43 687.94 726.65 687.48 727.36 687.01 728.29 687.01 728.75 687.24 729.22 689.56 731.77 691.65 732.47 693.51 733.16 695.14 733.63 695.37 734.54 696.07 735.95 696.29 737.34 696.29 740.12 697.23 742.22 698.16 743.84 698.85 744.53 699.32 745 700 745.47 700.7 745.93 702.33 746.4 703.5 747.09 703.73 748.02 703.96 748.71 704.65 749.18 705.33 750.33 707.21 751.26 708.6 751.5 709.75 752.42 710.68 753.6 710.92 754.76 710.92 755.44 710.68 757.07 710 758.45 709.3 759.38 709.06 760.08 709.06 760.32 709.75 760.54 710.68 760.78 711.16 761.7 711.62 762.4 711.62 762.63 710.92 762.63 710.68 762.63 707.91 762.63 705.59 762.63 703.5 762.63 700.7 762.63 698.39 762.63 696.53 762.63 694.44 761.94 694.67 761.24 695.14 759.38 695.14 758.93 695.59 758.7 696.07 758.7 696.76 758.7 697.69 758.45 697.92 757.77 698.16 756.6 697.92 754.76 697.45 753.12 696.99 751.96 695.83 750.33 693.98 749.18 693.05 746.61 691.19 746.15 690.96 745.47 690.03 744.77 688.4 744.53 687.48 744.08 686.78 743.84 685.62 744.53 683.99 745.24 680.74 745.93 678.42 746.15 676.56 747.55 674.95 747.55 673.31 747.09 671.22 747.31 670.3 747.31 667.51 747.31 664.96 747.31 663.79 747.09 661.95 746.15 659.62 744.3 656.61 744.3 655.91 745.47 655.21 747.31 653.83 748.02 653.11 748.94 652.2 749.41 651.5 750.57 650.11 751.02 648.95 751.26 647.56 750.79 646.17 751.72 645.93 754.98 645.45 758.45 645.01 762.17 644.31 765.89 643.85 769.6 643.15 772.85 642.69 774.94 642.45 775.41 643.38 776.1 645.01 776.79 646.17 777.72 647.09 778.65 647.79 779.35 647.79 782.83 647.79 783.99 648.48 785.15 649.19 785.38 650.34 786.31 651.27 786.99 651.74 787.47 651.97 787.94 651.74 788.87 651.74 789.79 651.97 790.26 652.2 790.26 653.11 790.48 653.6 791.64 653.83 793.04 653.83 794.2 654.52 795.36 654.75 796.98 654.99 797.67 655.69 799.31 656.61 801.15 657.3 802.56 657.99 803.24 658.22"/>
                                </g>
                                <g id="Angola">
                                    <path class="cls-2 activeCountry" d="m559.34,612.04l2.77.23h3.25l1.16-.23h8.82l3.95-.22h4.41l3.25.22,1.62.23,1.38.93.46.7.47.47.22.92.94.48.22,1.15-.22,1.38v1.63l.46,1.86.91,2.1,1.4,2.08.7,1.63-.22,1.15.46,1.39.92,1.39.71.7.45.46,1.16,2.32,2.34,3.25,1.61,2.55.46.24h.94l1.62-.24h1.86l1.16.47h.47l2.08-1.16,1.86-.23,1.86-.46.93-.46h1.16l3.25.93h3.25l2.54-.46.47-3.48v-.7l.46-1.16.93-1.16v-2.55l.7-1.63,1.62-1.39,2.79-.7,1.62-.23,2.55-.46,3.72-.23h1.39l.23.23-.93,2.32v.95l.23.68.7.46h7.65l4.18.23,3.01.23h.46l.25.23.45,1.16v2.32l-.7,3.48.25,3.25,1.16,3.02.21,4.64-.46,2.79-.47,3.47-.22,3.95.46,1.63,1.15,1.61,1.86,1.87,1.39,2.31,1.16,3.02.23,1.63-.23.93v1.16l.23,1.86-.23,1.16-.92.7-.47.94.47,1.39.24,1.62.45.46.23.47h.46l.93-.47,1.4-.93.92-.46h1.39l1.86.23,3.48.23.93-.23,3.25-1.39h2.09l1.62.47h1.86l.92-.23v-.7l.24-.46.7-.46.23,1.86.23,2.31.23,1.86.23.7v.46l-.23.46-.23.93-.23.93-.23.7.23,1.16-.23,1.63-.23,1.86v1.62l.46,3.02v1.16l-.71,1.38-.46,1.4-.22,1.39v.7l1.16,2.08-.23.47h-.71l-.69.22h-24.38v39.23l.7,3.95,1.62,4.64.46.46,1.39.7,2.09,1.63,1.17,1.39,2.31,2.08,3.02,3.03,3.02,2.3,2.55,2.33-4.18.7-5.8,1.16-3.95.7-4.64.92-3.25.7-3.71.7h-.7l-1.15-.45-2.34-.25-2.54.7-2.1.24-1.62-.24-1.63-.7-1.38-.92-2.55-.23-3.71.23-3.48-.23-3.48-.46-2.32-.22h-1.62l-1.62-.25-1.63-.45-1.38-.71-1.63-1.84-1.39-1.63-.23-.22-.46-.47h-34.81l-4.88-.24h-12.54l-2.55.24-2.79.23-.21-.23-.71-.24-.46-.22-1.39-.92-1.39-.93-1.61-1.16-1.17-1.39-.94-.48-1.62-.21-1.38-.25h-.94l-1.86.71-1.39.69-.92.47-1.62.7-1.39.69h-4.41l-1.15-.46h-1.4l-1.39.7-2.09.23.46-4.88.46-2.31v-2.79l-.23-6.73-.46-1.16-.23-.93,1.39-.93.46-.69.93-1.17.7-1.62.69-3.48,2.8-8.13,1.16-7.88,1.63-3.71.7-4.41,4.41-5.33,1.16-3.25,2.31-1.63,3.48-1.86,2.33-3.03,1.14-2.08,1.17-4.18v-4.41l.94-5.57-.24-1.62-1.17-2.32-.22-1.63-1.15-1.62-1.4-1.16-.46-2.09-2.09-3.48-.7-2.32-.92-1.63-.24-1.85-.46-2.33-1.17-2.08-.92-2.31v-.7l.7-.93.46-.23v.46l-.46.45v.46l3.94-4.16.23-.93v-3.25l-3.71-7.9-3.02-7.19-.46-3.48-3.95-4.88-1.39-3.02-.93-2.09-.7-.93.23-.46h.93l2.32-.46,3.02-.7,3.02-1.16.68-.7h1.63l1.4.22.68-.22h9.99l.94.22Zm-25.31-1.62h-.46l-.23-.22-.46-1.41.23-1.16.46-.92-.46-1.86-.93-1.63-.93-2.08-.23-.47.69-.7,1.17-1.38.7-.93h1.16l.7-.7.23-.7.23-.71,1.39-.22,1.84-.92.95-.7,1.15-.46h.7l.47.23,1.16,1.39.92.68.22.25-.22.22-1.38.7-1.39.46-2.11,2.1-.91,1.15-.46.22-.93.47-.7.46.22.24.24.23.46.46v3.71l-.23,3.48-.23.23-1.16.24-1.86.22Z"/>
                                </g>
                                <g id="Mauritius">
                                    <polygon class="cls-2 activeCountry" points="1128.5,813.3 1122.9,814.9 1116.5,814.9 1114,811.7 1113.2,810.9 1115.6,810.1 1115.6,806.9 1116.5,801.2 1118.1,799.6 1120.5,798 1122,793.1 1124.5,790.7 1128.5,790.7 1132.5,795.6 1135,801.2 1134.2,806.1 1131.8,808.5 1130.9,811.7 	"/>
                                </g>
                                <g id="Madagascar">
                                    <path class="cls-2" d="m1024.89,757.57l-1.39,2.54.22-2.08,1.87-3.02.46-.23-1.16,2.79Zm-20.89-47.11v.46l-1.62-.23-.22-1.62h.91v-.69h.71l.46,1.37-.23.72Zm15.56-12.3l.7,1.38.7,1.4,2.09,3.25.92,1.14.94,1.41.46,2.55,1.39,4.17,1.16,6.04.46,6.26.46,3.02.94,2.55,1.63,2.79.46,3.24-.93,3.25-1.4,3.02-.46.7-.71.7h-.22l-1.17-.7-.92-1.38-1.39-3.03-.46-1.62-.47-.23-1.38.23-.93.93-.23.46.23,1.86.23,1.62.23,1.39v2.08l.46.47.46.7.7,1.16v3.02l-.24,1.63-1.15,1.39.23.69.23.71-.23.46-1.39.7-.46.46-.7,1.39-1.16,2.79-.23,1.39.7,4.39-.23,3.02-1.39,5.8-.93,2.79-1.16,3.48-1.86,4.41-1.86,5.57-1.62,5.57-1.17,3.47-1.39,3.49-1.85,6.03-1.4,6.05-2.31,6.72-3.25,7.65-.24.94-.68,3.71-.71,3.48-.7,3.25-1.84,5.57-.24,1.62-.46,1.63-1.63,3.47-.7,1.4-.46,1.39-.24,1.62-.68,1.63-1.16,3.25-1.86,2.54-1.16.95-2.8,1.38-1.38.24-3.02.22-3.02.7-3.02,1.63-3.03,1.62-1.15.94-1.39.46h-3.95l-1.16-.24-3.95-3.01-1.39-.46-3-.47-.7-.22-.94-.23-1.17-1.62-2.32-1.16-.46-.46-.46-.93-.23-.94-.46-1.16-.47-1.85-.93-1.39-2.07-2.55-.24-.7-.23-2.79.23-1.86-.23-3.25.23-1.39.7-1.39-.23-1.63-.94-1.39-.22-1.62-.47-1.62-2.3-2.55-.47-1.4-.46-1.39-.92-4.18v-1.38l.22-3.27.24-1.62.46-.92.24-.94.22-.69.7-.47.24-.69.91-3.96.93-.92,1.63-.46,1.4-1.17.69-1.38.69-2.79,1.87-3.02.7-1.39,1.61-2.32,1.4-3.02.46-1.62.22-1.39.46-3.48.25-1.63v-1.62l-.94-1.63-1.87-3.25v-.46l.23-2.31-.23-1.63-.7-1.62-.91-1.63-.94-2.79-.46-4.86v-1.63l-.23-1.63-.46-1.39.46-2.55,5.58-9.28.21-1.16-.21-2.8.21-1.62.25-.7.23-.23,1.14-.23,4.64-.46.46-.23,1.16-.7,1.62-1.62.7-.23h.7l.46.7.46.23,1.86-.46.7-.23.7.23.46-.7.23-.7.23-.7.46-.23,2.56-.22,1.39-.24,2.09-.7.46.22,1.62,2.1.46.23h.46l.7-.46-1.41-.93-.21-.7.21-.69.72-1.63,1.16-1.16,2.55-1.63,2.55-2.09.93-.23.69.46.47,2.32v.46h.22l.7-.23.46-.93v-.93l-.46-.7-.23-.7v-.46l1.39-1.63,1.15-1.16.46-1.63.48-.92,1.15-.7h.24l.22.7.23.7-.23.92-.46.7-.23.94h.7l.7-.24.7-1.62,1.15-1.62.48-.7.69-.7,1.39.23,1.15.24-1.85-1.63-.7-2.31,2.54-4.18v-.7l.24-.22.22-.47-1.16-1.15-.21-.71v-1.15l.7-.92.46-.47.69-.24.7.24,1.41,1.16.91.23.93-1.17.93-1.38,1.4-.92,1.38-.47,2.33-2.08,1.38-4.41.23-1.39-.46-1.39-.46-1.62-.94-1.86.24-.23,1.38.23.23-.23,1.39-1.63,2.32-3.25h.7l.7.7.23.7.46.69,1.39,1.4.7,1.16Z"/>
                                </g>
                                <g id="Mozambique">
                                    <path class="cls-2 activeCountry" d="m802.78,893.58h-1.38l-2.57.23h-3l-1.86-.47-1.16.24-.25-4.63-.22-.94-.46-1.4v-.93l.23-.92v-2.78l-1.17-.71h-.46l-.22-1.14v-1.4l.93-2.1-.24-3.7.24-1.4v-2.55l-.24-3.24v-2.79l.24-2.56-.46-1.15v-.7l-.7-1.16-.71-2.79-.7-2.09-.92-1.39-.23-.7-.48-.93-.91-1.62-.7-.93-.22-.7v-2.09l-.94-3.71-.47-2.55-.91-2.78-.7-1.86v-.47l-.23-.7,1.84-1.39,1.88-2.08,2.32-2.31,1.85-2.1,1.63-1.62,2.31-2.55,2.31-2.55.71-.22v-.23l-.93-2.1,1.62-2.55v-3.25l.22-.7.47-.7,1.86-1.39,1.4-2.09,1.15-1.85,1.62-3.26.22-.7v-.7l-.45-1.15-1.18-1.63-.7-1.63-.68-2.31.68-1.87.24-1.16v-.7l-.24-.69-.93-.47-.46-.23-.24-.91v-.94l.24-.47,1.62-.92.46-.46.24-.47v-.7l.46-2.08.71-1.63v-.7l-.22-.46v-1.15l-.24-1.4v-4.39l.24-4.41v-2.32l-1.18-3.02v-2.1l.7-1.38.24-.93h-1.86l-.94-.46-1.38-1.16-2.32-.93-2.8-.93-3.94-.23-3.24-2.79-2.56-.46-.94-.46-2.3-1.62-3.95-.23-3.95-.23h-2.55l-.47-.23-.23-2.31v-2.1l-.23-1.86-.23-2.09-.7-.93-.7-1.39-.23-1.39v-1.16l2.79-.92,1.15-.71,1.86-.46,3.02-.92,3.02-.94,2.55-.7,2.55-.69,1.16-.7,1.39-.47,3.24-1.14.94-.24,2.09-.7.92-.24,3.49-1.16,4.17-1.39,1.62-.68,2.79-.94.46.46,1.85,3.25,1.62,1.86,1.63,1.85h.22l.7-.46.7-.24,2.79-.23.93-.23.7-.46,1.39-.22,1.63-.22.7.22,1.63,2.31.21,1.62.47,2.56v3.02l-.22,2.08-1.39,2.32-.23,1.16-.7,1.86-1.16.93-.46.93.23.7.46.7,1.16,1.16.23.69v1.63l.23.7.23.23,1.14.7,1.18,1.39,1.86,1.86,2.32,2.54,1.16.94h.69l.47.93-.23.91-.7.71.23.92.46.24.46.22.93.24.92-.24.23-.22-.23-3.95-.46-2.32-.69-.93h-.23l.23-.93.69-1.62.7-1.62.46-.93.47-.23,3-.46,1.41-.46.69-.46.47-1.39.46-3.71v-3.48l-.23-2.09.47-3.02.7-1.86-.47-.46-.22-2.54-2.11-2.78-2.54-3.49-1.4-1.86-1.62-2.1-3.25-3.48-1.39-1.14-.7-.47-2.32-.47-.7-.45-.7-1.16-.22-1.86v-1.39l-.25-2.56-.45-3.25-.23-1.15-.7-2.55-.7-2.32v-.7l.23-.7,1.16-1.62.68-1.39.47-.7.7-1.86v-.93l.7-.46,2.09-.23,1.62.23,3.02-.23,3.01.23h.47l.7.23h.7l.92-.23.94-.69,1.16-1.17,1.62.23,2.1.94,1.38.92.25.94,1.38.46h2.8l2.08-.23,1.16-1.16,1.38-.46h1.4l.92.23.7.7,1.39.7,2.08.23,2.09-.46,2.55-1.17,1.16-1.39.46-1.39.46-.7.46-.46h3.25l1.86.22,2.32,1.39,1.39-.93,2.55-1.39,2.32-.93h2.55l1.84-.46,1.63-1.16,1.63-.93,1.62-.22,1.62-.47,2.3-1.16,2.33-1.86,2.33-1.86,1.38-1.16.7,1.4,1.38,1.16-.92.92-.71.46,1.4.93-1.16,1.39v.94l.22.22.24.46-.71,1.62-.92,1.16-.24.93.7,1.39-.24,2.79.7,2.55.24,1.16.22.93-.46,1.39.24,2.55.22,1.16-.46,1.39.71.46.46,1.4-.22,1.62-.23.93-1.4.92v1.17h1.63v3.02l-.23.92.46,1.16-.46,1.17.23.92v1.16l.22,3.01.24,3.71v.46l.7.47.69.24v.92l-.91,1.39v.69l.22,1.17.92-1.39h.7l.46.47v.92l.23.46-.23.93.47,1.16-.24.95-.7.68-.93,1.16-.23,1.16v.7l-.68.24-.25.47.47.91v.93l-1.16,2.79-3.25,3.95-1.39,1.39-1.4,1.39v1.16l-1.62,2.09-1.62.46-.71.46.71,1.86-1.18.46-1.84,1.63-4.88,2.78-.92.7-1.16,1.61-1.62.46-.93.48-1.63.23h-1.15l-.46.46-3.48,1.16-3.02.94-.69.46-.47.7-2.79.92-4.18,2.33-3.48,2.31-2.55,2.32-.7.24-.7.68-.46,1.17-.23.69-1.86,2.55-2.79,2.8-.45.69-1.16,1.62-.24.94-.94.46-.7-.92-.46,1.86h-.7l-.68-.23-1.86.92-1.63.93-2.78,2.32-3.72,4.64-5.34,4.18-.7.23h-.7l-1.62-1.63h-.94l.94.93.46.7-.23,1.39.23,2.09-.7,4.41v.93l.93,1.16,1.39,1.39,1.39,1.86,1.62,5.33.23,2.8,1.85,3.48v1.38l.7,3.95v3.04l-.22,1.84.92.7.46-.7-.22-1.14.22-1.86.47-.94.46.22.23.94.23.7.23.69v1.16l-.7,3.72.23,1.62.92,2.57-1.15,3.01-1.4,7.19-.22,1.39.46.46.93.24.23-.94h.46l.23.47-.7,3.47-.7,1.38-2.32,3.49-1.39,1.62-2.09,1.39-4.88,2.55-10.22,3.25-3.95,1.62-2.31,1.16-5.11,3.25-2.1,2.08-.92,2.33-.93,1.16-.94,1.39.94,1.39.69.94.95.46.21.68.7.23.7-1.85.22-.7h.47l-.24,2.31-.7,8.13v.24Zm23.91-199.6l.23-.23.23-.46v-.46l-.23-.23h-.7l-.23.46v.7l.23.23h.46Zm-.94-1.39l-.22-.23-.47-.23-.46.23-.22.46.46.46h.46l.46-.7Z"/>
                                </g>
                                <g id="Eswatini">
                                    <polygon class="cls-2 activeCountry" points="790.48 881.27 790.95 881.27 792.12 881.98 792.12 883.37 792.12 884.76 791.89 885.68 791.89 886.61 792.34 888.01 792.56 888.95 792.81 893.58 792.34 893.34 791.64 893.12 791.19 893.12 790.95 895.2 790.48 898.21 790.73 900.07 787.94 900.07 784.46 899.84 781.9 899.14 779.11 897.29 777.49 894.5 776.79 892.89 775.63 892.64 775.63 892.42 775.41 890.32 775.41 888.01 775.63 887.54 777.49 884.76 778.65 883.13 779.35 881.5 780.96 879.66 782.6 878.25 783.3 878.25 783.74 878.25 786.54 879.88 789.56 881.5 790.26 881.27 790.48 881.27"/>
                                </g>
                                <g id="Zimbabwe">
                                    <polygon class="cls-2 activeCountry" points="781.9 832.06 780.74 831.38 779.11 830.67 777.26 830.46 774.47 830.67 771.22 831.13 767.74 830.67 764.02 829.06 760.78 828.59 757.07 829.29 756.36 828.82 755.2 827.65 753.6 827.43 753.12 827.19 752.66 826.72 752.42 826.28 752.42 825.81 752.66 823.94 752.42 823.71 752.18 823.71 751.26 823.49 748.94 822.55 746.15 821.87 741.5 820.94 739.67 820.71 739.43 820.46 738.73 819.77 738.04 817.91 737.11 816.53 735.25 814.42 734.79 813.74 735.01 812.12 735.01 810.94 735.25 809.79 735.25 808.62 735.25 807.47 735.25 806.54 735.01 806.31 734.32 805.85 732.24 805.85 729.91 805.85 729.68 804.45 729.45 802.6 728.99 801.44 728.52 800.74 727.36 800.04 725.03 799.35 722.02 797.95 719.23 795.86 716.22 793.55 715.29 793.08 714.14 790.76 712.5 787.04 712.5 785.65 712.28 784.97 710.64 783.09 710.41 782.17 709.95 781 707.39 778.23 706.46 777.07 705.77 775.43 705.09 774.29 704.62 773.82 703.68 772.88 703.23 771.97 702.98 771.26 703.23 770.34 703.46 769.63 706.01 770.34 707.39 770.34 708.33 770.1 709.72 770.57 711.34 771.72 712.97 771.97 715.06 771.26 717.39 771.5 720.63 772.65 723.18 772.88 726.43 771.72 729.22 768.71 731.77 765.93 734.54 762.45 735.95 759.66 738.27 757.57 741.29 755.95 744.3 754.55 749.18 752.7 750.1 751.3 750.33 749.68 750.33 747.59 750.57 745.96 751.02 745.5 751.96 744.8 752.88 744.34 755.92 742.71 758.45 741.55 761.7 740.86 765.19 740.86 768.44 740.86 770.29 740.86 770.29 742.96 770.53 745.27 771 745.5 773.54 745.5 777.49 745.73 781.44 745.96 783.74 747.59 784.68 748.05 787.24 748.52 790.48 751.3 794.42 751.54 797.23 752.47 799.55 753.39 800.92 754.55 801.86 755.02 803.02 755.02 803.72 755.02 803.48 755.95 802.78 757.33 802.78 759.43 803.96 762.45 803.96 764.77 803.72 769.18 803.72 773.57 803.96 774.98 803.96 776.13 804.18 776.59 804.18 777.29 803.48 778.92 803.02 781 803.02 781.7 802.78 782.17 802.32 782.64 800.7 783.56 800.46 784.03 800.46 784.97 800.7 785.88 801.15 786.11 802.08 786.59 802.32 787.27 802.32 787.97 802.08 789.13 801.4 791 802.08 793.31 802.78 794.93 803.96 796.57 804.41 797.72 804.41 798.42 804.18 799.11 802.56 802.37 801.4 804.22 800 806.31 798.15 807.7 797.67 808.4 797.45 809.1 797.45 810.72 797.45 812.35 795.83 814.89 796.76 816.99 796.76 817.22 796.05 817.45 793.73 820 791.42 822.55 789.79 824.18 787.94 826.28 785.62 828.59 783.74 830.67 781.9 832.06"/>
                                </g>
                                <g id="Botswana">
                                    <polygon class="cls-2" points="703.46 769.63 703.23 770.34 702.98 771.26 703.23 771.97 703.68 772.88 704.62 773.82 705.09 774.29 705.77 775.43 706.46 777.07 707.39 778.23 709.95 781 710.41 782.17 710.64 783.09 712.28 784.97 712.5 785.65 712.5 787.04 714.14 790.76 715.29 793.08 716.22 793.55 719.23 795.86 722.02 797.95 725.03 799.35 727.36 800.04 728.52 800.74 728.99 801.44 729.45 802.6 729.68 804.45 729.91 805.85 732.24 805.85 734.32 805.85 735.01 806.31 735.25 806.54 735.25 807.47 735.25 808.62 735.25 809.79 735.01 810.94 735.01 812.12 734.79 813.74 735.25 814.42 737.11 816.53 738.04 817.91 738.73 819.77 739.43 820.46 739.67 820.71 741.5 820.94 746.15 821.87 748.94 822.55 751.26 823.49 752.18 823.71 752.42 823.71 752.66 823.94 752.42 825.81 752.42 826.28 752.66 826.72 753.12 827.19 753.6 827.43 755.2 827.65 756.36 828.82 757.07 829.29 753.82 829.52 752.42 830.46 751.5 832.06 750.1 833.23 748.25 833.93 746.15 834.39 744.08 834.64 741.98 836.01 739.43 838.56 738.27 840.19 738.27 840.65 737.81 841.36 736.65 841.81 736.17 842.28 735.95 842.98 735.48 843.21 734.54 843.21 733.85 843.67 733.4 844.83 732.7 845.3 731.31 845.53 730.15 845.99 729.22 846.92 728.52 847.39 728.06 847.39 727.36 848.32 725.97 849.94 725.73 850.88 724.11 857.37 722.95 858.07 721.1 859.46 719.7 861.08 719 862.01 718.32 862.48 714.82 863.41 713.43 863.64 712.04 864.34 711.57 865.03 711.12 866.89 710.18 869.9 709.25 872 708.8 873.85 707.63 876.18 706.93 877.09 705.77 877.79 704.62 878.02 702.98 878.25 701.36 878.25 700.2 878.25 698.35 879.19 696.73 879.19 694.41 878.72 692.32 878.25 691.38 878.25 689.53 876.62 688.36 876.62 686.75 876.62 685.81 876.18 684.89 875.47 682.8 873.85 680.94 872.69 679.09 872 677.47 871.52 676.07 871.76 674.91 872.22 674.44 872.46 673.51 872.93 672.59 874.31 671.66 876.18 671.43 877.34 670.5 879.88 669.34 882.66 668.87 883.6 668.18 884.29 667.25 884.76 663.77 887.07 662.14 889.87 661.22 890.55 659.82 891.02 658.89 891.26 658.19 891.71 657.73 893.12 657.03 893.58 656.35 893.58 654.49 893.58 654.03 893.34 648.92 893.58 647.51 893.12 646.37 893.12 644.73 893.58 643.8 893.34 643.34 892.18 643.12 889.87 643.12 888.01 644.04 886.61 644.73 885.45 645.67 884.07 645.67 883.37 645.67 882.9 645.43 881.74 645.43 880.59 644.27 878.02 642.89 874.77 641.26 870.84 640.56 869.9 639.4 868.28 635.45 865.26 634.75 864.79 634.75 864.34 634.75 861.55 634.75 857.37 634.75 853.42 634.75 849.48 634.75 845.53 634.75 841.58 634.75 837.65 634.75 833.93 634.75 829.98 634.75 826.49 637.78 826.49 641.26 826.49 645.67 826.49 647.76 826.49 647.76 826.05 647.76 823.71 647.76 818.15 647.76 812.58 647.76 807.01 647.76 801.44 647.76 795.86 647.76 790.29 647.76 784.97 647.76 779.39 647.76 776.59 651 776.59 654.95 775.91 661.22 774.98 667.01 774.04 670.74 773.35 675.37 772.41 677 772.41 677.23 772.41 677.93 772.65 680.01 775.43 681.41 777.52 681.64 778.45 681.86 778.7 682.57 778.45 683.27 777.99 685.36 775.91 685.81 775.43 687.21 774.51 688.83 773.35 690.22 772.65 691.86 772.18 692.55 772.18 693.24 772.65 693.95 773.12 697.41 770.57 698.82 769.88 702.98 769.63 703.46 769.63"/>
                                </g>
                                <g id="Namibia">
                                    <polygon class="cls-2 activeCountry" points="679.09 767.55 681.86 767.09 684.43 766.4 687.68 765.93 690 765.47 690.69 765.47 696.73 765.93 699.29 766.15 700.2 766.63 701.36 767.55 703.46 769.63 702.98 769.63 698.82 769.88 697.41 770.57 693.95 773.12 693.24 772.65 692.55 772.18 691.86 772.18 690.22 772.65 688.83 773.35 687.21 774.51 685.81 775.43 685.36 775.91 683.27 777.99 682.57 778.45 681.86 778.7 681.64 778.45 681.41 777.52 680.01 775.43 677.93 772.65 677.23 772.41 677 772.41 675.37 772.41 670.74 773.35 667.01 774.04 661.22 774.98 654.95 775.91 651 776.59 647.76 776.59 647.76 779.39 647.76 784.97 647.76 790.29 647.76 795.86 647.76 801.44 647.76 807.01 647.76 812.58 647.76 818.15 647.76 823.71 647.76 826.05 647.76 826.49 645.67 826.49 641.26 826.49 637.78 826.49 634.75 826.49 634.75 829.98 634.75 833.93 634.75 837.65 634.75 841.58 634.75 845.53 634.75 849.48 634.75 853.42 634.75 857.37 634.75 861.55 634.75 864.34 634.75 864.79 634.75 870.6 634.75 876.85 634.75 882.9 634.75 889.17 634.75 895.43 634.75 901.7 634.75 907.97 634.75 914.23 634.75 916.09 633.37 916.09 630.8 916.79 628.96 917.95 628.26 919.1 627.33 919.81 625.95 920.04 625.47 920.73 625.71 921.66 625.24 922.59 624.08 923.06 622.22 922.82 619.9 922.13 616.65 921.9 612.94 922.36 610.38 922.13 608.53 921.2 606.9 920.73 605.05 920.73 603.89 920.26 601.8 919.56 601.35 918.64 601.1 917.72 600.42 916.79 600.42 916.09 600.88 915.63 600.88 914.69 600.64 913.54 599.94 913.07 599.01 913.07 598.55 912.61 598.31 911.68 597.85 910.98 596.69 910.29 595.07 910.74 594.38 911.68 593.9 912.84 593.43 913.54 593.22 914.46 593.22 915.39 592.76 916.09 592.28 916.56 591.83 916.31 591.12 916.56 589.26 917.95 588.81 918.41 587.18 917.25 583 913.07 581.38 911.91 579.29 909.36 574.41 901.24 573.72 899.61 572.79 895.67 571.64 892.64 571.4 891.02 572.09 890.09 571.64 888.95 571.16 887.77 569.54 886.15 569.08 881.27 567.92 878.02 568.15 875.25 567.45 872.93 567.45 871.3 567.68 868.28 566.75 865.03 565.13 861.55 563.28 856.91 563.04 854.82 563.28 849.01 563.04 846.92 563.04 844.14 562.34 841.36 562.11 839.74 562.57 838.56 562.81 839.03 563.28 839.26 563.73 837.65 563.73 836.24 562.81 832.76 560.95 829.29 556.55 823.49 555.39 821.16 554.69 819.3 549.82 811.88 547.49 806.54 546.11 801.9 544.47 799.81 536.81 784.97 535.19 782.4 531.94 779.61 531.25 778.7 530.09 776.13 528 772.41 527.29 768.94 527.07 765.22 527.53 762.22 529.62 761.98 531.02 761.28 532.42 761.28 533.57 761.74 534.95 761.74 535.43 761.74 537.98 761.74 539.37 761.06 541 760.36 541.92 759.89 543.31 759.2 545.17 758.48 546.11 758.48 547.49 758.73 549.11 758.95 550.05 759.43 551.21 760.82 552.82 761.98 554.21 762.91 555.61 763.83 556.07 764.06 556.79 764.3 557 764.53 559.79 764.3 562.34 764.06 564.9 764.06 569.77 764.06 574.88 764.06 579.75 764.3 584.86 764.3 589.74 764.3 594.84 764.3 599.72 764.3 604.83 764.3 606.9 764.3 610.38 764.3 614.1 764.3 614.56 764.3 615.03 764.77 615.26 764.99 616.65 766.63 618.29 768.47 619.67 769.18 621.29 769.63 622.92 769.88 624.54 769.88 626.87 770.1 630.35 770.57 633.83 770.8 637.55 770.57 640.1 770.8 641.48 771.72 643.12 772.41 644.73 772.65 646.83 772.41 649.37 771.72 651.71 771.97 652.86 772.41 653.56 772.41 657.26 771.72 660.51 771.02 665.16 770.1 669.1 769.41 674.91 768.24 679.09 767.55"/>
                                </g>
                                <g id="Lesotho">
                                    <polygon class="cls-2 activeCountry" points="748.71 939.54 747.55 940 746.61 939.75 745.69 940 745 940.22 744.3 940.22 743.36 941.16 741.5 943.95 741.06 944.63 740.83 945.57 740.36 946.49 739.89 947.19 739.43 947.19 737.81 946.95 735.95 946.73 734.79 945.79 733.85 944.86 733.4 943.95 732.7 943.47 732.47 943.26 731.77 943.01 731.54 942.78 731.31 942.54 730.84 942.08 730.6 941.62 730.84 940.46 730.15 939.54 729.22 938.37 728.75 937.21 727.82 935.81 727.36 934.66 726.9 933.27 726.9 932.8 727.36 932.33 728.75 931.86 729.91 931.16 730.84 930.24 731.77 929.08 732.24 928.16 732.47 927.92 732.93 927.24 733.85 926.07 734.79 924.67 735.72 923.06 736.88 922.59 738.73 922.13 740.36 920.73 742.22 919.56 745.24 918.41 746.86 918.18 747.31 917.95 747.77 918.18 748.02 918.88 748.47 919.56 749.88 920.5 750.33 920.73 751.5 922.13 752.88 923.29 754.52 924.45 755.67 924.91 756.13 925.14 756.6 926.31 757.07 927 757.29 927.69 757.29 928.39 756.83 930.24 756.13 931.86 755.44 932.8 754.76 933.27 754.04 933.96 753.82 935.34 753.6 936.97 752.66 937.68 751.96 938.13 750.79 938.6 748.71 939.54"/>
                                </g>
                                <g id="South_Africa" data-name="South Africa">
                                    <path class="cls-2 activeCountry" d="m867.54,1196l-.46.23-2.79-.23-.23-.7.7-.93.46-.47,1.39.24,1.16.92.23.24-.46.7Zm-99.8-365.33l3.48.46,3.25-.46,2.79-.21,1.85.21,1.63.7,1.16.69.23.7v.47l.7,1.86.91,2.78.47,2.55.94,3.71v2.09l.22.7.7.93.91,1.62.48.93.23.7.92,1.39.7,2.09.71,2.79.7,1.16v.7l.46,1.15-.24,2.56v2.79l.24,3.24v2.55l-.24,1.4.24,3.7-.93,2.1v1.4l.22,1.14h-.22l-.7.23-3.03-1.62-2.8-1.63h-1.14l-1.63,1.41-1.62,1.84-.7,1.63-1.16,1.62-1.86,2.79-.22.46v2.31l.22,2.1v.22l1.16.25.7,1.61,1.62,2.79,2.79,1.86,2.55.7,3.48.23h2.79l-.24-1.86.46-3.02.24-2.08h.45l.7.22.47.24,1.16-.24,1.86.47h3l2.57-.23h1.38l-.46,3.25-1.87,5.11-.69,2.31-1.62,8.36-2.1,4.18-1.15,1.86-3.26,3.02-.91.7-.94.23-1.38.46-5.8,6.27-2.09,3.01-1.86,4.41-2.08,2.32-2.8,5.35-2.33,3.95-2.55,3.7-3.95,4.88-1.86,1.63-1.39.7-3.25,2.79-4.64,4.88-3.49,4.18-5.33,4.64-3.02,2.32-4.63,3.95-1.17.69-5.33,3.94-3.71,2.33-6.03,2.55-2.33.92-5.8-.7-2.33.24-2.09,1.63-.22,2.54-.7.23h-1.39l-3.94-1.15-2.1.22-1.38,1.39-.94,1.4-3.01.23-5.35-1.63-6.26-1.14h-1.62l-3.02,1.14-1.15.24-4.42-.24-2.31-.92h-2.55l-1.86.7-2.09.22-6.05,4.41h-3l-2.57.7h-1.39l-2.3-.7h-.94l-1.4.24-1.62.92-3,.24-1.39.7-5.34,4.17-1.16-.22-.93-.23-2.79-.24-3.25-2.09-1.15.23.22-.7v-1.14l-.46-.94-.46-.24h-1.39l-.7-.92h-1.86l-.7.22h-.93v-2.55l-.23-1.14-.69-.47-.46-.24-1.4.24h-.93l-.45.47-.48.69v2.79l-.7-.94-.68-1.39-.47-1.62.47-2.1,1.38-.68-.23-1.41-.23-1.16-1.62-3-.7-1.41-1.16-.91-1.15-2.09-1.16-.7-.46-1.63-.94-1.16-.46-2.1.71-1.16.92-.46.94.93,1.15-.47,1.62-1.39.94-2.08v-3.49l-.24-2.08-1.16-5.34-.7-1.39-3.01-3.94-3.24-5.1-4.41-8.36-2.11-4.88-3-9.99-2.79-5.57-3.26-5.1-.46-.46.46-.46,1.86-1.39.7-.24.46.24.47-.46.46-.7v-.94l.21-.92.46-.7.48-1.16.69-.94,1.62-.46,1.15.7.46.7.24.93.46.46h.94l.7.46.24,1.15v.94l-.46.46v.7l.69.93.24.93.46.92,2.09.7,1.16.46h1.85l1.63.47,1.86.93,2.55.23,3.72-.46,3.25.23,2.32.7,1.86.23,1.16-.46.46-.93-.23-.94.47-.69,1.38-.23.93-.7.7-1.15,1.85-1.16,2.56-.7h1.38v-51.3l.7.46,3.96,3.03,1.16,1.62.7.94,1.62,3.94,1.38,3.25,1.16,2.56v1.15l.24,1.16v1.17l-.94,1.38-.7,1.16-.92,1.4v1.86l.22,2.31.46,1.16.94.24,1.63-.46h1.14l1.41.46,5.11-.24.46.24h2.54l.7-.46.46-1.4.7-.46.93-.24,1.4-.46.92-.69,1.63-2.8,3.47-2.31.94-.46.69-.7.46-.94,1.16-2.78.93-2.54.23-1.16.93-1.87.93-1.38.93-.46.46-.24,1.16-.46,1.4-.24,1.62.47,1.86.7,1.86,1.16,2.09,1.62.92.7.94.45h2.78l1.86,1.63h.94l2.09.47,2.32.46h1.62l1.85-.94h2.79l1.63-.22,1.15-.23,1.16-.71.7-.91,1.18-2.32.45-1.86.93-2.1.94-3.01.46-1.86.46-.7,1.39-.7,1.39-.23,3.5-.93.68-.46.7-.93,1.4-1.62,1.85-1.39,1.16-.7,1.62-6.49.23-.94,1.39-1.63.7-.93h.46l.7-.46.93-.93,1.16-.46,1.39-.23.7-.46.46-1.16.69-.46h.94l.47-.23.22-.7.47-.46,1.16-.46.46-.7v-.46l1.16-1.63,2.54-2.55,2.1-1.38,2.07-.25,2.1-.46,1.86-.7,1.39-1.17.92-1.61,1.4-.94,3.25-.23,3.71-.7,3.25.47,3.72,1.61Zm-30.86,91.92l-1.16.46-.93,1.62-.94,1.4-.92,1.16-.46.69-.23.24-.46.92-.93,1.16-.93.92-1.16.7-1.39.47-.46.46v.47l.46,1.38.46,1.15.93,1.4.46,1.16.93,1.17.7.92-.24,1.16.24.46.46.46.23.24.23.23.7.24.23.21.7.48.46.91.94.93,1.16.94,1.86.22,1.62.24h.46l.47-.7.46-.92.23-.94.45-.68,1.86-2.79.94-.94h.7l.7-.21.92-.25.94.25,1.16-.46,2.08-.94,1.17-.47.7-.45.94-.71.22-1.63.22-1.38.72-.69.68-.47.7-.94.7-1.62.46-1.86v-.7l-.22-.69-.47-.7-.46-1.17-.46-.22-1.15-.46-1.63-1.16-1.38-1.16-1.17-1.4-.45-.22-1.41-.94-.45-.69-.25-.7-.46-.23-.45.23-1.62.23-3.02,1.15-1.86,1.16-1.63,1.4-1.85.46Z"/>
                                </g>
                                <g id="Disputed_Boundaries" data-name="Disputed Boundaries">
                                    <polyline class="cls-3" points="737.58 410.13 736.88 408.03 736.88 402.7 751.96 402.7 751.96 410.13"/>
                                    <polyline class="cls-3" points="751.96 410.13 750.1 411.98 749.88 412.91 750.1 413.6 739.89 413.6 739.2 412.91 737.58 410.13"/>
                                    <polyline class="cls-3" points="806.73 244.4 806.97 244.64 807.19 244.64 807.43 244.87 807.43 245.1 807.66 245.1 807.91 245.34 808.12 245.34 808.35 245.56 808.59 245.8 808.83 246.03 809.07 246.03 809.28 246.26 809.52 246.5 809.74 246.73 809.98 246.73 809.98 246.96 810.21 246.96 810.44 247.19 810.68 247.19 810.91 247.42 810.91 247.66 811.14 247.66 811.38 247.89 811.6 247.89 811.84 248.13 812.07 248.13 812.3 248.13 812.54 248.13 812.76 247.89 813 247.89 813.24 247.89 813.46 247.89 813.69 247.89 813.92 247.89 814.16 247.89 814.39 247.89 814.62 247.89 814.85 247.89 815.08 247.66 815.32 247.66 815.55 247.66 815.79 247.66 816.01 247.66 816.25 247.66 816.48 247.66 816.71 247.66 816.94 247.66 817.17 247.42 817.41 247.42 817.64 247.42 817.64 247.19 817.64 246.96 817.87 246.73 817.87 246.5 817.87 246.26 818.1 246.03 818.1 245.8 818.1 245.56 818.34 245.56 818.34 245.34 818.34 245.1 818.34 244.87 818.57 244.64 818.57 244.4 818.57 244.18 818.8 243.94 818.8 243.71 819.03 243.48 819.03 243.25 819.03 243.02 819.26 242.78 819.26 242.55 819.26 242.32 819.26 242.08 819.5 242.08 819.5 241.85 819.5 241.62 819.73 241.62 819.96 241.62 820.2 241.62 820.42 241.39 820.66 241.39 820.89 241.39 821.12 241.39 821.36 241.39 821.59 241.39 821.82 241.16 822.05 241.16 822.28 241.16 822.51 241.16 822.75 241.16 822.98 240.93 823.21 240.93 823.44 240.93 823.68 240.93 823.91 240.93 824.12 240.93 824.37 240.7 824.6 240.7 824.84 240.7 825.06 240.7 825.3 240.7 825.53 240.46 825.76 240.46 826 240.46 826.23 240.46 826.46 240.46 826.69 240.46 826.69 240.23 826.69 240 826.92 239.76 826.92 239.53 827.16 239.3 827.16 239.07 827.16 238.84 827.39 238.84 827.39 238.61 827.39 238.37 827.62 238.14 827.62 237.91 827.85 237.67 827.85 237.45 827.85 237.22 828.09 236.98 828.32 236.75 828.32 236.51 828.32 236.29 828.55 236.29 828.55 236.05 828.55 235.82 828.78 235.59 828.78 235.36 829.02 235.12 829.02 234.89 829.02 234.66 829.25 234.43 829.25 234.2 829.48 233.96 829.48 233.73 829.71 233.49 829.71 233.26 829.71 233.04 829.94 232.8 830.18 232.8 830.41 232.8 830.64 233.04 830.87 233.04 831.1 233.04 831.33 233.04 831.57 233.26 831.8 233.26 832.03 233.26 832.26 233.49 832.5 233.49 832.73 233.49 832.96 233.73 833.19 233.73 833.42 233.49 833.66 233.49 833.88 233.26 834.11 233.04 834.11 232.8 834.35 232.8 834.58 232.57 834.82 232.34 835.05 232.11 835.27 231.87 835.52 231.64 835.74 231.41 835.98 231.41 835.98 231.18 836.21 230.95 836.44 230.72 836.68 230.72 836.68 230.48 836.91 230.48 836.91 230.25 837.14 230.02 837.37 230.02 837.6 229.78 837.6 229.55 837.83 229.33 838.05 229.33 838.29 229.09 838.52 228.86"/>
                                    <polyline class="cls-3" points="202.85 165.96 206.55 165.96 210.27 165.96 213.99 165.96 217.47 165.96 221.18 165.96 224.9 165.96 228.61 165.96 232.09 165.96 235.81 165.96 239.51 165.96 243.23 165.96 246.71 165.96 250.42 165.96 254.14 165.96 257.85 165.96 259.7 165.96 261.57 165.96"/>
                                </g>
                                <g id="Bodies_of_water" data-name="Bodies of water">
                                    <path class="cls-1" d="m833.66,722.76l.46,1.16-.23.23-.22.24h-.24l-.46-.71-.93-.92-1.16-1.4-.46-.23-.7-.22v-.24l-.23-.68v-.47l-.46-.24-.23-.22h-.23l-.23.46.23.47-.23.46-.23.46-.23.46.23.24v.24l-.7.46-.46.22h-.46v-.46l-.23-.21-.7-.24-.47-.47-.46-.68-.22-.94v-1.4l.46-.46.22-.7.24-1.38v-.94l-.93-.94-.46-.46h-.7l-.46-.46-1.16-2.09-.22-.46v-.47l.46-.45v-1.39l-.23-3.02v-.24l-.46-.92-.23-.46v-.7l-.23-.46-.23-.24-.22-.46-.24-.46v-2.09l-.23-.7-.46-.46-.46-.24-.46-.22-.46-1.39-.23-.7.46-2.09.46-.93.93-.7.23-.46v-.7l.47-.7,1.16-1.38.22-.24-.46-3.02-.46-1.62-.23-.93-.22-.93.22-.46v-.7l-.46-6.04-.23-.46.23-.22.24-.24v-.92l.22-.24.23-.23v-.46l-.7-1.15-.7-.94-1.16-1.16-.7-.7-.23-1.16-.7-1.84-.23-.47-.23-.46v-.69l.23-.48.46-.69v-1.86l.46-.47,1.39.72,3.02,2.54v.25l1.86,2.55.68,1.15v.48l.71,2.3v2.32l.22.93-.22.47.7,1.15.46,1.16v1.16l-.24.94-.46,1.62-.24.69.24.46.23.7.7.7.46.7.46.93.23.46h.46l.46.23.7.46.46.93.23,1.39v2.09l-.23,1.86-.46,1.62-.46.7-.46.23-.46.46-.93.7-.23.7v.23l.23.7-.23,1.16v.93l1.16,2.79.23,1.62-.46,1.39v1.15l.46,1.16v1.4l-.23,1.38.23.94.46.46.23,1.39v3.24h.23l1.86.23.46.24.46,1.14,1.16,3.03v.46l.93,2.55Zm-7.66-29.01l.23.23h.46l.23-.23.23-.46v-.46l-.23-.23h-.7l-.23.46v.7Zm-.7-.46l.46-.7-.22-.23-.47-.23-.46.23-.22.46.46.46h.46Z"/>
                                    <polygon class="cls-1" points="775.63 647.56 775.63 647.33 774.47 646.86 774.01 646.63 774.01 646.39 773.78 646.39 773.31 647.09 772.38 647.56 772.15 647.56 772.62 647.09 772.62 646.86 772.62 646.63 772.38 646.39 771.69 646.39 771.22 646.39 771 645.69 771.22 644.77 771.45 644.54 771.92 644.31 772.38 643.85 772.62 643.38 772.85 642.22 772.62 641.29 772.15 640.59 771.69 640.36 771.22 640.13 771 639.9 770.29 638.97 770.06 638.51 769.13 638.27 768.9 637.81 768.67 636.87 768.2 635.49 767.97 634.56 767.97 634.09 767.97 632.93 767.05 630.61 766.35 629.45 765.42 628.75 764.26 628.06 763.1 627.6 761.94 626.66 760.32 624.35 759.15 623.66 758.93 623.19 758.45 622.02 758.45 620.64 758.45 620.4 757.99 620.4 757.77 620.16 756.36 616.22 755.92 616 755.67 615.29 755.44 615.07 754.98 614.83 754.76 614.14 754.52 613.2 754.76 612.51 754.98 612.27 756.6 611.12 756.6 610.65 756.6 610.42 756.6 609.26 756.6 609.02 757.07 608.79 757.07 608.56 755.2 605.55 754.29 603.69 753.82 602.3 753.6 601.38 753.6 600.9 753.6 596.97 753.82 595.33 754.04 594.41 754.52 594.17 754.76 594.17 754.76 593.25 755.44 588.84 755.44 588.37 755.2 588.61 754.98 589.07 754.04 591.86 753.82 592.08 753.12 591.62 752.88 591.39 753.35 590.7 753.6 590.23 753.35 589.77 753.35 588.84 753.6 586.29 753.82 585.59 753.82 585.12 753.6 584.89 753.6 584.43 754.04 583.73 754.04 582.57 754.04 580.95 754.04 579.79 754.29 579.32 754.52 579.09 754.76 578.85 756.13 579.09 756.6 579.55 756.6 580.48 756.6 583.27 756.6 584.89 756.83 585.59 757.77 587.21 757.77 587.67 758.93 590.23 759.85 591.39 760.32 592.32 760.78 593.02 760.54 593.95 760.08 597.88 760.08 599.05 760.08 599.29 760.08 599.5 760.54 599.5 761.01 599.74 761.94 600.44 762.4 601.14 762.86 602.07 762.86 602.54 762.63 602.77 762.63 603.93 762.4 604.15 762.17 605.31 761.94 606.01 762.17 606.71 762.86 607.4 763.56 608.79 764.26 610.65 764.49 611.82 764.49 612.27 764.02 612.97 763.34 613.43 762.86 613.67 762.63 613.43 762.4 613.67 761.94 614.14 761.7 614.83 761.48 616.22 761.7 616.92 761.94 617.38 762.86 618.54 764.49 619.93 764.49 620.16 764.72 620.16 765.42 619.72 766.35 619.72 767.05 619.72 767.28 619.93 767.51 620.4 769.13 621.79 769.83 622.72 770.29 623.89 771.22 624.81 772.15 625.74 772.62 626.66 772.62 627.6 772.38 628.06 771.92 628.06 771.92 628.29 771.92 628.99 772.85 630.38 773.08 631.77 772.85 632.93 773.08 633.86 773.54 634.79 774.7 635.95 775.86 638.74 777.03 640.36 777.03 640.82 777.26 641.06 777.72 641.51 777.72 641.76 777.49 641.98 777.49 642.45 777.72 642.92 778.42 643.85 779.58 645.01 780.04 645.69 780.04 646.39 780.27 647.09 780.74 647.79 780.74 648.48 780.74 649.42 780.51 649.65 780.27 649.19 780.04 649.42 779.8 649.65 779.35 650.11 778.88 650.34 778.42 650.11 777.96 649.65 775.63 647.56 775.63 647.56"/>
                                    <path class="cls-1" d="m826.69,536.61l1.16,2.33-.23.46-1.16.24h-1.63l-1.39.46-.23.23.23,1.16-.23.46h-.46l-.46-.46h-.7l-.7-.24h-.69l-.71.24-1.16,1.62-.23.7v1.15l.7.94.7.92-.23.94-.46.46-.46.22-.23.46-1.62,2.11-.46.69v.47l.7.46-.23.24h-1.16l-.23.45v.23l-.23.24.23.22,1.16.94v.22h-.46l-1.17-.46h-.93l-.46.24-.24.22-.23.7.23.46v.46l-.93.7v.93l-.69.47-.7.22h-1.16l-.46.24-.47.47-.47.68,2.09-.23.23.23.24.46.46.24v.22h-1.39l-.68.46h-.95l-.69.46-.24.46.72.23,3.23.46.7.24,2.31-.94.46.23.23.46v.46l-.46.7-2.32,1.39-1.16.93-.93.46v.93h-.23l-2.78-.46-1.86-1.16h-.93l-.71.23-.46.23-.24.47v2.54l.94,1.87v.46l-.94.23-1.38,1.39-.25.23.25-1.15-.25-.47,1.17-.93.23-.46-.7-1.39.22-1.39-.22-.46h-.7l-.7-.23h-.21l-.24-.46-.46-.23-1.16.93-.24-.23v-.46l-1.17-.23-.69-.23-.46-.46-.47-1.16-.46-.22h-.47l-.45.46-.47.7h-.69l-.25.23.47,1.39.22.46h-.22l-1.4-.23-.45.46-.24.7v.93l.47,1.16-.47-.23-.69-.93h-.23l-.24.46h-.46l-.46,1.16h-.24l-.22-.7v-.7l.46-.92-.7-.24-.23-.46v-.47l.46-.93v-.23l-.23-.46-.23-.7-.46-.22-.48-.7v-4.65l1.18-5.56.7-.94.22-.93.47-3.48v-.45l-.24-.47h-.92l-.23-.92v-.7l-.46-.71v-.69l1.16-2.79.7-1.16,1.62-2.08v-.93l-.24-.46-.46-.46v-.23l.24-.46.91-.47.25-.46,1.38-.22.71-.46h.7l.22-.23h.94l.46-.7.47-.23h.21l.25.46h.45l.47-.22.46-.46.22-1.17.47-.46.46-.24.47.7.21.24-.46.46v.23l.24.23h.68l.48-.23.92-.7.46.23.24.7.22.23.47-.23.94-.46.46-.71.22-.46.7-.24.94-1.85-.24-.46.7-.23.47-.23.45.23.71-.23v.23l-.47.92.23.24,1.38.23.47.23v.46l-.71.23.23.22.47.47h1.16l.7-1.16.7-.23.22.23.23.93.23.23,1.62.46,1.16-.46h.23v1.62l.23.93.23.46.23.7.46.22.7-.22.23.22-.23.47v.46l1.4,1.39.92,1.16h.24l.22-.24.23-.22.23-.93.7-.7,1.62-.71,2.32-.7Zm-17.63-3.94l.21-.7-.93-.46-1.16.23v1.15l-.46.24v.46h.93l.25.23.92.7-.23-.93-.24-.46.71-.46Zm-2.57,30.86l.23-.46-.7-.7-.23-.68-.23-.71-.91.24h-.7l-.24-.94h-.94l-.7.47.24.46v1.15l.7.23,1.16.23.93.47.69.22h.7Zm-11.36-22.98l.22-.7-.69-.69-.94.69-.23-.46.23-.93h-1.39l-.22.24.22.46.22.46.47.22.94.24v.46l-.47.7-.46.46v.47l1.86-.47.24-1.16Zm-3.25,25.31l.23-.93-.23-.24-.94.24-.92.23-.46-.23-.47-.71-.22-.23-.24.47-.22,1.15.92.93v.93l.71-.23.23-.93h.46l.91-.23.25-.22Z"/>
                                    <polygon class="cls-1" points="784.46 504.35 783.3 505.75 783.3 505.98 783.3 506.91 783.53 510.85 783.3 511.32 783.06 511.54 782.83 511.54 782.37 512.01 782.13 512.71 781.67 513.41 780.51 514.57 780.04 514.8 778.65 515.04 778.42 515.26 777.96 515.49 776.79 516.65 775.41 518.52 774.7 519.44 774.47 519.9 772.85 521.99 772.62 521.99 772.15 522.22 771.92 521.99 771.69 521.76 771.69 520.83 771.69 520.13 771.69 519.67 771.69 519.44 771.45 519.21 771.22 518.97 771 518.97 770.53 519.21 770.29 518.52 771 517.35 771.45 516.42 772.15 515.49 772.85 514.8 774.01 513.64 774.47 513.17 776.57 511.32 777.49 510.63 777.96 510.38 780.51 508.53 781.67 507.37 782.13 506.44 782.83 505.52 783.74 504.35 784.46 504.12 784.46 504.35 784.46 504.35"/>
                                    <polygon class="cls-1" points="860.11 381.11 859.64 380.88 859.19 380.88 858.48 380.88 858.25 380.64 857.79 380.41 857.55 380.64 857.33 380.64 857.09 380.64 856.86 380.41 856.63 379.95 856.39 379.72 856.39 378.79 856.39 378.1 856.63 377.4 856.63 376.23 857.09 375.77 857.33 375.31 857.55 374.85 858.02 374.85 858.48 375.08 858.95 375.31 859.19 375.31 859.88 375.54 860.8 374.85 861.27 374.61 861.74 374.61 862.44 374.38 862.9 374.61 863.36 375.08 863.59 375.54 863.82 376.23 864.05 376.94 864.05 377.4 864.05 377.86 863.82 378.1 863.59 378.56 863.59 379.25 863.13 379.72 862.9 380.18 862.9 380.64 862.2 381.8 861.5 383.2 861.04 383.2 860.8 383.2 860.58 383.2 860.34 382.74 860.58 382.27 860.34 381.8 860.11 381.35 860.11 381.11 860.11 381.11"/>
                                </g>
                            </svg>
                            <div class="legend"> 
                                <span class="legend-box"></span>Countries
                                <em class="disclaimer-map">Countries in map not to scale</em>
                            </div>
                        </div>
                    </div>
                    <p class="full-description" v-html="descriptionCountryLonger.value"></p>
                    <p class="full-description" v-if="descriptionCountryName !== 'All Countries'">
                        <em>Note: All population and total land figures are retrieved from World Factbook: <a href="https://www.cia.gov/the-world-factbook/">https://www.cia.gov/the-world-factbook/</a>. Internet penetration rate is being measured by the total number of internet users not population.</em>
                    </p>

                </div>
            </div>
        </div>
        <div class="anchor-point" name="topTable" id="topTable"></div>
        <div class="section filter-area">
            <div class="filters">
                <div class="all-filters">
                    <h4>Legal Documents: {{descriptionCountryName}}</h4>
                    <em>Filter by:</em>
                    <div class="form-dropdown"> 
                        <div class="form-opener category">Categories <span class="ceip-icon icon-arrow-down"></span></div>
                        <div class="form-menu"> 
                            <form class="clean-form africa-regulations-category toggle-wrapper padded-less" name="africa-regulations-category" id="africa-regulations-category" action="" method="get" enctype="multipart/form-data" autocomplete="off">
                                <fieldset>
                                    <div class="control-group">
                                        <div class="checkbox"> 
                                            <label for="Policy" @click="filterCategory">
                                                Policy
                                                <input type="checkbox" id="PolicyCheckbox" name="Policy" value="Policy">
                                                <span class="checkmark"></span>
                                            </label>
                                        </div>
                                        <div class="checkbox"> 
                                            <label for="Law" @click="filterCategory">
                                                Law
                                                <input  type="checkbox" id="LawCheckbox" name="Law" value="Law">
                                                <span class="checkmark"></span>
                                            </label>
                                        </div>
                                        <div class="checkbox"> 
                                            <label for="Regulation" @click="filterCategory">
                                                Regulation
                                                <input  type="checkbox" id="RegulationCheckbox" name="Regulation" value="Regulation">
                                                <span class="checkmark"></span>
                                            </label>
                                        </div>
                                    </div>
                                </fieldset>
                            </form>
                        </div>
                    </div>
                    <div class="form-dropdown" id="pillars">
                        <div class="form-opener">Digital Economy Pillars <span class="ceip-icon icon-arrow-down"></span></div
                        ><div class="form-menu"> 
                            <form class="clean-form africa-regulations-dig-economy toggle-wrapper padded-less" name="africa-regulations-dig-economy" id="africa-regulations-dig-economy" action="" method="get" enctype="multipart/form-data" autocomplete="off">
                                <fieldset>
                                    <div class="control-group radio">
                                        <div class="checkbox"> 
                                            <label for="digitalInfrastructure" @click="dropdownList"><input type="radio" id="digitalInfrastructureCheckbox" name="pillar" value="digitalInfrastructure">Digital Infrastructure</label>
                                        </div>
                                        <div class="checkbox"> 
                                            <label for="digitalPlatform" @click="dropdownList"> <input type="radio" id="digitalPlatformCheckbox" name="pillar" value="digitalPlatform">Digital Platform</label>
                                        </div>
                                        <div class="checkbox"> 
                                            <label for="digitalSkills" @click="dropdownList"> <input type="radio" id="digitalSkillsCheckbox" name="pillar" value="digitalSkills">Digital Skills</label>
                                        </div>
                                        <div class="checkbox"> 
                                            <label for="entrepreneurship" @click="dropdownList"> <input type="radio" id="entrepreneurshipCheckbox" name="pillar" value="entrepreneurship">Digital Innovation</label>
                                        </div>
                                    </div>
                                </fieldset>
                            </form>
                        </div>
                    </div>
                    <div class="form-dropdown" id="subcategories">
                        <div class="form-opener">Sub-Pillars</div>
                        <div class="form-menu"> 
                            <form class="clean-form africa-regulations-subcategory toggle-wrapper padded-less" name="africa-regulations-subcategory" id="africa-regulations-subcategory" action="" method="get" enctype="multipart/form-data" autocomplete="off">
                                <fieldset>
                                    <div class="control-group radio">
                                        <div class="checkbox subcategory digital-infrastruct-group">
                                            <label for="physicalInfrastructure" @click="dropdownList"> <input type="radio" id="physicalInfrastructureCheckbox" name="subcategory" value="physicalInfrastructure">Physical </label>
                                        </div>
                                        <div class="checkbox subcategory digital-infrastruct-group">
                                            <label for="invisibleInfrastructureCheckbox" @click="dropdownList"> <input type="radio" id="invisibleInfrastructure" name="subcategory" value="invisibleInfrastructure">Invisible </label>
                                        </div>
                                        <div class="checkbox subcategory digital-platforms-group">
                                            <label for="digitalGovernment" @click="dropdownList"> <input type="radio" id="digitalGovernmentCheckbox" name="subcategory" value="digitalGovernment">Government</label>
                                        </div>
                                        <div class="checkbox subcategory digital-platforms-group"> 
                                            <label for="digitalBusiness" @click="dropdownList"> <input type="radio" id="digitalBusinessCheckbox" name="subcategory" value="digitalBusiness">Business</label>
                                        </div>
                                        <div class="checkbox subcategory digital-platforms-group"> 
                                            <label for="financialServices" @click="dropdownList"><input type="radio" id="financialServicesCheckbox" name="subcategory" value="financialServices">Financial Services</label>
                                        </div>
                                        <div class="checkbox subcategory digital-skills-group"> 
                                            <label for="basic" @click="dropdownList"> <input type="radio" id="basicCheckbox" name="subcategory" value="basic">Basic</label>
                                        </div>
                                        <div class="checkbox subcategory digital-skills-group"> 
                                            <label for="advanced" @click="dropdownList"><input type="radio" id="advancedCheckbox" name="subcategory" value="advanced">Advanced</label>
                                        </div>
                                        <div class="checkbox subcategory digital-innovation-group">
                                            <label for="digitalEntrepreneurship" @click="dropdownList"><input type="radio" id="digitalEntrepreneurshipCheckbox" name="subcategory" value="digitalEntrepreneurship">Entrepreneurship</label>
                                        </div>
                                        <div class="checkbox subcategory digital-innovation-group"> 
                                            <label for="innovationHubs" @click="dropdownList"><input type="radio" id="innovationHubsCheckbox" name="innovationHubs" value="innovationHubs">Innovation</label>
                                        </div>
                                    </div>
                                </fieldset>
                            </form>
                        </div>
                    </div>
                    <strong>OR </strong>
                    <div class="no-gutters keyword-panel" id="keyword-panel">
                        <form autocomplete="off"><input type="text" id="search" v-model="searchTerm" placeholder="Search key data points..." />
                            <ul class="search-dropdown" v-if="searchkeyDataValues.length">
                                <li
                                    v-for="keyDataValue in searchkeyDataValues"
                                    :key="keyDataValue.value"
                                    @click="selectkeyDataValue(keyDataValue)"
                                >
                                    {{ keyDataValue.value }}
                                </li>
                            </ul>
                        </form>
                    </div>
                    <div class="button blue" id="reset" @click="resetFilter">Clear Filters</div>
                </div>
            </div>
        </div>
        <div class="section results-table" id="resultsTable">
            <div class="backToTop"> <a href="#topTable">↑</a></div>
            <table id="protestTable">
                <thead id="headings">
                    <tr>
                        <th data-attr="classification" data-asc="true" class="protest-name sortable active">Category</th> 
                        <th data-attr="country">Name</th> 
                        <th class="infrastructure-pillar hide-true-mobile">
                            <span class="img"></span>
                            Digital<br />Infrastructure
                        </th>
                        <th class="platforms-pillar hide-true-mobile">
                            <span class="img"></span>
                            Digital<br />Platforms
                        </th>
                        <th class="skills-pillar hide-true-mobile">
                            <span class="img"></span>
                            Digital<br />Skills
                        </th>
                        <th class="innovation-pillar hide-true-mobile">
                            <span class="img"></span>
                            Digital<br />Innovation
                        </th>
                        <th class="outcomes responsive-hide">Key Data Points</th>
                    </tr>
                </thead>
                <tbody id="table-results"></tbody>
            </table>
        </div>
        <div class="section download-buttons">
            <div class="foreground">
                <a class="button" href="https://docs.google.com/spreadsheets/d/1h3DTbFEoHPM4ztTdLQj7N_FHKgTYhaSFYF8fT7cJO1E/edit?usp=sharing" target="_blank">Download Spreadsheet</a>
            </div>
        </div>
        <div class="section foreground explanatory">
            <p> <em>Thanks for interacting with the Africa Technology Policy Tracker (Aftech). Aftech is part of ongoing research. The dataset is updated periodically with new legal documents as they become available and as the project expands to cover all African countries. For questions and comments, email us at <a href="mailto:africatechtracker@ceip.org">africatechtracker@ceip.org</a>.</em></p>
            <h3>Project Contacts</h3>
            <p><a href="https://carnegieendowment.org/people/jane-munga?lang=en">Jane Munga</a> and Sylvester Quansah</p>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import FeatureFragment from '@fragments/pages/FeatureFragment.ts';
const { scrollProgress } = useScrollProgress();

const pageData = await usePageData(FeatureFragment, 'features');

let usedDataArray = [];
let responsive = false;
let mobile = false;
let binaryValue = "";
let categoryValue = "";

// does this need to happen any time other than on load?  We could repeat with each button, just not sure it's worth it ...
function getResponsive() {
    const windowWidth = ref(window.innerWidth);
    if (windowWidth.value <= 767) {
        mobile = true;
    } else if (windowWidth.value <= 1024) {
        responsive = true;
    }
}

const dataArray = [
    {
        arrayItem: true, 
        row: 292, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/44004-doc-EN-_Continental_AI_Strategy_July_2024.pdf" target="_blank">Continental Artificial Intelligence Strategy</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        ArtificialIntelligence: true,
        BroadbandInternet: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
    },
    {
        arrayItem: true, 
        row: 293, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://www.nepad.org/publication/ai-and-future-of-work-africa-white-paper" target="_blank">AI and the Future of Work In Africa White Paper</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        ArtificialIntelligence: true,
        BroadbandInternet: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
    },
    {
        arrayItem: true, 
        row: 294, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/38756-doc-stisa_science_tech_innovation_strategy.pdf" target="_blank">Science, Technology, and Innovation Strategy for Africa</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        BroadbandInternet: true,
        BroadcastingandMultimediaServices: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
    },
    {
        arrayItem: true, 
        row: 295, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/44080-doc-AI_for_Sustainable_Youth_Development_finalized_copy-1.pdf" target="_blank">Artificial Intelligence (AI) for Sustainable Youth Development In Africa</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        ArtificialIntelligence: true,
        Elearning: true,
    },
    {
        arrayItem: true, 
        row: 295, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/44005-doc-AU_Digital_Compact_V4.pdf" target="_blank">African Digital compact</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        Cybersecurity: true,
        DigitalTransformation: true,
        DigitalGovernance: true,
    },
    {
        arrayItem: true, 
        row: 296, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/44005-doc-AU_Digital_Compact_V4.pdf" target="_blank">African Digital compact</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        Cybersecurity: true,
        DigitalTransformation: true,
        DigitalGovernance: true,
    },
    {
        arrayItem: true, 
        row: 297, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/38507-doc-DTS_for_Africa_2020-2030_English.pdf" target="_blank">The Digital Transformation Strategy for Africa</a></span>', 
        date: '01/01/2020', 
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        ArtificialIntelligence: true,
        BroadbandInternet: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
    },
    {
        arrayItem: true, 
        row: 298, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/43798-doc-African_Union_Child_Online_Safety_and_Empowerment_Policy_Feb_2024.pdf" target="_blank">The African Union Child Online Safety and Empowerment Policy</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        OnlineChildExploitation: true,
        DataProtection: true,
        DigitalIdentification: true,
    },
    {
        arrayItem: true, 
        row: 299, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/42078-doc-AU-DATA-POLICY-FRAMEWORK-ENG1.pdf" target="_blank">African Union (AU) Data Policy Framework</a></span>', 
        date: '01/01/2022', 
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        DataEconomy: true,
        DataGovernance: true,
        DataProtection: true,
        DataCentersandCloudServices: true,
    },
    {
        arrayItem: true, 
        row: 300, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/documents/43393-doc-AU_Interoperability_framework_for_D_ID_English.pdf" target="_blank">African Union (AU) Interoperability Framework for Digital ID</a></span>', 
        date: '01/01/2022', 
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        techHubs: true,
        digitalEntrepreneurship: true,
        DigitalIdentification: true,
        DataProtection: true,
        DigitalPayments: true,
        ElectronicTransactions: true,
    },
    {
        arrayItem: true, 
        row: 301, 
        country: 'African_Union', 
        reg_name: '<span class="reg-name"><a href="https://au.int/sites/default/files/treaties/29560-treaty-0048_-_african_union_convention_on_cyber_security_and_personal_data_protection_e.pdf" target="_blank">Africa Union Convention on Cybersecurity (Malabo Convention)</a></span>', 
        date: '01/01/2023', 
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        techHubs: true,
        digitalEntrepreneurship: true,
        Cybersecurity: true,
        DataProtection: true,
    },
    {
        arrayItem: true,
        row: 239,
        country: 'Algeria',
        reg_name: '<span class="reg-name"><a href="https://www.mpt.gov.dz/wp-content/uploads/2023/11/Loi-n%C2%B0-18-05-du-10-mai-2018.-fr_0_0.pdf" target="_blank">Electronic Commerce Act</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 240,
        country: 'Algeria',
        reg_name: '<span class="reg-name"><a href="https://www.mpt.gov.dz/wp-content/uploads/2023/11/Loi-n%C2%B0-18-04-du-24-Chaabane-1439-correspondant-au-10-mai-2018.fr_0.pdf" target="_blank">Post and Electronic Communications Act</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        PostalServices: true 
    }, 
    {
        arrayItem: true,
        row: 241,
        country: 'Algeria',
        reg_name: '<span class="reg-name"><a href="https://www.mpt.gov.dz/wp-content/uploads/2023/11/Loi-20-04.pdf" target="_blank">Radiocommunications Act</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadcasting: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 242,
        country: 'Algeria',
        reg_name: '<span class="reg-name"><a href="https://www.mpt.gov.dz/wp-content/uploads/2023/11/Loi-n%C2%B015-04.pdf" target="_blank">Electronic Signature and Certification Act</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        DigitalIdentification: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 243,
        country: 'Algeria',
        reg_name: '<span class="reg-name"><a href="https://www.mpt.gov.dz/wp-content/uploads/2023/11/Loi-n%C2%B0-09-04-fr.pdf" target="_blank">Cybercrime Act</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        physicalInfrastructure: true,
        Cybersecurity: true,
        DataProtection: true 
    }, 
    {
        arrayItem: true,
        row: 244,
        country: 'Algeria',
        reg_name: '<span class="reg-name"><a href="https://www.arpce.dz/fr/file/c3e9y7" target="_blank">Personal Data Protection Act</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        DigitalIdentification: true 
    }, 
    {
        arrayItem: true, 
        row: 276, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://apd.ao/fotos/frontend_1/editor2/110617_lei_22-11_de_17_junho-proteccao_dados_pessoais.pdf" target="_blank">Law 22/11 Personal Data Protection</a></span>', 
        date: '01/01/2011', 
        date_text: "2011",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        DigitalIdentification: true,
    },
    {
        arrayItem: true, 
        row: 277, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://apd.ao/fotos/frontend_1/editor2/110620_lei_23-11_de_20_junho-das_comunicacoes_electronicas_e_dos_servicos.pdf" target="_blank">Law 23/11 Electronic Communications and Information Society Services</a></span>', 
        date: '01/01/2011', 
        date_text: "2011",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        QualityofService: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 278, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://apd.ao/fotos/frontend_1/editor2/200420_lei_11-20_de_23_abril-identificacao_celular_vigilancia_electronica.pdf" target="_blank">Law 11/20 Identification or Mobile Phone Traceability</a></span>', 
        date: '01/01/2020', 
        date_text: "2020",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalSignatures: true,
    },
    {
        arrayItem: true, 
        row: 279, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://www.apd.ao/fotos/frontend_1/editor2/190709_resolucao_33-19_de_9_julho-convencao_ciberseguranca_proteccao_dados.pdf" target="_blank">Resolution 33/19 African Union Convention on Cybersecurity and Personal Data Protection</a></span>', 
        date: '01/01/2019', 
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
    },
    {
        arrayItem: true, 
        row: 280, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://minttics.gov.ao/fotos/frontend_10/gov_documentos/plano_nacional_de_arquitectura_global_de_interoperabilidade_da_administracao_central_do_estado-pnagia_1__15289524825f18810ab529e.pdf" target="_blank">Global Architecture National Plan for Interoperability in Local and Central Administration</a></span>', 
        date: '01/01/2018', 
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        QualityofService: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 281, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://minttics.gov.ao/fotos/frontend_10/gov_documentos/redes_e_sistemas_informaticos_20864175955f109a14374be.pdf" target="_blank">Law 7/17 Network and Computer Systems Protection</a></span>', 
        date: '01/01/2017', 
        date_text: "2017",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
    },
    {
        arrayItem: true, 
        row: 282, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://minttics.gov.ao/fotos/frontend_10/gov_documentos/regulamento_partilha_infraestruturas_comunicacoes_electronicas-dp_166_14_11710857305f18dfde594d0.pdf" target="_blank">Electronic Communications Infrastructure Sharing</a></span>', 
        date: '01/01/2014', 
        date_text: "2014",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
    },
    {
        arrayItem: true, 
        row: 283, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://minttics.gov.ao/fotos/frontend_10/gov_documentos/regulamento_das_tecnologias_e_1461939785f1885f1b0e94...a_sociedade_da_informacao.pdf" target="_blank">Information Society Services and Technologies Regulation </a></span>', 
        date: '01/01/2011', 
        date_text: "2011",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        BroadbandInternet: true,
        BroadcastingandMultimediaServices: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Drones: true,
        Ecommerce: true,
        Ewaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        PostalServices: true,
        QualityofService: true,
        SmartCities: true,
        SpectrumandTVWhiteSpaces: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 284, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="http://freedominfo.org/documents/angolalaw.pdf" target="_blank">Law 11/02 Access to Documents Held by Public Authorities</a></span>', 
        date: '01/01/2002', 
        date_text: "2002",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        DataProtection: true,
        DigitalIdentification: true,
    },
    {
        arrayItem: true, 
        row: 285, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://documents1.worldbank.org/curated/en/099021406262319882/pdf/IDU0cfa535a90914f043630bb860ddc79fa75def.pdf" target="_blank">Law 2/20 Video Surveillance</a></span>', 
        date: '01/01/2020', 
        date_text: "2020",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        VideoSurveillance: true, 
        DataProtection: true, 
        DigitalIdentification: true,
    },
    {
        arrayItem: true, 
        row: 286, 
        country: 'Angola', 
        reg_name: '<span class="reg-name"><a href="https://apd.ao/fotos/frontend_1/editor2/201103_carta_pr_1-20_ratificacao_ciberseguranca.pdf" target="_blank">Ratification Letter 1/20 African Union Convention on Cybersecurity and Personal Data Protection</a></span>', 
        date: '01/01/2020', 
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        DigitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
    },
    {
        arrayItem: true,
        row: 49,
        country: 'Benin',
        reg_name: '<span class="reg-name"><a href="https://numerique.gouv.bj/code-du-numerique" target="_blank">Digital Code</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        DigitalPlatform: true,
        DigitalSkills: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        financialServices: true,
        basic: true,
        advanced: true,
        Broadband: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true
    },
    {
        arrayItem: true,
        row: 50,
        country: 'Benin',
        reg_name: '<span class="reg-name"><a href="https://finances.bj/wp-content/uploads/2019/12/loi-n-2015-07-du-20-03-2015-portant-code-de-information-et-de-la-communication-1.pdf" target="_blank">Information and Communication Code</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalPlatform: true,
        digitalBusiness: true,
        Broadcasting: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        row: 51,
        country: 'Benin',
        reg_name: '<span class="reg-name"><a href="https://numerique.gouv.bj/assets/Documents/DPS.pdf" target="_blank">Sectoral Policy Statement</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Cybersecurity: true, 
        DataCentersandCloudServices: true,
        DataProtection: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 52,
        country: 'Benin',
        reg_name: '<span class="reg-name"><a href="https://numerique.gouv.bj/assets/documents/strategie-nationale-d%27intelligence-artificielle-et-des-megadonnees-2023-2027.pdf" target="_blank">National Strategy for Artificial Intelligence and Big Data</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true
    },
    {
        arrayItem: true,
        row: 53,
        country: 'Benin',
        reg_name: '<span class="reg-name"><a href="https://numerique.gouv.bj/assets/documents/regles-de-politique-de-protection-des-infrastructures-dinformation-critiques-en-republique-du-benin.pdf" target="_blank">Rules of Policy for the Protection of Critical Information Infrastructures in the Republic of Benin</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        CustomerProtection: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        row: 54,
        country: 'Benin',
        reg_name: '<span class="reg-name"><a href="https://numerique.gouv.bj/assets/documents/referentiel-des-exigences-relatives-a-la-qualification-des-fournisseurs-de-services-de-securite-numerique-en-republique-du-benin-1698397466.pdf" target="_blank">Reference Framework of Requirements Relating to the Qualifications of Digital Security Service Providers in the Republic of Benin</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        CustomerProtection: true,
        DataProtection: true

    },
    {
        arrayItem: true, 
        row: 2,
        country: 'Burundi',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1gmDYxR-LIRaaNdCw-e6oHnIwiIqACChW" target="_blank">National ICT Development Policy</a></span>',
        date: "01/01/2011",
        date_text: "2011",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        DigitalIdentification: true,
        Cybersecurity: true,
        Broadband: true,
        EgovernmentServices: true,
        Ecommerce: true,
        DigitalPayments: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 3,
        country: 'Burundi',
        reg_name: '<span class="reg-name"><a href="https://setic.gov.bi/wp-content/uploads/2024/06/PLAN-DIRECTEUR-DE-DIGITALISATION-2023-2033-10.pdf" target="_blank">Master Plan For Digitalization Of Public Services In Burundi “PDDSP 2023-2033”</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        Broadband: true,
        EmergingTechnologies: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        ServiceQuality: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 4,
        country: 'Burundi',
        reg_name: '<span class="reg-name"><a href="https://arct.gov.bi/2015/05/26/decret-no-100-166-du-26-mai-2015-portant-reglementation-de-lacces-unique-aux-reseaux-internationaux-des-telecommunications-ouverts-au-public/" target="_blank">Decree No. 100/166 of 26 May 2015 Regulating Single Access to International Telecommunications Networks Open to the Public</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        financialServices: true,
        LastMile: true,
        UniversalAccess: true
    },
    {
        arrayItem: true, 
        row: 291, 
        country: 'Cameroon', 
        reg_name: '<span class="reg-name"><a href="https://www.prc.cm/en/news/the-acts/laws/4228-law-no-2020-004-of-23-april-2020-governing-postal-activity-in-cameroon" target="_blank">Law No.2020/004 of 23 April 2020 governing Postal Activity In Cameroon</a></span>', 
        date: '01/01/2020', 
        date_text: "2020",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        PostalServices: true
    },
    {
        arrayItem: true,
        row: 40,
        country: 'Chad',
        reg_name: '<span class="reg-name"><a href="https://ansice.td/assets/documents/loi_09.pdf" target="_blank">Law on Cybersecurity and the Fight Against Cybercrime</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Cybersecurity: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
        TypeApproval: true,
        EndUserEquipment: true
    },
    {
        arrayItem: true,
        row: 41,
        country: 'Chad',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1XzdyYkXtGzVWzvQE5keQilGs1uDUTBWs" target="_blank">Law No. 006/PR/2015 Creating the National Agency for Computer Security and Electronic Certification</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Chad',
        digitalInfrastructure: true,
        digitalPlatform: true, 
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        innovationHubs: true,
        Cybersecurity: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true
    },
    {
        arrayItem: true,
        row: 42,
        country: 'Chad',
        reg_name: '<span class="reg-name"><a href="http://www.droit-afrique.com/upload/doc/tchad/Tchad-Loi-2014-14-communication-electronique.pdf" target="_blank">A Text on Electronic Communications</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        financialServices: true,
        Broadband: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        ServiceQuality: true
    },
    {
        arrayItem: true,
        row: 43,
        country: 'Chad',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=17E01pf86lZZByuNDV-_MTUI03tdfmgwm" target="_blank">Strategic Plan 2020-2030 for the Postal, Electronic Communication, and the Digital Economy Sector of Chad</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        advanced: true,
        innovationHubs: true,
        Broadband: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        Ecommerce: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true, 
        InternetResources: true,
        PostalServices: true,
        ServiceQuality: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 174,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://telecom.gouv.ci/new/publications/sous-categorie/8" target="_blank">Order Of March 21, 2012, Relating To Telecommunications And Information And Communication Technologies (This Order Was Revised To Result In The Adoption Of The Law Relating To Electronic Communications Which Will Be Promulgated Very Soon)</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadcasting: true 
    }, 
    {
        arrayItem: true,
        row: 175,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://telecom.gouv.ci/new/publications/sous-categorie/1" target="_blank">National Digital Development Strategy of Ivory Coast</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        ArtificialIntelligence: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Drones: true,
        Ecommerce: true,
        ElectronicWaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        PostalServices: true,
        ServiceQuality: true,
        SMARTCities: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 176,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://telecom.gouv.ci/new/publications/sous-categorie/1" target="_blank">National Cybersecurity Strategy</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true 
    }, 
    {
        arrayItem: true,
        row: 177,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://telecom.gouv.ci/new/publications/sous-categorie/7" target="_blank">Law on the Protection of Personal Data</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true 
    }, 
    {
        arrayItem: true,
        row: 178,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://telecom.gouv.ci/new/publications/sous-categorie/7" target="_blank">Law on the Fight Against Cybercrime</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true 
    }, 
    {
        arrayItem: true,
        row: 179,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://telecom.gouv.ci/new/publications/sous-categorie/7" target="_blank">Law on Electronic Transactions</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        digitalPayments: true 
    }, 
    {
        arrayItem: true,
        row: 180,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://telecom.gouv.ci/new/publications/sous-categorie/7" target="_blank">Law on Postal Code</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        PostalServices: true 
    }, 
    { 
        arrayItem: true,
        row: 181,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1bN8H89Dpq-eGgoIWqWfuH6IYyM9974DR" target="_blank">Law No. 2013-450 of June 19, 2013 Relating to the Protection of Personal Data</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true 
    }, 
    {
        arrayItem: true,
        row: 182,
        country: "Cote_D_I_viore",
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1U_WQ8bO5PL_dShOEaclV18qTHuU81Wmg" target="_blank">General Framework for Information Systems Security and the Critical Infrastructure Protection Plan</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        Ecommerce: true,
        PostalServices: true,
        ServiceQuality: true,
        SMARTCities: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 245,
        country: 'Dem_Rep_Congo',
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/fr/document/w8w2ra57ss?page=1" target="_blank">National ICT Policy</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 246,
        country: 'Dem_Rep_Congo',
        reg_name: '<span class="reg-name"><a href="https://internews.org/wp-content/uploads/2023/11/ARISA-IEA-CHAPTER-6-DRC.pdf" target="_blank">Cybersecurity Law</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,	 
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 247,
        country: 'Dem_Rep_Congo',
        reg_name: '<span class="reg-name"><a href="https://www.dlapiperdataprotection.com/system/modules/za.co.heliosdesign.dla.lotw.data_protection/functions/handbook.pdf?country-1=CD" target="_blank">Data Protection Law</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 32,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1aMYYwSwACvE5tKzw8gtBbn-IWADYQGmS" target="_blank">Law no. 175 of 2018 Regarding Combating Information Technology Crimes</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        row: 33,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1RirjoeGmpaQ1k3o4sVhIMJwmf2FRQQr4" target="_blank">Deployment Operation and Leasing Infrastructure of Indoor Wireless Telecom Networks Framework</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        IndoorCoverage: true
    },
    {
        arrayItem: true,
        row: 34,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1lFfqH2xZv0Q_KqWYZBP_C_a81jLM8eJg" target="_blank">Regulatory Framework For Constructing & Leasing Telecommunication Towers</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        LastMile: true
    },
    {
        arrayItem: true,
        row: 35,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/Publications_2282024000_Cloud_First_Policy_Egypt_2024.pdf" target="_blank">Cloud First Policy</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        DataCentersandCloudServices: true
    },
    {
        arrayItem: true,
        row: 36,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/Publications_1412024000_National_Cybersecurity_Strategy_2023_2027.pdf" target="_blank">National Cybersecurity Strategy (2023-2027)</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        row: 37,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/Publications_1532018000_e-Commerce-Strategy-March2018.pdf" target="_blank">E-Commerce Strategy</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true, 
        financialServices: true,
        Ecommerce: true
    },
    {
        arrayItem: true,
        row: 38,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/Publications_1382015000_Digital%20Arabic%20Content%20strategy-%20English.pdf" target="_blank">Digital Arabic Content National Strategy</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        OpenSource: true,
        ArabicContent: true
    },
    {
        arrayItem: true,
        row: 39,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/Publications_18112014000_National_FOSS_Strategy_EN_17_11_2014.pdf" target="_blank">National Free and Open Source Software (FOSS) Strategy</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        OpenSource: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/wp-content/uploads/2020/11/Law-No-10-of-2003.pdf " target="_blank">Egypt Telecommunication Regulation Law No 10 of 2023</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        UniversalAccess: true,
        Communication: true,
        Cybersecurity: true,
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.ilo.org/dyn/natlex/natlex4.detail?p_lang=en&p_isn=111246&p_count=7&p_classification=01#:~:text=Law%20151%2F2020%20on%20the%20Protection%20of%20Personal%20Data.,-Country%3A&text=Abstract%2FCitation%3A,appoint%20a%20Data%20Protection%20Officer." target="_blank">Personal Data Protection Law No.151 of 2020 (the "Law")</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/wp-content/uploads/2022/03/IoT-Framework-En.pdf" target="_blank">IoT Regulatory Framework</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EndUserEquipment: true,
        Spectrum: true,
        InternetofThings: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/Publications_672021000_Egypt-National-AI-Strategy-English.pdf" target="_blank">Egypt National Artificial Intelligence Strategy</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        HighlySpecializedSkills: true,
        Startups: true,
        TechHubs: true,
        DataCenter: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/en/ICT_Strategy" target="_blank">Egypt Digital Transformation Strategy</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        DigitalIdentification: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        Ecommerce: true, 
        Payments: true, 
        EgovernmentServices: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true,
        PostalServices: true,
        MobileWallets: true,
        Broadband: true,
        ArtificialIntelligence: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/en/regulations/regulatory-framework/mobile-wallets-regulatory-rules/" target="_blank">Mobile Wallet Regulations</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        MobileWallets: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/en/regulations/regulatory-framework/regulatory-framework-for-establishing-operating-data-centers-and-providing-hosting-and-cloud-computing-services/" target="_blank">Regulatory Framework for Establishing Data Centers and Providing Cloud Hosting Services</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        MiddleMile: true,
        DataCenter: true,
        CloudPolicy: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name">Mobile Number Portability Regulations (Operators)</span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Portability: true,
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.pwc.com/m1/en/tax/documents/2019/egypt-usage-of-non-cash-payments-methods-law-no-18-year-2019.pdf" target="_blank">Egypt: Usage of the Non-Cash Payments’ Methods Law (Law no.18) for Year 2019</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Law',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Payments: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/en/regulations/regulatory-framework/framework-of-competition-protection/" target="_blank">Framework of Competition Protection</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Competition: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.itu.int/en/ITU-D/Cybersecurity/Documents/National_Strategies_Repository/EgyptNational%20Cybersecurity%20Strategy-English%20version-18%20Nov%202018.pdf" target="_blank">National Cybersecurity Strategy 2018</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true, 
        Communication: true, 
        DigitalSignatures: true, 
        Cybersecurity: true, 
        DigitalIdentification: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://cybercrime-fr.org/wp-content/uploads/2020/04/Egyptian-cybercrime-law-.pdf" target="_blank">Anti-Cyber and Information Technology Crimes Law (Law No175 of 2018)</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/en/ICT_Strategy" target="_blank">Egypt ICT 2030 Strategy</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        DigitalIdentification: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        Ecommerce: true, 
        Payments: true, 
        EgovernmentServices: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://idsc.gov.eg/Upload/DocumentLibrary/Attachment_A/4798/3%20-%20EGYPT%20DIGITALIZATION.pdf" target="_blank">Egypt Digitalization</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Ecommerce: true,
        Cybersecurity: true,
        IntermediateSkills: true,
        HighlySpecializedSkills: true,
        CloudPolicy: true,
        Payments: true,
        SMARTCities: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/ICT%20Strategy%202012-2017.pdf" target="_blank">Egypt ICT Strategy 2014-2017</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        DigitalIdentification: true, 
        Ecommerce: true, 
        Payments: true, 
        EgovernmentServices: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true, 
        PostalServices: true, 
        Broadband: true, 
        TechHubs: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/Egypt-ICT-Strategy.pdf" target="_blank">Egypt ICT Strategy 2007-2010</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        EgovernmentServices: true,
        IntermediateSkills: true,
        HighlySpecializedSkills: true,
        Cable: true,
        PostalServices: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/Upcont/Documents/EgyInfo_Society.pdf" target="_blank">Egypt Information Society 2005</a></span>',
        date: '01/01/2005',
        date_text: "2005",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        DigitalMarketRegulation: true, 
        FoundationalSkills: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true, 
        DigitalSignatures: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.mcit.gov.eg/en/ICT_Industry_Development/Support_Services/Signature#:~:text=The%20e%2DSignature%20Law%2C%20issued,when%20performing%20an%20online%20transaction" target="_blank">Electronic Signature Law No. 15 of 2004.</a></span>',
        date: '01/01/2004',
        date_text: "2004",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        EgovernmentServices: true,
        Ecommerce: true,
        DigitalSignatures: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://mcit.gov.eg/en/Publication/Publication_Summary/77" target="_blank">Egypt Vision of the Information Society 2003</a></span>',
        date: '01/01/2003',
        date_text: "2003",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Affordability: true,
        UniversalAccess: true,
        InternetPenetration: true,
        Broadband: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/en/rules/law-no-10-of-2003/#:~:text=It%20is%20prohibited%20to%20establish,Law%20and%20its%20executive%20resolutions." target="_blank">Egypt Telecommunication Regulation Law</a></span>',
        date: '01/01/2003',
        date_text: "2003",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        UniversalAccess: true, 
        Spectrum: true, 
        License: true, 
        Communication: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/en/regulations/regulatory-framework/the-regulatory-framework-for-the-provision-of-mobile-value-added-services/" target="_blank">Regulatory Framework for Provision of MVAS</a></span>',
        date: '01/01/2003',
        date_text: "2003",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EndUserSubscriberService: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/en/regulations/regulatory-framework/mobile-sim-cards-sales-regulations-for-individuals/" target="_blank">Mobile SIM Card Sales Regulations for Individuals</a></span>',
        date: '',
        date_text: "",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        SimCards: true
    },
    {
        arrayItem: true,
        country: 'Egypt',
        reg_name: '<span class="reg-name"><a href="https://www.tra.gov.eg/en/regulations/regulatory-framework/mobile-sim-cards-sales-regulations-for-corporate-accounts/" target="_blank">Mobile SIM Card Sales Regulations for Corporate Users</a></span>',
        date: '',
        date_text: "",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        SimCards: true
    },
    {
        arrayItem: true,
        row: 219,
        country: 'Eswatini',
        reg_name: '<span class="reg-name"><a href="https://www.esccom.org.sz/legislation/DATA%20PROTECTION%20ACT.pdf" target="_blank">Data Protection Act</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        Cybersecurity: true 
    }, 
    {
        arrayItem: true,
        row: 220,
        country: 'Eswatini',
        reg_name: '<span class="reg-name"><a href="https://www.esccom.org.sz/legislation/COMPUTER%20CRIME%20&%20CYBERCRIME%20ACT.pdf" target="_blank">The Computer Crime and Cybercrime Act</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
        TypeApproval: true,
        EndUserEquipment: true 
    }, 
    { 
        arrayItem: true,
        row: 221,
        country: 'Eswatini',
        reg_name: '<span class="reg-name"><a href="https://esccom.org.sz/publications/reports/docs/Eswatini_National_Cybersecurity_Strategy_2022-2027.pdf" target="_blank">Eswatini National Cybersecurity Strategy (2022-2027)</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 222,
        country: 'Eswatini',
        reg_name: '<span class="reg-name"><a href="https://www.esccom.org.sz/legislation/SwazilandCommunicationsCommissionAct.pdf" target="_blank">Swaziland  Communications Commission Act</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadcasting: true 
    }, 
    {
        arrayItem: true,
        row: 223,
        country: 'Eswatini',
        reg_name: '<span class="reg-name"><a href="https://www.esccom.org.sz/legislation/SwazilandElectronicCommunicationsAct.pdf" target="_blank">Electronic Communications Act</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        entrepreneurship: true,
        invisibleInfrastructure: true,
        innovationHubs: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        ServiceQuality: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 224,
        country: 'Eswatini',
        reg_name: '<span class="reg-name"><a href="https://www.esccom.org.sz/legislation/ELECTRONIC%20COMMUNICATIONS%20TRANSACTIONS%20ACT.pdf" target="_blank">Electronic Communications and Transactions Act</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        entrepreneurship: true,
        invisibleInfrastructure: true,
        innovationHubs: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true 
    }, 
    {
        arrayItem: true,
        row: 225,
        country: 'Eswatini',
        reg_name: '<span class="reg-name"><a href="https://www.esccom.org.sz/legislation/The%20Eswatini%20Broadcasting%20Act%202023.pdf" target="_blank">Broadcasting Act</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadcasting: true 
    }, 
    {
        arrayItem: true,
        row: 55,
        country: 'Gabon',
        reg_name: '<span class="reg-name"><a href="https://journal-officiel.ga/5431-00000013-pr-2018-/" target="_blank">Regulation of Electronic Communications In The Gabonese Republic</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadcasting: true,
        LastMile: true
    },
    {
        arrayItem: true,
        row: 56,
        country: 'Gabon',
        reg_name: '<span class="reg-name"><a href="https://journal-officiel.ga/18189-030-cc-/" target="_blank">Regulation Of Electronic Transactions In The Gabonese Republic</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true
    },
    {
        arrayItem: true,
        row: 57,
        country: 'Gabon',
        reg_name: '<span class="reg-name"><a href="https://journal-officiel.ga/20085-025-2023-/" target="_blank">Protection of Personal Data</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Law',
        digitalIPlatform: true,
        digitalGovernment: true,
        financialServices: true,
        basic: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        row: 58,
        country: 'Gabon',
        reg_name: '<span class="reg-name"><a href="https://journal-officiel.ga/20087-027-2023-/" target="_blank">Regulation Of Cybersecurity And The Fight Against Cybercrime In The Gabonese Republic</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Law',
        digitalPlatform: true,
        digitalSkills: true,
        digitalGovernment: true,
        financialServices: true,
        basic: true,
        advanced: true,
        innovationHubs: true,
        digitalEntrepreneurship: true,
        DataProtection: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        row: 59,
        country: 'Gabon',
        reg_name: '<span class="reg-name"><a href="https://journal-officiel.ga/3780-18-pr-2015/" target="_blank">Conditions And Modalities Of Exercising Freedom Of Communication In The Gabonese Republic</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalPlatform: true,
        digitalBusiness: true,
        ServiceQuality: true,
        ConsumerProtection: true,
        License: true
    },
    {
        arrayItem: true,
        row: 60,
        country: 'Gabon',
        reg_name: '<span class="reg-name"><a href="https://journal-officiel.ga/3665-000544-mtac-sgacc/" target="_blank">Conditions Of Use Of The Global Navigation Satellite System And Installation Of Global Positioning System Equipment On Board Aircraft Operating In The Airspace Of The Gabonese Republic</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        FirstMile: true
    },
    {
        arrayItem: true, 
        row: 61, 
        country: 'Gabon', 
        reg_name: '<span class="reg-name"><a href="https://journal-officiel.ga/5055-026-2018-/" target="_blank">Regulation of Electronic Communications In The Gabonese Republic</a></span>', 
        date: '01/01/2019', 
        date_text: "2019", 
        classification: 'Law', 
        digitalPlatform: true, 
        digitalSkills: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        financialServices: true, 
        basic: true, 
        Broadcasting: true 
    }, 
    { 
        arrayItem: true, 
        row: 96, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155949/https://moici.gov.gm/sites/default/files/2021-08/Universal%20Access%20%26%20Service%20Policy%202020.pdf" target="_blank">Gambia Universal Access and Service Policy - Succeeded by the 2020 National Broadband Internet Policy</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalBusiness: true, 
        financialServices: true, 
        UniversalService: true
    }, 
    { 
        arrayItem: true, 
        row: 97, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://www.ellipsis.co.za/wp-content/uploads/2016/07/Gambia.pdf" target="_blank">National Information and Communication Infrastructure Policy (NICI II) Policy Statement and Action Plan (2017- 2025</a></span>', 
        date: '01/01/2017', 
        date_text: "2017", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Broadcasting: true 
    }, 
    { 
        arrayItem: true, 
        row: 98, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/1pS7fH5vJ2oaq0zF5d3MM_ygWJtM__nKI/view?usp=sharing" target="_blank">The Gambia ICT4D Policy Statement 2018-2028</a></span>', 
        date: '01/01/2018', 
        date_text: "2018", 
        classification: 'Policy”', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        ArtificialIntelligence: true, 
        Broadband: true, 
        Broadcasting: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Ecommerce: true, 
        ElectronicWaste: true, 
        FirstMile: true, 
        SecondMile: true, 
        LastMile: true, 
        InternetResources: true, 
        ServiceQuality: true, 
        SMARTCities: true, 
        Spectrum: true, 
        TypeApproval: true, 
        EndUserEquipment: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 99, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2023/11/Gambia-G-Cloud-Policy_Final-Draft.pdf" target="_blank">Critical Information Infrastructure Protection (CIIP) Policy Framework</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        entrepreneurship: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        financialServices: true, 
        innovationHubs: true, 
        digitalEntrepreneurship: true, 
        ServiceQuality: true, 
        ConsumerProtection: true, 
        License: true 
    }, 
    { 
        arrayItem: true, 
        row: 100, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155949/https://moici.gov.gm/sites/default/files/2021-08/Universal%20Access%20%26%20Service%20Policy%202020.pdf" target="_blank">Universal Access and Service Policy 2020</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        entrepreneurship: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        financialServices: true, 
        innovationHubs: true, 
        digitalEntrepreneurship: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 101, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630160002/https://moici.gov.gm/sites/default/files/2021-09/Data%20Protection%20and%20Privacy%20Policy%20and%20Strategy%20August%202019%20Final.pdf" target="_blank">Data Protection And Privacy 2019</a></span>', 
        date: '01/01/2019', 
        date_text: "2019", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        invisibleInfrastructure: true, 
        DataProtection: true, 
        DigitalSignatures: true, 
        Ecommerce: true 
    }, 
    { 
        arrayItem: true, 
        row: 102, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://gmcsirt.gm/wp-content/uploads/2021/12/FINAL-POST-Validation-CYBERSECURITY-POLICY-20201.pdf" target="_blank">Cybersecurity Policy 2020</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalPlatform: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        financialServices: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalSignatures: true, 
        Ecommerce: true 
    }, 
    { 
        arrayItem: true, 
        row: 103, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/1pS7fH5vJ2oaq0zF5d3MM_ygWJtM__nKI/view?usp=sharing" target="_blank">The Gambia ICT for Development (ICT4D) Policy Statement (2018-2028)</a></span>', 
        date: '01/01/2018', 
        date_text: "2018", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        digitalSkills: true, 
        entrepreneurship: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        financialServices: true, 
        basic: true, 
        advanced: true, 
        innovationHubs: true, 
        ArtificialIntelligence: true, 
        Broadband: true, 
        Broadcasting: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Ecommerce: true, 
        ElectronicWaste: true, 
        FirstMile: true, 
        SecondMile: true, 
        LastMile: true, 
        InternetResources: true, 
        ServiceQuality: true, 
        SMARTCities: true, 
        Spectrum: true, 
        TypeApproval: true, 
        EndUserEquipment: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 104, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155914/https://moici.gov.gm/sites/default/files/2021-05/Broadband Internet%20POLICY%202024.pdf" target="_blank">National Broadband Internet Policy 2020 - 2024</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        Broadband: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 105, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/1G5f0Tcbcjj-t0uMIBQPXV7JSJG0wmL9g/view?usp=sharing" target="_blank">National Information & Communication Infrastructure (NICI) Policy and Plans for The Gambia (First Sector Policy)</a></span>', 
        date: '01/01/2004', 
        date_text: "2004", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DataCentersandCloudServices: true, 
        Broadband: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 106, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2023/10/Final-Government-Email-Policy.pdf" target="_blank">Government Email Policy 2023 - 2027</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true,
        DataCentersandCloudServices: true, 
        Broadband: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 107, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2023/11/Gambia-G-Cloud-Policy_Final-Draft.pdf" target="_blank">Government Cloud Policy 2023</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true,
        DataCentersandCloudServices: true, 
        Broadband: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 108, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2023/11/Gambia-G-Cloud-Policy_Final-Draft.pdf" target="_blank">National Fiber Protection Policy 2023</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Broadband: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 109, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2024/04/Final-National-Digital-Addressing-Policy-20231.pdf" target="_blank">National Digital Addressing Policy  2023</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DigitalIdentification: true, 
        Ecommerce: true, 
        SMARTCities: true, 
        DataProtection: true, 
        UniversalAccess: true, 
        ServiceQuality: true, 
        DigitalPayments: true 
    }, 
    { 
        arrayItem: true, 
        row: 110, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155923/https://moici.gov.gm/sites/default/files/2021-05/E-GOVERNMENT%20STRATEGY.pdf" target="_blank">The Gambia E-Government Implementation Strategy and Action Plan</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Ecommerce: true, 
        DigitalIdentification: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 111, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/18xDEXqMfF4bdqm8UoGAcwW81aJA5iqRF/view?usp=sharing" target="_blank">Gambia National Cybersecurity Strategy -  2020-2024</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true 
    },
    { 
        arrayItem: true, 
        row: 112, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155914/https://moici.gov.gm/sites/default/files/2021-05/Broadband Internet%20POLICY%202024.pdf" target="_blank">The Gambia Broadband Internet Policy 2020 and National Broadband Internet Strategic Plan 2020-2024</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Broadband: true, 
        LastMile: true 
    },
    { 
        arrayItem: true, 
        row: 113, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155923/https://moici.gov.gm/sites/default/files/2021-05/E-GOVERNMENT%20STRATEGY.pdf" target="_blank">Gambia E-Government Strategic Plan 2020-2024</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        digitalBusiness: true, 
        Ecommerce: true, 
        DigitalIdentification: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 114, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://gmcsirt.gm/wp-content/uploads/2021/12/Data-Protection-and-Privacy-Policy-and-Strategy-August-2019-Final.pdf" target="_blank">Data Protection and Privacy Policy and Strategy</a></span>', 
        date: '01/01/2019', 
        date_text: "2019", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Broadband: true, 
        LastMile: true
    }, 
    { 
        arrayItem: true, 
        row: 115, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155919/https://moici.gov.gm/sites/default/files/2021-05/Broadband Internet%20STRATEGY%202024.pdf" target="_blank">Broadband Internet Strategic Plan 2020–2024</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Broadband: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 116, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155923/https://moici.gov.gm/sites/default/files/2021-05/E-GOVERNMENT%20STRATEGY.pdf" target="_blank">The Gambia’s E-Government Strategic Plan 2020–2024</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Broadband: true,	 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 117, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/1pS7fH5vJ2oaq0zF5d3MM_ygWJtM__nKI/view?usp=sharing" target="_blank">ICT4D Policy Statement 2018–2028</a></span>', 
        date: '01/01/2018', 
        date_text: "2018", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        ArtificialIntelligence: true, 
        Broadband: true, 
        Broadcasting: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Drones: true, 
        Ecommerce: true, 
        ElectronicWaste: true, 
        FirstMile: true, 
        SecondMile: true, 
        LastMile: true, 
        InternetResources: true, 
        PostalServices: true, 
        ServiceQuality: true, 
        SMARTCities: true, 
        Spectrum: true, 
        TypeApproval: true, 
        EndUserEquipment: true, 
        UniversalAccess: true
    }, 
    { 
        arrayItem: true, 
        row: 118, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155919/https://moici.gov.gm/sites/default/files/2021-05/Broadband Internet%20STRATEGY%202024.pdf" target="_blank">National Broadband Internet Strategy 2020-2024</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Broadband: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 119, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155932/https://moici.gov.gm/sites/default/files/2021-05/PRIVATE%20SECTOR%20DEVELOPMENT%20AND%20EMPOWERMENT%20STRATEGY%20-%202024%20%20Through%20ICTs.pdf" target="_blank">Private Sector Development and Empowerment Strategy 2021-2024</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Broadband: true, 
        Ecommerce: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 120, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155935/https://moici.gov.gm/sites/default/files/2021-05/Promoting%20Technology-Driven%20Social%20and%20Community%20Services%20Provision%20%26%20Delivery.pdf" target="_blank">The Strategy For Promoting Technology-Driven Social And Community Services Provision & Delivery</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        ServiceQuality: true, 
        ConsumerProtection: true, 
        License: true 
    }, 
    { 
        arrayItem: true, 
        row: 121, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155938/https://moici.gov.gm/sites/default/files/2021-05/Technology-Enabled%20Education%20AND%20Science%20Technology%20and%20Innovation%20Strategy.pdf" target="_blank">Strategy For Promoting Technology-Enabled Education (TEE) & Science, Technology And Innovation (STI) 2021-2024</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DataCentersandCloudServices: true, 
        UniversalAccess: true
    }, 
    { 
        arrayItem: true, 
        row: 122, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155942/https://moici.gov.gm/sites/default/files/2021-05/YOUTH%20AND%20WOMEN%20DEVELOPMENT%20AND%20EMPOWERMENT%20Through%20ICTs.pdf" target="_blank">Strategy For Youth and Women Development and Empowerment Through ICTs 2021-2024</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DigitalIdentification: true, 
        ServiceQuality: true, 
        UniversalAccess: true, 
        Ecommerce: true 
    }, 
    { 
        arrayItem: true, 
        row: 123, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155910/https://moici.gov.gm/sites/default/files/2021-05/Agricultural%20Development%20and%20Modernization%20Through%20ICTs.pdf" target="_blank">The E-Agriculture Development and Modernization Strategy (E-ADM) 2021-2024</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Ecommerce: true, 
        DigitalIdentification: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 124, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://repository.uneca.org/ds2/stream/?#/documents/f14ed4b8-a542-5b5a-b386-8e1255e1e2f9/page/2" target="_blank">E-Government Strategy 2003</a></span>', 
        date: '01/01/2003', 
        date_text: "2003", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DigitalIdentification: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 125, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630155928/https://moici.gov.gm/sites/default/files/2021-05/Human%20Capital%20Development%20strategy-%20%202024%20Through%20ICTs.pdf" target="_blank">Human Capital Development Strategy 2021-2024</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        ArtificialIntelligence: true, 
        Broadband: true, 
        Broadcasting: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Drones: true, 
        Ecommerce: true, 
        ElectronicWaste: true, 
        FirstMile: true, 
        SecondMile: true, 
        LastMile: true, 
        InternetResources: true, 
        PostalServices: true, 
        ServiceQuality: true, 
        SMARTCities: true, 
        Spectrum: true, 
        TypeApproval: true, 
        EndUserEquipment: true, 
        UniversalAccess: true
    },
    { 
        arrayItem: true, 
        row: 126, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2024/05/National-Digital-Economy-Masterplan.pdf" target="_blank">National Digital Economy Masterplan 2024 - 2034</a></span>', 
        date: '01/01/2024', 
        date_text: "2024", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        ArtificialIntelligence: true, 
        Broadband: true, 
        Broadcasting: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Drones: true, 
        Ecommerce: true, 
        ElectronicWaste: true, 
        FirstMile: true, 
        SecondMile: true, 
        LastMile: true, 
        InternetResources: true, 
        PostalServices: true, 
        ServiceQuality: true, 
        SMARTCities: true, 
        Spectrum: true, 
        TypeApproval: true, 
        EndUserEquipment: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 127, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2023/11/Gambia-G-Cloud-Strategy-document_Final-Draft.pdf" target="_blank">Government Cloud Strategy 2023</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DigitalIdentification: true, 
        DataCentersandCloudServices: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 128, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2023/11/Gambia-G-cloud-Strategy-_-Implmentation-Plan-_Final-Draft.pdf" target="_blank">Government Cloud Implementation Plan 2023</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DigitalIdentification: true, 
        DataCentersandCloudServices: true, 
        ServiceQuality: true, 
        UniversalAccess: true
    }, 
    { 
        arrayItem: true, 
        row: 129, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2024/04/Final-Government-Open-Data-Strategy-2024-2027.pdf" target="_blank">Government Open Data Strategy 2024 -2027</a></span>', 
        date: '01/01/2024', 
        date_text: "2024", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DigitalIdentification: true, 
        DataCentersandCloudServices: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 130, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2023/12/Digital-ID-Transformation-Strategy-_Gambia-V_92.pdf" target="_blank">National Digital ID Strategy 2024</a></span>', 
        date: '01/01/2024', 
        date_text: "2024", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DigitalIdentification: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 131, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/02/Pura-Act-2001.pdf" target="_blank">The Gambia Public Utilities Regulatory Authority Act</a></span>', 
        date: '01/01/2001', 
        date_text: "2001", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Ecommerce: true 
    }, 
    { 
        arrayItem: true, 
        row: 132, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/02/IC-Info-Comms-Act-2009.pdf" target="_blank">Information and Communications Act (IC Act of 2009)</a></span>', 
        date: '01/01/2009', 
        date_text: "2009", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        Broadcasting: true 
    }, 
    { 
        arrayItem: true, 
        row: 133, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://gcc.gm/wp-content/uploads/2018/05/GAMBIA-CONSUMER-PROTECTION-ACT-2014.pdf" target="_blank">Consumer Protection Act</a></span>', 
        date: '01/01/2014', 
        date_text: "2014", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Ecommerce: true, 
        LastMile: true, 
        InternetResources: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 134, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://mocde.gov.gm/wp-content/uploads/2023/12/Digital-ID-Transformation-Strategy-_Gambia-V_92.pdf" target="_blank">The Gambia Information & Communication Technology Agency (GICTA) Act</a></span>', 
        date: '01/01/2019', 
        date_text: "2019", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Broadcasting: true, 
        Broadband: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 135, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://gmcsirt.gm/wp-content/uploads/2021/12/DRAFT-CYBER-CRIME-BILL-2019-.pdf" target="_blank">Cybercrime Bill</a></span>', 
        date: '01/01/2019', 
        date_text: "2019", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true 
    }, 
    { 
        arrayItem: true, 
        row: 136, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/1F9sIJU2GXPu1oPQkXjBu9Yh7l3AqyLJf/view?usp=sharing" target="_blank">National Data Protection and Privacy Bill</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true 
    }, 
    { 
        arrayItem: true, 
        row: 137, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://web.archive.org/web/20220630160005/https://moici.gov.gm/sites/default/files/2022-02/ACCESS%20TO%20INFORMATION%20ACT%2C2021.pdf" target="_blank">Access to Information Act</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        ArtificialIntelligence: true, 
        Broadband: true, 
        Broadcasting: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Drones: true, 
        Ecommerce: true, 
        ElectronicWaste: true, 
        FirstMile: true, 
        SecondMile: true, 
        LastMile: true, 
        InternetResources: true, 
        PostalServices: true, 
        ServiceQuality: true, 
        Spectrum: true, 
        TypeApproval: true, 
        EndUserEquipment: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 138, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://pura.gm/ict/regulatory-instruments/regulation-guidelines/" target="_blank">Registration of Telephone Subscribers</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        ArtificialIntelligence: true, 
        Broadband: true, 
        Broadcasting: true, 
        Cybersecurity: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalPayments: true, 
        DigitalSignatures: true, 
        Drones: true, 
        Ecommerce: true, 
        ElectronicWaste: true, 
        FirstMile: true, 
        SecondMile: true, 
        LastMile: true, 
        InternetResources: true, 
        PostalServices: true, 
        ServiceQuality: true, 
        Spectrum: true, 
        TypeApproval: true, 
        EndUserEquipment: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 139, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/NATIONAL-NUMBERING-PLAN-REGULATIONS-201.pdf" target="_blank">National Numbering Plan Regulation 2017</a></span>', 
        date: '01/01/2017', 
        date_text: "2017", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        ShortCodes: true, 
        NumberResources: true, 
        CustomerDialing: true, 
        SignalingPointCodes: true, 
        NetworkCodes: true 
    }, 
    { 
        arrayItem: true, 
        row: 140, 
        country: 'Gambia', 
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/1I_BzB0X03i3zo5MWILU1EcMzRFOwQtTx/view?usp=sharing" target="_blank">Digital Literacy Development Feasibility Report</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        FirstMile: true, 
        SecondMile: true, 
        LastMile: true, 
        InternetResources: true, 
        PostalServices: true, 
        ServiceQuality: true, 
        SMARTCities: true, 
        Spectrum: true, 
        TypeApproval: true, 
        EndUserEquipment: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true,
        row: 141,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2022/06/PURA-QoS-Guidelines-2022.pdf" target="_blank">Quality of Service Guidelines for Mobile Service Providers</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        LastMile: true,
        ServiceQuality: true 
    }, 
    {
        arrayItem: true,
        row: 142,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines_Spectrum-Application-Form-Radio-Comm-Service.pdf" target="_blank">Application For Radiocommunication Services (Fixed And Mobile Station)</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 143,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines-for-the-application-of-a-VSAT-2.pdf" target="_blank">Guidelines And Application Form To Operate And Install A VSAT</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        VSAT: true 
    }, 
    {
        arrayItem: true,
        row: 144,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines_for_short_codes.pdf" target="_blank">Guidelines For Short Code Applications</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Regulation',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        ShortCodes: true 
    }, 
    {
        arrayItem: true,
        row: 145,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines_for_Number-block-application.pdf" target="_blank">Guidelines And Application Forms For Numbers Blocks And Short Codes</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        NumberBlocks: true,
        ShortCodes: true 
    }, 
    {
        arrayItem: true,
        row: 146,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/GUIDELINES-FOR-INTERCONNECTION.pdf" target="_blank">Guidelines For Interconnection</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        Broadband: true,
        LastMile: true,
        SecondMile: true,
        InternetResources: true,
        ServiceQuality: true,
        TypeApproval: true,
        EndUserEquipment: true 
    }, 
    {
        arrayItem: true,
        row: 147,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines-for-3rd-Party-service.pdf" target="_blank">Guidelines For Deploying A New Technologies</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        DataCentersandCloudServices: true,
        DigitalPayments: true,
        LastMile: true,
        ServiceQuality: true,
        TypeApproval: true,
        EndUserEquipment: true 
    }, 
    {
        arrayItem: true,
        row: 148,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines-for-3rd-Party-service.pdf" target="_blank">Guidelines For Deployment Of Third Party Services</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        ArtificialIntelligence: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Drones: true,
        Ecommerce: true,
        ElectronicWaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        PostalServices: true,
        ServiceQuality: true,
        SMARTCities: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 149,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines-on-Cross-Ownership-Broadcasting_compressed.pdf" target="_blank">Guidelines Of Ownership And Cross- Ownership In The Broadcasting Sector</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        Broadcasting: true,
        MediaOwnership: true,
        OwnershipControlReg: true,
        MergersandAcquisitions: true 
    }, 
    {
        arrayItem: true,
        row: 150,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines_for_TV-application_Signal_distributor_compressed.pdf" target="_blank">Guidelines And Application Form To Obtain Digital Terrestrial Television Broadcasting License As Signal Distributor</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        Broadcasting: true,
        DigitalIdentification: true,
        ServiceQuality: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 151,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines_for_Private_mobile_radio_compressed.pdf" target="_blank">Guidelines For Private Mobile Radio Frequency</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        TypeApproval: true,
        EndUserEquipment: true,
        ServiceQuality: true,
        RadioSpectrumManagement: true 
    }, 
    {
        arrayItem: true,
        row: 152,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Guidelines-for-FM-Broadcast-Standards-Max-Edit-19-02-2014_compressed.pdf" target="_blank">Technical Guidelines For FM Broadcast Standards</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        Broadcasting: true,
        ServiceQuality: true,
        TypeApproval: true,
        EndUserEquipment: true 
    }, 
    {
        arrayItem: true,
        row: 153,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://pura.gm/wp-content/uploads/2021/09/Code_of_conduct_Guidelines_compressed.pdf" target="_blank">Code Of Conduct Guidelines For The Television Broadcasting Sector</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        Broadcasting: true,
        ServiceQuality: true,
        HumanRights: true	 
    }, 
    {
        arrayItem: true,
        row: 154,
        country: 'Gambia',
        reg_name: '<span class="reg-name"><a href="https://www.ellipsis.co.za/wp-content/uploads/2016/07/Gambia.pdf" target="_blank">The Gambian ICT4D-2012 Plan</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        EmergingTechnologies: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DigitalIdentification: true,
        Ecommerce: true,
        UniversalAccess: true,
        ServiceQuality: true,
        DigitalPayments: true 
    }, 
    {
        arrayItem: true, 
        row: 265, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nca.org.gh/wp-content/uploads/2020/09/National-Telecommunications-Policy-NTP-2005.pdf" target="_blank">National Telecommunications Policy</a></span>', 
        date: '01/01/2004', 
        date_text: "2004",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        BroadbandInternet: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        QualityofService: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 266, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://csa.gov.gh/resources/National%20Cybersecurity%20Policy%20and%20Strategy.pdf" target="_blank">National Broadband Internet Policy and Implementation Strategy</a></span>', 
        date: '01/01/2012', 
        date_text: "2012",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        BroadbandInternet: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
    },
    {
        arrayItem: true, 
        row: 267, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nca.org.gh/wp-content/uploads/2020/09/Mobile-Number-Portability-Regulations-2011-L.I.-1994-31st-May-2011.pdf" target="_blank">Mobile Number Portability Regulations</a></span>', 
        date: '01/01/2011', 
        date_text: "2011",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        MobileNumberPortability: true,
    },
    {
        arrayItem: true, 
        row: 268, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nca.org.gh/wp-content/uploads/2020/09/Subscriber-Identity-Module-Registration-Regulations-2011-L.I.-2006-24th-Nov-2011.pdf" target="_blank">Subscriber Identity Module (SIM) Registration Regulations</a></span>', 
        date: '01/01/2011', 
        date_text: "2011",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        SimRegistration: true
    },
    {
        arrayItem: true, 
        row: 269, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://csa.gov.gh/resources/National%20Cybersecurity%20Policy%20and%20Strategy.pdf" target="_blank">National Cyber Security Policy & Strategy</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
    },
    {
        arrayItem: true, 
        row: 270, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nita.gov.gh/theevooc/2023/07/LI-FOR-ACT-772_NITA-.pdf" target="_blank">Electronic Transactions Regulations (Act 772)</a></span>', 
        date: '01/01/2019', 
        date_text: "2019",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalIdentification: true,
        ElectronicTransactions: true,
    },
    {
        arrayItem: true, 
        row: 271, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nita.gov.gh/theevooc/2017/12/National-Information-Technology-Agency-Act-771.pdf" target="_blank">National Information Technology Agency Act (Act 771)</a></span>', 
        date: '01/01/2008', 
        date_text: "2008",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        BroadbandInternet: true,
        BroadcastingandMultimediaServices: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Drones: true,
        Ecommerce: true,
        Ewaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        PostalServices: true,
        QualityofService: true,
        SmartCities: true,
        SpectrumandTVWhiteSpaces: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 272, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nita.gov.gh/theevooc/2017/12/Electronic-Communications-Act-775.pdf" target="_blank">Electronic Communications Act (Act 775)</a></span>', 
        date: '01/01/2008', 
        date_text: "2008",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        QualityofService: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 273, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nita.gov.gh/theevooc/2017/12/Ghana-National-Communications-Authority-Act-769.pdf" target="_blank">National Communications Authority Act (Act 769)</a></span>', 
        date: '01/01/2008', 
        date_text: "2008",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        TypeApproval: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 274, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nita.gov.gh/theevooc/2017/12/Data-Protection-Act-2012-Act-843.pdf" target="_blank">Data Protection Act</a></span>', 
        date: '01/01/2012', 
        date_text: "2012",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        DigitalIdentification: true,
    },
    {
        arrayItem: true, 
        row: 275, 
        country: 'Ghana', 
        reg_name: '<span class="reg-name"><a href="https://nita.gov.gh/theevooc/2017/12/Ghana-ICT4AD-Policy.pdf" target="_blank">The Ghana ICT for Accelerated Development (ICT4D) Policy</a></span>', 
        date: '01/01/2003', 
        date_text: "2003",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true,
        row: 195,
        country: 'Guinea_Bissau',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1QKbkzF8rQY85WpJxd6wzw8gQRoF86J2q" target="_blank">Basic Law of Information and Communication Technologies</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        ServiceQuality: true 
    }, 
    {
        arrayItem: true, 
        row: 62, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/CA/Statutes%20and%20Regulations/National-ICT-Policy-Guidelines-2020.pdf" target="_blank">The National Information Communication and Technology (ICT) Policy Guidelines 2020</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        DataProtection: true, 
        Cybersecurity: true, 
        ConsumerProtection: true 
    }, 
    { 
        arrayItem: true, 
        row: 63, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2024-02/Amateur%20Radio%20Licensing%20Guidelines%202023.pdf" target="_blank">Amateur Radio Licensing Guidelines 2023</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        RadioLicensing: true 
    }, 
    {
        arrayItem: true, 
        row: 64, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-on-the-Use-of-Radiofrequency-Spectrum-by-Short-Range-Devices-2022.pdf" target="_blank">Guidelines on the Use of Radiofrequency Spectrum by Short-Range Devices 2022</a></span>', 
        date: '01/01/2022', 
        date_text: "2022", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        invisibleInfrastructure: true, 
        physicalInfrastructure: true, 
        RadioFrequency: true, 
        ServiceQuality: true 
    }, 
    { 
        arrayItem: true, 
        row: 65, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Consumer-Protection-Guidelines-and-Customer-Care-Standards.pdf" target="_blank">Consumer Protection Guidelines and Customer Care Standards</a></span>', 
        date: '01/01/2022', 
        date_text: "2022", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        ServiceQuality: true, 
        ConsumerProtection: true 
    }, 
    { 
        arrayItem: true, 
        row: 66, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Procedures-and-Guidelines-for-the-Management-and-Administration-of-Numbering-Resources-July-2020-min.pdf" target="_blank">Procedures and Guidelines for the Management and Administration of Numbering Resources July 2020</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        InternetResources: true 
    }, 
    {
        arrayItem: true, 
        row: 67, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Frequency-Spectrum-Management-Guidelines-2020.pdf" target="_blank">Frequency Spectrum Management Guidelines 2020</a></span>', 
        date: '01/01/2020', 
        date_text: "2020", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        InternetResources: true 
    }, 
    { 
        arrayItem: true, 
        row: 68, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/IPv4-to-IPv6-Migration-Strategy-.pdf" target="_blank">IPv4 to IPv6 Migration Strategy</a></span>', 
        date: '01/01/2022', 
        date_text: "2022", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        InternetResources: true, 
        InternetProtocol: true 
    }, 
    { 
        arrayItem: true, 
        row: 69, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-on-the-Minimum-Features-and-Technical-Specifications-for-Mobile-Cellular-Devices-Imported-into-and-Distributed-in-Kenya-June-2018.pdf" target="_blank">Guidelines on the Minimum Features and Technical Specifications for Mobile Cellular Devices Imported into and Distributed in Kenya-June 2018</a></span>', 
        date: '01/01/2018', 
        date_text: "2018", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 70, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-for-the-Provision-of-Mobile-Number-Portability-Services-in-Kenya-2018.pdf" target="_blank">Guidelines for the Provision of Mobile Number Portability Services in Kenya 2018</a></span>', 
        date: '01/01/2018', 
        date_text: "2018", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 71, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-on-the-use-of-Internet-of-Things-IoT-Devices.pdf" target="_blank">Guidelines on the use of Internet of Things (IoT) Devices</a></span>', 
        date: '01/01/2018', 
        date_text: "2018", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        InternetofThings: true, 
        DataProtection: true 
    }, 
    { 
        arrayItem: true, 
        row: 72, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-for-Undertaking-ICT-Infrastructure-Works.pdf" target="_blank">Guidelines for Undertaking ICT Infrastructure Works</a></span>', 
        date: '01/01/2018', 
        date_text: "2018", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        InstallationStandards: true 
    }, 
    { 
        arrayItem: true, 
        row: 73, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-for-Network-RedundancyResilience-and-Diversity-for-ICT-Networks-in-Kenya-1.pdf" target="_blank">Guidelines for Network Redundancy, Resilience and Diversity for ICT Networks in Kenya</a></span>', 
        date: '01/01/2017', 
        date_text: "2017", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Broadband: true, 
        DataCentersandCloudServices: true, 
        ServiceQuality: true, 
        UniversalAccess: true 
    }, 
    { 
        arrayItem: true, 
        row: 74, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Framework-for-Assesment-of-Quality-of-Service-of-Telecommunications-Systems-and-Services-1.pdf" target="_blank">Framework for Assessment of Quality of Service of Telecommunications Systems and Services</a></span>', 
        date: '01/01/2017', 
        date_text: "2017", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Broadcasting: true 
    }, 
    { 
        arrayItem: true, 
        row: 75, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sector-guidelines?page=1" target="_blank">Guidelines For Postal And Courier Licensees On Promotion Of E-Commerce</a></span>', 
        date: '01/01/2015', 
        date_text: "2015", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Ecommerce: true 
    }, 
    { 
        arrayItem: true, 
        row: 76, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-for-Sale-of-Foreign-Sim-Cards-Guidelines-1.pdf" target="_blank">Guidelines for Sale of Foreign Sim Cards</a></span>', 
        date: '01/01/2017', 
        date_text: "2017", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        SIMCards: true 
    }, 
    { 
        arrayItem: true, 
        row: 77, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-For-Postal-And-Courier-Licensees-On-Promotion-Of-E-Commerce-1.pdf" target="_blank">Guidelines For Postal And Courier Licensees On Promotion Of E-Commerce</a></span>', 
        date: '01/01/2015', 
        date_text: "2015", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        PostalServices: true 
    }, 
    { 
        arrayItem: true, 
        row: 78, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-For-Postal-And-Courier-Licensees-On-Promotion-Of-E-Commerce-1.pdf" target="_blank">Guidelines on Prevention of Dissemination of Undesirable Bulk and Premium Rate Political Messages and Political Social Media Content Via Electronic Networks</a></span>', 
        date: '01/01/2017', 
        date_text: "2017", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        SocialMediaPlatforms: true, 
        DataProtection: true 
    }, 
    { 
        arrayItem: true, 
        row: 79, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-For-Postal-And-Courier-Licensees-On-Promotion-Of-E-Commerce-1.pdf" target="_blank">Guidelines on Fixed Wireless Links (FWLs) on Non Protected Basis</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        DataCentersandCloudServices: true, 
        DataProtection: true 
    }, 
    { 
        arrayItem: true, 
        row: 80, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Guidelines-For-Postal-And-Courier-Licensees-On-Promotion-Of-E-Commerce-1.pdf" target="_blank">Guidelines on the Implementation and Provision of VOIP Services</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        DataProtection: true, 
        DigitalIdentification: true, 
        DigitalSignatures: true, 
        VoiceOverInternetProtocol: true 
    }, 
    { 
        arrayItem: true, 
        row: 81, 
        country: 'Kenya', 
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/sites/default/files/2023-06/Procedures-and-Guidelines-for-the-management-of-Telecommunications-Short-Codes-and-Premium-Rate-numbers-in-Kenya-1.pdf" target="_blank">Procedures and Guidelines for the management of Telecommunications Short Codes and Premium Rate numbers in Kenya</a></span>', 
        date: '01/01/2012', 
        date_text: "2012", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Broadcasting: true 
    }, 
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://vision2030.go.ke/" target="_blank">Vision 2030</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalEconomy: true,
        FoundationalSkills: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        TechHubs: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://cms.icta.go.ke/sites/default/files/2022-04/Kenya%20Digital%20Masterplan%202022-2032%20Online%20Version.pdf" target="_blank">The Kenyan National Digital Master Plan (2022-2032)</a></span>',
        date: '01/01/2022',
        date_text: "2022-2032",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        ArtificialIntelligence: true,
        DigitalEconomy: true,
        DigitalIdentification: true,
        DigitalMarketRegulation: true,
        Ecommerce: true,
        EgovernmentServices: true,
        EmergingTechnologies: true,
        FirstMile: true,
        FoundationalSkills: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        LastMile: true,
        MiddleMile: true,
        Spectrum: true,
        Startups: true,
        TechHubs: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.centralbank.go.ke/wp-content/uploads/2022/03/L-.N.-No.-46-Central-Bank-of-Kenya-Digital-Credit-Providers-Regulations-2022.pdf" target="_blank">The Central Bank of Kenya Act</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalFinance: true,
        Ecommerce: true,
        Payments: true,
        PostalServices: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2021/04/Data-Protection-General-regulations.pdf" target="_blank">The Data Protection Act (General) Regulations (2021)</a></span>',
        date: '2021/01/01',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataCenter: true,
        DataProtection: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2021/04/Data-Protection-Registration-of-Data-Controllers-and-Data-Processor-Regulations.pdf" target="_blank">The Data Protection Act (Registration of Data Controllers and Data Processors) Regulations (2021)</a></span>',
        date: '2021/01/01',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProtection: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2021/04/Data-Protection-Compliance-and-Enforcement-Regulations-2021.pdf" target="_blank">The Data Protection Act (Compliance and Enforcement Procedures) Regulations (2021)</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ConsumerProtection: true,
        DataProtection: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.education.go.ke/node/286 " target="_blank">Policy on Information Communication and Technology In Education and Training (2021)</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        AdvancedSkills: true,
        BasicSkills: true,
        Innovation: true,
        FoundationalSkills: true,
        IntermediateSkills: true,
        AdvancedSkills: true
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://kra.go.ke/images/publications/Draft-VAT-Digital-Supply-Regulations-2020---29-05-2020.pdf" target="_blank">The Value Added Tax (Digital Marketplace Supply) Regulations (2020)</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalMarketRegulation: true,
        DigitalTax: true,
        Ecommerce: true,
        FinancialServices: true,
        Payments: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.odpc.go.ke/dpa-act/" target="_blank">The Data Protection Act (2019)</a></span>',
        date: '2019/01/01',
        date_text: "2019",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProtection: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ict.go.ke/wp-content/uploads/2019/12/NATIONAL-ICT-POLICY-2019.pdf" target="_blank">National ICT Policy Guidelines (2019)</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        ArtificialIntelligence: true,
        DigitalIdentification: true,
        DigitalMarketRegulation: true,
        Ecommerce: true,
        EgovernmentServices: true,
        FirstMile: true,
        FoundationalSkills: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        LastMile: true,
        MiddleMile: true,
        Payments: true,
        Cybersecurity: true,
        Spectrum: true,
        Startups: true,
        TechHubs: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ict.go.ke/wp-content/uploads/2019/05/Kenya-Digital-Economy-2019.pdf" target="_blank">Kenya Digital Economy Blueprint</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        ArtificialIntelligence: true,
        DigitalEconomy: true,
        Ecommerce: true,
        EmergingTechnologies: true,
        Innovation: true,
        Payments: true,
        PersonalData: true,
        Startups: true,
        TechHubs: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ict.go.ke/blockchain.pdf" target="_blank">Emerging Technologies for Kenya</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        ArtificialIntelligence: true,
        Blockchain: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ict.go.ke/wp-content/uploads/2019/05/National-Broadband-Strategy-2023-FINAL.pdf" target="_blank">National Broadband Strategy (2018-2023)</a></span>',
        date: '01/01/2018',
        date_text: "2018-2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EndUserEquipment: true,
        FirstMile: true,
        Inclusivity: true,
        LastMile: true,
        MiddleMile: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://nc4.go.ke/the-computer-misuse-and-cybercrimes-act/#:~:text=CMCA%20provides%20for%20offences%20relating,matters%2C%20and%20for%20connected%20purposes." target="_blank">Computer Misuse and Cybercrimes Act (2018)</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        Cyberterrorism: true,
        Phishing: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://cms.icta.go.ke//sites/default/files/2021-12/downloads_3.pdf" target="_blank">The Access to Information Act (2016)</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        AccessToInformation: true,
        Openness: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Registration-of-SIM-–Cards-Regulations-2015-1.pdf" target="_blank">The Kenya Information and Communications (Registration of Sim Cards Regulation 2015)</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Registration: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://cms.icta.go.ke//sites/default/files/2021-12/downloads_6.pdf" target="_blank">National Cybersecurity Strategy (2014)</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.kemri.go.ke/wp-content/uploads/2019/11/LN108_2014.pdf\" target="_blank">Science, Technology, and Innovation Act (Research Licensing Regulation) 2014</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Regulation',
        digitalInfrastructure: false,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        Innovation: true,
        License: true,
        ResearchandDeveloment: true,
        Startups: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.centralbank.go.ke/wp-content/uploads/2016/08/The-Money-Remittance-Regulations-2013.pdf" target="_blank">The Money Remittance Regulations (2013)</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Payments: true,
        Remittance: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.nacosti.go.ke/nacosti/Docs/Information%20Centre/Science-Technology-and-Innovation-Act-No.-28-of-20131.pdf" target="_blank">Science, Technology and Innovation Act (2013)</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: false,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        Innovation: true,
        License: true,
        Startups: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/en/document/98xcy43el8v" target="_blank">The Kenya Information and Communications (Compliance Monitoring, Inspections and Enforcement) Regulations, 2010</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Compliance: true,
        ConsumerProtection: true,
        Inspection: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Consumer-Protection-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Consumer Protection) Regulations (2010)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true,
        Communication: true,
        ConsumerProtection: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Dispute-Resolution-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Dispute Resolution) Regulations (2010)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ConsumerProtection: true,
        Numbering: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Electronic-Certification-and-Domain-Name-Administration-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Electronic Certification and Domain Name Administration) Regulations, 2010</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProtection: true,
        Domain: true,
        License: true,
        Registration: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Fair-Competition-and-Equality-of-Treatment-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Fair Competition and Equality of Treatment) Regulations, 2010</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Competition: true,
        License: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Interconnection-and-Provision-of-Fixed-Links-Access-and-Facilities-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Interconnection and Provision of Fixed Links, Access and Facilities) Regulations, 2010</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        IntercityLinks: true,
        Interconnectivity: true,
        MiddleMile: true,
        TerrestialInfrastructure: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Licensing-and-Quality-of-Service-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Licensing and Quality of Service) Regulations (2010)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        ServiceQuality: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Numbering-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Numbering) Regulations (2010)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Communication: true,
        Numbering: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Postal-and-Courier-Services-Regulations-2010-2.pdf" target="_blank">The Kenya Information and Communications (Postal and Courier Services) Regulations (2010)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Communication: true,
        License: true,
        PostalServices: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Radio-Communications-and-Frequency-Spectrum-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Radio Communications and Frequency Spectrum) Regulations, 2010</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Communication: true,
        License: true,
        RadioFrequency: true,
        Spectrum: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Tariff-Regulations-2010.pdf" target="_blank">The Kenya Information and Communications (Tariff) Regulations (2010)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Tariffs: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Universal-Access-and-Services-Regulations-2010-2.pdf" target="_blank">The Kenya Information and Communications (Universal Access and Service) Regulations (2010)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        UniversalAccess: true,
        UniversalServiceFund: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Importation-Type-Approval-and-Distribution-of-Communications-Equipment-Regulations-2010-1.pdf" target="_blank">The Kenya Information and Communications (Importation, Type Approval and Distribution of Communications Equipment) Regulations (2010)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EndUserEquipment: true,
        TypeApproval: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/Broadcasting-Regulations-2009-1.pdf" target="_blank">The Kenya Information and Communications (Broadcasting) Regulations (2009)</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true,
        Communication: true,
        Data: true,
        License: true,
        PostalServices: true,
        RadioFrequency: true,
        UniversalServiceFund: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ict.go.ke/wp-content/uploads/2019/05/KENYA-E-GOVERNMENT-STRATEGY-2004.pdf" target="_blank">E-Government Strategy (2004): The Strategic Framework, Administrative Structure, Training Requirements and Standardization Framework</a></span>',
        date: '01/01/2004',
        date_text: "2004",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalIdentification: true,
        Interoperability: true,
        Standards: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://www.ca.go.ke/wp-content/uploads/2018/02/The-Kenya-Communications-Regulations-2001-1.pdf" target="_blank">The Kenya Communications Regulations (2001)</a></span>',
        date: '01/01/2001',
        date_text: "2001",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        AssignmentofFrequencies: true,
        Competition: true,
        Courier: true,
        Inspection: true,
        IntercityLinks: true,
        Interconnectivity: true,
        License: true,
        Numbering: true,
        PostalServices: true,
        Tariffs: true,
        TypeApproval: true,
    },
    {
        arrayItem: true,
        country: 'Kenya',
        reg_name: '<span class="reg-name"><a href="https://ca.go.ke/wp-content/uploads/2018/02/Kenya-Information-Communications-Act-1.pdf" target="_blank">The Kenya Information and Communications Act</a></span>',
        date: '01/01/1998',
        date_text: "1998 (revised 2020)",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true,
        Communication: true,
        License: true,
        PostalServices: true,
        RadioFrequency: true,
        Spectrum: true,
        UniversalServiceFund: true,
    }, 
    {
        arrayItem: true,
        row: 214,
        country: 'Lesotho',
        reg_name: '<span class="reg-name"><a href="file:///D:/ATU/ATU-DEVELOPMENT/Lesotho_DPA.pdf" target="_blank">Data Protection Act</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        DigitalIdentification: true 
    }, 
    {
        arrayItem: true,
        row: 215,
        country: 'Lesotho',
        reg_name: '<span class="reg-name"><a href="file:///D:/ATU/ATU-DEVELOPMENT/RESEARCH-AND-INNOVATION-POLICY-2022327.pdf" target="_blank">Research and Innovation Policy</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        basic: true,
        advanced: true,
        innovationHubs: true,
        digitalEntrepreneurship: true,
        ArtificialIntelligence: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 216,
        country: 'Lesotho',
        reg_name: '<span class="reg-name"><a href="https://www.gov.ls/download/lesotho-communications-policy-2008/" target="_blank">National Communications Policy</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadcasting: true,
        PostalServices: true,
        Broadband: true 
    }, 
    {
        arrayItem: true,
        row: 217,
        country: 'Lesotho',
        reg_name: '<span class="reg-name"><a href="https://www.gov.ls/download/lesotho-national-digital-transformation-strategy/" target="_blank">National Digital Transformation Strategy</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        innovationHubs: true,
        digitalEntrepreneurship: true,
        Broadband: true,
        DataProtection: true,
        DigitalPayments: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 218,
        country: 'Lesotho',
        reg_name: '<span class="reg-name"><a href="https://media.lesotholii.org/files/legislation/akn-ls-act-2012-4-eng-2012-02-17.pdf" target="_blank">Communications Act</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadcasting: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 212,
        country: 'Libya',
        reg_name: '<span class="reg-name"><a href="file:///D:/ATU/ATU-DEVELOPMENT/Data-Protection-Libya.pdf" target="_blank">Cybercrime Law No.5 of 2022</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataProtection: true 
    }, 
    {
        arrayItem: true,
        row: 213,
        country: 'Libya',
        reg_name: '<span class="reg-name"><a href="file:///D:/ATU/ATU-DEVELOPMENT/Data-Protection-Libya.pdf" target="_blank">Electronic Transactions Law No.6 of 2022</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalSignatures: true,
        DigitalTransactions: true 
    }, 
    {
        arrayItem: true,
        row: 183,
        country: 'Mali',
        reg_name: '<span class="reg-name"><a href="https://communication.gouv.ml/wp-content/uploads/2021/01/DraftMN2020.pdf" target="_blank">Mali</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Drones: true,
        Ecommerce: true,
        ElectronicWaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        PostalServices: true,
        ServiceQuality: true,
        SMARTCities: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 184,
        country: 'Mali',
        reg_name: '<span class="reg-name"><a href="https://apdp.ml/documents/LOI%20DE%20L%27APDP.pdf" target="_blank">Amending Law No.20133-015 on the Protection of Personal Data In the Republic of Mali</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        DigitalIdentification: true 
    }, 
    {
        arrayItem: true,
        row: 185,
        country: 'Mali',
        reg_name: '<span class="reg-name"><a href="https://sgg-mali.ml/JO/2019/mali-jo-2019-43.pdf" target="_blank">Cybercrime Law</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataProtection: true 
    }, 
    {
        arrayItem: true,
        row: 19,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://mtnima.gov.mr/sites/default/files/2024-01/قانون%20رقم%202013-025%20متعلق%20بالاتصالات%20الالكترونية.pdf" target="_blank">Law No. 2013-025 on Electronic Communications</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true
    },
    {
        arrayItem: true,
        row: 20,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://mtnima.gov.mr/sites/default/files/2024-01/القانون%20رقم%202022-014%20المعدل%20والمكمل%20لبعض%20أحكام%20القانون%20رقم%202013-0025%20المتعلق%20بالاتصالات%20الالكترونية%28ج.ر%201515%29.pdf" target="_blank">Law No. 2022-014 Amending and Supplementing Certain Provisions of Law No. 2013-025, Relating to Electronic Communications</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true,
        Spectrum: true
    },
    {
        arrayItem: true,
        row: 21,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://mtnima.gov.mr/sites/default/files/loi_2016_-_007_relative_la_cybercriminalite.pdf" target="_blank">Law No. 2016-007 Relating to Cybercrime</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        row: 22,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/api/documents/download?_id=5db6a3618e6f37001b2b5a74" target="_blank">Law No. 2017-020 on the Protection of Personal Data (PDCP)</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        row: 23,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://mtnima.gov.mr/sites/default/files/2024-01/قانون%20رقم%202018-022%20يتعلق%20بالمبادلات%20الالكترونية%20%28ج.ر1417%29.pdf" target="_blank">Law No. 2018-022 Relating to Electronic Transactions</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        DataProtection: true,
        DigitalSignatures: true,
        Ecommerce: true	
    },
    {
        arrayItem: true,
        row: 24,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://mtnima.gov.mr/sites/default/files/2024-02/قانون%20رقم%202021-014%20يتعلق%20بخدمات%20و%20وساءل%20الدفع%20الإلكتروني%20%28ج.ر%201490%29.pdf" target="_blank">Law No. 2021-014 Relating to Electronic Payment Services and Means</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Law',
        digitalPlatform: true,
        financialServices: true,
        DigitalPayments: true,
        DigitalSignatures: true
    },
    {
        arrayItem: true,
        row: 25,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://mtnima.gov.mr/sites/default/files/2024-01/قانون%20رقم%202024-002%20يتعلق%20بالشركات%20الناشئة%20التكنولوجية%20والابتكارية.pdf" target="_blank">Law No. 2024-002 Relating to Technological and Innovative Startups</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Law',
        entrepreneurship: true,
        digitalEntrepreneurship: true
    },
    {
        arrayItem: true,
        row: 26,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1TsszOgDddCo3JLryzpRkhiHYQ0CqVy6Q" target="_blank">National Digital Transformation Agenda 2022-2025</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCenterandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayment: true,
        DigitalSignatures: true,
        Ecommerce: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        PostalServices: true,
        ServiceQuality: true,
        Spectrum: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 27,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://mtnima.gov.mr/wp-content/uploads/2024/05/Stratégie-VF-AR-21-05-2024.pdf" target="_blank">Administration Modernization Strategy</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        financialServices: true,
        basic: true,
        advanced: true,
        DigitalIdentification: true,
        ServiceQuality: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 28,
        country: 'Mauritania',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1Axi1fG4okd-bWcvGQ2JXa2BcKjdMEOMr" target="_blank">Development Of The Strategy and Operational Plan for Digitalization (Digital Health) for The Ministry of Public Health In Mauritania</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Ehealth: true,
        ServiceQuality: true
        
    },
    {
        arrayItem: true,
        row: 196,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://www.icta.mu/documents/2022/04/ict_act.pdf" target="_blank">Information and Communication Technologies Act 2001</a></span>',
        date: '01/01/2001',
        date_text: "2001",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 197,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://cert-mu.govmu.org/cert-mu/wp-content/uploads/2023/11/Policies-and-Legislation.pdf" target="_blank">National Cyber Security Strategy 2023-2026</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalGovernment: true,
        invisibleInfrastructure: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 198,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://www.nef.mu/shared-files/11905/Computer-Misuse-and-Cybercrime-Act-2003.pdf" target="_blank">Computer Misuse and Cybercrime Act 2003</a></span>',
        date: '01/01/2003',
        date_text: "2003",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 199,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/strategicplans/DigitalMauritius2030.pdf" target="_blank">Digital Mauritius 2030 strategic plan</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        ArtificialIntelligence: true,	 
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 200,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="http://www.ist-africa.org/home/files/Mauritius_ICTPolicy_2007-11.pdf" target="_blank">National Information Security Policy</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        Cybersecurity: true 
    }, 
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/legislations/THE%20CYBERSECURITY%20AND%20CYBERCRIME%20ACT%202021.pdf" target="_blank">The Cybersecurity and Cybercrime Act 2021</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/strategicplans/DigitalGovernmentTransformation.pdf" target="_blank">Digital Government Transformation Strategy 2018-2022</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        LastMile: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        FoundationalSkills: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/strategicplans/MauritiusAIStrategy2018.pdf" target="_blank">Mauritius Artificial Intelligence Strategy 2018</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        HighlySpecializedSkills: true,
        ArtificialIntelligence: true,
        DataCenter: true,
        TechHubs: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/strategicplans/DigitalMauritius2030.pdf" target="_blank">Digital Mauritus Strategy 2030</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        MiddleMile: true, 
        LastMile: true, 
        EgovernmentServices: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true, 
        Ecommerce: true, 
        Payments: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://dataprotection.govmu.org/Documents/DPA_2017_updated.pdf?csf=1&e=0rlrff" target="_blank">Data Protection Act 2017 (updated)</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        DataProtection: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/strategicplans/MauritiusNationalExportStrategy.pdf" target="_blank">Mauritius National Export Strategy: Software Development Sector 2017-2021</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        HighlySpecializedSkills: true, 
        IntermediateSkills: true, 
        CloudPolicy: true, 
        TechHubs: true, 
        Funding: true, 
        EgovernmentServices: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/strategicplans/CybercrimeStrategy2017-2019.pdf" target="_blank">Cybercrime Strategy 2017-2019</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        Broadcasting: true,
        Communication: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/Mauritius%20Open%20Data%20Policy%20May%202017.pdf" target="_blank">National Open data Policy 2017</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/Anti-Corruption%20Policy%20for%20MTCI%20-%20FInal%20-Nov%202015.pdf" target="_blank">MTCI Anti-corruption Policy</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        Broadband: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/strategicplans/NationalCyberSecurityStrategy2014-2019.pdf" target="_blank">National Cybersecurity Strategy 2014-2019</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        Cybersecurity: true,
        Broadcasting: true,
        Communication: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/strategicplans/OpenSourceSoftwareStrategy.pdf" target="_blank">Open Source Software Strategy for the Republic of Mauritius</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        OpenCloud: true,
        EgovernmentServices: true,
        Ehealth: true,
        FoundationalSkills: true,
        TechHubs: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/eGovernment%20Strategy%20finalv1.pdf" target="_blank">Mauritius E-Government Strategy(2013-2017)</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        Cybersecurity: true, 
        Ehealth: true, 
        Eagriculturegriculture: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/NationalBroadband.pdf" target="_blank">National Broadband Policy 2012-2020</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        Broadband: true, 
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        EgovernmentServices: true, 
        FoundationalSkills: true, 
        HighlySpecializedSkills: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/NICTSP20112014.pdf" target="_blank">National Information Communication Technology Strategic Plan 2011-2014</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        EndUserEquipment: true, 
        EgovernmentServices: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true, 
        TechHubs: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/OpenAccessPolicy.pdf" target="_blank">Open Access Policy for Undersea cable landing stations in Mauritius</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FirstMile: true,
        OpenAccess: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/ICT%20Policy%202007-2011.pdf" target="_blank">National ICT Strategy 2007-2011</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        LastMile: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/legislations/DATA_PROTECTION.pdf" target="_blank">Data Protection Act (2004)</a></span>',
        date: '01/01/2004',
        date_text: "2004",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/finalntp.pdf" target="_blank">National Telecommunication Policy (NTP-2004)</a></span>',
        date: '01/01/2004',
        date_text: "2004",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FirstMile: true,
        MiddleMile: true,
        Spectrum: true,
        LastMile: true,
        EndUserEquipment: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/Policy%20Framework.pdf" target="_blank">Policy Framework for Internet Service Providers 2001</a></span>',
        date: '01/01/2001',
        date_text: "2001",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalMarketRegulation: true,
        License: true,
        Interconnectivity: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/legislations/ELECTRONIC_TRANSACTIONS.pdf" target="_blank">Electronic Transactions Act 2000</a></span>',
        date: '01/01/2000',
        date_text: "2000",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        DigitalSignatures: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://mitci.govmu.org/Documents/Strategies/Policy_Of_Republic_Mauritius_Telecommunications_Sector.pdf" target="_blank">The Policy of Republic of Mauritius in Respect to the Telecommunication Sector</a></span>',
        date: '01/01/1999',
        date_text: "1999",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalMarketRegulation: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Mauritius',
        reg_name: '<span class="reg-name"><a href="https://ncb.govmu.org/ncb/legislations/NATIONAL_COMPUTER_BOARD.pdf" target="_blank">National Computer Board Act</a></span>',
        date: '01/01/1988',
        date_text: "1988",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        row: 44,
        country: 'Morocco',
        reg_name: '<span class="reg-name"><a href="https://www.dgssi.gov.ma/fr/publications" target="_blank">National Cybersecurity Strategy</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        row: 45,
        country: 'Morocco',
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/en/document/dnpcjxsh98?page=1" target="_blank">Law 24-96 Relating to Post and Telecommunications</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        LastMile: true,
        InternetResources: true,
        PostalServices: true,
        ServiceQuality: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 46,
        country: 'Morocco',
        reg_name: '<span class="reg-name"><a href="https://www.dgssi.gov.ma/sites/default/files/legislative/brochure/2022-10/presentation_note_of_the_law_n_deg_05-20_on_cybersecurity_-_english_version.pdf" target="_blank">Law No. 05-20 Relating to Cybersecurity</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        row: 47,
        country: 'Morocco',
        reg_name: '<span class="reg-name"><a href="http://www.sgg.gov.ma/portals/0/AvantProjet/1/Projet_dec_CG_Ar.pdf">Decree No. 2-97-1026 Relating to the General Operating Conditions of Public Telecommunications Networks</a></span>',
        date: '01/01/1998',
        date_text: "1998",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        LastMile: true,
        InternetResources: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 48,
        country: 'Morocco',
        reg_name: '<span class="reg-name"><a href="http://bdj.mmsp.gov.ma/Ar/Document/10454-loi-n-4320-relative-aux-services-de-confiance-co.aspx#:~:text=قانون%20رقم%2020%2D43%20يتعلق%20بخدمات%20الثقة%20بشأن%20المعاملات%20الإلكترونية.&text=يهدف%20هذا%20القانون%20إلى%20تحديد,ومن%20لدن%20أصحاب%20الشهادات%20الإلكترونية." target="_blank">Law No. 43-20 Relating to Trust Services for Electronic Transactions</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        DigitalSignatures: true,
        DigitalTransactions: true
    },
    {
        arrayItem: true,
        row: 226,
        country: 'Mozambique',
        reg_name: '<span class="reg-name"><a href="https://portaldogoverno.gov.mz/por/content/download/7051/51882/version/2/file/LEI_DE_TRANSACCOES_ELECTRONICAS.pdf" target="_blank">Electronic Transactions Act</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalIdentification: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 227,
        country: 'Mozambique',
        reg_name: '<span class="reg-name"><a href="https://portaldogoverno.gov.mz/por/content/download/1631/13436/version/1/file/Politica+de+informatica.BR.pdf" target="_blank">ICT Policy</a></span>',
        date: '01/01/2000',
        date_text: "2000",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 228,
        country: 'Mozambique',
        reg_name: '<span class="reg-name"><a href="https://intic.gov.mz/wp-content/uploads/2022/05/BR_253_I_SERIE_12.o-SUPLEMENTO_2021_Politica-de-Seguranca-Cibernetica-e-Protecao-e-Estrategia-da-sua-Implementacao.pdf" target="_blank">National Cybersecurity Policy and Strategy</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true 
    }, 
    {
        arrayItem: true,
        row: 229,
        country: 'Mozambique',
        reg_name: '<span class="reg-name"><a href="https://intic.gov.mz/wp-content/uploads/2021/04/BR_122_I_SERIE_2018.pdf" target="_blank">Information Society Policy</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        innovationHubs: true,
        digitalEntrepreneurship: true 
    }, 
    {
        arrayItem: true,
        row: 230,
        country: 'Mozambique',
        reg_name: '<span class="reg-name"><a href="https://intic.gov.mz/wp-content/uploads/2023/11/BR_206_I_SERIE_2023.pdf" target="_blank">Regulation for the Registration and Licensing of Intermediary Providers of Electronic Services and Digital Platform Operators</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        LastMile: true,
        ISP: true 

        
    }, 
    {
        arrayItem: true,
        row: 231,
        country: 'Mozambique',
        reg_name: '<span class="reg-name"><a href="https://intic.gov.mz/wp-content/uploads/2024/03/mz-government-gazette-series-i-dated-2020-09-10-no-174.pdf" target="_blank">Domain Regulation</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        InternetResources: true 
    }, 
    {
        arrayItem: true,
        row: 232,
        country: 'Mozambique',
        reg_name: '<span class="reg-name"><a href="https://intic.gov.mz/wp-content/uploads/2021/04/BR_127_I_SERIE_2019.pdf" target="_blank">Regulation of the Mozambique Digital Certification System (SCDM)</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        DigitalIdentification: true,
        DigitalSignatures: true 
    }, 
    { 
        arrayItem: true, 
        row: 82, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.lac.org.na/laws/annoSTAT/Electronic%20Transactions%20Act%204%20of%202019.pdf" target="_blank">Electronic Transaction Act, 2019 (Act No. 4 of 2019)</a></span>', 
        date: '01/01/2019', 
        date_text: "2019", 
        classification: 'Law', 
        digitalPlatform: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        financialServices: true, 
        DigitalIdentification: true, 
        Etransactions: true 
    }, 
    { 
        arrayItem: true, 
        row: 83, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.lac.org.na/laws/annoSTAT/Communications%20Act%208%20of%202009.pdf" target="_blank">Communication Act, 2008( Act No.8 of 2009)</a></span>', 
        date: '01/01/2009', 
        date_text: "2009", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Broadcasting: true 
    }, 
    { 
        arrayItem: true, 
        row: 84, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.lac.org.na/laws/2022/7986.pdf" target="_blank">Access to Information Act, 2022( Act No.8 of 2022)</a></span>', 
        date: '01/01/2022', 
        date_text: "2022", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        invisibleInfrastructure: true, 
        PersonalData: true, 
        Cybersecurity: true
    }, 
    { 
        arrayItem: true, 
        row: 85, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://media.namiblii.org/files/legislation/akn-na-act-1992-17-eng-2011-05-18.pdf" target="_blank">Posts and Telecommunications Companies Establishment Act</a></span>', 
        date: '01/01/1992', 
        date_text: "1992", 
        classification: 'Law', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadcasting: true 
    }, 
    { 
        arrayItem: true, 
        row: 86, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.npc.gov.na/wp-content/uploads/2022/06/NamibiaBroadband Internet-Policy-2018.pdf" target="_blank">National Broadband Internet Policy</a></span>', 
        date: '01/01/2019', 
        date_text: "2019", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Broadband: true 
    }, 
    { 
        arrayItem: true, 
        row: 87, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.cran.na/yglilidy/2019/12/QUALITY_OF_SERVICE_REGULATIONS.pdf" target="_blank">Regulations Prescribing Quality of Service Standards</a></span>', 
        date: '01/01/2015', 
        date_text: "2015", 
        classification: 'Regulation', 
        digitalPlatform: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        financialServices: true,
        ServiceQuality: true, 
        ConsumerProtection: true, 
        License: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 88, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.cran.na/yglilidy/2020/02/5983_1Apr16.pdf" target="_blank">Regulations Prescribing The National Numbering Plan For Use In The Provision Of Telecommunication Services In Namibia</a></span>', 
        date: '01/01/2016', 
        date_text: "2016", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Numbering: true 
    }, 
    { 
        arrayItem: true, 
        row: 89, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.cran.na/yglilidy/2023/08/8180-Gen-N494-495-21-Aug-2023.pdf" target="_blank">Type Approval Regulations</a></span>', 
        date: '01/01/2023', 
        date_text: "2023", 
        classification: 'Regulation',
        digitalIPlatform: true, 
        financialServices: true, 
        TypeApproval: true, 
        EndUserEquipment: true 
    }, 
    { 
        arrayItem: true, 
        row: 90, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.cran.na/yglilidy/2022/06/GG-7481-dated-15-March-2021.pdf" target="_blank">Regulations In Terms Of Part 6 Of Chapter V Of The Communications Act</a></span>', 
        date: '01/01/2021', 
        date_text: "2021", 
        classification: 'Regulation', 
        digitalPlatform: true, 
        digitalBusiness: true, 
        financialServices: true, 
        SIMCards: true, 
    }, 
    { 
        arrayItem: true, 
        row: 91, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.cran.na/yglilidy/2022/05/GG-7797-dated-28-April-2022.pdf" target="_blank">Imposition of Further Conditions On Telecommunications Service</a></span>', 
        date: '01/01/2022', 
        date_text: "2022", 
        classification: 'Regulation', 
        digitalPlatform: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        financialServices: true, 
        SIMCards: true 
    }, 
    { 
        arrayItem: true,
        row: 92, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.cran.na/yglilidy/2019/12/6589_Gen_N178.pdf" target="_blank">Regulations Prescribing The Provision Of Universal Service By Telecommunications Service Licensees</a></span>', 
        date: '01/01/2018', 
        date_text: "2018", 
        classification: 'Regulation', 
        digitalInfrastructure: true, 
        digitalPlatform: true, 
        digitalSkills: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        digitalGovernment: true, 
        digitalBusiness: true, 
        basic: true,
        advanced: true,
        UniversalService: true 
    }, 
    { 
        arrayItem: true, 
        row: 93, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.nmt.africa/uploads/614346b1d2ebb/NMTsubmision-Reviewofnationalcybersecuritystrat(22-27).pdf" target="_blank">National Cybersecurity Strategy & Awareness Raising Plan</a></span>', 
        date: '01/01/2022', 
        date_text: "2022", 
        classification: 'Policy', 
        digitalSkills: true, 
        entrepreneurship: true, 
        basic: true, 
        advanced: true, 
        innovationHubs: true,
        digitalEntrepreneurship: true, 
        Cybersecurity: true 
    }, 
    { 
        arrayItem: true, 
        row: 94, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.cran.na/yglilidy/2022/12/IMT-2020-5G-Strategy-2022-for-final-approval_310822.pdf" target="_blank">Strategy In Respect of Implementation of 5G</a></span>', 
        date: '01/01/2022', 
        date_text: "2022", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        invisibleInfrastructure: true, 
        physicalInfrastructure: true, 
        ServiceQuality: true, 
        ConsumerProtection: true, 
        License: true, 
        LastMile: true 
    }, 
    { 
        arrayItem: true, 
        row: 95, 
        country: 'Namibia', 
        reg_name: '<span class="reg-name"><a href="https://www.cran.na/yglilidy/2022/03/CRAN-Spectrum-Assignment-Strategy-2021.pdf" target="_blank">Spectrum Assignment Strategy</a></span>', 
        date: '01/01/2022', 
        date_text: "2022", 
        classification: 'Policy', 
        digitalInfrastructure: true, 
        physicalInfrastructure: true, 
        invisibleInfrastructure: true, 
        Spectrum: true 
    }, 
    {
        arrayItem: true,
        row: 5,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://fmcide.gov.ng/wp-content/uploads/2023/11/blueprint.pdf" target="_blank">Ministry of Communications, Innovation and Digital Economy Strategic Plan/Blueprint</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        basic: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Broadcasting: true,
        Broadband: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        SMARTCities: true,
        InternetResources: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 6,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://www.ppiaf.org/documents/1424#:~:text=The%20Nigerian%20Communications%20Act%202003,carry%20out%20its%20Regulatory%20Activities." target="_blank">Nigerian Communications Act, 2003</a></span>',
        date: '01/01/2003',
        date_text: "2003",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        innovationHubs: true,
        Broadband: true,
        Cybersecurity: true, 
        DataCentersandCloudServices: true,
        DataProtection: true,
        ElectronicWaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true,
        SMARTCities: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 7,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://www.ppiaf.org/sites/default/files/documents/2003-10/Legislation-Nigerian_Communications_Act_2003-2.pdf" target="_blank">Guidelines on Technical Specifications for the Deployment of Infrastructure in the Communications Sector</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        Broadband: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true
    },
    {
        arrayItem: true,
        row: 8,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2022/10/APPROVED-NITDA-CODE-OF-PRACTIVE-FOR-INTERACTIVE-COMPUTER-SERVICE-PLATFORMS-INTERNET-INTERMEDIARIES-2022-002.pdf" target="_blank">Internet Code of Practice</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        SecondMile: true,
        LastMile: true, 
        InternetResources: true, 
        ServiceQuality: true
    },
    {
        arrayItem: true,
        row: 9,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2020/11/FrameworkAndGuidelinesForPublicInternetAccessPIA1.pdf" target="_blank">Guidelines for the Provision of Internet Service</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true, 
        invisibleInfrastructure: true,
        Broadband: true,
        LastMile: true
    },
    {
        arrayItem: true,
        row: 10,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1hm5oz-8CEXnUnPbzJ5o7iNiqAADgso1k" target="_blank">National Office for Technology Acquisition and Promotion Act</a></span>',
        date: '01/01/1992',
        date_text: "1992",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        invisibleInfrastructure: true,
        digitalBusiness: true,
        advanced: true,
        digitalEntrepreneurship: true,
        DigitalIdentification: true,
        ServiceQuality: true,
        UniversalAccess: true	
    },
    {
        arrayItem: true,
        row: 11,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2020/11/NITDA-ACT-2007-2019-Edition1.pdf" target="_blank">National Information Technology Development Agency Act</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        basic:true,
        digitalEntrepreneurship: true,
        Broadband: true,
        InternetResources: true, 
        TypeApproval: true,
        EndUserAgreement: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncair.nitda.gov.ng/wp-content/uploads/2024/08/National-AI-Strategy_01082024-copy.pdf" target="_blank">National Artificial (AI) Intelligence Strategy</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        ArtificialIntelligence: true, 
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/1fFtsDMTWoMWd0TXAcTeSRlUfjbOXZNhN/view?pli=1" target="_blank">Accelerating Our Collective Prosperity through Technical Efficiency</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        UniversalAccess: true, 
        ArtificialIntelligence: true, 
        InternetofThings: true, 
        Blockchain: true, 
        Startups: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        FoundationalSkills: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true, 
        TechHubs: true, 
        Broadcasting: true, 
        Communication: true, 
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://placng.org/i/wp-content/uploads/2023/06/Nigeria-Data-Protection-Act-2023.pdf" target="_blank">National Data Protection Act 2023</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProtection: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2023/05/National-Blockchain-Policy.pdf" target="_blank">Nigeria Blockchain Policy for Nigeria</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: true,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Blockchain: true,
        DigitalMarketRegulation: true,
        Funding: true,
        HighlySpecializedSkills: true,
        Payments: true,
        RegulatorySandboxes: true,
        Cybersecurity: true,
        Startups: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/digital-economy-nitda-launches-policy-to-leverage-digital-innovation-entrepreneurship/4801/" target="_blank">The National Digital Innovation, Entrepreneurship and Startup Policy 2023</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        LastMile: true, 
        Cybersecurity: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        Startups: true, 
        Funding: true, 
        TechHubs: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://docplayer.net/19086138-Guidelines-on-short-code-operation-in-nigeria.html" target="_blank">Regulatory Guidelines on Shortcode Operation in Nigeria</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ShortCodes: true,
        License: true,
        EndUserSubscriberService: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/66-g-guidelines-on-technical-standards-for-interconnectivity-of-networks/file" target="_blank">Regulatory Guidelines on Technical Standards for Interconnectivity of Networks</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Interconnectivity: true, 
        LastMile: true, 
        Cybersecurity: true, 
        EndUserSubscriberService: true, 
        Standards: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="http://fmcde.gov.ng/index.php/federal-government-approves-national-policy-on-the-nigerian-government-second-level-domains/#:~:text=The%20Policy%20gives%20direction%20for,domain%20and%20email%20services%20on%20" target="_blank">National Policy on the Nigerian Government Second-Level Domains</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        LastMile: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2022/11/Final-Draft-National-Data-Strategy.pdf" target="_blank">National Data Strategy</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        EgovernmentServices: true, 
        DigitalMarketRegulation: true, 
        AdvancedSkills: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/1089-registration-of-communications-subscibers-regulation-business-rules-2022/file" target="_blank">Business Rules and Operational Processes for Implementation of the Registration of Communications Subscribers Regulations</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        SimCards: true,
        InternetofThings: true,
        Portability: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/802-legal-guidelines-spectrum-trading/file" target="_blank">Regulatory Guidelines on Spectrum Trading</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Spectrum: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href=https://drive.google.com/file/d/1Hq1VXKPC7NjM32rNLV-0G7HpAV4J0BhV/view" target="_blank">Nigeria Start-Up Act</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        Funding: true,
        TechHubs: true,
        License: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="http://ctc.gov.ng/wp-content/uploads/2021/02/NATIONAL-CYBERSECURITY-POLICY-AND-STRATEGY-2021_E-COPY_24223825.pdf" target="_blank">National Cybersecurity Policy and Strategy 2021</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2022/01/NATIONAL-POLICY-ON-5G-ISBN_compressed-1.pdf" target="_blank">National Policy on 5G network</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        Spectrum: true, 
        Cybersecurity: true, 
        Satellite: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2021/03/National-Policy-on-VSAT-Installation-Core-Skills-for-Nigerians_fin.pdf" target="_blank">National Policy on VSAT Satellite Core Skills 2021</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        HighlySpecializedSkills: true,
        Broadband: true,
        Satellite: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2021/05/REVISED-NATIONAL-DIGITAL-IDENTITY-POLICY-ON-SIM.pdf" target="_blank">National Identity Policy for Sim Card Registration 2021</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalIdentification: true,
        EgovernmentServices: true,
        SimCards: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/992-guidelines-on-collocation-and-infrastructure-sharing-2021/file" target="_blank">Regulatory Guidelines on Co-Location and Infrastructure Sharing</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Fiber: true,
        Frequency: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/993-guidelines-on-national-roaming-in-nigeria-2021/file" target="_blank">Regulatory Guidelines on National Roaming</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Roaming: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/documents/880-nigerian-national-broadband-plan-2020-2025/file" target="_blank">Nigerian National Broadband Strategic Plan</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: false,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        Affordability: true,
        Broadband: true,
        EndUserEquipment: true,
        Fiber: true,
        FoundationalSkills: true,
        Funding: true,
        LastMile: true,
        Satellite: true,
        Spectrum: true,
        Startups: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ndpb.gov.ng/Files/Policy-National_Digital_Economy_Policy_and_Strategy.pdf" target="_blank">National Digital Economy Policy 2020-2030</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        EmergingTechnologies: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Broadband: true, 
        Fiber: true, 
        Fintech: true, 
        Funding: true, 
        Inclusivity: true, 
        DataCenter: true, 
        Satellite: true, 
        Frequency: true, 
        DataProtection: true, 
        Cybersecurity: true, 
        BigData: true, 
        ArtificialIntelligence: true, 
        Blockchain: true, 
        MachineLearning: true, 
        VirtualandAugmentedReality: true, 
        InternetofThings: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/documents/880-nigerian-national-broadband-plan-2020-2025/file" target="_blank">Nigerian National Broadband Plan 2020-2025</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: false,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        FirstMile: true,
        LastMile: true,
        EgovernmentServices: true,
        Ecommerce: true,
        Spectrum: true,
        Fiber: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ndpb.gov.ng/Files/Policy-National_Digital_Economy_Policy_and_Strategy.pdf" target="_blank">National Digital Economic Policy and Strategy (2020-2030)</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        DigitalMarketRegulation: true, 
        FoundationalSkills: true, 
        IntermediateSkills: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2020/11/NITDA-Public-Key-Infrastructure-Regulations-Final-Draft11.pdf" target="_blank">NITDA Public Key Infrastructure Regulations</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2021/01/NDPR-Implementation-Framework.pdf" target="_blank">Nigeria Data protection Regulation (NDPR) Under the NITDA Act of 2007</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataCenter: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="http://fmcde.gov.ng/index.php/nigeria-e-govt-master-plan/" target="_blank">National E-government Master Plan</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true, 
        Payments: true, 
        Ehealth: true, 
        Ecustoms: true, 
        Efinance: true, 
        Eprocurement: true, 
        Eeducation: true, 
        Eimmigration: true, 
        Eagriculture: true, 
        Evoting: true, 
        TVET: true, 
        Interoperability: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2020/11/Ne-GIFFinal1.pdf" target="_blank">Nigeria E-government Interoperability Framework 2019</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        Interoperability: true,
        Networks: true,
        Ecommerce: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://www.dataguidance.com/sites/default/files/ndpr_implementation_framework_november_2020.pdf" target="_blank">Nigeria Data Protection Regulation 2019</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        DataPrivacy: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2020/11/NCCPolicy_New1.pdf" target="_blank">Nigeria Cloud Computing Policy 2019</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        EgovernmentServices: true, 
        HighlySpecializedSkills: true, 
        ConsumerProtection: true, 
        CloudPolicy: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2020/11/govng-mil_ng-Domain-Management-Policy.pdf" target="_blank">Domain Management Policy 2019</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/839-lawful-interception-of-comunications-regulations-1/file" target="_blank">Lawful Interception of Communication Regulations</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Communication: true,
        Cybersecurity: true,
        Interception: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/63-guidelines-for-international-access-and-voice-over-internet-protocol-voip/file" target="_blank">Regulatory Guidelines on International Access and Voice Over Internet Protocol</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        VoiceOverInternetProtocol: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/819-guidelines-on-commercial-satellite-communications-2018/file" target="_blank">Regulatory Guidelines for Commercial Satellite Communications</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FirstMile: true,
        Satellite: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/803-guidelines-on-the-use-of-short-range-devices/file" target="_blank">Regulatory Guidelines on the Use of Short Range Devices</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EndUserEquipment: true,
        Spectrum: true,
        Frequency: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/department/e-government-development-and-regulation/" target="_blank">E-Government Development and Regulation 2017</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalMarketRegulation: true,
        EgovernmentServices: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://www.health.gov.ng/doc/HealthICTStrategicFramework.pdf" target="_blank">National E-Health Strategic Framework</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Ehealth: true

    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://www.nfiu.gov.ng/images/Downloads/downloads/cybercrime.pdf" target="_blank">Cybercrimes (Prohibition, Prevention Etc) Act 2015</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        Phishing: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/560-regulations-on-mobile-number-portability/file" target="_blank">Mobile Number Portability Regulation</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Portability: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/731-legal-regulations-quality-of-service-regulation-2013/file" target="_blank">Quality of Service Regulation </a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ConsumerProtection: true,
        Communication: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2020/11/National-ICT-Policy11.pdf" target="_blank">National ICT Policy 2012</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        LastMile: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        FoundationalSkills: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/330-guidelines-on-procedure-for-granting-approval-to-disconnect-telecommunications-operators/file" target="_blank">Regulatory Guidelines on the Procedure for Granting Approval to Disconnect Telecommunications Operators</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Tariffs: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://www.cbn.gov.ng/foi/freedom%20of%20information%20act.pdf" target="_blank">Freedom of Information Act, 2011 (FOI Act)</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/file/d/0B1DAmtM1BcbMM09tNDlQTksyNnM/view?resourcekey=0-2zmxZxrkW7BZeroPDBOM2w" target="_blank">Nigerian Communications Commission (Registration of Telephone Subscribers) Regulation 2011</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name">Registration of Telephone Subscribers</span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Database: true,
        License: true,
        SimCards: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/59-guidelines-for-deployment-of-broadband-services-on-the-5-2-5-9ghz-band/file" target="_blank">Regulatory Guidelines for Deployment of Broadband Services on the 5.2-5.9 Ghz Band</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadband: true,
        LastMile: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/64-guidelines-for-deployment-of-wifi-services/file" target="_blank">Regulatory Guidelines for the Use of 2.4GHz ISM Band Wifi for Commercial Telecom Services </a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadband: true,
        LastMile: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/65-guidelines-for-the-installation-of-telecommunications-masts-and-towers-1/file" target="_blank">Regulatory Guidelines for the Installation of Telecommunication Mast and Towers</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        LastMile: true,
        Towers: true,
        Mast: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/107-regulations-for-type-approval-1/file" target="_blank">Type Approval Regulations</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        TypeApproval: true,
        Interoperability: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://www.nimc.gov.ng/docs/reports/nimc_act.pdf" target="_blank">National Identity Management Commission Act 2007</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalIdentification: true,
        EgovernmentServices: true,
        Database: true,
        MiddleMile: true,
        LastMile: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://nitda.gov.ng/wp-content/uploads/2020/11/NITDA-ACT-2007-2019-Edition1.pdf" target="_blank">National Information Technology Act Development Act 2007</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/101-regulations-for-competition-practices/file" target="_blank">Nigerian Communications Act - Competition Practices Regulation</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FairCompetition: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/329-regulations-for-telecommunications-networks-interconnection-2/file" target="_blank">Telecommunication Networks Interconnection Regulation</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Interconnectivity: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/108-regulations-on-universal-access-and-universal-service-1/file" target="_blank">Universal Access and Universal Service Regulation</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/104-frequency-pricing-regulations/file" target="_blank">Frequency Pricing Regulation</a></span>',
        date: '01/01/2004',
        date_text: "2004",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Frequency: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/regulations/1088-registration-of-communications-subsribers-regulation/file" target="_blank">Nigerian Communications Act No19 of 2023</a></span>',
        date: '01/01/2003',
        date_text: "2003",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Communication: true
    },
    {
        arrayItem: true,
        country: 'Nigeria',
        reg_name: '<span class="reg-name"><a href="https://ncc.gov.ng/docman-main/legal-regulatory/guidelines/62-guidelines-for-the-provision-of-internet-service/file" target="_blank">Regulatory Guidelines for the Provision of Internet Services</a></span>',
        date: '',
        date_text: "",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EndUserSubscriberService: true
    },
    {
        arrayItem: true, 
        row: 258, 
        country: 'Rwanda', 
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=107417&token=6fe9f09b32c074900f09dd3f1095a8eed2551075" target="_blank">AI Playbook for Small States</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true, 
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        ArtificialIntelligence: true,
        Broadband: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        ECommerce: true,
    },
    {
        arrayItem: true, 
        row: 259, 
        country: 'Rwanda', 
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=92164&token=9d676844e199d9d5e7310a16ffee8da58cb26541" target="_blank">An Integrated ICT-led Socio-Economic Development Policy and Plan for Rwanda (2001-2006)</a></span>', 
        date: '01/01/2001', 
        date_text: "2001",
        classification: 'Policy',
        digitalInfrastructure: true, 
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 260, 
        country: 'Rwanda', 
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1108&token=911713ddd7b53d278eeedce39c6c535488308c2b" target="_blank">Local Digital Content Promotion Strategy and Implementation Plan</a></span>', 
        date: '01/01/2018', 
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true, 
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        BroadcastingandMultimediaServices: true,
    },
    {
        arrayItem: true, 
        row: 261, 
        country: 'Rwanda', 
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1113&token=6bd5df141510ec9655c37705edb3482d5b576058" target="_blank">Smart Rwanda 2020 Master Plan</a></span>', 
        date: '01/01/2015', 
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: true, 
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        ECommerce: true,
        LastMile: true,
        InternetResources: true,
        QualityofService: true,
        UniversalAccess: true,
        SmartCities: true,
    },
    {
        arrayItem: true, 
        row: 262, 
        country: 'Rwanda', 
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1112&token=9ce1e92138d6fe8eb84966d06c920711b3f6ca03" target="_blank">An Integrated ICT-led Socio-Economic Development Plan for Rwanda (2006-2010)</a></span>', 
        date: '01/01/2006', 
        date_text: "2006",
        classification: 'Policy',
        digitalInfrastructure: true, 
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        UniversalAccess: true,
    },
    {
        arrayItem: true, 
        row: 263, 
        country: 'Rwanda', 
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1106&token=5b64ad32319780612bdf9d25c59314eb3b662c48" target="_blank">Rwanda Child Online Protection Policy</a></span>', 
        date: '01/01/2019', 
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        DataProtection: true,
        DigitalIdentification: true,
    },
    {
        arrayItem: true, 
        row: 264, 
        country: 'Rwanda', 
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1104&token=a1ddb470de121cc3d6ad8f6363bd254abdb5d0ec" target="_blank">Broadcasting Policy of Rwanda</a></span>', 
        date: '01/01/2011', 
        date_text: "2011",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        BroadcastingandMultimediaServices: true,
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=67550&token=6195a53203e197efa47592f40ff4aaf24579640e" target="_blank">Artificial Intelligence Policy</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        EgovernmentServices: true, 
        Ecommerce: true, 
        DigitalMarketRegulation: true, 
        HighlySpecializedSkills: true, 
        Startups: true, 
        Funding: true, 
        TechHubs: true, 
        Payments: true, 
        ArtificialIntelligence: true, 
        MachineLearning: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=55955&token=4f911fb560969db5f90a30e7247393d25ba8d6c9" target="_blank">National Broadband Policy and Strategy 2022</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        LastMile: true, 
        IntermediateSkills: true, 
        Broadband: true, 
        DigitalMarketRegulation: true, 
        Satellite: true, 
        Spectrum: true, 
        Cybersecurity: true,
        FoundationalSkills: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=41306&token=d582a81725e7b8289f1579fd34e9dbc40b8ad087" target="_blank">Rwanda Fintech Strategy 2022-2027</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        Cybersecurity: true, 
        Ecommerce: true, 
        Payments: true, 
        License: true, 
        Fintech:true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=41305&token=4bcac6970c5d433ff070e4b9c25bb346763debed" target="_blank">Rwanda Fintech Policy 2022-2027</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: true,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        Cybersecurity: true, 
        Ecommerce: true, 
        Payments: true, 
        License: true, 
        Fintech: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/FINAL_REGULATION_GOVERNING__BROADCASTING_SERVICES_IN_RWANDA_.pdf" target="_blank">Regulations N° 017 /MR-ICT/RURA/2022 of 17/01/22 Governing Broadcasting Service in Rwanda</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true, 
        Communication: true, 
        License: true, 
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/docs/statistics/ICT/Rwanda_National_Table_of_Frequency_Allocation_for_2022.pdf" target="_blank">Rwanda National Frequency Allocation Table 2022</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Spectrum: true,
        Broadband: true,
        Satellite: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=30041&token=c1f6f28d83d3c9e4193287057afafdbb6fd5aa14" target="_blank">Data Protection Law 2021</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/OFFICIAL_GAZETTE_for__Regulations_Governing_Importation__Supply_and_Type_Approval_of_Electronic_Communications_Equipment.pdf" target="_blank">Regulations N° 012/R/Stdict/ Rura/2021 Of 14/12/2021 Governing Importation, Supply and Type Approval of Electronic Communications Equipment</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        TypeApproval: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/RURA_REGULATION_GOVERNING__SUBSCRIPTION_SATELLITE_.pdf" target="_blank">Regulation N°016/R/Mrict/Rura/2021 Of 23rd/12/2021 Governing Subscription Satellite Broadcasting Services Provision</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true,
        Communication: true,
        Satellite: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Regulation_Governing_the_Operation_of_Intelligent_Connected_Fare_Meter_in_Rwanda.pdf" target="_blank">Regulation N⁰15/R/Crcsi/Rura/2021 Of 27/9/2021 Governing the Operation of Intelligent Connected Fare Meter in Rwanda</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        FareMeter: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/regulation_governing_the_use_of_the_unlicensed_and_light_licensed_frequency_bands__May_2021.pdf" target="_blank">Regulation No 014/R/Sm-Ict/Rura/2021 Of 04/05/2021 Governing the Use of The Unlicensed and Light Licensed Frequency Bands</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Spectrum: true,
        Broadband: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Regulation_Governing_Licensing_in_Electronic_Communication_in_Rwanda.pdf" target="_blank">Regulation No 013/R/Ec-Ict/Rura/2021 Of 25/02/2021 Governing Licensing in Electronic Communication</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Spectrum: true,
        Broadband: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1114&token=6eeaaaa5fe61f6956fc8a452ec7ce802d96497c8" target="_blank">Smart City Rwanda MasterPlan version 2.0</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecialisedSkills: true, 
        Startups: true, 
        TechHubs: true, 
        Funding: true, 
        SMARTCities: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1113&token=6bd5df141510ec9655c37705edb3482d5b576058" target="_blank">Smart Rwanda Masterplan</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecialisedSkills: true, 
        Startups: true, 
        TechHubs: true, 
        Funding: true, 
        SMARTCities: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/fr/document/vrwdaqi24xo?page=4" target="_blank">Law Establishing the NCSA</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        Domain: true, 
        Communication: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Regulation_Governing_Cybersecurity.pdf" target="_blank">Regulation No 010/R/Crcsi/Rura/020 Of 29/05/2020 Governing Cybersecurity</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        License: true, 
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Regulation_Governing_Licensing_of_Multimedia_Services_Provision_in_Rwanda_issued_in_May_2020.pdf" target="_blank">Regulation N˚ 012/R/Mrcer/Rura/020 Of 29/05/2020 Governing Licensing of Multimedia Services Provision in Rwanda</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        Multimedia: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1111&token=1e7f43bc7743a313b4ca89236debdd1b7e50baa4" target="_blank">ICT Hub Strategy 2019-2024</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecialisedSkills: true, 
        Startups: true, 
        TechHubs: true, 
        Funding: true, 
        SMARTCities: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Rwanda_Child_Online_Protection_Policy.pdf" target="_blank">Rwanda Online Protection Policy</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FoundationalSkills: true, 
        DataProtection: true, 
        Cybersecurity: true, 
        DigitalMarketRegulation: true, 
        ChildProtection: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Regulation_on_Radio_Frequency_Management_in_Rwanda.pdf" target="_blank">Regulation N°007/R/Sm-Ict/Rura/2019 Of 15/11/2019 On Radio Frequency Management</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Spectrum: true, 
        License: true, 
        RadioFrequency: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Regulation_Governing_Digital_Terrestrial_Television_Services_in_Rwanda.pdf" target="_blank">Regulation N° 005/R/Mr-Mca/Rura/2019 of 27th/06/2019 Governing Digital Terrestrial Television Services In Rwanda</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        RadioFrequency: true, 
        License: true, 
        TerrestrialTelevisionService: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/REGULATIONS_FOR__QUALITY_OF_SERVICE_OF_CELLULAR_MOBILE_NETWORKS_SERVICES_.pdf" target="_blank">Regulation N0 006/R/Stdqos/Ict/Rura/2019 of 30/01/2019 Governing the Quality of Service Of Cellular Mobile Networks Services</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Spectrum: true, 
        License: true, 
        EndUserSubscriberService: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=604&tx_news_pi1%5Bday%5D=27&tx_news_pi1%5Bmonth%5D=9&tx_news_pi1%5Byear%5D=2018&cHash=b189cfc41c96b15b49032d5c5d7c451f" target="_blank">Guideline on Minimum Bandwidth for Broadband Internet Connectivity in Rwanda</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadband: true,
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=590&tx_news_pi1%5Bday%5D=27&tx_news_pi1%5Bmonth%5D=8&tx_news_pi1%5Byear%5D=2018&cHash=1a8c8c576659c9cb1ee9b5027a8cd562" target="_blank">Regulation Governing the Management, Assignment and Use of Telecom Numbering Resources in Rwanda</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Numbering: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=513&tx_news_pi1%5Bday%5D=13&tx_news_pi1%5Bmonth%5D=2&tx_news_pi1%5Byear%5D=2018&cHash=33a6596107d4ad17bc5de2e4fee3f4df" target="_blank">Regulation Governing E-Waste Management in Rwanda</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ElectronicWaste: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/REGULATION__GOVERNING_THE_SIM_CARDS_REGISTRATION_IN_RWANDA.pdf" target="_blank">Regulation Governing Sim Card Registration in Rwanda</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        SimCards: true, 
        EndUserSubscriberService: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1110&token=3c72357ef5a933870448878fe42162871262fcc6" target="_blank">ICT Sector Strategic Plan 2018-2024</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecialisedSkills: true, 
        Startups: true, 
        TechHubs: true, 
        Funding: true, 
        SMARTCities: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1102&token=3ed7ab432cba52e4b70aeff3fef6d1eb2ba0b51a" target="_blank">Law on Prevention of Cybercrimes 2018</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        Cybercrime: true, 
        DigitalSignatures: true, 
        Phishing: true, 
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=38794&token=74b679f3035bb39771564ee83aac03538b7d3d6d" target="_blank">National Digital Talent Policy 2017</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FoundationalSkills: true, 
        IntermediateSkills: true, 
        HighlySpecializedSkills: true, 
        GenderInclusion: true, 
        SpecialNeeds: true, 
        TVET: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1101&token=ce06c0d057b3da82d1586487a836280b2da2c1ac" target="_blank">Law Establishing RISA</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=472&tx_news_pi1%5Bday%5D=7&tx_news_pi1%5Bmonth%5D=11&tx_news_pi1%5Byear%5D=2017&cHash=9e9eeb68278891c8ce9d88cc4ee3098f" target="_blank">Regulation Governing Satellite Network Filling in Rwanda</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Satellite: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1103&token=62e5552ee854db65fda22c4e75e7460f8b57fea4" target="_blank">ICT Law 2016</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        UniversalAccess: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Law_governing_Information_and_Communication_Technologies_in_Rwanda.pdf" target="_blank">Law N24/2016 of 18/06/2016 Governing Information and Communication Technology</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Interconnectivity: true,
        Broadcasting: true,
        Communication: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/REGULATIONS_ON_PROMOTION_BY_TELECOMMUNICATION_OPERATORS__July_2016.pdf" target="_blank">Regulation on Promotion of Telecommunication Operations</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ConsumerProtection: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/docs/Regulations_Governing__Telecom_Network_Security.pdf" target="_blank">Regulation Governing Telecom Network Security in Rwanda</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=14150&token=0b33637cdb68faff93a156d5a7fdb43a51ff8b20" target="_blank">National Cyber Security Strategic Plan 2015</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        EgovernmentServices: true, 
        HighlySpecializedSkills: true, 
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=14149&token=e52639123cc23fbb644ea5b6b7a21212abef6c31" target="_blank">National Cyber Security Policy</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        EgovernmentServices: true, 
        HighlySpecializedSkills: true, 
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1109&token=938b9da044cf18d62b000bea1b5eb0a3a428f188" target="_blank">National ICT Plan 2015</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecialisedSkills: true, 
        Startups: true, 
        TechHubs: true, 
        Funding: true, 
        SMARTCities: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=106&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=937fc4bbe44be37027d62c32bf1e3a27" target="_blank">Regulation on Short Code Allocation</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ShortCodes: true,
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=24017&token=27022a3472f75d0e07381f48cca993cfb1b77868" target="_blank">National Broadband Policy 2013</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        LastMile: true, 
        IntermediateSkills: true, 
        OpenAccess: true, 
        Interconnectivity: true, 
        Broadband: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1100&token=b780b7fc46e8f535135506278f4097ede0063ccc" target="_blank">Media Law 2013</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        Multimedia: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=452&tx_news_pi1%5Bday%5D=10&tx_news_pi1%5Bmonth%5D=10&tx_news_pi1%5Byear%5D=2017&cHash=f016c00683e74ea621bbfc02e5d4545a" target="_blank">Law Establishing Rwanda Utilities Regulatory Authorities</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalMarketRegulation: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=113&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=fba61207d063195bbe0c5bd903e36489" target="_blank">Regulation on Quality of Service of Cellular Mobile and Fixed Network Services</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Bandwidth: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=108&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=1ecb7af5cf992710e8e0d24679ce72f8" target="_blank">Regulation on the Use of Unlicenced Frequency</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=107&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=7dcdbfd465a2bed14e820a6f577b0c7b" target="_blank">Guidelines for Fibre Optic Underground Installation</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FirstMile: true,
        Fiber: true,
        Cable: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/Publication/laws/GUIDELINES_for_Fiber_and_Duct_Sharing_v12.pdf" target="_blank">Guideline on Sharing Fibre and Duct Infrastructure in Rwanda</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Fiber: true,
        Duct: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=119&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=564b8fc67a09b3446e81011af616e490" target="_blank">Guidelines on Siting and Sharing of Telecommunication Base Infrastructure</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        TelecommunicationTowers: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=112&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=a60eb1ba2380566599f8cb07407fcb8c" target="_blank">Regulation for International Gateway Traffic Verification System</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1112&token=9ce1e92138d6fe8eb84966d06c920711b3f6ca03" target="_blank">National Informational and Communication Infrastructure Policy 2010 (The NICI-2010 Plan)</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecialisedSkills: true, 
        Startups: true, 
        TechHubs: true, 
        Funding: true, 
        SMARTCities: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/ICT/Laws/Law__On_the_Intellectual_Property.pdf" target="_blank">Law N 31/2009 of 26/10/2009 on Protection of Intellectual Property</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Copyright: true,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=124&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=e7fd8d78eb0553ff6ed541835787aa6a" target="_blank">Guidelines for the Operation of Internet Cafes in Rwanda</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        Broadband: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=122&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=97f82817ba07e7f03adec4faec077e34" target="_blank">Guidelines on Rwanda Internet Exchange Point (RINEX) Point</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/index.php?id=104&tx_news_pi1%5Bnews%5D=121&tx_news_pi1%5Bday%5D=17&tx_news_pi1%5Bmonth%5D=5&tx_news_pi1%5Byear%5D=2017&cHash=52936ea6cf24b80e36eda73387cc5b76" target="_blank">Guidelines for Internet Resource Management and Allocation in General and Domain in Particular</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProtection: true,
        Domain: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/docs/Board_Decisions/GUIDELINES_EMF.pdf" target="_blank">Guidelines for Limiting Human Exposure to Electromagnetic Fields Under 300ghz</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        RadioFrequency: true,
        ElectromagneticFields: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://rura.rw/fileadmin/Documents/docs/Interconnect_guidlines.pdf" target="_blank">Interconnection Guidelines</a></span>',
        date: '01/01/2003',
        date_text: "2003",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Interconnectivity: true,
        Numbering: true
    },
    {
        arrayItem: true,
        country: 'Rwanda',
        reg_name: '<span class="reg-name"><a href="https://www.minict.gov.rw/index.php?eID=dumpFile&t=f&f=1107&token=19a0fd85a4bcc12c0543ac8c0dc3d969a358b1d8" target="_blank">ICT Policy NICI 2001-2005</a></span>',
        date: '01/01/2001',
        date_text: "2001",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        IntermediateSkills: true, 
        HighlySpecialisedSkills: true, 
        Startups: true, 
        TechHubs: true, 
        Funding: true, 
        SMARTCities: true
    },
    {
        arrayItem: true,
        row: 155,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.cdp.sn/sites/default/files/transactions.pdf" target="_blank">Law No. 2008-08 Of January 25, 2008, On Electronic Transactions</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        ConsumerProtection: true 
    }, 
    {
        arrayItem: true,
        row: 156,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://cdp.sn/sites/default/files/crytologie.pdf" target="_blank">Law No. 2008-41 Of August 20, 2008, Relating To Cryptology</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Cybersecurity: true 
    }, 
    {
        arrayItem: true,
        row: 157,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://adie.sn/sites/default/files/lois/4-cybercrime.pdf" target="_blank">Law No. 2008-11 Of January 25, 2008, Relating To Cybercrime</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Cybersecurity: true 
    }, 
    {
        arrayItem: true,
        row: 158,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://asutic.org/wp-content/uploads/2020/01/Loi%202018-28.pdf" target="_blank">Law No. 2018-28 On The Electronic Communications Code</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        DataProtection: true,
        DigitalPayments: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true 
    }, 
    {
        arrayItem: true,
        row: 159,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.droit-afrique.com/uploads/Senegal-Loi-2020-01-creation-promotion-startup.pdf" target="_blank">Law No. 2020-01 Of January 6, 2020, Relating To The Creation And Promotion Of Startups In Senegal</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Law',
        entrepreneurship: true,
        digitalEntrepreneurship: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DataProtection: true,
        DigitalSignatures: true,
        Ecommerce: true,
        ArtificialIntelligence: true 
    }, 
    {
        arrayItem: true,
        row: 160,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.asn.sn/fr/download/52070/Loi%20N%C2%B02021-25%20sur-les-prix-et-la-protection-du-consommateur1.pdf" target="_blank">Law No. 2021-25 On Prices And Consumer Protection</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Drones: true,
        ElectronicWaste: true,
        Ecommerce: true 
    }, 
    { 
        arrayItem: true,
        row: 161,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.sentresor.org/app/uploads/Loi-2008-10-25-01-2008-orientation-societe-information.pdf" target="_blank">Law No. 2008-10 Of January 25, 2008, Relating To The Framework Law On The Information Society</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true 
    }, 
    {
        arrayItem: true,
        row: 162,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.africa-laws.org/Senegal/Competition%20law/la%20loi%20n%C2%B0%2094-63%20du%2022%20Ao%C3%BBt%201994%20sur%20les%20prix,%20la%20concurrence%20et%20le%20contentieux%20%C3%A9conomique.pdf" target="_blank">Law No. 94-63 Of August 22, 1994, On Prices, Competition And Economic Litigation</a></span>',
        date: '01/01/1994',
        date_text: "1994",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 163,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://artp.sn/decret-ndeg2019591-portant-organisation-et-fonctionnement-de-lartp" target="_blank">Decree No. 2019-591 On The Organization And Operation Of The ARTP</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalBusiness: true,
        financialServices: true,
        Broadband: true,
        DataProtection: true,
        DigitalPayments: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true 
    }, 
    {
        arrayItem: true,
        row: 164,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://fdsut.sn/wp-content/uploads/2022/10/Decret-2019-593-relatif-a-Acces-Service-Universel.pdf" target="_blank">Decree No. 2019-593 Relating To Access To The Universal Service</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 165,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://paens.sn/wp-content/uploads/2024/07/TDR-Renforcement-des-reglementations-sur-partage-infrastrcutures_BM_-Vfin-valide.pdf" target="_blank">Decree No. 2022-1357 Of July 7, 2022, Relating To Interconnection To Infrastructure Sharing And Access</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 166,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.cdp.sn/sites/default/files/D%C3%A9cret_2008_719_Communications_Electroniques.pdf" target="_blank">LAW, POLICY OR REGULATION NAME</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Regulation',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalPayments: true 
    }, 
    {
        arrayItem: true,
        row: 167,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.cdp.sn/content/d%C3%A9cret-n%C2%B0-2008-720-du-30-juin-2008-relatif-%C3%A0-la-certification-%C3%A9lectronique-pris-pour" target="_blank">Decree No. 2008-720 Of June 30, 2008, Relating To Electronic Certification</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Regulation',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        TypeApproval: true,
        EndUserEquipment: true 
    }, 
    {
        arrayItem: true,
        row: 168,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://artp.sn/decret-ndeg2019592-precisant-les-regles-applicables-aux-operateurs-puissants" target="_blank">Decree No. 2019-592 Specifying The Rules Applicable To Operators With Significant Power In A Market In The Electronic Communications Sector</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 169,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://artp.sn/decret-ndeg2019-1877-relatif-aux-frequences-radioelectriques" target="_blank">Decree No. 2019-1877 Relating To Radio Frequencies</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        Spectrum: true 
    }, 
    {
        arrayItem: true,
        row: 170,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://asutic.org/wp-content/uploads/2020/01/Arrete_Conjoint_2019_Fixant_les_frais_et_Redevances_des_Frequences_Radioelectriques.pdf" target="_blank">Joint decree No. 025982 setting the costs and fees for radio frequencies</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Spectrum: true 
    }, 
    {
        arrayItem: true,
        row: 171,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.adie.sn/sites/default/files/lois/Decret%202019-593%20relatif%20a%20Acces%20Service%20Universel.pdf" target="_blank">Joint Decree No. 025753 Specifying The Areas Eligible For The Universal Service</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Regulation',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 172,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.numerique.gouv.sn/sites/default/files/SNC2022-vf.pdf" target="_blank">Senegal National Cybersecurity Strategy (SNC2022)</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true 
    }, 
    {
        arrayItem: true,
        row: 173,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.numerique.gouv.sn/sites/default/files/loi%20n°2018-28%20du%2012%20décembre%202018%20portant%20Code%20des%20Communications%20électroniques.pdf" target="_blank">Law No. 2018-28 of December 12, 2018 Relating to the Electronic Communications Code</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Broadband: true,
        Broadcasting: true,
        DigitalPayments: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name">National Data Strategy</span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProtection: true,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.sec.gouv.sn/publications/lois-et-reglements/decret-ndeg-2022-1814-du-26-septembre-2022-relatif-aux-attributions" target="_blank">Decree No 2022-1814 relating to the powers of the Minister of Communication, Telecommunications and the Digital Economy</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalMarketRegulation: true
    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.itu.int/en/ITU-D/Cybersecurity/Documents/National_Strategies_Repository/SNC2022-Senegal-NCS-Jan-2018_eng.pdf" target="_blank">Senegal National Cybersecurity Strategy</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.itu.int/net4/wsis/archive/stocktaking/Project/Details?projectId=1488401022" target="_blank">Digital Senegal 2025</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        EndUserEquipment: true, 
        Cybersecurity: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        DigitalMarketRegulation: true, 
        FoundationalSkills: true, 
        IntermediateSkills: true
    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.sec.gouv.sn/actions-et-realisations/telecommunications-et-economie-numerique/les-reformes-du-secteur-du" target="_blank">Decree No. 2015-678 of May 22, 2015</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalMarketRegulation: true
    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.sec.gouv.sn/actions-et-realisations/telecommunications-et-economie-numerique/les-reformes-du-secteur-du" target="_blank">Decree No. 2014-770 of June 14, 2014</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ConsumerProtection: true
    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/en/document/4wvmzgfvky?page=6" target="_blank">Law no 2011-01 on the Telecommunications Code</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true,
        Communication: true,
        UniversalAccess: true,
        Frequency: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://www.dataguidance.com/legal-research/laws-regulations-and-community-measures-relative-personal-data-protection-senegal" target="_blank">Personal Data Protection, Act No 2008-12 of 25 January 2008</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProtection: true,
        Cybercrime: true,
        Cybersecurity: true,
        Communication: true

    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="http://www.ist-africa.org/home/default.asp?page=doc-by-id&docid=5557" target="_blank">Law No 2008-46 Establishing a Royalty on Access or Use of Public Telecommunication Networks</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        EndUserSubscriberService: true
    },
    {
        arrayItem: true,
        country: 'Senegal',
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/api/documents/download?_id=5d94329c9b93fa001b803d36" target="_blank">Law No 2008 12 on The Protection of Personal Data</a></span>',
        date: '01/01/2008',
        date_text: "2008",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProtection: true,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        row: 29,
        country: 'Somalia',
        reg_name: '<span class="reg-name"><a href="https://moct.gov.so/en/wp-content/uploads/2019/11/National-ICT-Policy-Strategy-2019-2024.pdf" target="_blank">National ICT Policy & Strategy 2019-2024</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        basic: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Broadband: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalPayments: true,
    },
    {
        arrayItem: true,
        row: 30,
        country: 'Somalia',
        reg_name: '<span class="reg-name"><a href="https://dpa.gov.so/assets/legal/act/Somalia%20Data%20Protection%20Act-2.pdf" target="_blank">Law No.005 of 2023 Data Protection Act</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        row: 31,
        country: 'Somalia',
        reg_name: '<span class="reg-name"><a href="https://moct.gov.so/en/wp-content/uploads/2023/06/National-Communications-law.pdf" target="_blank">National Communication Law</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Spectrum: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 248,
        country: 'South Africa',
        reg_name: '<span class="reg-name"><a href="https://cybercrimesact.co.za/act/" target="_blank">Cybercrimes Act</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 249,
        country: 'South Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/201409/13000.pdf" target="_blank">Independent Communications Authority of South Africa Act (ICASA)</a></span>',
        date: '01/01/2000',
        date_text: "2000",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,  
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 250,
        country: 'South Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/201409/ndp-2030-our-future-make-it-workr.pdf" target="_blank">National Development Plan (NDP) 2030</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Policy',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        physicalInfrastructure: true,
        UniversalAccess: true 
    },  
    {
        arrayItem: true,
        row: 251,
        country: 'South Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/201409/ndp-2030-our-future-make-it-workr.pdf" target="_blank">National Cybersecurity Policy</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true  
    }, 
    {
        arrayItem: true, 
        row: 252, 
        country: 'South Africa', 
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/201409/31999277.pdf" target="_blank">Broadcasting Act</a></span>', 
        date: '01/01/2009', 
        date_text: "2009",
        classification: 'Law', 
        digitalInfrastructure: true, 
        invisibleInfrastructure: true, 
        BroadcastingandMultimediaServices:true
    },
    {
        arrayItem: true, 
        row: 253, 
        country: 'South Africa', 
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/202406/50741gen2533.pdf" target="_blank">National Policy on Data and Cloud</a></span>', 
        date: '01/01/2024', 
        date_text: "2024",
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true,
        invisibleInfrastructure: true, 
        digitalGovernment: true,
        DataCentersandCloudServices: true,
    },
    {
        arrayItem: true, 
        row: 254, 
        country: 'South Africa', 
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/202406/50741gen2533.pdf" target="_blank">National Broadband Internet Policy</a></span>', 
        date: '01/01/2010', 
        date_text: "2010",
        classification: 'Policy', 
        digitalInfrastructure: true, 
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true, 
        digitalGovernment: true,
        digitalBusiness: true,
        BroadbandInternet: true,
        DataProtection: true,
        DigitalPayments: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        QualityofService: true,
    },
    {
        arrayItem: true, 
        row: 255, 
        country: 'South Africa', 
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/32186_467.pdf" target="_blank">Consumer Protections Act</a></span>', 
        date: '01/01/2008', 
        date_text: "2008",
        classification: 'Law', 
        digitalInfrastructure: true, 
        invisibleInfrastructure: true, 
        CustomerProtection: true, 
        DataProtection: true, 
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/documents/electronic-communications-act-policy-high-demand-spectrum-and-policy-direction-licensing-0" target="_blank">Electronic Communications Act: Proposed Amendment of Policy on High Demand Spectrum and Policy Direction on the Licensing of a  Wireless Open Access Network 2022</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        OpenAccess: true,
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/202104/44389gon206.pdf" target="_blank">National Policy on Data and Cloud 2021</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Broadband: true,
        Broadcasting: true,
        CloudPolicy: true,
        Communication: true,
        Cybersecurity: true,
        FoundationalSkills: true,
        IntermediateSkills: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/mobile-broadband-services-regulations-2021" target="_blank">Mobile Broadband Services Regulation 2021</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        License: true,
        Broadband: true,
        DigitalMarketRegulation: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/202009/43730gen513.pdf" target="_blank">National Digital and Future Skills Strategy</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        ArtificialIntelligence: true,
        FoundationalSkills: true,
        Funding: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        Startups: true,
        TechHubs: true,
        TVET: true,
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://nemisa.co.za/wp-content/uploads/2020/08/NEMISA-Strategic-Plan-2020-21-2024-25.pdf" target="_blank">National Electronic Media Institute of South Africa Strategic Plan (NEMISA) 2020-2024</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        CloudPolicy: true,
        Cybersecurity: true,
        Ecommerce: true,
        EgovernmentServices: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        SMARTCities: true,
        TechHubs: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/numbering-plan-amendment-regulations-2020" target="_blank">Numbering Plan Amendment Regulations 2020</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Numbering: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/number-portability-regulations-2018-1" target="_blank">Number Portability Regulation 201P</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Portability: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/end-user-and-subscriber-service-charter-regulations/end-user-and-subscriber-service-charter-amendment-regulations-2018" target="_blank">End-user and Subscriber Service Charter Amendment Regulation 2018</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EndUserSubscriberService: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://nemisa.co.za/wp-content/uploads/2020/07/NEMISA-Strat-Plan-2017-22-Final-2-1.pdf" target="_blank">National Electronic Media Institute of South Africa Strategic Plan (NEMISA) 2017-2021</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        Ecommerce: true,
        EgovernmentServices: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        SMARTCities: true,
        TechHubs: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.ellipsis.co.za/wp-content/uploads/2018/05/Final_ICT_SMME_Development_Strategy.pdf" target="_blank">ICT SMME Development Strategy 2017</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Ecommerce: true,
        FirstMile: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        LastMile: true,
        MiddleMile: true,
        TechHubs: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/201704/40772gon343.pdf" target="_blank">National E-strategy Digital Society South Africa</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Broadcasting: true,
        Ecommerce: true,
        EgovernmentServices: true,
        LastMile: true,
        Payments: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.ellipsis.co.za/wp-content/uploads/2018/05/Final_ICT_SMME_Development_Strategy.pdf" target="_blank">ICT SMME Development Strategy Unlocking the Potential of ICT SMMEs</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Ecommerce: true,
        EgovernmentServices: true,
        Payments: true,
        TechHubs: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/201711/41241gen886.pdf" target="_blank">National E-government Strategy and Roadmap 2017</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        DigitalGovernment: true,
        Ecommerce: true,
        EgovernmentServices: true,
        FoundationalSkills: true,
        IntermediateSkills: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/documents/electronic-communications-act-national-integrated-ict-policy-white-paper-3-oct-2016-0000" target="_blank">National Integrated ICT Policy White Paper</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        DigitalIdentification: true,
        EgovernmentServices: true,
        PostalServices: true,
        Cybersecurity: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://nemisa.co.za/wp-content/uploads/2020/07/Strategic-Plan-2015-2020-9-March-2015-FNL-signed.pdf" target="_blank">National Electronic Media Institute of South Africa Strategic Plan (NEMISA) 2015-2020</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        Ecommerce: true,
        EgovernmentServices: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        SMARTCities: true,
        TechHubs: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/documents/disability-and-ict-strategy" target="_blank">Disability and ICT Strategy</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalMarketRegulation: true,
        FoundationalSkills: true,
        IntermediateSkills: true,
        PostalServices: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://pmg.org.za/files/140219nbpolicy.ppt" target="_blank">National Broadband Policy South Africa Connect</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        CloudPolicy: true,
        Ecommerce: true,
        EgovernmentServices: true,
        EndUserEquipment: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        LastMile: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.ellipsis.co.za/wp-content/uploads/2015/08/Final-Draft-Children-Strategy-July-2014-002.pdf" target="_blank">Childrens Empowerment and Information Communication Technology Strategy</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FoundationalSkills: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/code-for-people-with-disabilities" target="_blank">Code for Persons with Disabilities Regulations 2014</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ConsumerProtection: true, 
        Spectrum: true, 
        License: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/201409/3706726-11act4of2013popi.pdf" target="_blank">The Protection of Personal Information Act 4 of 2013 (“POPI”)</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        PersonalData: true,
        Cybersecurity: true,
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="http://www.usaasa.org.za/export/sites/usaasa/resource-centre/download-centre/downloads/USAASA_Strategic_Plan_2012-2015-_FINAL.pdf" target="_blank">USAASA Strategic Plan 2012-2015</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Broadband: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/sites/default/files/gcis_document/201409/35255gon308b.pdf" target="_blank">ICT Policy 2012</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: false,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DigitalIdentification: true,
        Ecommerce: true,
        EgovernmentServices: true,
        Payments: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://biz-file.com/f/1108/NeSPA_draft__11_October_2010.pdf" target="_blank">National E-Skill Plan of Action</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Ecommerce: true,
        EgovernmentServices: true,
        FoundationalSkills: true,
        IntermediateSkills: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/interconnection-regulations" target="_blank">Interconnections Regulatons 2010</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Interoperability: true,
        Interconnectivity: true,
        DigitalMarketRegulation: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/e-rate-regulations-2009" target="_blank">E-rate Regulations</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Erate: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/code-for-persons-with-disabilities-2007" target="_blank">Code for Persons with Disabilities Regulations 2007</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ConsumerProtection: true,
        License: true,
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/geographic-number-portability-regulations-2007" target="_blank">Geographic Number Portability Regulations</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Portability: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/code-of-conduct-for-electronic-communications-and-electronic-communications-network-services-licences" target="_blank">Code of Conduct for Electronic Communications and Electronic Communication Network Services License</a></span>',
        date: '01/01/2007',
        date_text: "2007",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        ConsumerProtection: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.icasa.org.za/legislation-and-regulations/regulations-regarding-the-provisions-of-asymmetrical-digital-subscriber-line-adsl-services" target="_blank">Regulations Asymmetric Digital Subscriber Line 29141</a></span>',
        date: '01/01/2006',
        date_text: "2006",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EndUserSubscriberService: true,
        ConsumerProtection: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.dcdt.gov.za/documents/legislations/acts/file/8-electronic-communications-act-2005.html" target="_blank">Electronic Communications Act 2005</a></span>',
        date: '01/01/2005',
        date_text: "2005",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true,
        Communication: true,
        Competition: true,
        ConsumerProtection: true,
        EndUserEquipment: true,
        Interconnectivity: true,
        License: true,
        Numbering: true,
        RadioFrequency: true,
        TypeApproval: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/documents/electronic-communications-act" target="_blank">Electronic Communication Acts 2005</a></span>',
        date: '01/01/2005',
        date_text: "2005",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadcasting: true,
        Communication: true,
        Competition: true,
        License: true,
        RadioFrequency: true,
        UniversalServiceFund: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.dcdt.gov.za/documents/legislations/acts/file/9-electronic-communications-and-transactions-act-2002.html" target="_blank">Gazette No. 23708 - Electronic Communications and Transactions Act 2002</a></span>',
        date: '01/01/2002',
        date_text: "2002",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Accreditation: true,
        ConsumerProtection: true,
        Cryptography: true,
        Cybercrime: true,
        Database: true,
        DisputeResolution: true,
        EgovernmentServices: true,
        EndUserSubscriberService: true,
        License: true,
        DataProtection: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/documents/electronic-communications-and-transactions-act#:~:text=to%20promote%20universal%20access%20to,of%20e%2Dgovernment%20services%3B%20and" target="_blank">The Electronic Communications and Transactions Act 25 of 2002</a></span>',
        date: '01/01/2002',
        date_text: "2002",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        DigitalMarketRegulation: true,
        EgovernmentServices: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/documents/strategy-information-and-communication-technology-education" target="_blank">Strategy for Information and Communication Technology in Education</a></span>',
        date: '01/01/2001',
        date_text: "2001",
        classification: 'Policy',
        digitalInfrastructure: false,
        digitalPlatform: false,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: false,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        FoundationalSkills: true,
        HighlySpecializedSkills: true,
        IntermediateSkills: true,
        TVET: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.gov.za/documents/electronic-government-digital-future-public-service-it-policy-framework" target="_blank">Electronic Government: The Digital Future 2001</a></span>',
        date: '01/01/2001',
        date_text: "2001",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Ecommerce: true,
        EgovernmentServices: true,
        FoundationalSkills: true,
        IntermediateSkills: true,
        TechHubs: true,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'South_Africa',
        reg_name: '<span class="reg-name"><a href="https://www.dcdt.gov.za/documents/legislations/acts/file/130-state-information-technology-agency-act-no-88-of-1998-pdf-footer.html" target="_blank">State Information Technology Agency Act of 1998</a></span>',
        date: '01/01/1998',
        date_text: "1998",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        DataProcessing: true,
        DigitalIdentification: true,
        DigitalSignatures: true
    },
    {
        arrayItem: true, 
        row: 256, 
        country: 'Sudan', 
        reg_name: '<span class="reg-name"><a href="https://cyrilla.org/api/files/15895337760832wnwr7jhqe1.pdf" target="_blank">Electronic Transactions Act</a></span>', 
        date: '01/01/2007', 
        date_text: "2007",
        classification: 'Law', 
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalIdentification: true,
        ElectronicTransactions: true,
    },
    {
        arrayItem: true, 
        row: 257, 
        country: 'Sudan', 
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/es/document/oi11tiq4rq?page=1" target="_blank">Cybercrime Act</a></span>', 
        date: '01/01/2007', 
        date_text: "2007",
        classification: 'Law',
        digitalInfrastructure: true, 
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataProtection: true,
    },
    {
        arrayItem: true,
        row:12,
        country: 'Tanzania',
        reg_name: '<span class="reg-name"><a href="https://www.ictc.go.tz/public/documents/tanzania_digital_economy_strategy.pdf" target="_blank">Digital Economy Strategic Framework</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        ArtificialIntelligence: true,
        Broadband: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitialID: true,
        DigitalPayments: true,
        DigitalSignatures:true,
        Ecommerce: true,
        ElectronicWaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        PostalServices: true,
        ServicesQuality: true,
        SMARTCities: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserAgreement: true, 
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 13,
        country: 'Tanzania',
        reg_name: '<span class="reg-name"><a href="https://www.mawasiliano.go.tz/document-groups/strategies" target="_blank">Tanzania National Broadband Internet Strategy</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,
        Broadband: true, 
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalSignatures: true,
        Ecommerce: true,
        ElectronicWaste: true,
        FirstMile: true,
        SecondMiles: true,
        LastMile: true,
        InternetResources:true,
        ServicesQuality: true,
        SMARTCities: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserAgreement: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 14,
        country: 'Tanzania',
        reg_name: '<span class="reg-name"><a href="https://www.mawasiliano.go.tz/document-groups/policies" target="_blank">National Postal Policy</a></span>',
        date: '01/01/2003',
        date_text: "2003",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        PostalServices: true
    },
    {
        arrayItem: true,
        row: 15,
        country: 'Tanzania',
        reg_name: '<span class="reg-name"><a href="https://www.ictc.go.tz/public/documents/samias_transformation_agenda.pdf" target="_blank">Tanzania Digital Transformation Agenda</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalInnovation: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true, 
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true
    },
    {
        arrayItem: true,
        row: 16,
        country: 'Tanzania',
        reg_name: '<span class="reg-name"><a href="https://www.mawasiliano.go.tz/uploads/documents/sw-1693455522-DOCUMENT%20TO%20UPLOAD%20DRAFT%20AUGUST%20NICTP.pdf" target="_blank">National ICT Policy</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        ArtificialIntelligence: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
        ElectronicWaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true,
        SMARTCities: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true
    },
    {
        arrayItem: true,
        row: 17,
        country: 'Tanzania',
        reg_name: '<span class="reg-name"><a href="https://www.parliament.go.tz/polis/uploads/bills/acts/1452061463-ActNo-14-2015-Book-11-20.pdf" target="_blank">Cybercrimes Act</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        row: 18,
        country: 'Tanzania',
        reg_name: '<span class="reg-name"><a href="https://www.tcra.go.tz/uploads/documents/sw-1670493092-The%20Electronic%20and%20Postal%20Communications%20Act%20R_E%202022.pdf" target="_blank">Electronic and Postal Communications Act</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true
    },
    {
        arrayItem: true, 
        row: 287, 
        country: 'Togo', 
        reg_name: '<span class="reg-name"><a href="https://numerique.gouv.tg/wp-content/uploads/2019/12/Loi-n-2018-026-du-07-decembre-2018-cybersecurite-et-cybercriminalite.pdf" target="_blank">Law No. 2018-026 on Cybersecurity and the fight against Cybercrime</a></span>', 
        date: '01/01/2018', 
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        Cybersecurity: true, 
        DataProtection: true,
    },
    {
        arrayItem: true, 
        row: 288, 
        country: 'Togo', 
        reg_name: '<span class="reg-name"><a href="https://numerique.gouv.tg/wp-content/uploads/2020/01/PR-Loi-n%C2%B02017-007-relative-aux-transaction-e%CC%81lectroniques-du-12-07-17-n%C2%B00923.pdf" target="_blank">Law No. 2017-007/PR relating to Electronic Transactions</a></span>', 
        date: '01/01/2017', 
        date_text: "2017",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        ElectronicTransactions: true,
    },
    {
        arrayItem: true, 
        row: 289, 
        country: 'Togo', 
        reg_name: '<span class="reg-name"><a href="hhttps://numerique.gouv.tg/wp-content/uploads/2020/01/Loi-n-2019-014-du-29-octobre-2019-relative-a-la-protection-des-donnees-a-caractere-personnel-in-JO29102019.pdf" target="_blank">Law No. 2019-014 relating to the Protection of Personal data</a></span>', 
        date: '01/01/2019', 
        date_text: "2019",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        DataProtection: true
    },
    {
        arrayItem: true, 
        row: 290, 
        country: 'Togo', 
        reg_name: '<span class="reg-name"><a href="https://numerique.gouv.tg/wp-content/uploads/2024/05/FR-Strategie-Togo-Digital-2025.pdf" target="_blank">Togo Digital Strategy (2022-2025)</a></span>', 
        date: '01/01/2022', 
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        TechHubs: true,
        digitalEntrepreneurship: true,

    },
    {
        arrayItem: true,
        row: 233,
        country: 'Tunisia',
        reg_name: '<span class="reg-name"><a href="https://www.mtc.gov.tn/fileadmin/documents_utiles/Recueil_de_la_protection_des_donnees_personnelles.pdf" target="_blank">Personal Data Protection Law</a></span>',
        date: '01/01/2004',
        date_text: "2004",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        DataProtection: true,
        DigitalIdentification: true 
    }, 
    {
        arrayItem: true,
        row: 234,
        country: 'Tunisia',
        reg_name: '<span class="reg-name"><a href="https://paradigmhq.org/wp-content/uploads/2024/06/Tunisia-Country-Report.pdf" target="_blank">National Digital Development Strategy</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        ArtificialIntelligence: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 235,
        country: 'Tunisia',
        reg_name: '<span class="reg-name"><a href="https://www.icnl.org/wp-content/uploads/Decloi54.pdf" target="_blank">Cybercrime Act (Decree Law)</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataProtection: true 
    }, 
    {
        arrayItem: true,
        row: 236,
        country: 'Tunisia',
        reg_name: '<span class="reg-name"><a href="https://www.ancs.tn/sites/default/files/ncss24/downloads/FR%20-%20Natl%20Cyberspace%20Strategy%202020-2025.pdf" target="_blank">National Cyberspace Strategy (2020-2025)</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 237,
        country: 'Tunisia',
        reg_name: '<span class="reg-name"><a href="https://www.mtc.gov.tn/fileadmin/StartupAct/Startup_Act_-_procedures_conditions_obtention_du_label.pdf" target="_blank">Tunisia Start-up Act</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalPlatform: true,
        entrepreneurship: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        innovationHubs: true,
        digitalEntrepreneurship: true,
        Startups: true,
        PriPubPartnership: true 
    }, 
    {
        arrayItem: true,
        row: 238,
        country: 'Tunisia',
        reg_name: '<span class="reg-name"><a href="https://ictpolicyafrica.org/en/document/94x41x6wcz6?page=1" target="_blank">Electronic Exchanges and E-Commerce Act</a></span>',
        date: '01/01/2000',
        date_text: "2000",
        classification: 'Law',
        digitalPlatform: true,
        entrepreneurship: true,
        invisibleInfrastructure: true,
        innovationHubs: true,
        DigitalIdentification: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    { 
        arrayItem: true,
        row: 201,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://www.ucc.co.ug/wp-content/uploads/2023/10/UCC-Act-2013.pdf" target="_blank">Uganda Communications Act</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 202,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/wp-content/uploads/2019/12/UGANDA-Electronic-Transactions-Act-No.-8-of-2011.pdf" target="_blank">The Electronic Transactions Act</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalPayments: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 203,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/wp-content/uploads/2019/12/UGANDA-Electronic-Signatures-Act-No.-7-of-2011.pdf" target="_blank">The Electronic Signatures Act</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        DigitalIdentification: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 204,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://www.ict.go.ug/wp-content/uploads/2018/06/Regional-Broadband Internet-Strategy_Signed.pdf" target="_blank">Regional Broadband Internet Strategy</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Ecommerce: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 205,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://www.ict.go.ug/wp-content/uploads/2018/06/Spectrum-Policy-revised-011117.pdf" target="_blank">National Spectrum Policy</a></span>',
        date: '01/01/2017',
        date_text: "2017",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2023/01/24/national-cybersecuritystrategy/" target="_blank">National Cyber Security Strategy</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        EgovernmentServices: true, 
        Ecommerce: true,
        DigitalSignatures: true,
        Cybersecurity: true,
        TechHubs: true,
        Startups: true,
        Incubators: true,
        IntermediateSkills: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/wp-content/uploads/2023/08/MOICT-DIGITAL-TRANSFORMATION-ROADMAP.pdf" target="_blank">Digital Transformation Roadmap</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: true,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: true,
        EgovernmentServices: true, 
        Broadband: true,
        DataCenter: true,
        CloudPolicy: true,
        Incubators: true, 
        Cybersecurity: true,
        DataProtection: true,
        FoundationalSkills: true, 
        HighlySpecializedSkills: true,
        Startups: true, 
        FirstMile: true,
        MiddleMile: true,
        LastMile: true,
        Spectrum: true, 
        Payments: true, 
        Blockchain: true,
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="hhttps://ict.go.ug/wp-content/uploads/2023/08/MOICT-BIG-DATA-UTILIZATION-STRATEGY.pdf" target="_blank">Big Data Utilization Strategy</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        BigData: true, 
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2023/01/24/national-cybersecuritystrategy/" target="_blank">National Cyber Security Strategy</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: false,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: true,
        EgovernmentServices: true, 
        Ecommerce: true, 
        DigitalSignatures: true, 
        Cybersecurity: true, 
        TechHubs: true, 
        Startups: true, 
        Incubators: true,
        IntermediateSkills: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/wp-content/uploads/2019/03/Data-Protection-and-Privacy-Act-2019.pdf" target="_blank">The Data Protection and Privacy Act 2019</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        DataProtection: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2019/05/22/national-postcode-and-addressing-system-policy/" target="_blank">National Postcode and Addressing systems Policy 2019.</a></span>',
        date: '01/01/2019',
        date_text: "2019",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        PostalServices: true,
        Database: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2018/10/31/national-broadband-policy/" target="_blank">National Broadband Policy 2018</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Broadband: true, 
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        EndUserEquipment: true, 
        Interoperability: true, 
        EgovernmentServices: true, 
        License: true, 
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://www.ict.go.ug/wp-content/uploads/2018/06/Registration-of-Person-Act-2015.pdf" target="_blank">The Registration of Persons Act</a></span>',
        date: '01/01/2015',
        date_text: "2015",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        Database: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2018/11/23/national-ict-policy/" target="_blank">National ICT Policy 2014</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: true,
        innovationHubs: false,
        FirstMile: true, 
        MiddleMile: true, 
        LastMile: true, 
        Cybersecurity: true, 
        Broadcasting: true, 
        Communication: true, 
        EgovernmentServices: true, 
        Ecommerce: true, 
        Payments: true, 
        DigitalMarketRegulation: true, 
        HighlySpecializedSkills: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2018/06/17/national-information-technology-authority-uganda-egovernment-regulations-2014/" target="_blank">National Information Technology Authority Uganda (E-government Regulations 2014)</a></span>',
        date: '01/01/2014',
        date_text: "2014",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        Database: true,
        DataCenter: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2019/12/03/electronics-transaction-regulations-2013/" target="_blank">Electronic Transactions Regulations 2013</a></span>',
        date: '01/01/2013',
        date_text: "2013",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2018/06/17/electronic-waste-management-policy-2012/" target="_blank">Electronic Waste Management Policy 2012</a></span>',
        date: '01/01/2012',
        date_text: "2012",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        ElectronicWaste: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2019/12/03/the-electronics-signatures-act-2011" target="_blank">The Electronic Signature Act 2011</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        DigitalSignatures: true,
        License: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/wp-content/uploads/2019/12/UGANDA-Computer-Misuse-Act-No.-2-of-2011-1.pdf" target="_blank">The Computer Misuse Act 2011</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        ElectronicFraud: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2019/12/03/national-e-government-policy-framework-2011/" target="_blank">National E-government Policy Framework 2011</a></span>',
        date: '01/01/2011',
        date_text: "2011",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: false,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: false,
        basic: true,
        advanced: true,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        EgovernmentServices: true,
        IntermediateSkills: true,
        HighlySpecializedSkills: true,
        Ecommerce: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://www.ngobureau.go.ug/en/law-and-regulations/regulations-of-interception-of-communications-act-2010" target="_blank">The Regulation of Interception of Communication Act 2010</a></span>',
        date: '01/01/2010',
        date_text: "2010",
        classification: 'Regulation',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true,
        Interception: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="https://ict.go.ug/2018/06/17/national-spectrum-policy-for-uganda/" target="_blank">National Spectrum Policy for Uganda 2009</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Spectrum: true
    },
    {
        arrayItem: true,
        country: 'Uganda',
        reg_name: '<span class="reg-name"><a href="http://judiciary.go.ug/files/downloads/access%20to%20informatioinformation%20Act2005.pdf" target="_blank">Access to Information ACT 2005</a></span>',
        date: '01/01/2005',
        date_text: "2005",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: false,
        digitalSkills: false,
        entrepreneurship: false,
        physicalInfrastructure: false,
        invisibleInfrastructure: true,
        digitalGovernment: false,
        digitalBusiness: false,
        financialServices: false,
        basic: false,
        advanced: false,
        digitalEntrepreneurship: false,
        innovationHubs: false,
        Cybersecurity: true
    },
    {
        arrayItem: true,
        row: 186,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://www.mots.gov.zm/wp-content/uploads/2023/10/National-ICT-Policy-2023-Implimentation-Plan.pdf" target="_blank">National ICT Policy 2023</a></span>',
        date: '01/01/2023',
        date_text: "2023",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        ArtificialIntelligence: true,
        Broadband: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
        ElectronicWaste: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true,
        SMARTCities: true,
        Spectrum: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 187,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://zambia.misa.org/2021/01/27/national-cyber-security-policy-approved/" target="_blank">National cyber security Policy</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 188,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://www.szi.gov.zm/wp-content/uploads/2022/02/ECT-Act-2009.pdf" target="_blank">Electronic Communications and Transactions Act</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Law',
        digitalPlatform: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        DigitalPayments: true,
        DigitalSignatures: true 
    }, 
    {
        arrayItem: true,
        row: 189,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://www.parliament.gov.zm/sites/default/files/documents/acts/Act%20No.%203%20The%20Data%20Protection%20Act%202021_0.pdf" target="_blank">Data Protection Act</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Law',
        digitalInfrastructure: true,
        invisibleInfrastructure: true,
        DataProtection: true,
        DigitalIdentification: true 
    }, 
    {
        arrayItem: true,
        row: 190,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://www.dataguidance.com/sites/default/files/si_52_of_2021.pdf" target="_blank">Cyber security and cybercrime Act</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true 
    }, 
    {
        arrayItem: true,
        row: 191,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://www.mots.gov.zm/wp-content/uploads/2023/10/National-Digital-Transformation-Strategy.pdf" target="_blank">Digital Transformation Strategy</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        DataProtection: true,
        DigitalPayments: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true 
    }, 
    {
        arrayItem: true,
        row: 192,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://shorturl.at/dWWyl" target="_blank">The Information and Communication Technology Act</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices:true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true 
    }, 
    {
        arrayItem: true,
        row: 193,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://ictaz.org.zm/wp-content/uploads/2021/11/ICTAZ-Act-2018.pdf" target="_blank">The Information Communication Technology Association of Zambia  Act</a></span>',
        date: '01/01/2018',
        date_text: "2018",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        Broadcasting: true,
        Cybersecurity: true,
        DataCentersandCloudServices:true,
        DataProtection: true,
        DigitalIdentification: true,
        DigitalPayments: true,
        DigitalSignatures: true,
        Ecommerce: true,
        FirstMile: true 
    }, 
    {
        arrayItem: true,
        row: 194,
        country: 'Zambia',
        reg_name: '<span class="reg-name"><a href="https://www.zambialaws.com/Zambia2016Pdfsi/SI%2031%20of%202016.pdf" target="_blank">The Postal Services Act</a></span>',
        date: '01/01/2009',
        date_text: "2009",
        classification: 'Law',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        PostalServices: true 
    }, 
    {
        arrayItem: true,
        row: 206,
        country: 'Zimbabwe',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=1IkvTpXB1RssRPasOfAIlsvnvvd4ge5AA" target="_blank">Postal And Telecommunications Act</a></span>',
        date: '01/01/2001',
        date_text: "2001",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalBusiness: true,
        financialServices: true,
        Broadband: true,
        SecondMile: true,
        LastMile: true,
        PostalServices: true,
        ServiceQuality: true,
        TypeApproval: true,
        EndUserEquipment: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 207,
        country: 'Zimbabwe',
        reg_name: '<span class="reg-name"><a href="https://drive.google.com/open?id=187hhjATUAVxGqmRfuI20GDuTQ26dUrvP" target="_blank">Zimbabwe National Policy For Information Communication Technology (ICT) 2016</a></span>',
        date: '01/01/2016',
        date_text: "2016",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalPlatform: true,
        digitalSkills: true,
        entrepreneurship: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        digitalBusiness: true,
        financialServices: true,
        basic: true,
        advanced: true,
        innovationHubs: true,
        digitalEntrepreneurship: true,
        Broadband: true,
        Cybersecurity: true,
        DataCentersandCloudServices: true,
        Ecommerce: true,
        ElectronicWaste: true,
        SecondMile: true,
        LastMile: true,
        Spectrum: true,
        UniversalAccess: true 
    }, 
    {
        arrayItem: true,
        row: 208,
        country: 'Zimbabwe',
        reg_name: '<span class="reg-name"><a href="https://www.ictministry.gov.zw/wp-content/uploads/2024/01/Smart%20Zimbabwe%202030%20Master%20Plan.pdf" target="_blank">Smart Zimbabwe 2030 Masterplan</a></span>',
        date: '01/01/2024',
        date_text: "2024",
        classification: 'Policy',
        digitalInfrastructure: true,
        digitalSkills: true,
        entrepreneurship: true,
        digitalGovernment: true,
        basic: true,
        invisibleInfrastructure: true,
        innovationHubs: true,
        digitalEntrepreneurship: true,
        Broadband: true,
        DataProtection: true,
        DigitalPayments: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true,
        InternetResources: true,
        ServiceQuality: true 
    }, 
    {
        arrayItem: true,
        row: 209,
        country: 'Zimbabwe',
        reg_name: '<span class="reg-name"><a href="file:///D:/ATU/ATU-DEVELOPMENT/Zimbabwe_DPA.pdf" target="_blank">Cyber and Data Protection Act</a></span>',
        date: '01/01/2021',
        date_text: "2021",
        classification: 'Law',
        digitalInfrastructure: true,
        digitalPlatform: true,
        invisibleInfrastructure: true,
        digitalGovernment: true,
        Cybersecurity: true,
        DataProtection: true 
    }, 
    {
        arrayItem: true,
        row: 210,
        country: 'Zimbabwe',
        reg_name: '<span class="reg-name"><a href="https://www.ictministry.gov.zw/wp-content/uploads/2024/01/National%20ICT%20Policy%202022-2027.pdf" target="_blank">National ICT Policy(2022-2027)</a></span>',
        date: '01/01/2022',
        date_text: "2022",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        FirstMile: true,
        SecondMile: true,
        LastMile: true 
    }, 
    {
        arrayItem: true,
        row: 211,
        country: 'Zimbabwe',
        reg_name: '<span class="reg-name"><a href="https://www.ictministry.gov.zw/wp-content/uploads/2024/01/National%20Broadband Internet%20Plan.pdf" target="_blank">National Broadband Internet Plan (2020-2030)</a></span>',
        date: '01/01/2020',
        date_text: "2020",
        classification: 'Policy',
        digitalInfrastructure: true,
        physicalInfrastructure: true,
        invisibleInfrastructure: true,
        Broadband: true,
        LastMile: true 
    }, 
];

let mapDataArray = dataArray;
let filteredDataArray = [];
let categoryList = ["Law", "Regulation", "Policy"];
let filtered = false;
let html = "";
let clickedCountry = "All_Countries";

let filterIconsDigInfra = "";
let filterIconsDigPlatforms = "";
let filterIconsDigSkills = "";
let filterIconsDigInn = "";
let filterIconsKeyData = "";

// defaults for country display area
const descriptionCountryName = ref("All Countries");
const descriptionCountryNameEncoded = ref("All_Countries");
const descriptionCountryLonger = ref("")
const descriptionCountryPopulation = ref("1.515 billion");
const descriptionCountryLandMass = ref("29,922,059");
const infrastructureLaws = ref("0");
const infrastructurePolicies = ref("0");
const infrastructureRegs = ref("0");
const platformLaws = ref("0");
const platformPolicies = ref("0");
const platformRegs = ref("0");
const skillsLaws = ref("0");
const skillsPolicies = ref("0");
const skillsRegs = ref("0");
const innovationLaws = ref("0");
const innovationPolicies = ref("0");
const innovationRegs = ref("0");


const countryDataArray = [
    {
        countryName: 'All_Countries',
        checkbox: 'All_CountriesCheckbox',
        descriptionCountryNameValue: "All Countries",
        descriptionCountryPopulationValue: "",
        descriptionCountryLandMassValue: "",
        descriptionCountryLongerValue: "",
        clickableCountry: true,
        clickedCountry: "All Countries"
    },
    {
        countryName: 'African_Union',
        checkbox: 'African_UnionCheckbox',
        descriptionCountryNameValue: "African Union",
        descriptionCountryPopulationValue: "",
        descriptionCountryLandMassValue: "",
        descriptionCountryLongerValue: 'The African Union (AU) is driving Africa&rsquo;s digital transformation through its <a href="https://au.int/sites/default/files/documents/38507-doc-dts-english.pdf">Digital Transformation Strategy for Africa (2020&ndash;2030)</a>, which focuses on digital infrastructure, policy, skills, innovation, and digital services. Key initiatives include the <a href="https://au.int/en/treaties/african-union-convention-cyber-security-and-personal-data-protection">Malabo Convention on cybersecurity</a>, the <a href="https://au.int/sites/default/files/documents/42078-doc-AU-DATA-POLICY-FRAMEWORK-ENG1.pdf">AU Data Policy Framework</a>, and the African Digital Compact launched in 2024 to support inclusive growth. Additionally, the <a href="https://au.int/sites/default/files/documents/44004-doc-EN-_Continental_AI_Strategy_July_2024.pdf">Continental Artificial Intelligence Strategy</a> and the <a href="https://au.int/en/documents/20240521/african-union-child-online-safety-and-empowerment-policy#:~:text=The%20African%20Union%20Child%20Online,to%20promote%20child%20online%20safety">Child Online Safety and Empowerment Strategy</a> address AI ethics and digital protection for youth. As of 2024, the AU has fifty-five member states with a combined population of approximately 1.49 billion people, a nominal gross <a href="https://www.g20.org/en/news/african-union-in-the-g20-a-geopolitical-and-economic-leap-for-the-continent#:~:text=Economically%2C%20the%20African%20Union%20has,an%20abundance%20of%20natural%20resources.">domestic product (GDP) of approximately $3 trillion.</a>',
        clickableCountry: true,
        clickedCountry: "African_Union"
    },
    {
        countryName: 'Algeria',
        checkbox: 'AlgeriaCheckbox',
        descriptionCountryNameValue: "Algeria",
        descriptionCountryPopulationValue: "47,022,473",
        descriptionCountryLandMassValue: "2,381,740 sq km",
        descriptionCountryLongerValue: 'Algeria&rsquo;s digital economy is characterized by investments in digital services delivery and connectivity. The country has an internet penetration of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">72.9 percent</a> (34.2 million users) as of early 2024. In July 2024, the government launched <a href="https://www.ecofinagency.com/telecom/2507-45761-algeria-rolls-out-new-digital-strategy-to-drive-economic-modernization">a digital transformation strategy</a> to streamline management by transitioning from paper-based to digital systems, enhancing transparency and economic performance. The government has also <a href="https://www.trade.gov/country-commercial-guides/algeria-digital-economy#:~:text=The%20government%20of%20Algeria%20has,technology%20hubs%20around%20the%20country.">pursued key initiatives to expand digital infrastructure, foster ICT entrepreneurship, and technology hubs</a>.',
        clickableCountry: true,
        clickedCountry: "Algeria"
    },
    {
        countryName: 'Angola',
        checkbox: 'AngolaCheckbox',
        descriptionCountryNameValue: "Angola",
        descriptionCountryPopulationValue: "37,202,061",
        descriptionCountryLandMassValue: "1,246,700 sq km",
        descriptionCountryLongerValue: 'Angola&rsquo;s digital economy is progressing with targeted investments in broadband expansion, digital inclusion, and e-government services. In June 2024, <a href="https://www.worldbank.org/en/news/factsheet/2024/06/27/inclusive-digitalization-in-eastern-and-southern-africa-program-afe-angola">a $300 million loan</a> was approved to enhance affordable broadband connectivity and digital services, aiming to attract an additional $80 million in private sector investment. As of January 2024, internet penetration in Angola is <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">39.3 percent</a>, while <a href="https://datareportal.com/reports/digital-2024-angola#:~:text=There%20were%2014.63%20million%20internet,percent%20of%20the%20total%20population">cellular mobile penetration has reached 78.4 percent</a>, covering a significant portion of Angola&rsquo;s 37 million population.',
        clickableCountry: true,
        clickedCountry: "Angola"
    },
    {
        countryName: 'Benin',
        checkbox: 'BeninCheckbox',
        descriptionCountryNameValue: "Benin",
        descriptionCountryPopulationValue: "14,697,052",
        descriptionCountryLandMassValue: "112,622 sq km",
        descriptionCountryLongerValue: 'Benin&rsquo;s digital economy is characterized by significant investments aimed at expanding connectivity and modernizing public administration. In 2021, the government <a href="https://developingtelecoms.com/telecom-business/200-buddecom-market-reports/12699-benin-secures-funds-to-improve-national-fibre-network.html">prioritized enhancement of fiber optic networks, increased broadband coverage, and digitized government services</a>. As of January 2024, internet penetration in Benin reached <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">33.8 percent</a>, with 4.69 million users&mdash;a <a href="https://datareportal.com/reports/digital-2024-benin">2.7 percent increase</a> from the previous year.',
        clickableCountry: true,
        clickedCountry: "Benin"
    },
    {
        countryName: 'Burundi',
        checkbox: 'BurundiCheckbox',
        descriptionCountryNameValue: "Burundi",
        descriptionCountryPopulationValue: "13,590,102",
        descriptionCountryLandMassValue: "27,830 sq km",
        descriptionCountryLongerValue: 'Burundi is advancing its digital development through initiatives focused on expanding infrastructure, broadband access, and digital skills. In February 2024, the country completed the first phase of its <a href="https://techpoint.africa/2024/10/07/burundi-zambia-underwater-fibre/#:~:text=In%20February%202024%2C%20Burundi%20marked,including%20the%20state%2Downed%20Onatel.">national fiber optic network</a> project, launched in 2012, enhancing internet connectivity nationwide. As of January 2024, Burundi has an <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">internet penetration rate of 11. 3 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Burundi"
    },
    {
        countryName: 'Cameroon',
        checkbox: 'CameroonCheckbox',
        descriptionCountryNameValue: "Cameroon",
        descriptionCountryPopulationValue: "30,966,105",
        descriptionCountryLandMassValue: "475,440 sq km",
        descriptionCountryLongerValue: 'Cameroon&rsquo;s digital economy initiatives highlight a strong focus on modernizing communication systems and fostering technological innovation. <a href="https://www.minpostel.gov.cm/index.php/en/les-grands-chantiers/138-cameroon-digital-strategic-plan-2020">The Strategic Plan for a Digital Cameroon</a> outlines key objectives, including the expansion of broadband infrastructure, stimulation of domestic digital content production, and promotion of digital literacy across society. To support these goals, the government has established the <a href="https://www.cdic.cm">Cameroon Digital and Innovation Center (CDIC)</a>, to serve as a hub for ICT project development and innovation, thereby nurturing the local digital industry. As of January 2024, Cameroon had an internet penetration rate of approximately <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">43.9 percent.</a>',
        clickableCountry: true,
        clickedCountry: "Cameroon"
    },
    {
        countryName: 'Chad',
        checkbox: 'ChadCheckbox',
        descriptionCountryNameValue: "Chad",
        descriptionCountryPopulationValue: "19,093,595",
        descriptionCountryLandMassValue: "1,284,000",
        descriptionCountryLongerValue: 'Chad&rsquo;s digital economy is characterized by strategic initiatives to expand infrastructure and improve digital services. As of January 2024, internet penetration in the country reached <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">22.5 percent</a>, equating to approximately 4.18 million users&mdash;a <a href="https://datareportal.com/reports/digital-2024-chad">9.2 percent increase</a> from the previous year. To enhance nationwide internet access the country recently licensed <a href="https://www.reuters.com/technology/space/musks-starlink-gets-chad-go-ahead-improve-internet-access-2024-11-12/">a satellite internet</a> provider.',
        clickableCountry: true,
        clickedCountry: "Chad"
    },
    {
        countryName: 'Cote_D_I_viore',
        checkbox: 'Cote_D_I_vioreCheckbox',
        descriptionCountryNameValue: "Côte d'Ivoire",
        descriptionCountryPopulationValue: "29,981,758",
        descriptionCountryLandMassValue: "322,463 sq km",
        descriptionCountryLongerValue: 'C&ocirc;te d&apos;Ivoire&rsquo;s digital economy initiatives center on infrastructure development and digital education transformation through its <a href="https://www.ecofinagency.com/public-management/2910-46084-cote-divoire-invests-364mln-to-digitalize-education-system">National Education Digitalization Strategy 2024&ndash;2028</a>, which represents a $364 million investment to integrate digital technologies into the education sector. The nation has implemented legislative frameworks for data privacy and cybersecurity. As of early 2024, C&ocirc;te d&apos;Ivoire recorded 11.23 million internet users, with an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">38.4 percent</a> and <a href="https://datareportal.com/reports/digital-2024-cote-divoire">43.59 million mobile connections</a>, representing 149.1 percent of the population. The country&rsquo;s digital infrastructure development includes the construction of a <a href="https://www.businesswire.com/news/home/20240924503143/en/Ivory-Coast-Gains-Significant-Boost-to-Digital-Economy-with-Launch-of-Raxio-Data-Centre">national Tier 3 data center</a> to support growing data services demand.',
        clickableCountry: true,
        clickedCountry: "Cote_D_I_viore"
    },
    {
        countryName: 'Dem_Rep_Congo',
        checkbox: 'Dem_Rep_CongoCheckbox',
        descriptionCountryNameValue: "Democratic Republic of Congo",
        descriptionCountryPopulationValue: "115,403,027",
        descriptionCountryLandMassValue: "2,344,858 sq km",
        descriptionCountryLongerValue: 'The Democratic Republic of the Congo (DRC) is advancing its digital economy through strategic initiatives aimed at enhancing digital infrastructure, improving literacy, and fostering innovation. The government emphasizes expanding connectivity and developing digital skills, while implementing frameworks for secure digital transactions and personal data management. As of January 2024, the DRC reported an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">27.2 percent</a> with approximately 28.31 million internet users. In June 2024, the DRC joined the <a href="https://www.worldbank.org/en/news/factsheet/2024/06/27/inclusive-digitalization-in-eastern-and-southern-africa-program-democratic-republic-of-congo-afe-drc">Inclusive Digitalization in Eastern and Southern Africa</a> Program, aiming to extend internet access and digital skills to 30 million individuals, with a focus on gender parity.',
        clickableCountry: true,
        clickedCountry: "Dem_Rep_Congo"
    },
    {
        countryName: 'Egypt',
        checkbox: 'EgyptCheckbox',
        descriptionCountryNameValue: "Egypt",
        descriptionCountryPopulationValue: "111,247,248",
        descriptionCountryLandMassValue: "1,001,450 sq km",
        descriptionCountryLongerValue: 'Egypt&rsquo;s digital economy has experienced significant growth, with the Information and Communications Technology (ICT) sector achieving a <a href="https://www.trade.gov/country-commercial-guides/egypt-digital-economy#:~:text=Overview,in%20fiscal%20year%202019%2F2020">growth rate of 15.2 percent</a> in fiscal year 2022&ndash;2023, surpassing the overall GDP growth rate. The sector&rsquo;s contribution to GDP increased to 5.1 percent in the same period, up from 4.4 percent in 2019&ndash;2020. As of January 2024, Egypt had an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">72.2 percent</a>. The government has also recently implemented strategic initiatives like the <a href="https://mcit.gov.eg/en/ICT_Strategy">ICT 2030 Strategy</a>, focusing on digital transformation, capacity building, and infrastructure development to enhance the digital economy.',
        clickableCountry: true,
        clickedCountry: "Egypt"
    },
    {
        countryName: 'Eswatini',
        checkbox: 'EswatiniCheckbox',
        descriptionCountryNameValue: "Eswatini",
        descriptionCountryPopulationValue: "1,138,089",
        descriptionCountryLandMassValue: "17,364 sq km",
        descriptionCountryLongerValue: 'Eswatini is characterized by strategic initiatives and legislative measures. As of January 2024, internet penetration reached <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">58.3 percent,</a> with approximately 708,200 users, reflecting a <a href="https://datareportal.com/reports/digital-2024-eswatini">modest 0.8 percent growth</a> over the previous year. The government of Eswatini is investing in digital platforms and financial services, supported by institutions like the <a href="https://rstp.org.sz/about-us/">Royal Science and Technology Park</a>, to foster innovation and entrepreneurship.',
        clickableCountry: true,
        clickedCountry: "Eswatini"
    },
    {
        countryName: 'Gabon',
        checkbox: 'GabonCheckbox',
        descriptionCountryNameValue: "Gabon",
        descriptionCountryPopulationValue: "2,455,105",
        descriptionCountryLandMassValue: "267,667 sq km",
        descriptionCountryLongerValue: 'Gabon&rsquo;s digital economy is characterized by targeted investments to enhance public services, data protection, and cybersecurity. In early 2024, the government launched <a href="https://www.worldbank.org/en/news/press-release/2024/01/02/gabon-the-world-bank-boost-its-support-to-accelerate-digital-transformation">the Digital Gabon Project</a>, a $68.5 million initiative aimed at accelerating the digitization of public services and improving cybersecurity infrastructure. As of January 2024, internet penetration in Gabon reached <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">73.7 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Gabon"
    },
    {
        countryName: 'Gambia',
        checkbox: 'GambiaCheckbox',
        descriptionCountryNameValue: "Gambia",
        descriptionCountryPopulationValue: "2,523,327",
        descriptionCountryLandMassValue: "11,300 sq km",
        descriptionCountryLongerValue: 'Gambia&rsquo;s digital economy strategy emphasizes investing in foundational digital infrastructure and improved connectivity to broaden access to digital services. Universal access initiatives include the <a href="https://gambiadaily.gov.gm/index.php/digital-mapping-project-address-gambias-logistical-nightmare">digital addressing project</a>, which has installed over 32,000 address boards in the Kanifing Municipal Council, enhancing service accessibility and delivery. Additionally, <a href="https://www.biometricupdate.com/202406/presight-to-fast-track-the-gambias-transformation-through-digital-id-govt-initiative">Gambia is developing a unified digital identification system</a> to secure online transactions and streamline public services, aligning with the goals outlined in the National Digital Economy Master Plan. As of January 2024, Gambia has an <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">internet penetration rate of 54.2 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Gambia"
    },
    {
        countryName: 'Ghana',
        checkbox: 'GhanaCheckbox',
        descriptionCountryNameValue: "Ghana",
        descriptionCountryPopulationValue: "35,589,092",
        descriptionCountryLandMassValue: "238,533 sq km",
        descriptionCountryLongerValue: 'Ghana&rsquo;s digital economy initiatives highlight a significant focus on digital identification and universal access, particularly to bridge the digital divide and make services more affordable. The country&rsquo;s <a href="https://thedocs.worldbank.org/en/doc/412821598381054828-0090022020/original/GhanaDE4ALOWRes.pdf">national ID initiative, the &ldquo;Ghana Card,&rdquo; and e-government platform Ghana.gov aim to unify identification</a>, support secure digital services, financial inclusion, and efficient public service delivery. Ghana also has a strong emphasis on digital payments, with <a href="https://www.sciencedirect.com/science/article/pii/S1925209924005862#:~:text=The%20value%20of%20money%20transactions,mobile%20money%20market%20in%20Africa">over 19 million active mobile money</a> accounts as of 2023, driving financial inclusion. As of early 2024, Ghana has approximately <a href="https://iipgh.org/the-internet-we-want-in-ghana-a-narrative-on-current-realities/#:~:text=As%20of%20early%202024%2C%20Ghana,remains%20inconsistent%20and%20often%20subpar">24.06 million internet users</a>, translating to a penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">69.8 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Ghana"
    },
    {
        countryName: 'Guinea_Bissau',
        checkbox: 'Guinea_BissauCheckbox',
        descriptionCountryNameValue: "Guinea-Bissau",
        descriptionCountryPopulationValue: "2,132,325",
        descriptionCountryLandMassValue: "36,125 sq km",
        descriptionCountryLongerValue: 'Guinea Bissau&rsquo;s digital economy initiatives highlight a strong focus on expanding digital infrastructure to foster connectivity and access to digital services. In 2022, the country connected to <a href="https://blogs.worldbank.org/en/digital-development/digitalizing-guinea-bissau-future-starts-now#:~:text=For%20instance%2C%20the%20country&apos;s%20Africa,dynamism%20into%20the%20public%20sector">the Africa Coast to Europe (ACE) submarine cable network</a>, which significantly improved internet speed and affordability. As of January 2024, the country had approximately 686,200 internet users, representing an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">31.6 percent</a>. Mobile technology remains the <a href="https://datareportal.com/reports/digital-2024-guinea-bissau">primary driver of digital access in Guinea Bissau</a>, with 2.25 million cellular mobile connections active in early 2024.',
        clickableCountry: true,
        clickedCountry: "Guinea_Bissau"
    },
    {
        countryName: 'Kenya',
        checkbox: 'KenyaCheckbox',
        descriptionCountryNameValue: "Kenya",
        descriptionCountryPopulationValue: "58,246,378",
        descriptionCountryLandMassValue: "580,367 sq km",
        descriptionCountryLongerValue: 'Kenya&rsquo;s digital economy is expanding, with the ICT sector growing at an annual rate of 10.8 percent over the past decade and projected to <a href="https://www.trade.gov/country-commercial-guides/kenya-digital-economy#:~:text=According%20to%20the%20Business%20Monitor,the%20country&apos;s%20GDP%20by%202025">contribute 9.24 percent of GDP</a> by 2025. In 2023, the Government of Kenya unveiled the <a href="https://www.president.go.ke/government-has-prioritised-digital-super-highway-says-president-ruto/">Digital Superhighway Project</a>, aimed at enhancing the nation&rsquo;s ICT infrastructure. A central focus of the initiative is the nationwide expansion of Kenya&rsquo;s fiber optic network. As of January 2024, internet penetration in Kenya stands at <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">40.8 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Kenya"
    },
    {
        countryName: 'Lesotho',
        checkbox: 'LesothoCheckbox',
        descriptionCountryNameValue: "Lesotho",
        descriptionCountryPopulationValue: "2,227,548",
        descriptionCountryLandMassValue: "30,355 sq km",
        descriptionCountryLongerValue: 'Lesotho&rsquo;s digital economy initiatives center on infrastructure development through its <a href="https://www.gov.ls/download/draft-lesothos-national-digital-transformation-strategy/">National Digital Transformation Strategy</a>, and <a href="https://www.gov.ls/download/research-and-innovation-policy/">Research and Innovation Policy</a>, which also focuses on emerging technologies including AI. The nation has also implemented several legislative frameworks for digital infrastructure and data security. As of early 2024, Lesotho recorded an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">47 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Lesotho"
    },
    {
        countryName: 'Libya',
        checkbox: 'LibyaCheckbox',
        descriptionCountryNameValue: "Libya",
        descriptionCountryPopulationValue: "7,361,263",
        descriptionCountryLandMassValue: "1,759,540 sq km",
        descriptionCountryLongerValue: 'Libya&rsquo;s digital economy initiatives center on digital transformation through its <a href="https://www.gia.gov.ly/en/projects/digital-lab/">Digital Lab initiative</a>, which connects innovators, entrepreneurs, researchers, and investors across the country. The country has also implemented legislative frameworks for digital activities and secure transactions. As of January 2024, Libya recorded 6.13 million internet users, with an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">88.4 percent and approximately 12.85 million mobile connections</a>. The country&rsquo;s digital infrastructure includes connections to multiple undersea cables including <a href="https://www.submarinenetworks.com/en/systems/asia-europe-africa/silphium">SILPHIUM submarine cable system</a> connecting Libya to Greece and other Mediterranean countries, the <a href="https://www.submarinenetworks.com/en/systems/asia-europe-africa/eig">Europe India Gateway, </a>and <a href="https://www.submarinenetworks.com/en/systems/asia-europe-africa/imewe#:~:text=The%20IMEWE%20(%20India%2DMiddle%20East,Alexandria%20and%20Suez%20in%20Egypt.">India-Middle East-Western Europe submarine cable systems</a>.',
        clickableCountry: true,
        clickedCountry: "Libya"
    },
    {
        countryName: 'Mali',
        checkbox: 'MaliCheckbox',
        descriptionCountryNameValue: "Mali",
        descriptionCountryPopulationValue: "21,990,607",
        descriptionCountryLandMassValue: "1,240,192 sq km",
        descriptionCountryLongerValue: 'Mali&rsquo;s digital economy initiatives show efforts by the government to enhance digital infrastructure, promote electronic transactions, and bolster cybersecurity. The <a href="https://www.google.com/url?sa=t&amp;source=web&amp;rct=j&amp;opi=89978449&amp;url=https://communication.gouv.ml/wp-content/uploads/2021/01/DraftMN2020.pdf&amp;ved=2ahUKEwirxf2L2fCJAxWrMVkFHSciMqgQFnoECBcQAQ&amp;usg=AOvVaw1DFlt6lsUNAhqIxz4nOcyE">Mali Digital Plan 2020</a> has been central to these advancements. Mali currently has an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">33.1 percent</a>. To further improve connectivity, <a href="https://www.usaid.gov/digital-development/mali-deca-country-snapshot">the government has laid over 3,000 kilometers of fiber optic cable</a>, equipped schools with computers and satellite links, with priority given to rural areas.',
        clickableCountry: true,
        clickedCountry: "Mali"
    },
    {
        countryName: 'Mauritania',
        checkbox: 'MauritaniaCheckbox',
        descriptionCountryNameValue: "Mauritania",
        descriptionCountryPopulationValue: "4,328,040",
        descriptionCountryLandMassValue: "1,030,700 sq km",
        descriptionCountryLongerValue: 'Mauritania&rsquo;s digital economy is advancing through strategic initiatives and legislative reforms aimed at enhancing connectivity and digital services. As of January 2024, the country has an internet penetration of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">44.4 percent</a>. The government is also implementing the <a href="https://mtnima.gov.mr/وزير-التحول-الرقمي-يستعرض-الأجندة-الو/">National Digital Transformation Agenda 2022&ndash;2025</a>, focusing on e-government, regulation, infrastructure, digital administration, business sector transformation, and human resources.',
        clickableCountry: true,
        clickedCountry: "Mauritania"
    },
    {
        countryName: 'Mauritius',
        checkbox: 'MauritiusCheckbox',
        descriptionCountryNameValue: "Mauritius",
        descriptionCountryPopulationValue: "1,320,504",
        descriptionCountryLandMassValue: "2,040 sq km",
        descriptionCountryLongerValue: 'Mauritius has made significant strides in developing its digital economy. As of January 2024, the country had approximately 997,580 internet users, representing a <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">75.5 percent penetration rate among its 1.3 million residents</a>. <a href="https://mdpa.govmu.org/mdpa/wp-content/uploads/2024/04/NCB-Information-Society-Outlook-Aug-2020.pdf#:~:text=2.0%20Evolution%20of%20the%20ICT%20sector%20over,the%20Mauritian%20economy%20employing%20around%2024%2C000%20people">The ICT sector contributes notably to the national economy</a>, accounting for 7 percent of the Gross Value Added) in 2020, with a real growth rate of 4.9 percent from the previous year. <a href="https://nationalbudget2024.govmu.org/documents/2024_25_V_16_1_MITCI.pdf">Employment within the sector has also seen growth, with 33,500 individuals employed in large ICT establishments as of 2024.</a>',
        clickableCountry: true,
        clickedCountry: "Mauritius"
    },
    {
        countryName: 'Morocco',
        checkbox: 'MoroccoCheckbox',
        descriptionCountryNameValue: "Morocco",
        descriptionCountryPopulationValue: "37,387,585",
        descriptionCountryLandMassValue: "446,550 sq km",
        descriptionCountryLongerValue: 'Morocco&rsquo;s digital economy is characterized by strategic initiatives aimed at enhancing infrastructure, expanding internet accessibility, and fostering innovation. <a href="https://www.mmsp.gov.ma/sites/default/files/2024-09/PlaquetteInstitutionnel_18092024_Ang.pdf">The Digital Morocco 2030 Strategy</a> outlines the plan to generate 240,000 jobs and position the country as a leader in digital productivity. As of early 2024, internet penetration in the country reached <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">90.7 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Morocco"
    },
    {
        countryName: 'Mozambique',
        checkbox: 'MozambiqueCheckbox',
        descriptionCountryNameValue: "Mozambique",
        descriptionCountryPopulationValue: "33,350,954",
        descriptionCountryLandMassValue: "799,380 sq km",
        descriptionCountryLongerValue: 'Mozambique&rsquo;s digital economy is progressing with key investments aimed at enhancing digital services and infrastructure. <a href="https://www.worldbank.org/en/news/press-release/2021/10/22/world-bank-supports-mozambique-s-efforts-towards-access-to-identification-and-digital-transformation">A $150 million grant from 2021</a> supports the Digital Governance and Economy Project, focused on expanding digital public services and civil identification access. As of January 2024, internet penetration in the country is <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">23.2 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Mozambique"
    },
    {
        countryName: 'Namibia',
        checkbox: 'NamibiaCheckbox',
        descriptionCountryNameValue: "Namibia",
        descriptionCountryPopulationValue: "2,803,660",
        descriptionCountryLandMassValue: "825,292 sq km",
        descriptionCountryLongerValue: 'Namibia&rsquo;s digital economy is characterized by strategic investments aimed at enhancing ICT infrastructure and digital services. <a href="https://carnegieendowment.org/research/2024/04/a-digital-odyssey-the-convergence-of-rapid-digitization-population-dynamics-and-financial-risk-in-namibia?lang=en">Efforts to expand broadband access and strengthen cybersecurity frameworks are also underway by the government to support the country&rsquo;s goal of a secure and accessible digital environment</a>. As of January 2024, Namibia has an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">62.2 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Namibia"
    },
    {
        countryName: 'Nigeria',
        checkbox: 'NigeriaCheckbox',
        descriptionCountryNameValue: "Nigeria",
        descriptionCountryPopulationValue: "236,747,130",
        descriptionCountryLandMassValue: "923,768 sq km",
        descriptionCountryLongerValue: 'Nigeria&rsquo;s digital economy initiatives underscore a focus on security and trust, aimed at fostering a secure and reliable digital environment essential for economic growth. The country has prioritized e-government services in recent years through projects like the <a href="https://services.gov.ng/">Central Portal for Government Services</a>, which streamlines public service delivery and enhances citizen engagement through digital platforms. These efforts are part of a broader strategy that has fueled the Information and Communications Technology (ICT) sector, which, according to the Nigerian Bureau of Statistics, contributed approximately <a href="https://www.trade.gov/country-commercial-guides/nigeria-digital-economy#:~:text=The%20Nigerian%20Bureau%20of%20Statistics%20(NBS)%20reports,Gross%20Domestic%20Product%20(GDP)%20in%20Q2%202024">20 percent to Nigeria&rsquo;s real Gross Domestic Product (GDP) in 2024</a>. As of early 2024, Nigeria has an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">45.5 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Nigeria"
    },
    {
        countryName: 'Rwanda',
        checkbox: 'RwandaCheckbox',
        descriptionCountryNameValue: "Rwanda",
        descriptionCountryPopulationValue: "13,337,000",
        descriptionCountryLandMassValue: "26,338 sq km",
        descriptionCountryLongerValue: 'Rwanda&rsquo;s digital transformation strategy centers on building robust digital infrastructure, enhancing digital skills, and ensuring data protection to foster a secure and inclusive economy. With an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">34.4 percent</a> as of 2024, the country is prioritizing accessibility and trust in digital services. Key initiatives include the <a href="https://irembo.gov.rw/home/citizen/all_services">e-government platform Irembo</a>, which streamlines public service delivery, and comprehensive data protection laws that support secure data practices and AI integration.',
        clickableCountry: true,
        clickedCountry: "Rwanda"
    },
    {
        countryName: 'Senegal',
        checkbox: 'SenegalCheckbox',
        descriptionCountryNameValue: "Senegal",
        descriptionCountryPopulationValue: "18,847,519",
        descriptionCountryLandMassValue: "196,722 sq km",
        descriptionCountryLongerValue: 'Senegal&rsquo;s digital economy has experienced significant growth, with the <a href="https://www.giz.de/en/downloads/SectorBrief_Senegal_Technologies.pdf">ICT sector contributing approximately 3.5 percent to the nation&rsquo;s GDP</a>. As of January 2024, the country had an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">60 percent among its over 18 million residents</a>. The government has implemented strategic initiatives like the <a href="https://www.adie.sn/lancement-strategie-2025-2035">Digital Senegal 2025</a> plan, aiming to transform the nation into a digital hub by enhancing infrastructure, promoting e-governance, and fostering innovation.',
        clickableCountry: true,
        clickedCountry: "Senegal"
    },
    {
        countryName: 'Somalia',
        checkbox: 'SomaliaCheckbox',
        descriptionCountryNameValue: "Somalia",
        descriptionCountryPopulationValue: "13,017,273",
        descriptionCountryLandMassValue: "637,657 sq km",
        descriptionCountryLongerValue: 'Somalia&rsquo;s digital economy is characterized by a focus on expanding digital infrastructure, developing digital skills, and enhancing data privacy and cybersecurity. As of early 2024, Somalia has an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">22.7 percent</a>, supported by submarine cables such as the <a href="https://www.submarinenetworks.com/en/systems/asia-europe-africa/eassy">Eastern Africa Submarine System</a> and the <a href="https://wiocc.net/dare1/">Djibouti Africa Regional Express 1</a>, which provide data at $0.50 per GB. Somalia also has one of <a href="https://www.mfw4a.org/sites/default/files/resources/Mobile_Money_in_Somalia_-_WBank.pdf">Africa&rsquo;s highest mobile money usage rates, with 73 percent of people</a> over the age of 16 using these services.',
        clickableCountry: true,
        clickedCountry: "Somalia"
    },
    {
        countryName: 'South_Africa',
        checkbox: 'South_AfricaCheckbox',
        descriptionCountryNameValue: "South Africa",
        descriptionCountryPopulationValue: "60,442,647",
        descriptionCountryLandMassValue: "1,219,090 sq km",
        descriptionCountryLongerValue: 'South Africa&rsquo;s digital economy is robust and expanding, with the <a href="https://iclg.com/practice-areas/telecoms-media-and-internet-laws-and-regulations/south-africa#:~:text=South%20Africa&apos;s%20ICT%20sector%20grew,9%20billion%202021%20to%20R39.">Information and Communications Technology (ICT) sector generating R254.8 billion, in revenue in 2022</a>, reflecting a 4.61 percent growth from the previous year. The country has an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">74.7 percent</a>, as of January 2024. To drive digital transformation, the South African government has introduced strategic initiatives, including the <a href="https://www.gov.za/sites/default/files/gcis_document/202009/43730gen513.pdf">National Digital and Future Skills Strategy</a>, which aims to enhance digital literacy and skills across the population.',
        clickableCountry: true,
        clickedCountry: "South_Africa"
    },
    {
        countryName: 'Sudan',
        checkbox: 'SudanCheckbox',
        descriptionCountryNameValue: "Sudan",
        descriptionCountryPopulationValue: "50,467,278",
        descriptionCountryLandMassValue: "1,861,484 sq km",
        descriptionCountryLongerValue: 'Sudan&rsquo;s digital economy initiatives highlight a strong focus on digital transformation and financial inclusion. The country has established key legislative foundations including the <a href="https://cbos.gov.sd/ar/content/قانون-المعاملات-الالكترونية#:~:text=الأثر%20القانوني%20للسجلات%20الإلكترونية&amp;text=أن%20يتمكن%20المرسل%20إليه%20من,وتاريخ%20ووقت%20وصولها%20وإرسالها%20واستقبالها.">Electronic Transactions Act</a> and <a href="https://cbos.gov.sd/en/content/informatic-offences-combating-act-2007">Cybercrime Act</a> to govern digital identification and cybersecurity. As of January 2024, Sudan had an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">28.7 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Sudan"
    },
    {
        countryName: 'Tanzania',
        checkbox: 'TanzaniaCheckbox',
        descriptionCountryNameValue: "Tanzania",
        descriptionCountryPopulationValue: "67,462,121",
        descriptionCountryLandMassValue: "947,300 sq km",
        descriptionCountryLongerValue: 'Tanzania&rsquo;s digital economy initiatives focus on expanding broadband access and enhancing mobile connectivity. The <a href="https://www.ictc.go.tz/public/documents/tanzania_digital_economy_strategy.pdf">Digital Economy Strategic Framework 2024&ndash;2034</a>, launched in July 2024, aims to position the country as a regional leader in digital transformation and inclusive growth. This framework emphasizes developing platforms like the <a href="https://iafrica.com/wp-content/uploads/2024/11/Tanzania_case_study.pdf">Tanzania Technology Stack</a>, which includes the Jamii Number System for comprehensive service management. As of January 2024, internet penetration stood at <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">31.9 percent, with 21.82 million users</a>.',
        clickableCountry: true,
        clickedCountry: "Tanzania"
    },
    {
        countryName: 'Togo',
        checkbox: 'TogoCheckbox',
        descriptionCountryNameValue: "Togo",
        descriptionCountryPopulationValue: "8,917,994",
        descriptionCountryLandMassValue: "56,785 sq km",
        descriptionCountryLongerValue: 'Togo&rsquo;s digital economy initiatives center on infrastructure development and digital platforms through its <a href="https://numerique.gouv.tg/wp-content/uploads/2024/05/FR-Strategie-Togo-Digital-2025.pdf">Digital Strategy 2022&ndash;2025</a>. The nation has implemented several legislative frameworks to promote a secure digital environment. As of January 2024, Togo recorded 3.44 million internet users, with an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">37.6 percent</a>. The country&rsquo;s digital transformation is exemplified by the Novissi digital payment program, which leveraged mobile technologies and machine learning to distribute financial assistance to <a href="https://www.weforum.org/stories/2023/09/togo-digital-inclusion-journey-to-equitable-access/">citizens during the COVID-19 pandemic</a>.',
        clickableCountry: true,
        clickedCountry: "Togo"
    },
    {
        countryName: 'Tunisia',
        checkbox: 'TunisiaCheckbox',
        descriptionCountryNameValue: "Tunisia",
        descriptionCountryPopulationValue: "12,048,847",
        descriptionCountryLandMassValue: "163,610 sq km",
        descriptionCountryLongerValue: 'Tunisia has made significant advancements in digital transformation, highlighted by the <a href="https://startup.gov.tn/en/startup_act/discover">Startup Act of 2018</a>&mdash;the first of its kind in Africa. This legislation has supported the growth of the country&rsquo;s startup ecosystem, providing tax exemptions, startup leave for entrepreneurs, and dedicated funding mechanisms, resulting in over <a href="https://startup.gov.tn/sites/default/files/2021-11/Startup-Tunisia-Annual-Report-2020-en.pdf">500 officially labeled startups</a>, with a concentration of tech innovation in Tunis. As of January 2024, internet penetration in the country reached <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">79.6 percent, with approximately 9.96 million users.</a>',
        clickableCountry: true,
        clickedCountry: "Tunisia"
    },
    {
        countryName: 'Uganda',
        checkbox: 'UgandaCheckbox',
        descriptionCountryNameValue: "Uganda",
        descriptionCountryPopulationValue: "49,283,041",
        descriptionCountryLandMassValue: "241,038 sq km",
        descriptionCountryLongerValue: 'Uganda&rsquo;s digital economy has experienced significant growth, with the Information and Communications Technology <a href="https://ict.go.ug/wp-content/uploads/2023/08/Digital-Transformation-Roadmap.pdf">(ICT) sector contributing 9 percent to the nation’s Gross Domestic Product</a>. The government has also implemented key policies to foster a robust digital environment. As of January 2024, Uganda had an internet penetration rate of <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">27 percent</a>.',
        clickableCountry: true,
        clickedCountry: "Uganda"
    },
    {
        countryName: 'Zambia',
        checkbox: 'ZambiaCheckbox',
        descriptionCountryNameValue: "Zambia",
        descriptionCountryPopulationValue: "20,799,116",
        descriptionCountryLandMassValue: "752,618 sq km",
        descriptionCountryLongerValue: 'Zambia&rsquo;s digital economy efforts center on expanding connectivity and enhancing public services under the <a href="https://www.mots.gov.zm/wp-content/uploads/2023/10/National-Digital-Transformation-Strategy.pdf">National Digital Transformation Strategy 2023&ndash;2027</a>. As of early 2024, internet penetration reached <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">31.2 percent, with 6.51 million users</a>.',
        clickableCountry: true,
        clickedCountry: "Zambia"
    },
    {
        countryName: 'Zimbabwe',
        checkbox: 'ZimbabweCheckbox',
        descriptionCountryNameValue: "Zimbabwe",
        descriptionCountryPopulationValue: "17,150,352",
        descriptionCountryLandMassValue: "390,757 sq km",
        descriptionCountryLongerValue: 'Zimbabwe&rsquo;s digital economy initiatives prioritize mobile connectivity and financial inclusion, with digital financial services playing a critical role in the economy. <a href="https://documents1.worldbank.org/curated/en/982981621880260112/pdf/Digital-Economy-for-Zimbabwe-Country-Diagnostic-Report.pdf">Mobile money usage is extensive, comprising 96 percent of all transactions in the country, with approximately 7.1 million active mobile wallets supporting financial access across the population.</a> The government is also investing in broadband expansion and digital government platforms to address connectivity gaps and enhance service delivery. As of 2022, <a href="https://www.statista.com/statistics/1124283/internet-penetration-in-africa-by-country/">internet penetration stood at 32.6 percent.</a>',
        clickableCountry: true,
        clickedCountry: "Zimbabwe"
    },
]

function getNumber(countryValue) {
    if (countryValue === "All_Countries") {
        // count number of results in each country
        infrastructureLaws.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Law" && v.digitalInfrastructure == true);
        }).length
        infrastructurePolicies.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Policy" && v.digitalInfrastructure == true);
        }).length
        infrastructureRegs.value= mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Regulation" && v.digitalInfrastructure == true);
        }).length
        platformLaws.value  = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Law" && v.digitalPlatform == true);
        }).length;
        platformPolicies.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Policy" && v.digitalPlatform == true);
        }).length;
        platformRegs.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Regulation" && v.digitalPlatform == true);
        }).length;
        skillsLaws.value  = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Law" && v.digitalSkills == true);
        }).length
        skillsPolicies.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Policy" && v.digitalSkills == true);
        }).length;
        skillsRegs.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Regulation" && v.digitalSkills == true);
        }).length;
        innovationLaws.value  = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Law" && v.entrepreneurship == true);
        }).length
        innovationPolicies.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Policy" && v.entrepreneurship == true);
        }).length;
        innovationRegs.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Regulation" && v.entrepreneurship == true);
        }).length;

    }
    else {
        // count number of results in each country
        infrastructureLaws.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Law" && v.digitalInfrastructure == true && v.country === countryValue);
        }).length
        infrastructurePolicies.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Policy" && v.digitalInfrastructure == true && v.country === countryValue);
        }).length
        infrastructureRegs.value= mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Regulation" && v.digitalInfrastructure == true && v.country === countryValue);
        }).length
        platformLaws.value  = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Law" && v.digitalPlatform == true && v.country === countryValue);
        }).length;
        platformPolicies.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Policy" && v.digitalPlatform == true && v.country === countryValue);
        }).length;
        platformRegs.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Regulation" && v.digitalPlatform == true && v.country === countryValue);
        }).length;
        skillsLaws.value  = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Law" && v.digitalSkills == true && v.country === countryValue);
        }).length
        skillsPolicies.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Policy" && v.digitalSkills == true && v.country === countryValue);
        }).length;
        skillsRegs.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Regulation" && v.digitalSkills == true && v.country === countryValue);
        }).length;
        innovationLaws.value  = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Law" && v.entrepreneurship == true && v.country === countryValue);
        }).length
        innovationPolicies.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Policy" && v.entrepreneurship == true && v.country === countryValue);
        }).length;
        innovationRegs.value = mapDataArray.filter(function(v, i) {
            return (v["classification"] === "Regulation" && v.entrepreneurship == true && v.country === countryValue);
        }).length;
    }
}

// listing key data points to use in all of the results sections
function listKeyData(mapDataArray, e) {
    filterIconsDigInfra = '<div class="foci">'
    if (mapDataArray[e].physicalInfrastructure){
        filterIconsDigInfra = filterIconsDigInfra + '<div class="digital-infrastructure">Physical</div>'
    }
    if (mapDataArray[e].invisibleInfrastructure){
        filterIconsDigInfra = filterIconsDigInfra + '<div class="digital-infrastructure">Invisible</div>'
    }
    filterIconsDigInfra = filterIconsDigInfra + "</div>"
    filterIconsDigPlatforms = '<div class="foci">'
    if (mapDataArray[e].digitalGovernment){
        filterIconsDigPlatforms = filterIconsDigPlatforms + '<div class="digital-platform">Government</div>'
    }
    if (mapDataArray[e].digitalBusiness){
        filterIconsDigPlatforms = filterIconsDigPlatforms + '<div class="digital-platform">Business</div>'
    }
    if (mapDataArray[e].financialServices){
        filterIconsDigPlatforms = filterIconsDigPlatforms + '<div class="digital-platform">Financial Services</div>'
    }
    filterIconsDigPlatforms = filterIconsDigPlatforms + "</div>"
    filterIconsDigSkills = '<div class="foci">'
    if (mapDataArray[e].basic){
        filterIconsDigSkills = filterIconsDigSkills + '<div class="digital-skills">Basic</div>'
    }
    if (mapDataArray[e].advanced){
        filterIconsDigSkills = filterIconsDigSkills + '<div class="digital-skills">Advanced</div>'
    }
    filterIconsDigSkills = filterIconsDigSkills + "</div>"
    filterIconsDigInn = '<div class="foci">'
    if (mapDataArray[e].digitalEntrepreneurship){
        filterIconsDigInn = filterIconsDigInn + '<div class="innovation-entrepreneurship">Entrepreneurship</div>'
    }
    if (mapDataArray[e].innovationHubs){
        filterIconsDigInn = filterIconsDigInn + '<div class="innovation-entrepreneurship">Innovation</div>'
    }
    filterIconsDigInn = filterIconsDigInn + "</div>"
    filterIconsKeyData = '<div class="foci key-data">'
    if (mapDataArray[e].ArtificialIntelligence){
        filterIconsKeyData = filterIconsKeyData + '<div class="multiple">Artificial Intelligence</div>'
    }
    if (mapDataArray[e].Broadband){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Broadband Internet</div>'
    }
    if (mapDataArray[e].Broadcasting){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Broadcasting and Multimedia Services</div>'
    }
    if (mapDataArray[e].ChildProtection){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Child Protection</div>'
    }
    if (mapDataArray[e].CloudServices){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Cloud Services</div>'
    }
    if (mapDataArray[e].Competition){
        filterIconsKeyData = filterIconsKeyData + '<div class="multiple">Competition</div>'
    }
    if (mapDataArray[e].ConsumerProtection){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Consumer Protection</div>'
    }
    if (mapDataArray[e].Cybersecurity){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Cybersecurity</div>'
    }
    if (mapDataArray[e].Cyberterrorism){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Cyberterrorism</div>'
    }
    if (mapDataArray[e].Data){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Data</div>'
    }
    if (mapDataArray[e].DataCenter){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Data Centers</div>'
    }
    if (mapDataArray[e].DataProtection){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Data Protection</div>'
    }
    if (mapDataArray[e].DigitalIdentification){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">Digital Identification</div>'
    }
    if (mapDataArray[e].DigitalPayments){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">Digital Payments</div>'
    }
    if (mapDataArray[e].DigitalSignatures){
        filterIconsKeyData = filterIconsKeyData + '<div class="multiple">Digital Signatures</div>'
    }
    if (mapDataArray[e].DigitalTax){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">Digital Tax</div>'
    }
    if (mapDataArray[e].Drones){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Drones</div>'
    }
    if (mapDataArray[e].Eagriculture){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">E-agriculture</div>'
    }
    if (mapDataArray[e].Ecommerce){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">E-commerce</div>'
    }
    if (mapDataArray[e].Ecustoms){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">E-customs</div>'
    }
    if (mapDataArray[e].Eeducation){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">E-education</div>'
    }
    if (mapDataArray[e].EgovernmentServices){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">E-government Services</div>'
    }
    if (mapDataArray[e].Ehealth){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">E-health</div>'
    }
    if (mapDataArray[e].EndUserSubscriberService){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">End-User Subscriber Service</div>'
    }
    if (mapDataArray[e].Ewaste){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platforms">E-waste</div>'
    }
    if (mapDataArray[e].Fintech){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-platform">Fintech</div>'
    }
    if (mapDataArray[e].FirstMile){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">First Mile</div>'
    }
    if (mapDataArray[e].GenderInclusion){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-skills">Gender Inclusion</div>'
    }
    if (mapDataArray[e].Inclusivity){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Inclusivity</div>'
    }
    if (mapDataArray[e].LastMile){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Last Mile</div>'
    }
    if (mapDataArray[e].OpenAccess){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Open Access</div>'
    }
    if (mapDataArray[e].PostalServices){
        filterIconsKeyData = filterIconsKeyData + '<div class="multiple">Postal Services</div>'
    }
    if (mapDataArray[e].QualityofService){
        filterIconsKeyData = filterIconsKeyData + '<div class="multiple">Quality of Service</div>'
    }
    if (mapDataArray[e].RegulatorySandboxes){
        filterIconsKeyData = filterIconsKeyData + '<div class="innovation-entrepreneurship">Sandboxes</div>'
    }
    if (mapDataArray[e].SeondMile){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Second Mile</div>'
    }
    // if (mapDataArray[e].Security){
    //     filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Security</div>'
    // }
    if (mapDataArray[e].SMARTCities){
        filterIconsKeyData = filterIconsKeyData + '<div class="innovation-entrepreneurship">Smart Cities</div>'
    }
    if (mapDataArray[e].SpecialNeeds){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-skills">Special Needs</div>'
    }
    if (mapDataArray[e].Spectrum){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Spectrum and TV White Spaces</div>'
    }
    if (mapDataArray[e].Startups){
        filterIconsKeyData = filterIconsKeyData + '<div class="innovation-entrepreneurship">Start-ups</div>'
    }
    if (mapDataArray[e].TechHubs){
        filterIconsKeyData = filterIconsKeyData + '<div class="multiple">Tech Hubs</div>'
    }
    if (mapDataArray[e].VoiceOverInternetProtocol){
        filterIconsKeyData = filterIconsKeyData + '<div class="digital-infrastructure">Voice Over Internet Protocol</div>'
    }
    filterIconsKeyData = filterIconsKeyData + "</div>"
}

// desktop results
function desktopResults(html, actualData, activeFilter) {
    usedDataArray = actualData;
    for (let e in usedDataArray) {
        let filterThing = usedDataArray[e][activeFilter];
        listKeyData(usedDataArray, e)
        let classificationName = actualData[e].classification
        let countryName = actualData[e].country
        if (actualData[e].country == "South_Africa") {countryName = "South Africa"}
        else if (actualData[e].country == "African_Union") {countryName = "African Union"}
        else if (actualData[e].country == "Guinea_Bissau") {countryName = "Guinea-Bissau"}
        else if (actualData[e].country == "Dem_Rep_Congo") {countryName = "Democratic Republic of Congo"}
        else if (actualData[e].country == "Cote_D_I_viore") {countryName = "Côte d'Ivoire"}
        if (filterThing === true) {
            html += '<tr class="primary">'
                +'<td>'+classificationName+'</td>'
                +'<td class="reg-name"><strong>'+actualData[e].date_text+", "+countryName+'</strong>'+actualData[e].reg_name+'</td>'
                +'<td class="bubbles">'+filterIconsDigInfra+'</td>'
                +'<td class="bubbles">'+filterIconsDigPlatforms+'</td>'
                +'<td class="bubbles">'+filterIconsDigSkills+'</td>'
                +'<td class="bubbles">'+filterIconsDigInn+'</td>'
                +'<td class="bubbles key-data">'+filterIconsKeyData+'</td>'
            +'</tr>'
        }
    }
    document.querySelector('#table-results').innerHTML = html;
}

// responsive results
function responsiveResults(html, actualData, activeFilter) {
    if ( activeFilter !== "arrayItem" ) {
        usedDataArray = dataArray;
    }
    else {
        usedDataArray = actualData;
    }
    for (let e in usedDataArray) {
        let filterThing = usedDataArray[e][activeFilter];
        listKeyData(usedDataArray, e)
        let classificationName = actualData[e].classification
        let countryName = actualData[e].country
        if (actualData[e].country == "South_Africa") {countryName = "South Africa"}
        else if (actualData[e].country == "African_Union") {countryName = "African Union"}
        else if (actualData[e].country == "Guinea_Bissau") {countryName = "Guinea-Bissau"}
        else if (actualData[e].country == "Dem_Rep_Congo") {countryName = "Democratic Republic of Congo"}
        else if (actualData[e].country == "Cote_D_I_viore") {countryName = "Côte d'Ivoire"}
        if (filterThing === true) {
            html += '<tr class="primary">'
                +'<td>'+classificationName+'</td>'
                +'<td class="reg-name"><strong>'+actualData[e].date_text+", "+countryName+'</strong>'+actualData[e].reg_name+'</td>'
                +'<td class="bubbles">'+filterIconsDigInfra+'</td>'
                +'<td class="bubbles">'+filterIconsDigPlatforms+'</td>'
                +'<td class="bubbles">'+filterIconsDigSkills+'</td>'
                +'<td class="bubbles">'+filterIconsDigInn+'</td></tr>'
                +'<tr class="secondary last"><td colspan="2">&nbsp;</td><td class="bubbles key-data" colspan="4"><strong>Key Data Points</strong>'+filterIconsKeyData+'</td>'
            +'</tr>'
        }
    }
    document.querySelector('#table-results').innerHTML = html;
}

// mobile results
function mobileResults(html, actualData, activeFilter) {
    if ( activeFilter !== "arrayItem" ) {
        usedDataArray = dataArray;
    }
    else {
        usedDataArray = actualData;
    }
    for (let e in usedDataArray) {
        let filterThing = usedDataArray[e][activeFilter];
        listKeyData(usedDataArray, e)
        let classificationName = actualData[e].classification
        let countryName = actualData[e].country
        if (actualData[e].country == "South_Africa") {countryName = "South Africa"}
        else if (actualData[e].country == "African_Union") {countryName = "African Union"}
        else if (actualData[e].country == "Guinea_Bissau") {countryName = "Guinea-Bissau"}
        else if (actualData[e].country == "Dem_Rep_Congo") {countryName = "Democratic Republic of Congo"}
        else if (actualData[e].country == "Cote_D_I_viore") {countryName = "Côte d'Ivoire"}
        if (filterThing === true) {
            html += '<tr class="primary">'
            +'<td class="reg-name" colspan="4"><span class="classification">'+classificationName+'</span><strong>'+actualData[e].date_text+", "+countryName+'</strong>'+actualData[e].reg_name+'</td></tr><tr class="secondary">'
            if (filterIconsDigInfra !== '<div class="foci"></div>') {
                html+='<td class="bubbles" colspan="4"><strong>Digital Infrastructure:<strong>'+filterIconsDigInfra+'</td></tr><tr class="secondary">'
            }
            if (filterIconsDigPlatforms !== '<div class="foci"></div>') {
                html+='<td class="bubbles" colspan="4"><strong>Digital Platforms:<strong>'+filterIconsDigPlatforms+'</td></tr><tr class="secondary">'
            }
            if (filterIconsDigSkills !== '<div class="foci"></div>') {
                html+='<td class="bubbles" colspan="4"><strong>Digital Skills:<strong>'+filterIconsDigSkills+'</td></tr><tr class="secondary">'
            }
            if (filterIconsDigInn !== '<div class="foci"></div>') {
                html+='<td class="bubbles" colspan="4"><strong>Digital Innovation:<strong>'+filterIconsDigInn+'</td></tr><tr class="secondary last">'
            }
            html+='<td colspan="4"><strong>Key Data Points</strong>'+filterIconsKeyData+'</td>'
            html+='</tr>'
        }
    }
    document.querySelector('#table-results').innerHTML = html;
}
// filter table -- all filters use this tool
function binaryFilter(binaryValue, categoryValue, categoryType) {
    // reset data to default full array
    mapDataArray = dataArray;
    // clear the table of all results
    html = "";
    document.querySelector('#table-results').innerHTML = html;
    // first shoot it through the countries for results to filter by country
    countryFilter(clickedCountry, mapDataArray, filtered);
    // then filter by binary value and replace country data with applied binary array
    let binaryDataArray = [];
    if (binaryValue.length > 0) {
        binaryDataArray=filteredDataArray.filter(data => data[binaryValue]);
        mapDataArray = binaryDataArray;
    }
    // since categories are additive, need to build new table with multiple category options
    let countingCategories = 0;
    let newCategoryArray = [];
    for (let e in categoryList) { 
        let inputCategoryValue= categoryList[e]+"Checkbox";
        let categoryChecked=document.getElementById(inputCategoryValue).checked;
        if (categoryChecked) {
            countingCategories++;
            if (countingCategories > 1) {filtered = true;}
            categoryFilter(categoryList[e], mapDataArray, newCategoryArray, filtered);
        }
    }
    // if category is applied, it will fill a value for filteredDataArray so countries uses that new array -- it has BOTH binary filter and category filter pre-applied.    
    // if neither country nor category is applied, just run through the simple binary rules
    if (countingCategories == 0) {
        // updating the table
        let html = '';
        if (responsive) {
            responsiveResults(html, filteredDataArray, binaryValue);
        }
        else if (mobile) {
            mobileResults(html, filteredDataArray, binaryValue);
        }
        else {
            desktopResults(html, filteredDataArray, binaryValue);
        }	
    }
}
// category filter (called in binary)
function categoryFilter(categoryValue, filteredData, filtered) {
    // this table is additive so we do NOT reset the values
    // make sure we are using the filter with binary already applied
    mapDataArray = filteredData;
    
    if (responsive) {
        for (let e in mapDataArray) {
            // if the value of the input is listed in this field, update newCategoryArray for use on countries with this as an array item
            if (mapDataArray[e].classification.indexOf(categoryValue) > -1) {
                listKeyData(usedDataArray, e)
                let classificationName = mapDataArray[e].classification
                let countryName = mapDataArray[e].country
                if (mapDataArray[e].country == "South_Africa") {countryName = "South Africa"}
                else if (mapDataArray[e].country == "African_Union") {countryName = "African Union"}
                else if (mapDataArray[e].country == "Guinea_Bissau") {countryName = "Guinea-Bissau"}
                else if (mapDataArray[e].country == "Dem_Rep_Congo") {countryName = "Democratic Republic of Congo"}
                else if (mapDataArray[e].country == "Cote_D_I_viore") {countryName = "Côte d'Ivoire"}
                html += '<tr class="primary">'
                    +'<td>'+classificationName+'</td>'
                    +'<td class="reg-name"><strong>'+mapDataArray[e].date_text+", "+countryName+'</strong>'+mapDataArray[e].reg_name+'</td>'
                    +'<td class="bubbles">'+filterIconsDigInfra+'</td>'
                    +'<td class="bubbles">'+filterIconsDigPlatforms+'</td>'
                    +'<td class="bubbles">'+filterIconsDigSkills+'</td>'
                    +'<td class="bubbles">'+filterIconsDigInn+'</td>'
                    +'<td class="bubbles key-data">'+filterIconsKeyData+'</td>'
                +'</tr>'
            }
        }
        //update the table
        document.querySelector('#table-results').innerHTML = html;
    }
    else if (mobile) {
        for (let e in mapDataArray) {
            // if the value of the input is listed in this field, update newCategoryArray for use on countries with this as an array item
            if (mapDataArray[e].classification.indexOf(categoryValue) > -1) {
                listKeyData(usedDataArray, e)
                let classificationName = mapDataArray[e].classification
                let countryName = mapDataArray[e].country
                if (mapDataArray[e].country == "South_Africa") {countryName = "South Africa"}
                else if (mapDataArray[e].country == "African_Union") {countryName = "African Union"}
                else if (mapDataArray[e].country == "Guinea_Bissau") {countryName = "Guinea-Bissau"}
                else if (mapDataArray[e].country == "Dem_Rep_Congo") {countryName = "Democratic Republic of Congo"}
                else if (mapDataArray[e].country == "Cote_D_I_viore") {countryName = "Côte d'Ivoire"}
                html += '<tr class="primary">'
                    +'<td class="reg-name" colspan="4"><span class="classification">'+classificationName+'</span><strong>'+mapDataArray[e].date_text+", "+countryName+'</strong>'+mapDataArray[e].reg_name+'</td></tr><tr class="secondary">'
                    if (filterIconsDigInfra !== '<div class="foci"></div>') {
                        html+='<td class="bubbles" colspan="4"><strong>Digital Infrastructure:<strong>'+filterIconsDigInfra+'</td></tr><tr class="secondary">'
                    }
                    if (filterIconsDigPlatforms !== '<div class="foci"></div>') {
                        html+='<td class="bubbles" colspan="4"><strong>Digital Platforms:<strong>'+filterIconsDigPlatforms+'</td></tr><tr class="secondary">'
                    }
                    if (filterIconsDigSkills !== '<div class="foci"></div>') {
                        html+='<td class="bubbles" colspan="4"><strong>Digital Skills:<strong>'+filterIconsDigSkills+'</td></tr><tr class="secondary">'
                    }
                    if (filterIconsDigInn !== '<div class="foci"></div>') {
                        html+='<td class="bubbles" colspan="4"><strong>Digital Innovation:<strong>'+filterIconsDigInn+'</td></tr><tr class="secondary last">'
                    }
                    html+='<td colspan="4"><strong>Key Data Points</strong>'+filterIconsKeyData+'</td>'
                    html+='</tr>'
            }
        }
        //update the table
        document.querySelector('#table-results').innerHTML = html;
    }
    else {
        for (let e in mapDataArray) {
            // if the value of the input is listed in this field, update newCategoryArray for use on countries with this as an array item
            if (mapDataArray[e].classification.indexOf(categoryValue) > -1) {
                listKeyData(mapDataArray, e)
                let classificationName = mapDataArray[e].classification
                let countryName = mapDataArray[e].country
                if (mapDataArray[e].country == "South_Africa") {countryName = "South Africa"}
                else if (mapDataArray[e].country == "African_Union") {countryName = "African Union"}
                else if (mapDataArray[e].country == "Guinea_Bissau") {countryName = "Guinea-Bissau"}
                else if (mapDataArray[e].country == "Dem_Rep_Congo") {countryName = "Democratic Republic of Congo"}
                else if (mapDataArray[e].country == "Cote_D_I_viore") {countryName = "Côte d'Ivoire"}
                html += '<tr class="primary">'
                    +'<td>'+classificationName+'</td>'
                    +'<td class="reg-name"><strong>'+mapDataArray[e].date_text+", "+countryName+'</strong>'+mapDataArray[e].reg_name+'</td>'
                    +'<td class="bubbles">'+filterIconsDigInfra+'</td>'
                    +'<td class="bubbles">'+filterIconsDigPlatforms+'</td>'
                    +'<td class="bubbles">'+filterIconsDigSkills+'</td>'
                    +'<td class="bubbles">'+filterIconsDigInn+'</td>'
                    +'<td class="bubbles key-data">'+filterIconsKeyData+'</td>'
                +'</tr>'
            }
        }
        //update the table
        document.querySelector('#table-results').innerHTML = html;
    }	
}

// country table 
function countryFilter(countryValue, filteredData, filtered) {
    // reset the table so we aren't adding on to previous results
    let html = '';
    let newCountryArray = [];
    document.querySelector('#table-results').innerHTML = html;
    // make sure we are not applying previous filters -- country should clear everything
    mapDataArray = dataArray;
    if (countryValue === "All_Countries") {
        newCountryArray = dataArray;
    }
    else {
        for (let e in mapDataArray) {
            if (mapDataArray[e].country.indexOf(countryValue) > -1) {
                newCountryArray.push(mapDataArray[e]);
            }
        }

    }
    // pass back a new version of the array that only has the items true for category choice
    filteredDataArray = newCountryArray;	
}

// opening filter menus
function openForm(e) {
    e.target.parentElement.classList.toggle('active');
    e.target.nextSibling.classList.toggle('active');
};

function filterCategory(e) {
    e.preventDefault();
    // get value of checked category depending on if they checked label or input
    let input = "";
    if (e.target.className === "checkmark")
    {
        input = e.target.parentElement.children[0];
    }
    else {
        input = e.target.children[0];
    }
    input.checked = !input.checked;
    
    // pass NO value to binary filter -- country filtering is done in binary filter based on checks to allow for multiple countries to be selected
    binaryFilter(binaryValue, categoryValue, 'category');
};

//dropdown lists
function dropdownList(e) {
    e.preventDefault();
    // get value of checked category depending on if they checked label or input
    let input = e.target.children[0];
    if (e.target.type === "radio") {
        input = e.target
    }
    input.checked = !input.checked;
    binaryValue = input.value;
    binaryFilter(binaryValue, categoryValue);
    if( binaryValue == "digitalInfrastructure" && document.getElementById("digitalInfrastructureCheckbox").checked ){
        document.querySelector('#subcategories').classList.add("active");
        document.querySelector('#subcategories .form-menu').classList.add("active");
        let subcategoriesGroup = document.querySelectorAll('#subcategories .subcategory');
        for (let i = 0; i < subcategoriesGroup.length; i++) {
            subcategoriesGroup[i].classList.remove("active");
        }
        let subcategoriesGroupChosen = document.querySelectorAll('#subcategories .subcategory.digital-infrastruct-group');
        for (let i = 0; i < subcategoriesGroupChosen.length; i++) {
            subcategoriesGroupChosen[i].classList.add("active");
        }
    }
    if( binaryValue == "digitalPlatform" && document.getElementById("digitalPlatformCheckbox").checked ){
        document.querySelector('#subcategories').classList.add("active");
        document.querySelector('#subcategories .form-menu').classList.add("active");
        let subcategoriesGroup = document.querySelectorAll('#subcategories .subcategory');
        for (let i = 0; i < subcategoriesGroup.length; i++) {
            subcategoriesGroup[i].classList.remove("active");
        }
        let subcategoriesGroupChosen = document.querySelectorAll('#subcategories .subcategory.digital-platforms-group');
        for (let i = 0; i < subcategoriesGroupChosen.length; i++) {
            subcategoriesGroupChosen[i].classList.add("active");
        }
    }
    if( binaryValue == "digitalSkills" && document.getElementById("digitalSkillsCheckbox").checked ){
        document.querySelector('#subcategories').classList.add("active");
        document.querySelector('#subcategories .form-menu').classList.add("active");
        let subcategoriesGroup = document.querySelectorAll('#subcategories .subcategory');
        for (let i = 0; i < subcategoriesGroup.length; i++) {
            subcategoriesGroup[i].classList.remove("active");
        }
        let subcategoriesGroupChosen = document.querySelectorAll('#subcategories .subcategory.digital-skills-group');
        for (let i = 0; i < subcategoriesGroupChosen.length; i++) {
            subcategoriesGroupChosen[i].classList.add("active");
        }
    }
    if( binaryValue == "entrepreneurship" && document.getElementById("entrepreneurshipCheckbox").checked ){
        document.querySelector('#subcategories').classList.add("active");
        document.querySelector('#subcategories .form-menu').classList.add("active");
        let subcategoriesGroup = document.querySelectorAll('#subcategories .subcategory');
        for (let i = 0; i < subcategoriesGroup.length; i++) {
            subcategoriesGroup[i].classList.remove("active");
        }
        let subcategoriesGroupChosen = document.querySelectorAll('#subcategories .subcategory.digital-innovation-group');
        for (let i = 0; i < subcategoriesGroupChosen.length; i++) {
            subcategoriesGroupChosen[i].classList.add("active");
        }
    }
    
};

function resetFilter(e) {
    e.preventDefault();
    filtered = false;
    // reset everything
    html = "";
    document.querySelector('#table-results').innerHTML = html;
    countryFilter(clickedCountry, mapDataArray, filtered);
    // updating the table
    binaryValue = "arrayItem"
    if (responsive) {
        responsiveResults(html, filteredDataArray, binaryValue);
    }
    else if (mobile) {
        mobileResults(html, filteredDataArray, binaryValue);
    }
    else {
        desktopResults(html, filteredDataArray, binaryValue);
    }	
    // hide dropdowns
    let dropdownMenus = document.getElementsByClassName("form-menu");
    for (let i = 0; i < dropdownMenus.length; i++) {
        dropdownMenus[i].classList.remove('active');
    }
    let subCategoryMenus = document.getElementsByClassName("subcategory");
    for (let i = 0; i < subCategoryMenus.length; i++) {
        subCategoryMenus[i].classList.remove('active');
    }
    let radioButtons = document.querySelectorAll('input[type="radio"]')
    for (let i = 0; i < radioButtons.length; i++) {
        radioButtons[i].checked = false;
    }
    let checkboxes = document.querySelectorAll('input[type="checkbox"]')
    for (let i = 0; i < checkboxes.length; i++) {
        checkboxes[i].checked = false;
    }
    searchTerm.value = ''
    // check off the input for whichever country was checked and add the country count
    let inputCheckedCountry = clickedCountry+"Checkbox";
    const checkbox = document.getElementById(inputCheckedCountry);
    checkbox.checked = true;
    let dropdownFilterMenus = document.getElementsByClassName("form-dropdown");
    for (let i = 0; i < dropdownFilterMenus.length; i++) {
        dropdownFilterMenus[i].classList.remove('inactive');
    }
}

function revealOverlay(contentWrapperId) {
    document.querySelector(contentWrapperId).classList.add('active');
    const closeButton =
        '<div class="overlay-close"><svg data-v-459a476e="" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="none" class=""><circle cx="18" cy="18" r="17.5" stroke="currentColor"></circle><path stroke="currentColor" d="M25.07 10.93 10.93 25.07M25.07 25.07 10.93 10.93"></path></svg></div>';
    document.querySelector(contentWrapperId).firstElementChild.innerHTML += closeButton;
}

// pillar description boxes
function pillarBox(e){
    e.preventDefault();

    let pillarBoxes = document.querySelectorAll('.pillar-box')
    let parentBox = e.target.closest(".pillar-box")
    if (!parentBox.className.includes('active')) {    
        for (let i = 0; i < pillarBoxes.length; i++) {
            pillarBoxes[i].classList.remove('active')
        }
        parentBox.classList.add('active');
    }

    let pillarId = parentBox.id;
    if (pillarId == "pillarInfrastructure") {
        let html = "<div class='col col-50'><div class='gutters'><h4>Digital Pillar</h4><p><span>Digital Infrastructure</span> is the availability of affordable and quality internet is instrumental to bringing more people online.  </p></div></div><div class='col col-50'><div class='gutters'><h4>Sub-Pillars</h4><p><span>Physical Infrastructure:</span> Physical structures powered by technology that increase access and connectivity to the digital economy.</p><p><span>Invisible Infrastructure:</span> Systems that strengthen public confidence in the digital economy.</p></div></div></div>"
        let elList = document.querySelectorAll('.activeCountry');
        elList.forEach(el => {
            el.classList.add('infrastructure');
            el.classList.remove('platforms');
            el.classList.remove('skills');
            el.classList.remove('innovation');
        });
        document.querySelector('#pillarDescription').classList.add("infrastructure");
        document.querySelector('#pillarDescription').classList.remove("platforms");
        document.querySelector('#pillarDescription').classList.remove("skills");
        document.querySelector('#pillarDescription').classList.remove("innovation");
        document.querySelector('#pillarDescription').innerHTML = html;  
    }
    else if (pillarId == "pillarPlatforms") {
        let html = "<div class='col col-50'><div class='gutters'><h4>Digital Pillar</h4><p><span>Digital Platforms</span> are any software-based infrastructure that facilitate online exchanges, transactions, and access to public and private services.​ </p></div></div><div class='col col-50'><div class='gutters'><h4>Sub-Pillars</h4><p><span>Digital Government:</span> The use of data and technology to optimize and transform digital government services.</p><p><span>Digital Business:</span> Businesses that leverage digital technology as a fundamental part of their business model and operations.</p><p><span>Digital Financial Services:</span> The ability to pay, save, borrow, and invest through digital means, which is key to accessing digital services and increasing the rate of online transactions.</p></div></div></div>"
        let elList = document.querySelectorAll('.activeCountry');
        elList.forEach(el => {
            el.classList.remove('infrastructure');
            el.classList.add('platforms');
            el.classList.remove('skills');
            el.classList.remove('innovation');
        });
        document.querySelector('#pillarDescription').classList.remove("infrastructure");
        document.querySelector('#pillarDescription').classList.add("platforms");
        document.querySelector('#pillarDescription').classList.remove("skills");
        document.querySelector('#pillarDescription').classList.remove("innovation");
        document.querySelector('.legend-box').classList.remove("infrastructure");
        document.querySelector('#pillarDescription').innerHTML = html;  
    }
    else if (pillarId == "pillarSkills") {
        let html = "<div class='col col-50'><div class='gutters'><h4>Digital Pillar</h4><p><span>Digital Skills</span> are competencies to access, use, manage, and create digital information and digital tools.​</p></div></div><div class='col col-50'><div class='gutters'><h4>Sub-Pillars</h4><p><span>Basic Skills:</span> The skills, digital competence and confidence required to use information and communication technology (ICT) for work, leisure, learning and communication. </p><p><span>Advanced Skills:</span> These are skills that empower a country’s citizens to contribute advanced digital skills to the domestic and international digital economy. </p></div></div></div>"
        let elList = document.querySelectorAll('.activeCountry');
        elList.forEach(el => {
            el.classList.remove('infrastructure');
            el.classList.remove('platforms');
            el.classList.add('skills');
            el.classList.remove('innovation');
        });
        document.querySelector('#pillarDescription').classList.remove("infrastructure");
        document.querySelector('#pillarDescription').classList.remove("platforms");
        document.querySelector('#pillarDescription').classList.add("skills");
        document.querySelector('#pillarDescription').classList.remove("innovation");
        document.querySelector('#pillarDescription').innerHTML = html; 
    }
    else if (pillarId == "pillarInnovation") {
        let html = "<div class='col col-50'><div class='gutters'><h4>Digital Pillar</h4><p><span>Digital Innovation</span> is the presence of an ecosystem that supports firms to generate new products and services leveraging new technologies and business models, which help widen and deepen digital economic transformation. ​ </p></div></div><div class='col col-50'><div class='gutters'><h4>Sub-Pillars</h4><p><span>Technological hubs:</span> A network of systems, environments and stakeholders that foster technological innovation.</p><p><span>Digital Entrepreneurship:</span> Access to channels that support the launch and scale-up of innovative digital businesses and innovations. </p></div></div></div>"
        let elList = document.querySelectorAll('.activeCountry');
        elList.forEach(el => {
            el.classList.remove('infrastructure');
            el.classList.remove('platforms');
            el.classList.add('skills');
            el.classList.remove('innovation');
        });
        document.querySelector('#pillarDescription').classList.remove("infrastructure");
        document.querySelector('#pillarDescription').classList.remove("platforms");
        document.querySelector('#pillarDescription').classList.remove("skills");
        document.querySelector('#pillarDescription').classList.add("innovation");
        document.querySelector('#pillarDescription').innerHTML = html; 
    }
    
};

// country boxes
function countrySVG(e){
    e.preventDefault();
    let SVGCountryId = "";
    if (e.target.classList.contains("africa-regulations-country-checkbox-label")) {
        SVGCountryId = e.target.firstChild.value;
    }
    else if (e.target.classList.contains("country-input-value")) {
        SVGCountryId = e.target.value;
    }
    else {
        SVGCountryId = e.target.parentElement.id;
    }
    let clickableCountry = false;

    if (SVGCountryId === "All_Countries") {
        descriptionCountryName.value = "All Countries"
        descriptionCountryPopulation.value = ref(0);
        descriptionCountryLandMass.value = ref(0);
        descriptionCountryLonger.value = ref("");
        getNumber(SVGCountryId) 
        clickableCountry = true;
        clickedCountry =  "All_Countries";
        let preclickedCountries = document.getElementsByClassName("countryClicked");
        for (let i = 0; i < preclickedCountries.length; i++) {
            preclickedCountries[i].classList.remove('countryClicked');
        }
    }
    else {
        for (let index in countryDataArray) {
            if (countryDataArray[index].countryName.indexOf(SVGCountryId) > -1) {
                descriptionCountryName.value = countryDataArray[index].descriptionCountryNameValue;
                descriptionCountryPopulation.value = ref(countryDataArray[index].descriptionCountryPopulationValue);
                descriptionCountryLandMass.value = ref(countryDataArray[index].descriptionCountryLandMassValue);
                descriptionCountryLonger.value = ref(countryDataArray[index].descriptionCountryLongerValue);
                getNumber(SVGCountryId) 
                clickableCountry = true;
                clickedCountry = countryDataArray[index].clickedCountry;
                let preclickedCountries = document.getElementsByClassName("countryClicked");
                for (let i = 0; i < preclickedCountries.length; i++) {
                    preclickedCountries[i].classList.remove('countryClicked');
                }
            }
        }
    }
    if (clickableCountry) {        
        filtered = false;
        // reset everything
        html = "";
        document.querySelector('#table-results').innerHTML = html;
        countryFilter(clickedCountry, mapDataArray, filtered);
        // updating the table
        binaryValue = "arrayItem"
        if (responsive) {
            responsiveResults(html, filteredDataArray, binaryValue);
        }
        else if (mobile) {
            mobileResults(html, filteredDataArray, binaryValue);
        }
        else {
            desktopResults(html, filteredDataArray, binaryValue);
        }	
        // hide dropdowns
        let dropdownMenus = document.getElementsByClassName("form-menu");
        for (let i = 0; i < dropdownMenus.length; i++) {
            dropdownMenus[i].classList.remove('active');
        }
        let subCategoryMenus = document.getElementsByClassName("subcategory");
        for (let i = 0; i < subCategoryMenus.length; i++) {
            subCategoryMenus[i].classList.remove('active');
        }
        let radioButtons = document.querySelectorAll('input[type="radio"]')
        for (let i = 0; i < radioButtons.length; i++) {
            radioButtons[i].checked = false;
        }
        let checkboxes = document.querySelectorAll('input[type="checkbox"]')
        for (let i = 0; i < checkboxes.length; i++) {
            checkboxes[i].checked = false;
        }
        searchTerm.value = ''
        // check off the input for whichever country was checked and add the country count
        let inputCheckedCountry = SVGCountryId+"Checkbox";
        const checkbox = document.getElementById(inputCheckedCountry);
        checkbox.checked = true; 
        if (SVGCountryId !== "African_Union" && SVGCountryId !== "All_Countries") {
            let elementId = "#"+SVGCountryId+" .cls-2";
            let svgClickedCountry = document.querySelector(elementId);
            svgClickedCountry.classList.add('countryClicked')
        }
    }
};

let searchTerm = ref('')
let keyDataValues = [
    { value: 'Artificial Intelligence', binaryText: 'ArtificialIntelligence'},
    { value: 'Broadband Internet', binaryText: 'Broadband'},
    { value: 'Broadcasting and Multimedia Services', binaryText: 'Broadcasting'},
    { value: 'Child Protection', binaryText: 'ChildProtection'},
    { value: 'Cloud Services', binaryText: 'CloudServices'},
    { value: 'Competition', binaryText: 'Competition'},
    { value: 'Consumer Protection', binaryText: 'ConsumerProtection'},
    { value: 'Cybersecurity', binaryText: 'Cybersecurity'},
    { value: 'Data', binaryText: 'Data'},
    { value: 'Data Centers', binaryText: 'DataCenter'},
    { value: 'Data Protection', binaryText: 'DataProtection'},
    { value: 'Digital Identification', binaryText: 'DigitalIdentification'},
    { value: 'Digital Payments', binaryText: 'DigitalPayments'},
    { value: 'Digital Signatures', binaryText: 'DigitalSignatures'},
    { value: 'Digital Tax', binaryText: 'DigitalTax'},
    { value: 'Drones', binaryText: 'Drones'},
    { value: 'E-agriculture', binaryText: 'Eagriculture'},
    { value: 'E-commerce', binaryText: 'Ecommerce'},
    { value: 'E-customs', binaryText: 'Ecustoms'},
    { value: 'E-education', binaryText: 'Eeducation'},
    { value: 'E-government Services', binaryText: 'EgovernmentServices'},
    { value: 'E-health', binaryText: 'Ehealth'},
    { value: 'End User Subscriber Service', binaryText: 'EndUserSubscriberService'},
    { value: 'E-waste', binaryText: 'Ewaste'},
    { value: 'Fintech', binaryText: 'Fintech'},
    { value: 'First Mile', binaryText: 'FirstMile'},
    { value: 'Gender Inclusion', binaryText: 'GenderInclusion'},
    { value: 'Inclusivity', binaryText: 'Inclusivity'},
    { value: 'Internet Resources (Numbering/Domain Registration)', binaryText: 'InternetResources'},
    { value: 'Last Mile', binaryText: 'LastMile'},
    { value: 'Open Access', binaryText: 'OpenAccess'},
    { value: 'Postal Services', binaryText: 'PostalServices'},
    { value: 'Quality of Service', binaryText: 'QualityofService'},
    { value: 'Sandboxes', binaryText: 'RegulatorySandboxes'},
    { value: 'Second Mile', binaryText: 'SecondMile'},
    { value: 'Smart Cities', binaryText: 'SMARTCities'},
    { value: 'Special Needs', binaryText: 'SpecialNeeds'},
    { value: 'Spectrum and TV White Spaces', binaryText: 'Spectrum'},
    { value: 'Start-ups', binaryText: 'Startups'},
    { value: 'Tech Hubs', binaryText: 'TechHubs'},
    { value: 'Voice Over Internet Protocol', binaryText: 'VoiceOverInternetProtocol'},
]
const searchkeyDataValues = computed(() => {
    if (searchTerm.value === '') {
    return []
    }

    let matches = 0

    return keyDataValues.filter(keyDataValue => {
    if (keyDataValue.value.toLowerCase().includes(searchTerm.value.toLowerCase()) && matches < 10) {
        matches++
        return keyDataValue
    }
    })
});

const selectkeyDataValue = (keyDataValue) => {
    selectedKeyData.value = keyDataValue
    binaryValue = keyDataValue.binaryText;
    binaryFilter(binaryValue, categoryValue);
    searchTerm.value = 'Displaying: '+keyDataValue.value;
    // let dropdownFilterMenus = document.getElementsByClassName("form-dropdown");
    // for (let i = 0; i < dropdownFilterMenus.length; i++) {
    //     dropdownFilterMenus[i].classList.add('inactive');
    // }
}
 
let selectedKeyData = ref('')

const subscribeCarnegieCookie = useCookie('subscribeCarnegie', {maxAge: 432000})
let modalShown = false;
let subscribe = subscribeCarnegieCookie?.value;
const newsletterFormName = `CarnegieAfricaProgramForm`;
const isSubscribeModalOpen = ref(false);

const closeSubscribeModal = () => {
    isSubscribeModalOpen.value = false;
};

const handleSubscribeCtaClicked = () => {
    isSubscribeModalOpen.value = true;
};

watch(scrollProgress, () => {
    if (!modalShown && subscribe !== true && scrollProgress.value !== 'undefined' && scrollProgress.value > 5) {
        handleSubscribeCtaClicked();
        subscribeCarnegieCookie.value = true;
        modalShown = true;
    }
});


onMounted(() => {
    const checked = ref(true)
    const unchecked = ref(false)
    // I need to catch any time an existing accordion or overlay is clicked, so it looks for all clicks on anything and acts if the className includes sortable ....
    document.onclick = function (e) {
        if (e.target.classList && e.target.classList.contains('overlay-trigger')) {
            e.preventDefault();
            revealOverlay(e.target.hash);
        }
        if (e.target.classList && e.target.classList.contains('overlay-close')) {
            e.preventDefault();
            document.querySelector('.overlay-content').classList.remove('active');
        }
        if (e.target.classList && e.target.classList.contains('form-opener')) {
            e.preventDefault();
            openForm(e);
        }
        if (e.target.classList && e.target.classList.contains('cls-2')) {
            e.preventDefault();
            countrySVG(e);
        }
    };
    getResponsive();
    renderResults();
});

function renderResults () {
    let html = '';
    countryFilter(clickedCountry, mapDataArray, filtered);
    getNumber(clickedCountry) 
    if (responsive) {
        responsiveResults(html, filteredDataArray, "arrayItem");
    }
    else if (mobile) {
        mobileResults(html, filteredDataArray, "arrayItem");
    }
    else {
        desktopResults(html, filteredDataArray, "arrayItem");
    }
}

</script>

<style lang="scss" scoped>
/* layout */
$gutter: 30px;
$site-width: 1000px;

$carnegie-blue: #003a63;
$gray-light: #e4e3dd;
$black: #000;
$white: #fff;
$teal: #00a79d;
$dark-blue: #00365e;
$light-blue: #27aae1;
$dark-gray-new: #6e6e71;
$medium-gray-new: #a7a9ac;
$light-gray: #f5f5f5;
$dark-gray: #929497;
$yellow: #fbb040;
$light-blue-light: #a0d9f2;
$medium-blue: #3183b1;
$copper: #89684f;

$roman-normal-book: 'Frutiger Neue LT W06 Book', Arial, sans-serif;
$roman-italic-book: 'Frutiger Neue LT W06 Book It', Arial, sans-serif;
$roman-normal-bold: 'Frutiger Neue LT W06 Bold', 'Arial Black', Gadget, sans-serif;

//-overlay
:deep(.ceip-overlay),
:deep(.overlay-content) {
    display: none;
}

:deep(.overlay-content) {
    position: fixed;
    background: lightgray;
    background: rgba(lightgray, 0.8);
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10000;
    display: none;

    &.active {
        display: block;
    }

    > div {
        background: white;
        border-radius: 7px;
        color: #000000;
        display: block;
        margin: 0 auto;
        position: relative;
        top: 20%;
        width: 60rem;
        max-width: 60rem;
        max-height: 70vh;
        padding: 4rem 2rem;
        overflow: scroll;
        .overlay-close {
            position: absolute;
            top: 1rem;
            right: 1rem;
            svg {
                pointer-events: none;
            }
        }
        @include media-query(mobile) {
            max-width: 90vw;
        }
    }
}

/*layout basics*/
.foreground {
    position: relative;
    margin: 0 auto;
    box-sizing: content-box;
    max-width: $site-width;
    padding-left: calc($gutter / 2);
    padding-right: calc($gutter / 2);
}

.section {
    display: block;
    clear: both;
    margin-top: $gutter;
    margin-bottom: $gutter;
}

:deep(.hidden) {
    display: none;
}

:deep(.cols) {
    clear: both;
    /*use a .no-gutters class to undo these default styles */
    margin-left: calc($gutter / 2) * -1;
    margin-right: calc($gutter / 2) * -1;

    &:before,
    &:after {
        content: ' ';
        display: table;
        height: 0;
        clear: both;
        font-size: 1px;
        line-height: 0px;
        visibility: hidden;
    }

    @include media-query(phone) { 
        margin-left: 0 !important;
        margin-right: 0 !important;
    }
}

.col, :deep(.col) {
    float: left;

    @include media-query(phone) {
         float: none;
         width: 100% !important;
         
         > .gutters {
            margin-left: 0;
            margin-right: 0;
        }
    }
}
:deep(.cols--equal-heights) {
    flex-wrap: wrap;

    &,
    >.col {
        display: flex;

        @include media-query(phone) {
            display: block;
        }
    }

    >.col {
        flex-direction: column;
    }

    .banded {
        flex: 1 0 auto;
    }
}

.col-70 {
    width: 70%;
}

.col-60 {
    width: 60%;
}

/*two across*/
.col-50 {
    width: 50%;
}

:deep(.col-50) {
    width: 50%;
}

.col-40 {
    width: 40%;
}

:deep(.col-33) {
    float: left;
    width: 33%;
}

.col-30 {
    width: 30%;
}

/*four across*/
.col-25 {
    width: 25%;
}

.padded {
    padding: $gutter;
}

:deep(.col-20) {
    display: inline-block;
    width: 20%;
}

:deep(.col-80) {
    display: inline-block;
    width: 80%;
}

:deep(.nav-tabs) {
    div {
        border: 0 !important;
        text-transform: uppercase;
    }

    > li {
        margin-bottom: 0;
        float: left;
        position: relative;
        display: block;

        > div {
            line-height: 1;
            border-radius: 0;
        }
    }
}

:deep(.checkbox input) {
    border-radius: 2px;
    border: 1px solid #e6e7e8;
    -webkit-box-shadow: none;
    box-shadow: none;
    cursor: pointer;
    appearance: checkbox;
}

.gutters, :deep(.gutters) {
    margin-left: 15px;
    margin-right: 15px;
}

.gutter-right {
    margin-right: 30px;
}

.gutter-left {
    margin-left: 30px;
}

.mobile-only {
    display: none;
    @include media-query(tablet) {
        display: block;
    }
}
.hide-mobile {
    display: block;
    @include media-query(tablet) {
        display: none;
    }
}

.africa-digital-regulations {

    .hero {
        margin-bottom: 6em;
    }

    .dek {
        border-bottom: 1px solid var(--color-divider);
        margin-bottom: 2.4rem;
        padding-bottom: 2.4rem;
    }

    .rich-text {
        @include type-body-display-large;
        p {margin-bottom: 1.8rem;}
    }
    .about-button {
        margin: 1em 0 2.5em 0;
    }
    .button.teal {
        background-color: $teal;
        border-radius: 10px;
        color: $white;
        font-size: 1.2em;
        margin-left: 25%;
        padding: .5em;
    }
    .partnership {
        margin-bottom: 2em;
        span {
            margin: 0 auto;
            width: 100%;
            text-align: center;
            display: block;
        }
        .partner-logo {
            background-image: url(https://carnegie-production-assets.s3.amazonaws.com/static/media/images/ATU_UAT%20LOGO%20PRESS-PNG%2001.png);
            background-size: contain;
            background-repeat: no-repeat;
            width: 12em;
            height: 12em;
            margin: 0 auto;
        }
    }

    .main-with-asides-tier {margin-bottom: 5em;}

    .boxes {
        margin-bottom: 0;
        margin-top: 0;
        h3.title {margin-bottom: 2em; color: $carnegie-blue}
        .cols {margin-bottom: calc($gutter * -2);}
        .col-20 {
            background-color: $white;
            border: 1px solid $black;
            padding: 3px;
            margin-right: 5%;
            width: 21%;
            &:last-of-type {margin-right: 0;}
            .gutters {padding: calc($gutter / 2) 0 calc($gutter / 2) 0;}
            h3 {
                color: $black;
                font-family: $roman-normal-bold;
                font-size: 1.5em;
                line-height: 1.6em;
            }
            &.skills h3 {width: 70%;}
            &.infrastructure .icon {
                background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-01.png);
                background-size: contain;
                background-repeat: no-repeat;
                height:80px;
                // width: 80px;
            }
            &.platforms .icon {
                background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-02.png);
                background-size: contain;
                background-repeat: no-repeat;
                height:80px;
                // width: 80px;
            }
            &.skills .icon {
                background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-03.png);
                background-size: contain;
                background-repeat: no-repeat;
                height:80px;
                // width: 80px;
            }
            &.innovation .icon {
                background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-04.png);
                background-size: contain;
                background-repeat: no-repeat;
                height:80px;
                // width: 80px;
            }
            &.active, &:hover {
                padding: 0;
                &.infrastructure {border: 4px solid $teal;}
                &.platforms {border: 4px solid $copper;}
                &.skills {border: 4px solid $yellow;}
                &.innovation {border: 4px solid $light-blue;}
            }
        }
    }

    :deep(.description-section) {
        background-color: rgb(var(--color-rgb-grey-light));;
        color: $black;
        margin-top: 0;
        padding: calc($gutter * 3) 0 $gutter 0;
        @include type-body;
        > div > .cols {margin-top: calc($gutter / 2);}
        h4 {
            color: $dark-blue;
            display: inline-block;
            margin: 1em 0;
            padding: 3px 6px;
            text-transform: uppercase;
            @include type-body;
            & {
                font-size: 0.8em;
            }
            &:nth-of-type(2) {margin-top: calc($gutter / 2);}
        }
        .col:first-of-type p {font-size: 2.4rem;}
        p {
            font-size: 2rem;
            margin-bottom: 1em;
        }
        .infrastructure {
            span {color: $teal}
            h4 {background: $teal;}
        }
        .platforms {
            span {color: $copper}
            h4 {background: $copper;}
        }
        .skills {
            span {color: $yellow}
            h4 {background: $yellow;}
        }
        .innovation {
            span {color: $light-blue}
            h4 {background: $light-blue;}
        }
    }

    :deep(.countries) {
        color: $black;
        margin-top: 0;
        padding: $gutter 0 $gutter 0;
        @include type-body-display-small;
        > div > .cols {margin-top: calc($gutter / 2);}
        h4 {
            color: $dark-blue;
            display: inline-block;
            margin: 1em 0;
            padding: 3px 0;
            text-transform: uppercase;
            @include type-body;
            & {
                font-size: 1.6em;
            }
            &:nth-of-type(2) {margin-top: calc($gutter / 2);}
        }
        h5 {
            color: $teal;
            font-size: 2.5rem;
            line-height: 1em;
            margin-bottom: 1em;
        }
        p {
            font-size: 1.1em;
            margin-bottom: 1em;
        }
        .infrastructure {
            span {color: $teal}
            h4 {background: $teal;}
        }
        .platforms {
            span {color: $copper}
            h4 {background: $copper;}
        }
        .skills {
            span {color: $yellow}
            h4 {background: $yellow;}
        }
        .innovation {
            span {color: $light-blue}
            h4 {background: $light-blue;}
        }

        .disclaimer-map {
            display: block;
            font-family: $roman-italic-book;
            font-size: 0.9rem;
        }
        .legend {
            float: right;
            .legend-box {
                display: inline-block;
                height: calc($gutter / 2);
                width: calc($gutter / 2);
                background-color: #7f9cb1;
                margin-right: calc($gutter / 3);
            }
        }

        .full-description {
            clear: both;
            a {text-decoration: underline}
        }

        .country-select.filters {
            margin: 0;
            strong {
                display: block;
                font-size: 1.5em;
                margin-bottom: 1em;
            }
            .form-dropdown {
                margin-left: 0;
                width: 50%;
            }
        }

        

        .country-info {
            font-size: 1.2em;

            &#longerCountryDescription {
                display: block; 
                clear: both;
            }

            strong {
                font-family: $roman-normal-bold;
            }

            .dig-inf-icon {
                background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-01.png);
                background-size: contain;
                background-repeat: no-repeat;
                display: inline-block;
                height:40px;
                width: 40px;
            }
            .dig-pl-icon {
                background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-02.png);
                background-size: contain;
                background-repeat: no-repeat;
                display: inline-block;
                height:40px;
                width: 40px;
            }
            .dig-sk-icon {
                background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-03.png);
                background-size: contain;
                background-repeat: no-repeat;
                display: inline-block;
                height:40px;
                width: 40px;
            }
            .dig-inv-icon {
                background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-04.png);
                background-size: contain;
                background-repeat: no-repeat;
                display: inline-block;
                height:40px;
                width: 40px;
            }

            table {
                border-collapse: collapse;
                margin-bottom: 1em;
                tr {border-bottom: 1px solid $black;}
            }

            td {
                padding: 0 0.5em;
                &:not(:last-of-type) {border-right: 1px solid $black;}
                &.number {vertical-align: top; text-align: center}
                strong {
                    font-size: 0.7em;
                    vertical-align: super;
                }
            }

            

        }

        svg {
            .cls-1 {
            fill: none;
            stroke: #7ea3ad;
            stroke-width: .35px;
            }
    
            .cls-1, .cls-2, .cls-3, .cls-4 {
            stroke-linejoin: round;
            }
    
            .cls-2 {
            fill: #fff;
            }
    
            .cls-2, .cls-3 {
            stroke: silver;
            stroke-width: 2.29px;
            }
    
            .cls-2, .cls-3, .cls-4 {
            stroke-linecap: round;
            }
    
            .cls-3, .cls-4 {
            fill: #e6e6e6;
            }

            .cls-3 {
                stroke: #ffffff;
                stroke-dasharray: 5,5;
            }
    
            .cls-4 {
            stroke: #000;
            stroke-dasharray: 0 0 2 3;
            }

            .activeCountry {
                fill: #7f9cb1;
            }

            .countryClicked {
                fill: $carnegie-blue;
            }
        }
    }

    :deep(.results-table) {
        .backToTop {
            font-size: 1.5em;
            margin-right: calc($gutter / 2);
            position: sticky;
            position: -webkit-sticky;
            text-align: right;
            top: 90vh;
            z-index: 11;

            a {
                border: 1px solid $medium-blue;
                border-radius: 50%;
                padding: calc($gutter / 6) calc($gutter / 2);
            }
        }
        table {
            margin: calc($gutter / 2);
            width: 95%;

            thead {
                position: sticky;
                position: -webkit-sticky;
                top: 50px;
                z-index: 11;
            }

            td,
            th {
                padding: calc($gutter / 2) calc($gutter / 3);
            }

            th {
                background-color: $white;
                font-family: $roman-normal-bold;
                font-size: 1.2rem;
                font-weight: normal;
                text-align: left;
                text-transform: uppercase;
                vertical-align: bottom;

                &.sortable {
                    color: $dark-blue;
                    cursor: pointer;

                    &.active {
                        &:after {
                            border-top: 10px solid $dark-blue;
                            border-left: 7px solid transparent;
                            border-right: 7px solid transparent;
                            content: "";
                            display: inline-block;
                            margin: 5px 0 0 5px;
                            padding-left: 2px;
                        }

                        &.reverse:after {
                            -webkit-transform: rotate(180deg);
                            -ms-transform: rotate(180deg);
                            transform: rotate(180deg);
                        }
                    }
                }

                .img {
                    background-repeat: no-repeat;
                    background-size: contain;
                    display: block;
                    height: calc($gutter * 2);
                    margin-left: -10px;
                    width: calc($gutter * 2);
                }
                &.infrastructure-pillar {
                    .img {background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-01.png);}
                }
                &.platforms-pillar {
                    .img {background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-02.png);}
                }
                &.skills-pillar {
                    .img {background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-03.png);}
                }
                &.innovation-pillar {
                    .img {background-image: url(https://ceipimg.s3.amazonaws.com/static/AfricaTracker_Icons-04.png);}
                }
            }

            tr {
                &:hover,
                &.open,
                &.secondary {
                    background-color: $light-gray;
                }
            }

            .protest-name {
                min-width: 9em;
            }
            .participants {
                min-width: 16%;
            }

            td {
                border-bottom: none;
                border-top: 1px solid $gray-light;
                @include type-body;
                & {
                    line-height: 1.3em;
                }

                .protest {
                    display: block;
                    font-family: $roman-italic-book;
                }

                &.bubbles {vertical-align: top;}
                &.reg-name {
                    strong {display: block; font-weight: bold;}
                }

                .foci {
                    display: block;
                    line-height: 1.1em;
                    > div {
                        border-radius: 15px;
                        color: $white;
                        display: inline-block;
                        font-size: 0.9rem;
                        margin-bottom: 5px;
                        margin-right: 7px;
                        padding: 3px 10px 4px 10px;
                        background-color: $dark-blue;
                        &.digital-infrastructure {background-color: $teal;}
                        &.digital-platform {background-color: $copper;}
                        &.digital-skills {background-color: $yellow;}
                        &.innovation-entrepreneurship {background-color: $light-blue;}
                        &:last-of-type {margin-bottom: 0;}
                    }
                    &.sub-cat {
                        > div.digital-infrastructure {background-color: rgba(0, 167, 157, .5);}
                        > div.digital-platform {background-color: rgba(0, 54, 94, .5);}
                        > div.digital-skills {background-color: rgba(251, 176, 64, .5);}
                        > div.innovation-entrepreneurship {background-color: rgba(39, 170, 225, .5);}
                    }
                    &.key-data div {
                        // > div.digital-infrastructure {background-color: rgba(0, 167, 157, .5);}
                        // > div.digital-platform {background-color: rgba(0, 54, 94, .5);}
                        // > div.digital-skills {background-color: rgba(251, 176, 64, .5);}
                        // > div.innovation-entrepreneurship {background-color: rgba(39, 170, 225, .5);}
                        // > div.multiple {background-image: linear-gradient(to right, #7fd3ce, #7f9aae, #fdd79f, #92d4f0)}
                        background-color: transparent;
                        border-radius: 0;
                        border-right: 1px solid $medium-gray-new;
                        color: $black;
                        display: inline-block;
                        margin: 0 calc($gutter / 6) 0 0;
                        padding: 0 calc($gutter / 6) 0 0;
                        &:last-of-type {border-right: none;}
                    }
                }

                .active .icon-plus-circle:before {
                    // @extend %ceip-icon;
                    // @extend %icon-minus-circle:before;
                }

                a.ceip-icon {
                    color: $light-blue;
                    line-height: 1.4em;

                    &:hover {
                        text-decoration: none;
                    }
                }

                input {
                    display: block;
                    margin: 0 auto;
                }

                &.cols > div {
                    font-family: $roman-normal-bold;
                    font-weight: normal;
                }
            }

            tr.secondary {
                strong {
                    display: block;
                    font-family: $roman-normal-bold;
                    font-weight: normal;
                    margin-bottom: calc($gutter / 6);
                }

                .details {
                    padding-bottom: calc($gutter / 2);
                    padding-right: $gutter;
                }

                td {
                    border-top: none;
                }

                &.hidden--new {
                    display: none;
                }
                &.hidden--new--active {
                    display: table-row;
                }
            }
        }
    }

    .download-buttons {
        margin: $gutter 0;

        .foreground {
            text-align: center;
        }

        .button {
            background-color: $light-blue-light;
            color: $dark-gray-new;
            cursor: pointer;
            font-size: 1rem;
            margin: 0 calc($gutter / 2);
            padding: calc($gutter / 2);
            text-transform: uppercase;

            &:after {
                content: none;
            }

            &:hover {
                background-color: $light-blue;
                color: $white;
            }
        }
    }
    .explanatory {
        @include type-body;
        em {font-family: $roman-italic-book;}
        h3 {margin: 2em 0 .5em 0}
        a {text-decoration: underline}
    }

    .filter-area {
        background-color: $white;
        height: 100px;
        // position: sticky;
        // position: -webkit-sticky;
        // top: 67px;
    }

    .clean-form.africa-regulations-country {
        .control-group {
            height: 400px;
            overflow-y: scroll;
        }
    }

    .filters {
        background-color: $white;
        display: block;
        font-size: 1rem;
        height: 70px;
        margin: $gutter;
        width: 100%;;

        em {
            display: block;
            font-family: $roman-italic-book;
            font-size: 1.4rem;
            font-weight: normal;
            line-height: 2.1em;
            margin: auto calc($gutter / 3) 0 $gutter;
            text-align: left;
        }
        
        select {
            border: 1px solid $black;
            display: inline-block;
        }

        .form-dropdown, .keyword-panel {
            background-color: $white;
            border: 1px solid $black;
            display: inline-block;
            font-size: 1.5rem;
            padding: 5px;
            position: relative;
            margin-right: $gutter;
            width: 15%;
            &.active {
                z-index: 20;
                &#subcategories, &#pillars {z-index: 19;}
            }
            &:first-of-type {margin-left: $gutter;}

            .form-opener {
                cursor: pointer;
                font-family: $roman-normal-bold;
            }
            .form-menu {
                display: none;
                &.active {
                    display: block; 
                    position: relative;
                    z-index: 20;
                }

                .checkbox {
                    display: block;
                    label {
                        display: inline-block; 
                        @include type-body;
                        & {
                            font-size: 0.9em;
                        }
                        input {margin-right: calc($gutter / 6);}
                    }

                    &.subcategory, &.key-data {
                        display: none;
                        &.active {display: block;}
                    }
                }
            }
        }

        .form-dropdown.inactive {
            background-color: $light-gray;
            color: $dark-gray-new;
            border-color: $medium-gray-new;

            .form-menu.active {display: none;}
        }

        form {
            float: left;
            margin-bottom: 0;

            fieldset {
                float: left;
                padding-left: calc($gutter / 2);
                text-align: left;
            }
        }

        .keyword-panel {
            margin-left: 30px;
            form {float: none;}
        }

        .button.blue {
            background-color: $medium-blue;
            border-radius: 10px;
            color: $white;
            cursor: pointer;
            display: inline-block;
            font-size: 1.4rem;
            padding: .8rem;
            &:after {content: none;}
            &:hover {background-color: $light-blue-light;}
        } 
    }


    /* Create a custom checkbox */
    .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 15px;
        width: 15px;
        background-color: #eee;
    }

    /* Create the checkmark/indicator (hidden when not checked) */
    .checkmark:after {
        content: "";
        position: absolute;
        display: none;
    }

    .africa-regulations-category {
        label {
            display: block;
            position: relative;
            padding-left: 25px;
            margin-bottom: .5em;
            cursor: pointer;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
        }

        label input {
            position: absolute;
            opacity: 0;
            cursor: pointer;
            height: 0;
            width: 0;
        }
        /* When the checkbox is checked, add a blue background */
        label input:checked ~ .checkmark {
            background-color: $medium-blue;
        }
        /* Show the checkmark when checked */
        label input:checked ~ .checkmark:after {
            display: block;
        }
        /* Style the checkmark/indicator */
        label .checkmark:after {
            left: 6px;
            top: 3px;
            width: 5px;
            height: 10px;
            border: solid white;
            border-width: 0 3px 3px 0;
            -webkit-transform: rotate(45deg);
            -ms-transform: rotate(45deg);
            transform: rotate(45deg);
        }
    }

    .search-dropdown {
        background-color: $white;
        border: 1px solid #ccc;
        border-top: none;
        border-radius: 3px;
        height: 100px;
        overflow-y: scroll;
        padding: calc($gutter / 2);
        position: absolute;
        z-index: 20;
    }

    .map {
        background-color: #ffffff;
        margin: $gutter 0 0 0;
        position: relative;
    }

    #countrySelect {
        position: absolute;
        left: 6%;
        top: 10px;
    }

    #chartdiv {
        // border: 1px solid $black;
        float: none;
        margin: 0 auto;
        max-width: 1600px;
        height: 650px;
    }

    .learn-more {
        p,
        li {
            @include type-body;
        }
        .button-big {
            margin: $gutter auto $gutter * 4 auto;
            width: 20%;
        }
    }

    .overlay-content div {
        max-height: 80vh;
        width: 570px;
        max-width: 570px;
        top: 30%;
        transform: translateY(-30%);
        overflow-y: scroll;
        h4 {margin-bottom: .5em}
        p,
        li {
            margin-bottom: 1em;
            @include type-body;
            & {
                font-weight: 100;
            }
            em {font-family: $roman-italic-book;}
            strong {font-family: $roman-normal-bold;}
            img {margin: 1em 0}
        }
        ol {
            list-style-type: decimal;
            margin-left: 1em;
        }
        li ul {margin-top: calc($gutter / 3)}
    }

    @include media-query(tablet) {
        .filters {
            margin:0;
            width: auto;

            .form-dropdown {width: auto;}
        }

        .filter-area {
            form fieldset label {
                margin-right: calc($gutter / 5);
                span.name {font-size: 0.7rem;}
            }
        }
    }

    @include media-query(tablet) {
        .zone-title {
            .container-title {
                margin-top: 40px;
                h2 {font-size: 2.5rem;}
                .foreground {
                    margin-left: 15px;
                    margin-right: 15px;
                }
            }
        }
        .boxes .foreground, .countries .foreground {
            margin-left: 15px;
            margin-right: 15px;

            h3 {font-size: 1.2rem;}
        }

        .description-section .foreground {
            margin-left: 15px;
            margin-right: 15px;
        }

        .filters {margin: 0;}

        .filter-area {
            height: 200px;
            top: 100px;
            .filters .other-filters, .filters .participants-filters {float: none;}
        }

        #chartdiv {
            height: 550px;
        }

        .map #aboutTab #aboutTabContent {
            background-color: $white;

            .about-overlay {
                .foreground {
                    margin: 0 $gutter;
                }
            }

            .top-section {
                height: 70px;
                padding: calc($gutter / 2) 0;
            }

            .close-button {
                display: block;
                float: left;
            }
        }

        .results-table table {
            margin: 0;
            width: auto;

            // thead {top: 300px}
            thead th {
                &.responsive-hide {
                    display: none;
                }
                &.participants {
                    min-width: 0;
                }
                &.protest-name {
                    min-width: auto;
                }
            }

            tr.secondary {
                background-color: $white;
                &.last {
                    border-bottom: 1px solid #e4e3dd;
                    td {border-top: none}
                }
            }

            td {
                &:nth-child(3) {
                    // padding-right: $gutter;
                    text-align: right;
                }

                &:nth-child(4) {
                    padding-right: 0;
                    text-align: left;
                }
                .foci > div {
                    float: left;
                }
            }
        }

        .filters {
            form {
                padding-left: 0;

                fieldset {
                    padding-left: 0;

                    label {
                        margin: 5px 10px 0 0;
                    }
                    label:first-of-type {margin-left: $gutter;}
                    strong {
                        line-height: 2.7em;
                    }
                }
            }
            form.climate-radio fieldset label:first-of-type {margin-left: 0;}
        }
    }

    @include media-query(tablet) {

        #chartdiv {
            height: 450px;
        }

        .map {
            #aboutTab {
                #aboutTabContent {
                    .top-section {
                        width: 100%;
                        height: 0;

                        .foreground {
                            display: inline-block;
                            float: none;
                            left: 10px;
                            top: 60px;
                            width: 22vw;
                        }
                    }

                    .tab-content {
                        .tab-pane {
                            .col {
                                clear: none;
                                float: none;
                                width: 100%;

                                .gutter-left,
                                .gutter-right {
                                    margin: 0;
                                }

                                &.col-30 {
                                    position: relative;
                                    top: 280px;
                                }

                                &.col-70 {
                                    position: relative;
                                    top: -170px;
                                }
                            }
                        }
                    }

                    .section {
                        &.about-overlay {
                            &.section {
                                margin-left: 22vw;
                                width: 68vw;
                            }
                        }
                    }

                    .close-button {
                        button.close-panel {
                        }
                    }
                }
            }
        }

        .filter-area {
            height: 200px;
        }
        .filters {
            .table-search {
                float: none;
                margin: 0 auto calc($gutter / 3) auto;
                width: 50%;
            }
        }

        .results-table {
            table {
                width: 90%;
                thead {
                    top: 300px;
                }

                td:nth-child(4) {
                    width: 1px;
                }
            }
        }
    }

    @include media-query(mobile) {
        .hide-mobile, .hide-true-mobile {
            display: none;
        }

        .overlay-content div {
            width: 270px;
            max-width: 270px;
        }

        .boxes .col-20 {
            margin-bottom: $gutter;

            .gutters {
                text-align: center;
                width: 100%;
            }

            .icon {
                background-position: center center;
                width: 100%;
            }

            &.skills h3 {width: auto;}
        }

        .country-info {
            td {font-size: 0.8em}
        }


        .filters .keyword-panel {
            margin-left: 0;
            width: 50%;
        }

        .filter-area {
            background-color: $white;
            height: 320px;
            position: relative;
            z-index: 12;
            top: 0px;

            .filters {
                height: fit-content;
                margin: 0 auto calc($gutter / 2);

                strong {
                    font-size: 0.8rem; 
                    margin-left: calc($gutter / 2);
                }

                form {
                    padding-left: calc($gutter / 2);

                    fieldset {

                        .control-group {
                            text-align: center;
                        }

                        strong {
                            display: none;
                            float: none;
                            margin: 0;
                        }

                        label {
                            padding: 2px 5px;
                            span.name {font-size: 0.6rem;}
                        }
                    }
                }

                .form-dropdown {
                    // display: block;
                    margin: calc($gutter / 6) 0 calc($gutter / 2) 0;
                    width: 100%;

                    .form-menu .checkbox {float: left;}
                }

                label.table-search {
                    width: auto;
                }

                .half {
                    width: 100%;

                    .keyword-panel {
                         float: left;
                         margin-top: calc($gutter / 3);
                         width: 100%;
                    }
                }

                .all-filters {
                    // float: right;
                    // width: 28%;
                    margin: 0 calc($gutter / 2);
                    padding-left: 0;

                    strong {display: none;}
                    form {padding-left: 0;}

                    fieldset {
                        padding-left: 0;
                        padding-right: 0;

                        label:first-of-type {margin-left: 0;}
                    }

                    em {margin: 0;}
                }

                .participants-filters  {
                    float: left;
                    width: 70%;
                }
                .other-filters {
                    clear: both;

                    fieldset {
                        label:first-of-type {margin-left: 0;}
                    }
                }
            }

            &.section {
                margin-top: 0;
            }
        }

        .reponsive-mobile-hide {
            display: none;
        }

        #chartdiv {
            height: 280px;
        }

        #countrySelect {
            left: 25%;
        }

        .map #aboutTab {
            h3 {
                top: 20vh;

                span {
                    display: none;
                }
            }

            #aboutTabContent {
                width: 98vw;

                .top-section {
                    float: left;
                    padding: $gutter * 1.5 0;
                    width: 100%;

                    .foreground {
                        left: $gutter * 2;
                        top: -25px;
                        width: 74vw;
                    }
                }

                .nav-tabs li a {
                    font-size: 0.8rem;
                    padding: 0px 15px 5px 15px;
                }

                .about-overlay .foreground {
                    margin-right: 5px;
                }

                .section.about-overlay.section {
                    clear: both;
                    margin-left: 0;
                    float: none;
                    width: 100%;
                }
            }
        }

        .results-table {
            margin-bottom: 0;
            :deep(table) {
                margin: 0;
                width: 100%;
                thead {
                    top: 50px;
                    .participants {display: none;}
                }

                td:nth-child(3) {
                    padding-right: 15px;
                }
                td.cols {
                    .col.col-20 {
                        width: 20% !important;
                        float: left;
                    }
                    .col.col-80 {
                        width: 80% !important;
                        float: left;
                    }
                }
                .classification {
                    display: block;
                    font-size: 0.9em;
                }
                tr.secondary {
                    background-color: $white;
                    .details {clear: both;}
                    .foci {margin-top: calc($gutter / 3);}
                    &.last {border-bottom: 1px solid #e4e3dd;}
                }
            }
        }

        .learn-more .button-big {
            width: 60%;
        }

        .download-buttons {
            background-color: $white;
            margin: 0;
            padding: $gutter 0;
            position: relative;
            .button:first-child {
                margin-bottom: calc($gutter / 2);
            }
        }

        .ceip-overlay__inner {
            margin-left: 5%;
            max-height: 95%;
            max-width: 90%;
            top: 30%;
        }
    }
    @include media-query(phone) { 
        th.outcomes.responsive-hide {opacity: 0;}
    }
}



</style>